import LoopMiniSelect from "./BookingSelect";
import {t, useLanguage, interfaceLocale} from '../i18n';
import {wallDate} from '../utils/businessTime';
import {PhoneInput as InternationalPhoneInput} from "./PhoneKit";
import {callCallableNativeSafe} from '../utils/callableNativeSafe';
import {useSessionPrograms, SessionProgramPicker, selectSessionProgram, CustomerSessionPrograms} from "./SessionPrograms";
import { isSoloBookingBusiness } from "../utils/soloBookingBusiness";
import {scheduleSnapshot} from "../adminCalendar/scheduleExceptions";
import "./BookingReservationWorkspace.css";
import BookingCollectionEditor from "./BookingCollectionEditor";
import BookingDateInput from "./BookingDateInput";
import BookingModalTimeInput from "./BookingTimeInput";
// LOOP CRM FLAWLESS DEDUPE PREVENTION R29 — name-only bookings reuse compatible CRM customers and block true identity conflicts.
// LOOP BOOKING IOS WORKER CREATE STABILITY V17 — preserve customer selection during native hydration; worker create defers CRM safely.
// LOOP BOOKING MOBILE WORKER CUSTOMER + COMPACT UX V13 — worker customer defers CRM to function; mobile modal/keyboard compact.
// LOOP CUSTOMER PICKER MOBILE LAYERS V94 — sane customer layering + compact mobile picker.
// LOOP WORKER BOOKING CUSTOMER IDENTITY V8C — workers defer customer identity resolution to backend.
// LOOP WORKER BOOKING CUSTOMER IDENTITY V8 — preserve selected customer identity for worker-created notifications.
// src/components/BookingCreateModal.jsx
// LOOP CUSTOMER DOCUMENT ID INVARIANT V20 — quick-create and picker always retain canonical customer ids; nested errors cannot hide behind the modal.
// LOOP CUSTOMER IDENTITY BULLETPROOF V19 — contact-matched CRM customers win and manual booking names do not silently rename existing contacts.
// LOOP CUSTOMER NEW CTA V5.0 — typed new-customer result opens quick-create modal
// LOOP CUSTOMER PROFILE EDIT V4.8 — edit saved non-Loop business customers from Ver perfil
// LOOP CUSTOMER PROFILE LIVE DRAFT V4.7 — Ver perfil follows the active customer draft
// LOOP IDENTITY PRIORITY V4.4 — canonical ProfilePage snapshot wins over stale business customer snapshots
// LOOP PHASE 2.9 — Inventory automation metadata for reservation lifecycle
// LOOP CREATE MODAL — FINAL PROPOSAL LAYOUT HOTFIX
// LOOP CREATE MODAL — BRAND COLOR POLISH HOTFIX
// LOOP CREATE MODAL — FINAL NOISE REDUCTION PAYMENT HOTFIX
// LOOP CREATE MODAL — FINAL UX POLISH ACCORDIONS INPUTS HOTFIX
// LOOP CREATE MODAL — SERVICE COMBOBOX PAYMENT POPUP HOTFIX
// LOOP CREATE MODAL — TIP EDIT RESCHEDULE FIX HOTFIX
// LOOP CREATE MODAL — RESPONSIVE EDITOR FLOW HOTFIX
// LOOP CREATE MODAL — EFFICIENCY SCROLL RETAIL HOTFIX
// LOOP CREATE MODAL — TOP ACTIONS DATE STATUS HOTFIX
// LOOP PHASE 2.5D — payment/status guardrails
// LOOP D2.9.3J.6 — status dropdown opens down above content
// LOOP D2.9.3J.7 — status chip alignment + options closed by default
// LOOP BOOKING MOBILE DATE ACTIONS V50 — move availability/change actions into right rail.
// LOOP BOOKING MOBILE DATE ACTIONS V51 — lower availability pill in right action rail.
// LOOP BOOKING PAYMENT V52 — inline compact payment + cleaner commission header.
// LOOP BOOKING MOBILE SERVICE DATE V49 — compact mobile service/date card.
// LOOP BOOKING MOBILE HEADER V48 — status chip cleanup + centered mobile status picker.
// LOOP BOOKING MOBILE HEADER V47 — clean header + compact status/payment/membership chips.
// LOOP BOOKING UX V2 A2.6 — scalable pickers + agenda/professional UX.
// LOOP BOOKING NAME-ONLY CUSTOMER V64 — allow manual reservations without phone/email, even when a temporary/new customerId exists.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { addDoc, collection, doc, getDocs, serverTimestamp, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import fsClient from "../data/firestoreClient";
import { isIosCapacitor } from "../utils/nativeHttp";
import { getFunctions, httpsCallable } from "firebase/functions";
import BookingPaymentHistory from "./BookingPaymentHistory";
import AccessibleModal from "./AccessibleModal";
import { bookingCalendarDate as bookingScheduledAtToDate } from "../utils/dateUtils";
import CustomerPicker from "./CustomerPicker";
import { buildCustomer360Snapshot, resolveCustomerIdentityForBooking, findOrCreateCustomerForBusiness } from "../utils/customerCrm";
import PanamaLocationPickerModal from "./PanamaLocationPickerModal";
import { normalizeLoopBusinessSettings, normalizeWorkerPermissions } from "../adminCalendar/workerPermissions";
import { reprocessBookingLoyaltyAction, summarizeReprocessResult, reverseLoyaltyRedemptionAction, summarizeReverseLoyaltyRedemptionResult, reprocessBookingMembershipBenefitAction, summarizeMembershipBenefitResult } from "../adminCalendar/bookingActions";
import { cleanPhoneNationalInput, formatPhoneDisplay, maskPhoneDisplay, normalizePhoneToE164 } from "../utils/phoneUtils";

// LOOP BOOKING MEMBERSHIP FEATURE FLAG V1
// Keep membership code available for later, but fully hide/disable it inside
// BookingCreateModal while memberships are paused.
const LOOP_BOOKING_MEMBERSHIPS_ENABLED = false;

const LOOP_BOOKING_PAYMENT_METHOD_OPTIONS = [
  { value: "cash", label: "Efectivo" },
  { value: "yappy", label: "Yappy" },
  { value: "card", label: "Tarjeta" },
  { value: "transfer", label: "Transferencia" },
  ...(LOOP_BOOKING_MEMBERSHIPS_ENABLED ? [{ value: "membership", label: "Membresía" }] : []),
  { value: "loyalty_reward", label: "Fidelidad" },
  { value: "courtesy", label: "Cortesía" },
  { value: "other", label: "Otro" },
];

const LOOP_BOOKING_COVERED_PAYMENT_METHODS = new Set([
  ...(LOOP_BOOKING_MEMBERSHIPS_ENABLED ? ["membership"] : []),
  "loyalty_reward",
  "courtesy",
]);


function bookingModalMembershipSummary(booking = {}, selectedBenefit = null) {
  const applied = booking?.membershipApplied === true || booking?.membershipBenefitApplied === true || Boolean(selectedBenefit || booking?.customerMembershipId || booking?.membershipId);
  if (!applied) return null;
  const planName = String(booking.membershipPlanName || selectedBenefit?.plan?.name || selectedBenefit?.membership?.planName || "Membresía").trim() || "Membresía";
  const type = String(booking.membershipBenefitType || selectedBenefit?.type || (Number(booking.membershipCreditsUsed || 0) > 0 ? "credit" : "discount")).toLowerCase();
  const credits = Math.max(0, Number(booking.membershipCreditsUsed || selectedBenefit?.membershipCreditsUsed || 0) || 0);
  const discount = Math.max(0, Number(booking.membershipDiscountPercent || selectedBenefit?.discountPercent || 0) || 0);
  const processed = booking.membershipBenefitProcessed === true || booking.membershipBenefitStatus === "processed" || booking.membershipBenefitStatus === "credit_used";
  const label = booking.membershipBenefitLabel || selectedBenefit?.label || (type === "discount" ? `${discount}% descuento · ${planName}` : `${credits || 1} crédito · ${planName}`);
  return { planName, label, stateLabel: processed ? (type === "discount" ? "Descuento aplicado" : "Crédito descontado") : "Pendiente al completar/pagar" };
}

function normalizeText(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function bookingModalCustomerRecordId(customer = {}, fallback = "") {
  return String(
    fallback ||
    customer?.__documentId ||
    customer?.docId ||
    customer?.id ||
    customer?.customerId ||
    customer?.clientId ||
    ""
  ).trim();
}

// LOOP BOOKING NAME-ONLY CUSTOMER V64
// Real-world businesses often create walk-in reservations with only a name.
// A local CRM id/customerId is NOT a strong identity by itself because typed
// new-customer flows can create temporary ids before there is email/WhatsApp.
// Only email, phone/WhatsApp or a linked Loop uid should trigger strict de-dupe.
function bookingModalStrongCustomerIdentity(input = {}) {
  const snapshot = input.customerSnapshot && typeof input.customerSnapshot === "object" ? input.customerSnapshot : {};

  const linked = [
    input.linkedUserId,
    input.linkedUserUid,
    input.userId,
    input.userUid,
    snapshot.linkedUserId,
    snapshot.linkedUserUid,
    snapshot.userId,
    snapshot.userUid,
    snapshot.platformUserId,
    snapshot.platformUserUid,
  ].map((x) => String(x || "").trim()).filter(Boolean);

  const email = String(
    input.email ||
    input.customerEmail ||
    snapshot.emailNormalized ||
    snapshot.email ||
    snapshot.customerEmail ||
    ""
  ).trim();

  const phoneDigits = String(
    input.phone ||
    input.customerPhone ||
    snapshot.phoneNormalized ||
    snapshot.phoneE164 ||
    snapshot.phone ||
    snapshot.customerPhone ||
    snapshot.whatsapp ||
    snapshot.whatsApp ||
    ""
  ).replace(/\D/g, "");

  return Boolean(linked.length || email || phoneDigits.length >= 6);
}

function bookingModalNameOnlyCustomerSnapshot({ businessId, name, customerSnapshot = null, customerId = null, source = "booking_manual_name_only" } = {}) {
  const cleanName = String(name || "").trim() || "Cliente";
  const nowIso = new Date().toISOString();
  const previous = customerSnapshot && typeof customerSnapshot === "object" ? customerSnapshot : {};
  const id = String(customerId || previous.id || previous.customerId || previous.clientId || "").trim() || null;
  const nameKey = normalizeText(cleanName);

  return {
    ...previous,
    id,
    customerId: id,
    clientId: id,
    businessId: businessId || previous.businessId || null,
    name: cleanName,
    customerName: cleanName,
    displayName: cleanName,
    fullName: cleanName,
    nameKey,
    searchableName: nameKey,
    searchText: nameKey,
    phone: "",
    customerPhone: "",
    phoneDisplay: "",
    phoneNormalized: "",
    phoneE164: "",
    customerPhoneNormalized: "",
    whatsapp: "",
    whatsApp: "",
    email: "",
    customerEmail: "",
    emailNormalized: "",
    hasUserAccount: false,
    linkedUserId: null,
    linkedUserUid: null,
    userId: null,
    userUid: null,
    source,
    identityStatus: "manual_name_only",
    identityContactSource: source,
    identityUnverified: true,
    nameOnlyCustomer: true,
    crmNameOnly: true,
    updatedAt: nowIso,
  };
}

async function bookingModalCreateNameOnlyCustomerRecord(dbClient, businessId, { name, customerSnapshot = null, createdByRole = "booking_create" } = {}) {
  const cleanName = String(name || "").trim() || "Cliente";
  const base = bookingModalNameOnlyCustomerSnapshot({
    businessId,
    name: cleanName,
    customerSnapshot,
    customerId: customerSnapshot?.id || customerSnapshot?.customerId || customerSnapshot?.clientId || null,
  });

  if (!businessId) return base;

  try {
    // LOOP CRM DEDUPE R29:
    // Route name-only creation through the shared identity resolver. It now
    // reuses the best compatible exact-name profile even when several old
    // duplicate shells exist. A true same-name identity conflict is blocked
    // instead of silently creating one more customer.
    const resolved = await findOrCreateCustomerForBusiness(dbClient, businessId, {
      ...base,
      name: cleanName,
      customerName: cleanName,
      displayName: cleanName,
      fullName: cleanName,
      phone: "",
      customerPhone: "",
      email: "",
      customerEmail: "",
      source: base.source || "booking_manual_name_only_r29",
      createdByRole,
    }, {
      incrementBookings: false,
      linkPlatformUser: false,
      allowIdentityConflict: false,
      updateExistingCustomerName: false,
      createdByRole,
    });

    const resolvedId = bookingModalCustomerRecordId(resolved);
    return {
      ...base,
      ...(resolved || {}),
      __documentId: resolvedId || null,
      id: resolvedId || null,
      customerId: resolvedId || null,
      clientId: resolvedId || null,
      existed: resolved?.existed === true,
      created: resolved?.existed !== true && Boolean(resolvedId),
      identityMatchedBy: resolved?.identityMatchedBy || resolved?.identityStatus || "name_r29",
      nameOnlyCustomer: true,
      crmNameOnly: true,
    };
  } catch (err) {
    if (
      err?.code === "customer_identity_conflict" ||
      err?.code === "customer_name_identity_conflict"
    ) {
      const conflictError = new Error(
        err?.message ||
        "Hay varios clientes con este nombre y datos diferentes. Selecciona el perfil correcto antes de continuar."
      );
      conflictError.code = "customer_name_identity_conflict";
      conflictError.conflict = err?.conflict || null;
      throw conflictError;
    }

    console.warn("[BookingCreateModal] name-only customer CRM resolution skipped; booking can continue", err);
    return {
      ...base,
      id: null,
      customerId: null,
      clientId: null,
      crmSkipped: true,
      crmSkippedReason: String(err?.message || err || "customer_resolve_failed"),
    };
  }
}

function isEventBusinessDoc(businessDoc, forced = false) {
  if (forced) return true;
  if (!businessDoc) return false;
  const text = [
    businessDoc.businessMode,
    businessDoc.businessModel,
    businessDoc.businessType,
    businessDoc.type,
    businessDoc.category,
    businessDoc.vertical,
  ].map(normalizeText).join(" ");
  return text.includes("event") || text.includes("evento") || text.includes("fotografia") || text.includes("animacion");
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}


function bookingModalInventoryUsageRows(service = {}) {
  return safeArray(service?.inventoryUsage || service?.inventoryConsumption || service?.materialsUsage || service?.materials)
    .map((row, idx) => {
      const itemId = String(row?.itemId || row?.inventoryItemId || row?.id || "").trim();
      const quantityUsed = asMoneyNumber(row?.quantityUsed ?? row?.quantity ?? row?.qty, 0);
      if (!itemId || quantityUsed <= 0) return null;
      return { id: row?.id || `usage-${itemId}-${idx}`, itemId, quantityUsed };
    })
    .filter(Boolean);
}

function bookingModalInventoryDeductionStatusForDraft({ selectedService, initialBooking, autoDeductEnabled }) {
  const existing = String(initialBooking?.inventoryDeductionStatus || "").trim().toLowerCase();
  if (existing === "deducted" || initialBooking?.inventoryDeductedAt) return "deducted";
  if (existing === "error" || existing === "failed") return "error";
  if (!autoDeductEnabled) return "not_required";
  return bookingModalInventoryUsageRows(selectedService).length ? (existing || "pending") : "not_required";
}

function asMoneyNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function formatMoneyInputValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "";
  return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(2);
}

function paymentStatusLabel(value) {
  const s = String(value || "pending").trim().toLowerCase();
  if (["paid", "pagado", "completed", "complete"].includes(s)) return "Pagado";
  if (["partial", "parcial"].includes(s)) return "Parcial";
  if (["covered", "cubierto", "membership", "loyalty_reward", "fidelidad", "courtesy"].includes(s)) return "Cubierto";
  if (["refunded", "refund", "reembolsado"].includes(s)) return "Reembolsado";
  if (["cancelled", "canceled", "cancelado", "cancelada"].includes(s)) return "Cancelado";
  return "Pendiente";
}

function paymentMethodLabel(value) {
  const s = String(value || "").trim().toLowerCase();
  if (["cash", "efectivo"].includes(s)) return "Efectivo";
  if (["yappy", "japi", "yappi"].includes(s)) return "Yappy";
  if (["card", "tarjeta", "credit_card", "debit_card"].includes(s)) return "Tarjeta";
  if (["transfer", "transferencia", "bank_transfer"].includes(s)) return "Transferencia";
  if (["membership", "membresia", "membresía"].includes(s)) return "Membresía";
  if (["loyalty_reward", "loyalty", "fidelidad", "reward", "recompensa"].includes(s)) return "Fidelidad";
  if (["courtesy", "cortesia", "cortesía", "free", "gratis"].includes(s)) return "Cortesía";
  if (["other", "otro"].includes(s)) return "Otro";
  return "Sin método";
}

function normalizeCommissionType(value) {
  const raw = String(value || "none").trim().toLowerCase();
  if (["percentage", "percent", "%", "porcentaje"].includes(raw)) return "percentage";
  if (["fixed", "flat", "amount", "monto"].includes(raw)) return "fixed";
  return "none";
}

function calculateRetailWorkerPayout(row = {}) {
  const quantity = asMoneyNumber(row.quantity, 0);
  const unitPrice = asMoneyNumber(row.unitPrice, 0);
  const lineTotal = Math.max(0, quantity * unitPrice);
  const type = normalizeCommissionType(row.workerCommissionType || row.commissionType);
  const value = asMoneyNumber(row.workerCommissionValue ?? row.commissionValue, 0);
  if (type === "percentage") return Math.max(0, lineTotal * (value / 100));
  if (type === "fixed") return Math.max(0, value);
  return 0;
}

function getEmployeeDefaultCommission(employee = {}) {
  const type = normalizeCommissionType(employee.defaultCommissionType || employee.commissionType || employee.workerCommissionType);
  const value = asMoneyNumber(employee.defaultCommissionValue ?? employee.commissionValue ?? employee.workerCommissionValue ?? 0, 0);
  return {
    type: value > 0 ? type : "none",
    value: value > 0 ? value : 0,
    includesTips: Boolean(employee.commissionIncludesTips),
    onlyCompleted: employee.commissionOnlyCompleted !== false,
  };
}

function optionId(value, fallback) {
  return String(value?.id || value?.key || value?.value || value?.name || value?.label || fallback || "")
    .trim()
    .replace(/\s+/g, "_")
    .toLowerCase();
}


function normalizeDurationPricingTiersForBooking(tiers) {
  return (Array.isArray(tiers) ? tiers : [])
    .map((tier, idx) => {
      const hours = Number(tier?.hours ?? tier?.durationHours ?? 0);
      const priceRaw = tier?.price ?? tier?.amount ?? tier?.totalPrice;
      const price = priceRaw === "" || priceRaw == null ? 0 : Number(priceRaw || 0);
      if (!Number.isFinite(hours) || hours <= 0) return null;
      return {
        id: tier?.id || `duration-${String(hours).replace(".", "_")}-${idx}`,
        label: Number.isInteger(hours) ? `${hours} ${hours === 1 ? "hora" : "horas"}` : `${String(hours).replace(".", ",")} horas`,
        price: Number.isFinite(price) ? price : 0,
        durationMinutes: Math.round(hours * 60),
        visitHours: hours,
        pricingMode: "duration_tiers",
      };
    })
    .filter(Boolean)
    .sort((a, b) => Number(a.visitHours || 0) - Number(b.visitHours || 0));
}

function normalizeVisitOptions(service, eventMode = false) {
  const durationTiers = normalizeDurationPricingTiersForBooking(service?.durationPricingTiers);
  if (service?.pricingMode === "duration_tiers" && durationTiers.length) {
    return durationTiers;
  }

  const raw =
    safeArray(service?.visitTypeOptions).length ? safeArray(service.visitTypeOptions) :
    safeArray(service?.eventConfig?.visitTypes).length ? safeArray(service.eventConfig.visitTypes) :
    safeArray(service?.eventOptions?.visitTypes).length ? safeArray(service.eventOptions.visitTypes) :
    safeArray(service?.packageTypes).length ? safeArray(service.packageTypes) :
    [];

  const basePrice = asMoneyNumber(service?.price ?? service?.basePrice ?? service?.durationPrice, 0);
  const baseDuration = asMoneyNumber(service?.duration || service?.durationMinutes || service?.defaultDuration, 60);

  // Standard service businesses use the service base price. Some historical
  // service documents still carry event-only visitTypeOptions with price 0;
  // those options must never override the normal service price.
  if (!eventMode) {
    return [{
      id: "standard",
      label: "Servicio estándar",
      price: basePrice,
      durationMinutes: baseDuration,
      visitHours: Math.max(1, Math.round(baseDuration / 60)),
    }];
  }

  if (!raw.length) {
    return [{
      id: "standard",
      label: "Paquete estándar",
      price: basePrice,
      durationMinutes: baseDuration,
      visitHours: Math.max(1, Math.round(baseDuration / 60)),
    }];
  }

  return raw.map((item, idx) => {
    if (typeof item === "string") {
      return {
        id: optionId({ label: item }, idx),
        label: item,
        price: basePrice,
        durationMinutes: baseDuration,
        visitHours: Math.max(1, Math.round(baseDuration / 60)),
      };
    }

    const durationMinutes = asMoneyNumber(item.durationMinutes ?? item.duration ?? item.minutes, baseDuration);
    const visitHours = asMoneyNumber(item.visitHours ?? item.hours, Math.max(1, Math.round(durationMinutes / 60)));
    return {
      id: optionId(item, idx),
      label: item.label || item.name || item.title || `Opción ${idx + 1}`,
      price: asMoneyNumber(item.price ?? item.basePrice ?? item.amount, basePrice),
      durationMinutes,
      visitHours,
    };
  });
}

function normalizeExtraOptions(service) {
  const raw =
    safeArray(service?.selectableCharacters).length ? safeArray(service.selectableCharacters) :
    safeArray(service?.characters).length ? safeArray(service.characters) :
    safeArray(service?.eventConfig?.selectableCharacters).length ? safeArray(service.eventConfig.selectableCharacters) :
    safeArray(service?.eventOptions?.selectableCharacters).length ? safeArray(service.eventOptions.selectableCharacters) :
    safeArray(service?.extras).length ? safeArray(service.extras) :
    safeArray(service?.addons).length ? safeArray(service.addons) :
    [];

  return raw.map((item, idx) => {
    if (typeof item === "string") {
      return { id: optionId({ label: item }, idx), name: item, price: 0 };
    }

    return {
      ...item,
      id: optionId(item, idx),
      name: item.name || item.label || item.title || `Extra ${idx + 1}`,
      price: asMoneyNumber(item.price ?? item.extraPrice ?? item.amount, 0),
    };
  });
}

function isSelectableOperationalStaff(emp, eventMode = false) {
  if (!emp) return false;
  // En eventos/noWorkers, empleados que quedaron disabled=true por lógica anterior
  // igual deben poder asignarse como equipo operativo.
  if (eventMode) return true;
  return emp.disabled !== true;
}


function getCoverageAreasForBooking(businessDoc) {
  const advanced = Array.isArray(businessDoc?.serviceAreasAdvanced)
    ? businessDoc.serviceAreasAdvanced
    : Array.isArray(businessDoc?.deliveryAreas)
      ? businessDoc.deliveryAreas
      : [];
  if (advanced.length) return advanced;

  const legacy = Array.isArray(businessDoc?.serviceAreas)
    ? businessDoc.serviceAreas
    : String(businessDoc?.coverageArea || businessDoc?.serviceArea || "").split(",");

  return legacy.map((label) => ({ label: String(label || "").trim(), level: "zone", extraFee: 0 })).filter((x) => x.label);
}

function areaMatchesLocation(area, loc) {
  if (!area || !loc) return false;
  const level = area.level || (area.zone ? "zone" : area.corregimiento ? "corregimiento" : area.district ? "district" : area.province ? "province" : "");
  if (level === "province") return area.province && area.province === loc.province;
  if (level === "district") return area.province === loc.province && area.district === loc.district;
  if (level === "corregimiento") return area.province === loc.province && area.district === loc.district && area.corregimiento === loc.corregimiento;
  if (level === "zone") {
    if (area.zone) return area.province === loc.province && area.district === loc.district && area.corregimiento === loc.corregimiento && area.zone === loc.zone;
    return String(area.label || "").toLowerCase() === String(loc.label || "").toLowerCase();
  }
  return false;
}

function findMatchingCoverageArea(businessDoc, loc) {
  if (!loc) return null;
  const areas = getCoverageAreasForBooking(businessDoc);
  const matches = areas.filter((area) => areaMatchesLocation(area, loc));
  const score = (area) => area.zone ? 4 : area.corregimiento ? 3 : area.district ? 2 : area.province ? 1 : 0;
  return matches.sort((a, b) => score(b) - score(a))[0] || null;
}


function Customer360BookingCard({ snapshot }) {
  if (!snapshot) return null;
  const insight = snapshot.customer360 || buildCustomer360Snapshot(snapshot);
  const total = Number(insight.totalBookings || snapshot.totalBookings || 0);
  const avg = Number(insight.averageTicket || 0);
  const days = insight.daysSinceLastVisit;
  return (
    <div className="booking-customer360-card">
      <div>
        <span>Customer 360</span>
        <strong>{insight.segment}</strong>
      </div>
      <div>
        <span>{t("Historial")}</span>
        <strong>{total}{" " + t("reserva")}{total === 1 ? "" : "s"}</strong>
      </div>
      <div>
        <span>{t("Última visita")}</span>
        <strong>{days == null ? (insight.lastBookingAtText || "Sin data") : `${days}d`}</strong>
      </div>
      <div>
        <span>{t("Ticket prom.")}</span>
        <strong>{avg > 0 ? `$${avg % 1 === 0 ? avg.toFixed(0) : avg.toFixed(2)}` : "—"}</strong>
      </div>
      <p>{insight.nextAction}</p>
    </div>
  );
}

function bookingNeedsCustomerLocation(businessDoc) {
  const mode = String(businessDoc?.serviceMode || "").toLowerCase();
  return mode === "delivery" || mode === "hybrid";
}


const LOOP_BOOKING_CANCELLED_STATUSES = new Set(["cancelled", "canceled", "cancelada", "rejected", "declined", "rechazada", "no_show", "noshow"]);
const BOOKING_MODAL_DELETE_SENTINEL = "__delete_reservation__";


function bookingModalStatus(raw = {}) {
  return String(raw?.status || raw?.confirmationStatus || raw?.state || "").trim().toLowerCase();
}

function bookingModalIsCancelled(raw = {}) {
  return LOOP_BOOKING_CANCELLED_STATUSES.has(bookingModalStatus(raw));
}

function bookingModalId(raw = {}, fallback = "") {
  return String(raw?.id || raw?.bookingId || raw?.docId || raw?.uid || fallback || "").trim();
}

// LOOP BOOKING UX V2 A2.3A — schedule self-conflict hardening.
// Calendar wrappers can expose a different event id while their raw booking still
// points to the same Firestore reservation. Compare all stable booking identity
// candidates instead of trusting only the wrapper id.
function bookingModalIdentityIds(raw = {}, fallback = "") {
  const out = new Set();
  [
    raw?.id,
    raw?.bookingId,
    raw?.bookingID,
    raw?.docId,
    raw?.documentId,
    raw?.reservationId,
    raw?.reservationID,
    raw?.originalBookingId,
    raw?.sourceBookingId,
    raw?.uid,
    fallback,
  ].forEach((value) => {
    const clean = String(value || "").trim();
    if (clean) out.add(clean);
  });
  return out;
}

function bookingModalSameBookingIdentity(item = null, initialBooking = null, initialBookingId = "") {
  if (!item) return false;
  const currentIds = bookingModalIdentityIds(initialBooking || {}, initialBookingId);
  if (!currentIds.size) return false;

  const candidateIds = new Set([
    ...(item.identityIds instanceof Set ? Array.from(item.identityIds) : []),
    ...Array.from(bookingModalIdentityIds(item.raw || {}, item.id || "")),
  ]);

  for (const id of candidateIds) {
    if (currentIds.has(id)) return true;
  }
  return false;
}

function bookingModalRawFromEventLike(item) {
  const extended = item?.extendedProps || {};
  return extended.raw || extended.booking || item?.raw || item?.booking || item || {};
}

function bookingModalParseDate(raw = {}) {
  try {
    const byUtil = bookingScheduledAtToDate(raw);
    if (byUtil && !Number.isNaN(byUtil.getTime())) return byUtil;
  } catch (_) {}

  const candidates = [raw?.start, raw?.startAt, raw?.scheduledAt, raw?.dateTime];
  for (const value of candidates) {
    try {
      if (!value) continue;
      const d = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
      if (d && !Number.isNaN(d.getTime())) return d;
    } catch (_) {}
  }

  try {
    const date = String(raw?.date || "").trim();
    const time = /^\d{1,2}:\d{2}$/.test(String(raw?.time || "")) ? String(raw.time) : "00:00";
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const d = new Date(`${date}T${time}:00`);
      if (!Number.isNaN(d.getTime())) return d;
    }
  } catch (_) {}

  return null;
}

function bookingModalEndDate(raw = {}, start = null) {
  const candidates = [raw?.end, raw?.endAt, raw?.endsAt];
  for (const value of candidates) {
    try {
      if (!value) continue;
      const d = typeof value?.toDate === "function" ? value.toDate() : new Date(value);
      if (d && !Number.isNaN(d.getTime())) return wallDate(d,raw);
    } catch (_) {}
  }
  if (!start) return null;
  const mins = Number(raw?.duration ?? raw?.durationMins ?? raw?.minutes ?? raw?.visitMinutes ?? 60);
  const safeMins = Number.isFinite(mins) && mins > 0 ? mins : 60;
  return new Date(start.getTime() + safeMins * 60000);
}

function bookingModalResourceIds(raw = {}, eventLike = null) {
  const ids = new Set();
  const direct = [
    raw?.resourceId,
    raw?.employeeId,
    raw?.workerId,
    raw?.assignedEmployeeId,
    eventLike?.resourceId,
    eventLike?.extendedProps?.resourceId,
    eventLike?._def?.resourceIds?.[0],
  ];
  direct.forEach((id) => { if (id) ids.add(String(id)); });
  if (Array.isArray(raw?.assignedEmployeeIds)) raw.assignedEmployeeIds.forEach((id) => { if (id) ids.add(String(id)); });
  if (Array.isArray(raw?.assignedEmployees)) {
    raw.assignedEmployees.forEach((emp) => {
      const id = emp?.id || emp?.employeeId || emp?.uid;
      if (id) ids.add(String(id));
    });
  }
  return ids;
}

function bookingModalSameDay(a, b) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function bookingModalBuildDateAtTime(baseDate, hhmm) {
  const match = String(hhmm || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!baseDate || !match) return null;
  const d = new Date(baseDate);
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return null;
  d.setHours(hh, mm, 0, 0);
  return d;
}

function bookingModalFormatHHMM(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function bookingModalNormalizeHHMM(value, fallback = "10:00") {
  const match = String(value || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return fallback;
  const hh = Number(match[1]);
  const mm = Number(match[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) return fallback;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function bookingModalFormatTimeLabel(value, timeFormat = "24h") {
  const normalized = bookingModalNormalizeHHMM(value, "");
  if (!normalized) return "";
  const [hhRaw, mmRaw] = normalized.split(":");
  const hh = Number(hhRaw);
  const mm = String(mmRaw || "00").padStart(2, "0");
  if (timeFormat === "12h") {
    const suffix = hh >= 12 ? "PM" : "AM";
    const hour12 = hh % 12 || 12;
    return `${hour12}:${mm} ${suffix}`;
  }
  return normalized;
}








function bookingModalFormatRange(start, end, timeFormat = "24h") {
  return `${bookingModalFormatTimeLabel(bookingModalFormatHHMM(start), timeFormat)} - ${bookingModalFormatTimeLabel(bookingModalFormatHHMM(end), timeFormat)}`;
}

function bookingModalOverlaps(aStart, aEnd, bStart, bEnd) {
  if (!aStart || !aEnd || !bStart || !bEnd) return false;
  return aStart < bEnd && aEnd > bStart;
}

function bookingModalNormalizeExisting(items = [], source = "events") {
  return (Array.isArray(items) ? items : [])
    .map((item, index) => {
      const raw = bookingModalRawFromEventLike(item);
      const start = bookingModalParseDate({ ...raw, start: item?.start || raw?.start });
      const end = bookingModalEndDate({ ...raw, end: item?.end || raw?.end }, start);
      if (!start || !end) return null;
      return {
        id: bookingModalId(raw, item?.id || `${source}-${index}`),
        identityIds: bookingModalIdentityIds(raw, item?.id || ""),
        raw,
        source,
        start,
        end,
        resourceIds: bookingModalResourceIds(raw, item),
        title: raw?.serviceName || raw?.service || raw?.title || item?.title || raw?.customerName || "Reserva",
        customerName: raw?.customerName || raw?.clientName || raw?.name || "Cliente",
      };
    })
    .filter(Boolean);
}

function bookingModalMinutesFromHHMM(value) {
  const m = String(value || "").match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
  return hh * 60 + mm;
}

function bookingModalHHMMFromMinutes(value) {
  const mins = Math.max(0, Math.min(24 * 60 - 1, Number(value || 0)));
  const hh = Math.floor(mins / 60);
  const mm = mins % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function bookingModalExplicitAvailabilityRanges(employee, date) {
  const avail = employee?.availability && typeof employee.availability === "object" ? employee.availability : null;
  if (!avail || !date) return [];
  const keys = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const ranges = Array.isArray(avail[keys[new Date(date).getDay()]]) ? avail[keys[new Date(date).getDay()]] : [];
  return ranges
    .map((r) => ({ from: bookingModalMinutesFromHHMM(r?.from || r?.start), to: bookingModalMinutesFromHHMM(r?.to || r?.end) }))
    .filter((r) => r.from != null && r.to != null && r.to > r.from);
}

function bookingModalWorkingRanges(employee, date, businessDoc = {}) {
  const explicit = bookingModalExplicitAvailabilityRanges(employee, date);
  if (explicit.length) return explicit;
  const settings = businessDoc?.calendarSettings && typeof businessDoc.calendarSettings === "object" ? businessDoc.calendarSettings : {};
  const min = bookingModalMinutesFromHHMM(settings.minTime || "08:00");
  const max = bookingModalMinutesFromHHMM(settings.maxTime || "18:00");
  return [{ from: min ?? 8 * 60, to: max ?? 18 * 60 }];
}

function bookingModalSlotInsideRanges(start, end, ranges = []) {
  if (!start || !end) return false;
  const a = start.getHours() * 60 + start.getMinutes();
  const b = end.getHours() * 60 + end.getMinutes();
  return ranges.some((r) => a >= r.from && b <= r.to);
}

function bookingModalFindConflict({ existingReservations, resourceId, start, end, initialBookingId, initialBooking = null }) {
  if (!resourceId || !start || !end) return null;
  const rid = String(resourceId);
  const ignore = String(initialBookingId || "");
  return (existingReservations || []).find((item) => {
    if (!item || !item.start || !item.end) return false;
    if (bookingModalSameBookingIdentity(item, initialBooking, ignore)) return false;
    if (ignore && String(item.id || "") === ignore) return false;
    if (bookingModalIsCancelled(item.raw)) return false;
    if (!bookingModalSameDay(item.start, start)) return false;
    if (rid !== "__loop_company_agenda__" && (!item.resourceIds || !item.resourceIds.has(rid))) return false;
    return bookingModalOverlaps(start, end, item.start, item.end);
  }) || null;
}

function bookingModalAvailabilitySource(employee, date, businessDoc = {}) {
  const explicit = bookingModalExplicitAvailabilityRanges(employee, date);
  if (explicit.length) {
    return {
      source: "worker",
      label: `Horario de ${employeeLabel(employee)}`,
      ranges: explicit,
    };
  }
  const settings = businessDoc?.calendarSettings && typeof businessDoc.calendarSettings === "object" ? businessDoc.calendarSettings : {};
  const min = bookingModalMinutesFromHHMM(settings.minTime || "08:00");
  const max = bookingModalMinutesFromHHMM(settings.maxTime || "18:00");
  return {
    source: "business",
    label: "Horario general del negocio",
    ranges: [{ from: min ?? 8 * 60, to: max ?? 18 * 60 }],
  };
}

function bookingModalFormatRangeMinutes(range) {
  if (!range || range.from == null || range.to == null) return "";
  return `${bookingModalHHMMFromMinutes(range.from)} - ${bookingModalHHMMFromMinutes(range.to)}`;
}

function bookingModalGroupSlots(slots = []) {
  const groups = [
    { key: "morning", label: "Mañana", slots: [] },
    { key: "afternoon", label: "Tarde", slots: [] },
    { key: "evening", label: "Noche", slots: [] },
  ];
  (Array.isArray(slots) ? slots : []).forEach((slot) => {
    const mins = bookingModalMinutesFromHHMM(slot);
    if (mins == null) return;
    if (mins < 12 * 60) groups[0].slots.push(slot);
    else if (mins < 18 * 60) groups[1].slots.push(slot);
    else groups[2].slots.push(slot);
  });
  return groups.filter((g) => g.slots.length);
}

function bookingModalTimeSuggestions({ date, duration, resourceId, employee, businessDoc, existingReservations, initialBookingId, initialBooking = null, max = 96 }) {
  if (!date || !resourceId || !employee) return [];
  const safeDuration = Math.max(15, Number(duration || 60));
  const ranges = bookingModalWorkingRanges(employee, date, businessDoc);
  const out = [];
  for (const range of ranges) {
    const first = Math.ceil(Number(range.from || 0) / 15) * 15;
    const last = Number(range.to || 0) - safeDuration;
    for (let mins = first; mins <= last; mins += 15) {
      const hhmm = bookingModalHHMMFromMinutes(mins);
      const start = bookingModalBuildDateAtTime(date, hhmm);
      const end = start ? new Date(start.getTime() + safeDuration * 60000) : null;
      if (!start || !end) continue;
      const conflict = bookingModalFindConflict({ existingReservations, resourceId, start, end, initialBookingId, initialBooking });
      if (!conflict) out.push(hhmm);
      if (Number.isFinite(Number(max)) && out.length >= Number(max)) return out;
    }
  }
  return out;
}

function employeeLabel(emp) {
  return emp?.name || emp?.displayName || emp?.email || emp?.id || "Trabajador";
}


function bookingEditStatusInfo(statusValue) {
  const raw = String(statusValue || "confirmed").trim().toLowerCase();
  if (raw === "pending_payment") return { label: "Pendiente de pago", tone: "pending" };
  if (raw === BOOKING_MODAL_DELETE_SENTINEL || raw === "deleted") {
    return { label: "Quitar de agenda", tone: "cancelled" };
  }
  if (["confirmed", "confirmada", "accepted", "approved", "aprobada"].includes(raw)) {
    return { label: "Confirmada", tone: "confirmed" };
  }
  if (["pending", "pendiente", "to_confirm", "por_confirmar"].includes(raw)) {
    return { label: "Pendiente", tone: "pending" };
  }
  if (["in_progress", "in-progress", "en_progreso", "started"].includes(raw)) {
    return { label: "En progreso", tone: "progress" };
  }
  if (["completed", "complete", "completada", "done"].includes(raw)) {
    return { label: "Completada", tone: "completed" };
  }
  if (["no_show", "no-show", "noshow", "no_asistio"].includes(raw)) {
    return { label: "No asistió", tone: "warning" };
  }
  if (["cancelled", "canceled", "cancelada"].includes(raw)) {
    return { label: "Cancelada", tone: "cancelled" };
  }
  if (["rejected", "declined", "rechazada"].includes(raw)) {
    return { label: "Rechazada", tone: "cancelled" };
  }
  return { label: raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "Reserva", tone: "neutral" };
}

function bookingEditDisplayTime(booking) {
  try {
    const d = bookingScheduledAtToDate(booking);
    if (d && !Number.isNaN(d.getTime())) return bookingModalFormatHHMM(d);
  } catch (_) {}
  return String(booking?.time || "").slice(0, 5);
}

function bookingEditDisplayDate(booking) {
  try {
    const d = bookingScheduledAtToDate(booking);
    if (d && !Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(interfaceLocale(), { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    }
  } catch (_) {}
  return booking?.date || "";
}

function bookingModalLogDate(value) {
  try {
    const d = value && typeof value.toDate === "function" ? value.toDate() : value ? new Date(value) : null;
    if (!d || Number.isNaN(d.getTime())) return "Ahora";
    return d.toLocaleString(interfaceLocale(), { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_) {
    return "Ahora";
  }
}

function bookingModalNormalizeWa(value) {
  return String(value || "").trim().toLowerCase();
}

function bookingModalWhatsAppEffectiveStatus(row = {}) {
  const delivery = bookingModalNormalizeWa(row.deliveryStatus || row.providerDeliveryStatus || row.providerStatus || row.lastProviderStatus);
  if (["read", "delivered", "failed"].includes(delivery)) return delivery;
  const status = bookingModalNormalizeWa(row.status || (row.ok === true ? "sent" : row.ok === false ? "failed" : ""));
  return status || "registered";
}

function bookingModalWhatsAppReasonLabel(value) {
  const raw = String(value || "").trim();
  const s = bookingModalNormalizeWa(raw);
  const labels = {
    "monthly-limit-reached": "Límite mensual alcanzado",
    "customer-opted-out": "Cliente no acepta WhatsApp",
    "missing-customer-phone": "Cliente sin teléfono",
    "whatsapp-disabled": "WhatsApp apagado",
    "disabled-by-business-setting": "Regla apagada por el negocio",
    "business-setting-disabled": "Regla apagada por el negocio",
    "template-automation-not-live": "Recordatorios no aprobados todavía",
    "already-sent": "Ya se había enviado",
    "send-in-progress": "Envío en progreso",
    "not-confirmed": "Reserva no confirmada",
  };
  if (labels[s]) return labels[s];
  if (s.startsWith("disabled-by-business-setting")) return "Regla apagada por el negocio";
  if (s.startsWith("feature-disabled-by-billing")) return "Función desactivada por billing";
  return raw;
}

function bookingModalWhatsAppDeliveryLabel(row = {}) {
  const status = bookingModalNormalizeWa(row.deliveryStatus || row.providerDeliveryStatus || row.providerStatus || row.lastProviderStatus);
  if (status === "sent") return "Aceptado por Meta";
  if (status === "delivered") return "Entregado al teléfono";
  if (status === "read") return "Leído por el cliente";
  if (status === "failed") return "Falló en Meta";
  return "";
}

function bookingModalWhatsAppLogStatus(row = {}) {
  const status = bookingModalWhatsAppEffectiveStatus(row);
  if (status === "read") return "Leído";
  if (status === "delivered") return "Entregado";
  if (status === "sent") return "Enviado";
  if (status === "failed" || status === "error") return "Fallido";
  if (status === "skipped") return "Saltado";
  return "Registrado";
}

function bookingModalWhatsAppStatusHelper(row = {}) {
  const status = bookingModalWhatsAppEffectiveStatus(row);
  if (status === "read") return "El cliente abrió el mensaje.";
  if (status === "delivered") return "Llegó al teléfono del cliente.";
  if (status === "sent") return "Meta aceptó el envío.";
  if (status === "failed") return "El envío falló.";
  if (status === "skipped") return bookingModalWhatsAppReasonLabel(row.reason || row.skipReason || row.meta?.reason) || "No se envió por una regla de seguridad.";
  return "Mensaje registrado.";
}

function bookingModalWhatsAppTypeLabel(row = {}) {
  const raw = String(row.type || row.templateName || row.reason || "").toLowerCase();
  if (raw.includes("booking_confirmed") || raw.includes("confirmed")) return "Confirmación de reserva";
  if (raw.includes("booking_cancelled") || raw.includes("booking_canceled") || raw.includes("cancelled") || raw.includes("canceled")) return "Cancelación de reserva";
  if (raw.includes("reminder_24h") || raw.includes("24h")) return "Recordatorio 24h";
  if (raw.includes("reminder_2h") || raw.includes("2h")) return "Recordatorio 2h";
  if (raw.includes("manual") || raw === "text") return "Mensaje manual";
  if (row.templateName) return row.templateName;
  return row.reason || row.type || "WhatsApp";
}


function bookingModalWhatsAppAutomationLabel(value) {
  const raw = String(value || "").trim();
  const s = bookingModalNormalizeWa(raw);
  if (!raw) return "";
  if (s.includes("booking_confirmed_auto")) return "Automático · Confirmación";
  if (s.includes("booking_cancelled_auto") || s.includes("booking_canceled_auto")) return "Automático · Cancelación";
  if (s.includes("booking_reminder_24h_auto")) return "Automático · Recordatorio 24h";
  if (s.includes("booking_reminder_2h_auto")) return "Automático · Recordatorio 2h";
  if (s.includes("manual")) return "Manual";
  if (s.includes("auto")) return "Automático";
  return bookingModalWhatsAppReasonLabel(raw);
}

function bookingModalWhatsAppMetaLine(row = {}, showFullPhone = false) {
  const meta = row.meta && typeof row.meta === "object" ? row.meta : {};
  const phoneRaw = Array.isArray(row.to) ? row.to[0] : row.to;
  const phone = bookingModalMaskPhone(phoneRaw, showFullPhone);
  const reason = bookingModalWhatsAppAutomationLabel(meta.reason || row.reason || row.skipReason);
  const parts = [
    phone ? `Para ${phone}` : "",
    bookingModalWhatsAppDeliveryLabel(row),
    row.providerStatusUpdatedAt ? `Actualizado ${bookingModalLogDate(row.providerStatusUpdatedAt)}` : "",
    reason ? reason : "",
    row.providerError ? `Error de Meta` : "",
    row.error ? `Error registrado` : "",
  ].filter(Boolean);
  return parts.join(" · ");
}

function bookingModalMaskPhone(value, showFull = false) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (showFull) return `+${digits}`;
  if (digits.length <= 6) return `+${digits}`;
  return `+${digits.slice(0, 3)}••••${digits.slice(-4)}`;
}

function bookingModalNormalizePhone(value) {
  return normalizePhoneToE164(value, "PA");
}

function bookingModalCleanPhoneInput(value = "") {
  return String(value || "").trim();
}
function bookingModalFormatPhoneInput(value = "") {
  return value ? formatPhoneDisplay(value,"PA") : "";
}
function bookingModalPhoneForStorage(value = "") {
  return value ? normalizePhoneToE164(value,"PA") : "";
}

function bookingModalBuildLiveCustomerProfile({
  customerId = null,
  customerName = "",
  customerEmail = "",
  customerPhone = "",
  customerSnapshot = null,
} = {}) {
  const base = customerSnapshot && typeof customerSnapshot === "object" ? customerSnapshot : {};
  const name = String(customerName || base.name || base.customerName || base.displayName || "Cliente").trim();
  const email = String(customerEmail || base.email || base.customerEmail || base.emailNormalized || "").trim();
  const rawPhone = customerPhone || base.phone || base.customerPhone || base.phoneDisplay || base.phoneNormalized || base.phoneE164 || "";
  const phone = bookingModalPhoneForStorage(rawPhone);
  const normalizedPhone = bookingModalNormalizePhone(phone || rawPhone);
  const displayPhone = (() => {
    if (!phone && !rawPhone) return "";
    try {
      return formatPhoneDisplay(normalizedPhone || phone || rawPhone, "PA") || phone || rawPhone;
    } catch (_) {
      return phone || rawPhone;
    }
  })();
  const totalBookings = Number(base.totalBookings || base.importedTotalBookings || 0) || 0;

  return {
    ...base,
    id: customerId || base.id || base.customerId || null,
    customerId: customerId || base.customerId || base.id || null,
    name,
    customerName: name,
    displayName: name,
    email,
    customerEmail: email,
    emailNormalized: email ? email.toLowerCase() : (base.emailNormalized || ""),
    phone,
    customerPhone: phone,
    phoneDisplay: displayPhone,
    phoneNormalized: normalizedPhone || base.phoneNormalized || "",
    phoneE164: normalizedPhone || base.phoneE164 || "",
    totalBookings,
    importedTotalBookings: Number(base.importedTotalBookings || totalBookings) || 0,
  };
}




// LOOP BOOKING CUSTOMER SUBMIT CONTRACT V6
// The visual picker can show a selected customer while the local customerName
// state is temporarily empty (mobile focus/rerender or legacy customer shapes).
// Resolve the display name from every available source before blocking submit.
function bookingModalCustomerDisplayName(...sources) {
  const candidates = [];
  const collect = (source) => {
    if (!source) return;
    if (typeof source === "string") {
      candidates.push(source);
      return;
    }
    if (typeof source !== "object") return;
    candidates.push(
      source.name,
      source.customerName,
      source.displayName,
      source.fullName,
      source.clientName,
      source.label,
      source.title
    );
    if (source.firstName || source.lastName) {
      candidates.push([source.firstName, source.lastName].filter(Boolean).join(" "));
    }
    if (source.customerSnapshot && typeof source.customerSnapshot === "object") collect(source.customerSnapshot);
    if (source.customer && typeof source.customer === "object") collect(source.customer);
  };
  sources.forEach(collect);
  for (const value of candidates) {
    const clean = String(value || "").replace(/\s+/g, " ").trim();
    if (clean) return clean;
  }
  return "";
}

function bookingModalNormalizeCustomerSnapshot(snapshot = null, fallbackName = "") {
  const base = snapshot && typeof snapshot === "object" ? snapshot : {};
  const cleanName = bookingModalCustomerDisplayName(base, fallbackName);
  if (!cleanName && !Object.keys(base).length) return null;
  return {
    ...base,
    name: cleanName || base.name || base.customerName || base.displayName || "Cliente",
    customerName: cleanName || base.customerName || base.name || base.displayName || "Cliente",
    displayName: cleanName || base.displayName || base.name || base.customerName || "Cliente",
    fullName: cleanName || base.fullName || base.name || base.customerName || "Cliente",
  };
}

function bookingModalNormalizeSelectedCustomerDraft(draft = null, fallbackName = "") {
  if (!draft || typeof draft !== "object") return null;
  const rawSnapshot = draft.customerSnapshot && typeof draft.customerSnapshot === "object" ? draft.customerSnapshot : null;
  const cleanName = bookingModalCustomerDisplayName(draft, rawSnapshot, fallbackName);
  const rawId = String(draft.customerId || draft.clientId || rawSnapshot?.customerId || rawSnapshot?.id || rawSnapshot?.clientId || "").trim();
  const hasSnapshotIdentity = Boolean(rawSnapshot && Object.keys(rawSnapshot).length);
  if (!rawId && !hasSnapshotIdentity && !cleanName) return null;

  const snapshot = bookingModalNormalizeCustomerSnapshot(rawSnapshot || (rawId ? { id: rawId, customerId: rawId } : null), cleanName);
  const customerId = String(rawId || snapshot?.customerId || snapshot?.id || snapshot?.clientId || "").trim();
  const customerEmail = String(draft.customerEmail || snapshot?.emailNormalized || snapshot?.email || snapshot?.customerEmail || "").trim().toLowerCase();
  const customerPhone = bookingModalPhoneForStorage(draft.customerPhone || snapshot?.phoneDisplay || snapshot?.phone || snapshot?.phoneNormalized || snapshot?.phoneE164 || snapshot?.customerPhone || "");

  return {
    customerId: customerId || null,
    clientId: customerId || null,
    customerName: cleanName || snapshot?.name || snapshot?.customerName || "",
    customerEmail,
    customerPhone,
    customerSnapshot: snapshot ? {
      ...snapshot,
      id: customerId || snapshot.id || null,
      customerId: customerId || snapshot.customerId || null,
      clientId: customerId || snapshot.clientId || null,
      name: cleanName || snapshot.name || snapshot.customerName || "Cliente",
      customerName: cleanName || snapshot.customerName || snapshot.name || "Cliente",
      displayName: cleanName || snapshot.displayName || snapshot.name || snapshot.customerName || "Cliente",
      fullName: cleanName || snapshot.fullName || snapshot.name || snapshot.customerName || "Cliente",
    } : null,
  };
}

function bookingModalReadCustomerInputDomValue() {
  try {
    if (typeof document === "undefined") return "";
    const input = document.querySelector(".loop-customer-minimal-search input");
    const value = String(input?.value || "").replace(/\s+/g, " ").trim();
    if (!value) return "";
    if (value.includes("@")) return "";
    return value;
  } catch (_) {
    return "";
  }
}

function bookingModalRecordCustomerDebug(event, payload = {}) {
  try {
    if (typeof window === "undefined") return;
    const row = { event, at: new Date().toISOString(), ...(payload || {}) };
    window.__LOOP_BOOKING_CUSTOMER_DEBUG__ = row;
    window.__LOOP_BOOKING_CUSTOMER_DEBUG_LOG__ = [
      ...(Array.isArray(window.__LOOP_BOOKING_CUSTOMER_DEBUG_LOG__) ? window.__LOOP_BOOKING_CUSTOMER_DEBUG_LOG__ : []),
      row,
    ].slice(-50);
  } catch (_) {}
}

function LoopPhoneInput({value,onChange,disabled=false,onPointerDown,onTouchStart,onFocus}) {
  return <div onPointerDown={onPointerDown} onTouchStart={onTouchStart} onFocus={onFocus}><InternationalPhoneInput label="" value={value} disabled={disabled} onChange={(display)=>onChange?.(display)} /></div>;
}

function bookingModalWhatsAppReadiness(phoneValue, optOut = false) {
  const phone = bookingModalNormalizePhone(phoneValue);
  if (optOut) return { tone: "danger", label: "Cliente no acepta WhatsApp", helper: "No se enviarán mensajes automáticos ni manuales." };
  if (!phone) return { tone: "warning", label: "Sin WhatsApp", helper: "Agrega teléfono para poder enviar mensajes." };
  if (phone.length < 10) return { tone: "danger", label: "Teléfono inválido", helper: "Revisa el código de país y los dígitos." };
  return { tone: "ok", label: "WhatsApp listo", helper: `+${phone}` };
}

function bookingModalCustomerWhatsappOptOut(raw = {}) {
  const snapshot = raw?.customerSnapshot && typeof raw.customerSnapshot === "object" ? raw.customerSnapshot : {};
  const customer = raw?.customer && typeof raw.customer === "object" ? raw.customer : {};
  return raw?.customerWhatsappOptOut === true || raw?.whatsappOptOut === true || raw?.whatsappOptIn === false || raw?.customerWhatsappConsent === false || snapshot.whatsappOptOut === true || snapshot.whatsappOptIn === false || snapshot.customerWhatsappConsent === false || customer.whatsappOptOut === true || customer.whatsappOptIn === false;
}

function bookingModalLatestWhatsAppRow(rows = [], typeIncludes = "") {
  const needle = String(typeIncludes || "").toLowerCase();
  return (Array.isArray(rows) ? rows : []).find((row) => {
    const text = [row.type, row.templateName, row.reason, row.meta?.templateName, row.meta?.reason].filter(Boolean).join(" ").toLowerCase();
    return needle ? text.includes(needle) : true;
  }) || null;
}

function bookingModalSmartWhatsAppAction({ rows = [], status = "", canSend = false, phone = "", optOut = false } = {}) {
  const ready = bookingModalWhatsAppReadiness(phone, optOut);
  if (!canSend) return { label: "WhatsApp off", disabled: true, type: "manual" };
  if (ready.tone !== "ok") return { label: ready.label, disabled: true, type: "manual" };
  const s = String(status || "").toLowerCase();
  let templateType = "manual";
  if (["confirmed", "confirmada", "accepted", "approved"].includes(s)) templateType = "booking_confirmed";
  if (["cancelled", "canceled", "cancelada", "rejected", "declined"].includes(s)) templateType = "booking_cancelled";
  if (templateType !== "manual") {
    const row = bookingModalLatestWhatsAppRow(rows, templateType);
    if (row && (row.ok === true || String(row.status || "").toLowerCase() === "sent")) return { label: "Ya enviado", disabled: true, type: templateType };
    if (row && (row.ok === false || String(row.status || "").toLowerCase() === "failed" || String(row.status || "").toLowerCase() === "skipped")) return { label: "Reintentar WhatsApp", disabled: false, type: templateType };
  }
  return { label: "Enviar WhatsApp", disabled: false, type: templateType };
}

function bookingEditNormalizeComparable(value) {
  return String(value ?? "").trim().toLowerCase();
}

function bookingModalDateInputValue(value) {
  try {
    const d = value ? new Date(value) : new Date();
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  } catch (_) {
    return "";
  }
}

function bookingModalDateFromYmd(value, fallback = new Date()) {
  const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return fallback ? new Date(fallback) : new Date();
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
}

const BOOKING_MODAL_STATUS_OPTIONS = [
  { value: "pending", label: "Pendiente" },
  { value: "confirmed", label: "Confirmada" },
  { value: "completed", label: "Completada" },
  { value: "no_show", label: "No asistió" },
  { value: "cancelled", label: "Cancelada" },
];



function bookingEditIsClosedStatus(value) {
  const raw = String(value || "confirmed").trim().toLowerCase();
  return ["completed", "deleted", "cancelled", "canceled", "cancelada", "rejected", "declined", "rechazada", "no_show", "no-show", "noshow"].includes(raw);
}

function bookingEditCommissionLifecycleStatus({ bookingStatus, paymentStatus, commissionOnlyCompleted = true } = {}) {
  const status = String(bookingStatus || "confirmed").trim().toLowerCase();
  const payment = String(paymentStatus || "pending").trim().toLowerCase();
  if (["deleted", "cancelled", "canceled", "cancelada", "rejected", "declined", "rechazada", "no_show", "no-show", "noshow"].includes(status)) return "not_applicable";
  if (commissionOnlyCompleted && status !== "completed") return payment === "paid" ? "held_until_completed" : "estimated";
  if (status === "completed" && payment === "paid") return "payable";
  if (status === "completed" && payment === "partial") return "earned_pending_balance";
  if (status === "completed") return "earned_pending_collection";
  return "estimated";
}

function bookingEditCommissionLifecycleLabel(value) {
  const labels = {
    estimated: "Comisión estimada",
    held_until_completed: "Pago cobrado · falta completar",
    earned_pending_collection: "Ganada · falta cobrar",
    earned_pending_balance: "Ganada · saldo pendiente",
    payable: "Lista para pagar",
    not_applicable: "No aplica",
  };
  return labels[String(value || "estimated")] || labels.estimated;
}


// LOOP MOBILE KEYBOARD STABILITY V1
// Mobile keyboards can collapse when nested modals/search menus steal focus during rerenders.
// These helpers keep taps inside booking inputs local and only auto-focus on desktop.
function loopBookingIsMobileInputRuntime() {
  try {
    if (typeof window === "undefined") return false;
    const cap = window.Capacitor;
    const platform = cap && (typeof cap.getPlatform === "function" ? cap.getPlatform() : cap.platform);
    if (platform && platform !== "web") return true;
    return Boolean(window.matchMedia && window.matchMedia("(max-width: 980px)").matches);
  } catch (_) {
    return false;
  }
}

function stopBookingInputPointerPropagation(event) {
  try { event.stopPropagation(); } catch (_) {}
}

function stabilizeBookingMobileInput(event) {
  try {
    if (!loopBookingIsMobileInputRuntime()) return;
    const el = event && event.currentTarget ? event.currentTarget : null;
    if (!el || typeof el.scrollIntoView !== "function") return;
    const field = typeof el.closest === "function" ? el.closest(".booking-pro-field, .loop-customer-minimal-search, .loop-settings-field, .loop-datetime-modal-grid") : null;
    window.setTimeout(() => {
      try { (field || el).scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" }); } catch (_) {}
    }, 80);
    window.setTimeout(() => {
      try { (field || el).scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" }); } catch (_) {}
    }, 340);
  } catch (_) {}
}

function bookingModalRuntimePlatform() {
  try {
    if (typeof window === "undefined") return "web";
    const cap = window.Capacitor;
    return (cap && (typeof cap.getPlatform === "function" ? cap.getPlatform() : cap.platform)) || "web";
  } catch (_) {
    return "web";
  }
}

function bookingModalIsIosNativeRuntime() {
  try {
    return isIosCapacitor() || bookingModalRuntimePlatform() === "ios";
  } catch (_) {
    return false;
  }
}

function bookingModalRecordIdentityDebug(event, payload = {}) {
  try {
    if (typeof window === "undefined") return;
    const row = {
      event,
      at: new Date().toISOString(),
      platform: bookingModalRuntimePlatform(),
      ...payload,
    };
    window.__LOOP_BOOKING_CUSTOMER_IDENTITY_DEBUG__ = row;
    window.__LOOP_BOOKING_CUSTOMER_IDENTITY_DEBUG_LOG__ = [
      ...(Array.isArray(window.__LOOP_BOOKING_CUSTOMER_IDENTITY_DEBUG_LOG__) ? window.__LOOP_BOOKING_CUSTOMER_IDENTITY_DEBUG_LOG__ : []),
      row,
    ].slice(-30);
  } catch (_) {}
}

function bookingModalWithTimeout(promise, timeoutMs = 6500, fallback = null) {
  let timer = null;
  return Promise.race([
    promise,
    new Promise((resolve) => {
      timer = window.setTimeout(() => resolve(fallback), timeoutMs);
    }),
  ]).finally(() => {
    try { if (timer) window.clearTimeout(timer); } catch (_) {}
  });
}

function bookingModalIdentityPhoneVariants(value = "") {
  const digits = String(bookingModalNormalizePhone(value) || value || "").replace(/\D/g, "");
  const variants = new Set();
  if (digits.length >= 7) {
    variants.add(`+${digits}`);
    variants.add(digits);
    if (digits.length === 8) variants.add(`+507${digits}`);
    if (digits.length >= 11 && digits.startsWith("507")) variants.add(`+${digits}`);
  }
  return Array.from(variants).filter(Boolean);
}

function bookingModalCustomerIdentitySnapshotFromRow(row = {}, matchedBy = "customer") {
  const id = String(row.id || row.customerId || row.docId || "").trim();
  const name = String(row.name || row.customerName || row.fullName || row.displayName || "Cliente").trim() || "Cliente";
  const email = String(row.email || row.customerEmail || row.emailNormalized || "").trim();
  const phone = String(row.phone || row.customerPhone || row.phoneDisplay || row.phoneNormalized || row.phoneE164 || row.whatsapp || row.whatsApp || "").trim();
  const phoneNormalized = (() => {
    const variants = bookingModalIdentityPhoneVariants(phone);
    return variants.find((v) => v.startsWith("+")) || variants[0] || phone;
  })();

  return {
    ...(row || {}),
    id,
    customerId: id,
    name,
    customerName: name,
    displayName: name,
    email,
    customerEmail: email,
    emailNormalized: email.toLowerCase(),
    phone,
    customerPhone: phone,
    phoneDisplay: phone,
    phoneNormalized,
    phoneE164: phoneNormalized,
    customerPhoneNormalized: phoneNormalized,
    identitySource: row.identitySource || "business_customer_match",
    identityMatchedBy: row.identityMatchedBy || matchedBy,
  };
}

async function bookingModalFindBusinessCustomerNativeSafe(dbClient, businessId, payload = {}) {
  if (!dbClient || !businessId) return null;
  const path = `businesses/${businessId}/customers`;
  const email = String(payload.email || payload.customerEmail || "").trim().toLowerCase();
  const phoneVariants = bookingModalIdentityPhoneVariants(payload.phone || payload.customerPhone || "");
  const seen = new Set();
  const rows = [];

  const addRows = (items, matchedBy) => {
    (Array.isArray(items) ? items : []).forEach((item) => {
      if (!item) return;
      const id = String(item.id || item.customerId || item.docId || item.path || JSON.stringify(item).slice(0, 90));
      if (seen.has(id)) return;
      seen.add(id);
      rows.push({ ...(item || {}), id: item.id || item.customerId || item.docId || id, identityMatchedBy: matchedBy });
    });
  };

  for (const phoneValue of phoneVariants.slice(0, 3)) {
    try {
      const found = await bookingModalWithTimeout(
        fsClient.listCollectionByPath(path, { where: [{ field: "phoneNormalized", op: "EQUAL", value: phoneValue }], limit: 8 }, { db: dbClient }),
        4200,
        []
      );
      addRows(found, "phone");
    } catch (error) {
      bookingModalRecordIdentityDebug("native-phone-query-failed", { message: String(error?.message || error), phoneValue });
    }
    try {
      const found = await bookingModalWithTimeout(
        fsClient.listCollectionByPath(path, { where: [{ field: "phoneE164", op: "EQUAL", value: phoneValue }], limit: 8 }, { db: dbClient }),
        4200,
        []
      );
      addRows(found, "phone");
    } catch (_) {}
  }

  if (email && email.includes("@")) {
    try {
      const found = await bookingModalWithTimeout(
        fsClient.listCollectionByPath(path, { where: [{ field: "emailNormalized", op: "EQUAL", value: email }], limit: 8 }, { db: dbClient }),
        4200,
        []
      );
      addRows(found, "email");
    } catch (error) {
      bookingModalRecordIdentityDebug("native-email-query-failed", { message: String(error?.message || error), email });
    }
  }

  if (!rows.length && (phoneVariants.length || email)) {
    try {
      const scan = await bookingModalWithTimeout(
        fsClient.listCollectionByPath(path, { limit: 700 }, { db: dbClient }),
        5200,
        []
      );
      const phoneDigits = phoneVariants.map((v) => String(v).replace(/\D/g, "")).filter(Boolean);
      const matched = (Array.isArray(scan) ? scan : []).filter((row) => {
        const rowPhoneDigits = String(row?.phoneNormalized || row?.phoneE164 || row?.phone || row?.customerPhone || row?.whatsapp || row?.whatsApp || "").replace(/\D/g, "");
        const rowEmail = String(row?.emailNormalized || row?.email || row?.customerEmail || "").trim().toLowerCase();
        const phoneMatch = phoneDigits.some((d) => d.length >= 7 && rowPhoneDigits.endsWith(d.slice(-8)));
        const emailMatch = email && rowEmail === email;
        return phoneMatch || emailMatch;
      }).slice(0, 8);
      addRows(matched, matched.some((row) => String(row.emailNormalized || row.email || "").toLowerCase() === email) ? "email" : "phone");
    } catch (error) {
      bookingModalRecordIdentityDebug("native-customer-scan-failed", { message: String(error?.message || error) });
    }
  }

  if (!rows.length) return null;
  const snapshot = bookingModalCustomerIdentitySnapshotFromRow(rows[0], rows[0].identityMatchedBy || "customer");
  return {
    status: "existing_customer",
    customerId: snapshot.customerId || snapshot.id,
    existingCustomer: snapshot,
    customerSnapshot: snapshot,
    source: "native_business_customer_match",
  };
}

async function bookingModalResolveCustomerIdentitySafe(dbClient, businessId, payload = {}, options = {}) {
  const timeoutMs = Number(options.timeoutMs || 6500);
  const isIosNative = bookingModalIsIosNativeRuntime();

  if (isIosNative) {
    const nativeMatch = await bookingModalFindBusinessCustomerNativeSafe(dbClient, businessId, payload);
    if (nativeMatch) {
      bookingModalRecordIdentityDebug("native-existing-customer-match", {
        businessId,
        status: nativeMatch.status,
        customerId: nativeMatch.customerId,
      });
      return nativeMatch;
    }
  }

  const fallback = {
    status: "new_customer",
    customerSnapshot: null,
    identityTimeout: true,
    identityFallback: true,
  };

  const resolved = await bookingModalWithTimeout(
    resolveCustomerIdentityForBooking(dbClient, businessId, payload),
    timeoutMs,
    fallback
  );

  if (resolved?.identityTimeout) {
    bookingModalRecordIdentityDebug("identity-timeout-fallback", { businessId, timeoutMs, isIosNative });
  }

  return resolved || null;
}


function NewCustomerQuickModal({
  open,
  businessId = null,
  initialName = "",
  initialPhone = "",
  initialEmail = "",
  onClose,
  onSave,
}) {
  const [draftName, setDraftName] = useState("");
  const [draftPhone, setDraftPhone] = useState("");
  const [draftEmail, setDraftEmail] = useState("");
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [identityResolution, setIdentityResolution] = useState(null);
  const [identityError, setIdentityError] = useState("");
  const nameInputRef = useRef(null);

  const hydratedOpenRef = useRef(false);

  useEffect(() => {
    if (!open) {
      hydratedOpenRef.current = false;
      setSavingCustomer(false);
      setIdentityLoading(false);
      setIdentityResolution(null);
      setIdentityError("");
      return undefined;
    }
    if (hydratedOpenRef.current) return undefined;
    hydratedOpenRef.current = true;

    setDraftName(initialName || "");
    setDraftPhone(bookingModalPhoneForStorage(initialPhone || ""));
    setDraftEmail(initialEmail || "");

    // On iOS/Android, programmatic focus while a nested modal animates can open and
    // immediately collapse the keyboard. Let the user tap the field naturally.
    if (loopBookingIsMobileInputRuntime()) return undefined;

    const id = window.setTimeout(() => {
      try { nameInputRef.current?.focus?.(); } catch (_) {}
    }, 120);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    if (!open || !businessId) {
      setIdentityResolution(null);
      setIdentityError("");
      setIdentityLoading(false);
      return undefined;
    }
    const email = String(draftEmail || "").trim();
    const phone = bookingModalPhoneForStorage(draftPhone || "");
    const phoneDigits = phone.replace(/\D/g, "");
    const shouldLookup = email.includes("@") || phoneDigits.length >= 7;
    if (!shouldLookup) {
      setIdentityResolution(null);
      setIdentityError("");
      setIdentityLoading(false);
      return undefined;
    }

    let alive = true;
    const timer = window.setTimeout(async () => {
      setIdentityLoading(true);
      setIdentityError("");
      try {
        const res = await bookingModalResolveCustomerIdentitySafe(db, businessId, {
          name: draftName,
          customerName: draftName,
          phone,
          customerPhone: phone,
          email,
          customerEmail: email,
        });
        if (!alive) return;
        setIdentityResolution(res || null);
        if (res?.identityTimeout) {
          setIdentityError("La validación tardó más de lo normal. Puedes guardar y Loop volverá a revisar.");
        }
      } catch (error) {
        console.warn("[BookingCreateModal] quick customer identity check failed", error);
        if (alive) {
          setIdentityResolution(null);
          setIdentityError("No pudimos validar identidad ahora. Igual puedes guardar y Loop volverá a revisar.");
        }
      } finally {
        if (alive) setIdentityLoading(false);
      }
    }, 360);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [open, businessId, draftName, draftPhone, draftEmail]);

  const identityStatus = identityResolution?.status || "new_customer";
  const identitySnapshot = identityResolution?.customerSnapshot || null;
  const identityConflict = identityResolution?.conflict || null;
  const identityFieldConflicts = identityResolution?.fieldConflicts || identitySnapshot?.identityFieldConflicts || [];
  const hasExistingCustomer = identityStatus === "existing_customer" && identitySnapshot;
  const hasPlatformUser = identityStatus === "platform_user" && identitySnapshot;
  const hasMissingProfilePhone = identityStatus === "platform_user_missing_phone";
  const hasConflict = identityStatus === "conflict" && identityConflict;
  const hasBlockingIdentityIssue = hasConflict || hasMissingProfilePhone;
  const hasCanonicalLoopProfile = identitySnapshot?.identityCanonicalProfile === true || identitySnapshot?.identityContactSource === "platform_user_profile";
  const visibleIdentityFieldConflicts = hasCanonicalLoopProfile
    ? identityFieldConflicts.filter((item) => item.scope !== "typed_input" && item.scope !== "platform_user")
    : identityFieldConflicts;
  const hasSoftFieldConflict = !hasBlockingIdentityIssue && visibleIdentityFieldConflicts.length > 0;

  const typedPhoneNormalized = bookingModalNormalizePhone(draftPhone) ? `+${bookingModalNormalizePhone(draftPhone)}` : "";
  const typedEmailNormalized = String(draftEmail || "").trim().toLowerCase();

  const applyIdentitySnapshotToDraft = () => {
    if (!identitySnapshot) return;
    if (hasCanonicalLoopProfile) {
      // For real Loop users, the ProfilePage identity is canonical across all businesses.
      setDraftName(identitySnapshot.name || draftName || "");
      setDraftPhone(bookingModalPhoneForStorage(identitySnapshot.phone || identitySnapshot.phoneNormalized || ""));
      setDraftEmail(identitySnapshot.email || identitySnapshot.emailNormalized || draftEmail || "");
      return;
    }
    // For non-Loop/manual customers, do not overwrite what the business just typed.
    setDraftName(draftName || identitySnapshot.name || "");
    setDraftPhone(bookingModalPhoneForStorage(draftPhone || identitySnapshot.phone || identitySnapshot.phoneNormalized || ""));
    setDraftEmail(draftEmail || identitySnapshot.email || identitySnapshot.emailNormalized || "");
  };

  useEffect(() => {
    if (!open || !identitySnapshot || !hasCanonicalLoopProfile) return;
    const nextName = String(identitySnapshot.name || identitySnapshot.customerName || "").trim();
    const nextPhone = bookingModalPhoneForStorage(identitySnapshot.phone || identitySnapshot.phoneNormalized || identitySnapshot.phoneE164 || "");
    const nextEmail = String(identitySnapshot.email || identitySnapshot.emailNormalized || "").trim();

    if (nextName && nextName !== String(draftName || "").trim()) setDraftName(nextName);
    if (nextPhone && bookingModalNormalizePhone(nextPhone) !== bookingModalNormalizePhone(draftPhone)) setDraftPhone(nextPhone);
    if (nextEmail && nextEmail.toLowerCase() !== String(draftEmail || "").trim().toLowerCase()) setDraftEmail(nextEmail);
  }, [
    open,
    hasCanonicalLoopProfile,
    identitySnapshot?.name,
    identitySnapshot?.customerName,
    identitySnapshot?.phone,
    identitySnapshot?.phoneNormalized,
    identitySnapshot?.phoneE164,
    identitySnapshot?.email,
    identitySnapshot?.emailNormalized,
    draftName,
    draftPhone,
    draftEmail,
  ]);

  const submitLabel = (() => {
    if (savingCustomer) return "Buscando...";
    if (hasMissingProfilePhone) return "Falta WhatsApp en perfil";
    if (hasCanonicalLoopProfile && hasExistingCustomer) return "Sincronizar y usar usuario";
    if (hasCanonicalLoopProfile && hasPlatformUser) return "Vincular usuario Loop";
    if (hasExistingCustomer && hasSoftFieldConflict) return "Actualizar y usar cliente";
    if (hasExistingCustomer) return "Usar cliente existente";
    if (hasPlatformUser) return "Vincular usuario";
    if (hasConflict) return "Revisa los datos";
    return "Guardar cliente";
  })();

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (hasBlockingIdentityIssue) return;
    const name = String(hasCanonicalLoopProfile ? (identitySnapshot?.name || draftName) : (draftName || identitySnapshot?.name || "")).trim();
    if (!name) {
      try { nameInputRef.current?.focus?.(); } catch (_) {}
      return;
    }
    setSavingCustomer(true);
    try {
      await Promise.resolve(onSave({
        name,
        phone: bookingModalPhoneForStorage(hasCanonicalLoopProfile ? (identitySnapshot?.phone || identitySnapshot?.phoneNormalized || draftPhone || "") : (draftPhone || identitySnapshot?.phone || "")),
        email: String(hasCanonicalLoopProfile ? (identitySnapshot?.email || identitySnapshot?.emailNormalized || draftEmail || "") : (draftEmail || identitySnapshot?.email || "")).trim(),
        identityResolution,
      }));
    } finally {
      setSavingCustomer(false);
    }
  };

  return (
    <AccessibleModal
      open={open}
      onClose={onClose}
      labelledBy="new-customer-title"
      width={440}
      dialogClassName="loop-nested-settings-modal loop-new-customer-stable-modal"
      dialogStyle={{ padding: 0, overflow: "hidden", borderRadius: 18 }}
    >
      <form
        onSubmit={handleSubmit}
        onPointerDown={stopBookingInputPointerPropagation}
        onTouchStart={stopBookingInputPointerPropagation}
        onClick={stopBookingInputPointerPropagation}
      >
        <div className="loop-settings-modal-head">
          <div>
            <strong id="new-customer-title">{t("Nuevo cliente")}</strong>
            <span>{t("Completa solo los datos necesarios.")}</span>
          </div>
          <button type="button" onClick={onClose} aria-label={t("Cerrar")}>✕</button>
        </div>
        <div className="loop-settings-modal-body">
          <div className="loop-settings-grid is-single">
            <label className="booking-pro-field">
              <span>{t("Nombre")}</span>
              <input
                ref={nameInputRef}
                value={draftName}
                onPointerDown={stopBookingInputPointerPropagation}
                onTouchStart={stopBookingInputPointerPropagation}
                onFocus={stabilizeBookingMobileInput}
                onChange={(e) => setDraftName(e.target.value)}
                placeholder={t("Nombre del cliente")}
                autoComplete="name"
                enterKeyHint="next"
              />
            </label>
            <label className="booking-pro-field loop-phone-field">
              <span>WhatsApp</span>
              <LoopPhoneInput
                value={draftPhone}
                onPointerDown={stopBookingInputPointerPropagation}
                onTouchStart={stopBookingInputPointerPropagation}
                onFocus={stabilizeBookingMobileInput}
                onChange={setDraftPhone}
              />
            </label>
            <label className="booking-pro-field">
              <span>{t("Email opcional")}</span>
              <input
                value={draftEmail}
                onPointerDown={stopBookingInputPointerPropagation}
                onTouchStart={stopBookingInputPointerPropagation}
                onFocus={stabilizeBookingMobileInput}
                onChange={(e) => setDraftEmail(e.target.value)}
                placeholder="cliente@ejemplo.com"
                autoComplete="email"
                inputMode="email"
                enterKeyHint="done"
              />
            </label>
          </div>

          {identityLoading ? (
            <div className="loop-identity-resolve-card is-loading">
              <span className="loop-identity-dot" />
              <div>
                <strong>{t("Revisando identidad...")}</strong>
                <small>{t("Buscando en clientes de esta empresa y usuarios de Loop.")}</small>
              </div>
            </div>
          ) : null}

          {identityError && !identityLoading ? (
            <div className="loop-identity-resolve-card is-warning">
              <span className="loop-identity-dot">i</span>
              <div>
                <strong>{t("Validación no disponible")}</strong>
                <small>{identityError}</small>
              </div>
            </div>
          ) : null}

          {hasExistingCustomer ? (
            <div className={`loop-identity-resolve-card is-existing ${hasSoftFieldConflict ? "has-soft-conflict" : ""}`}>
              <span className="loop-identity-dot">✓</span>
              <div>
                <strong>{hasCanonicalLoopProfile ? "Usuario Loop · sincronizar cliente" : hasSoftFieldConflict ? "Cliente existente · actualizar contacto" : "Cliente existente"}</strong>
                <small>{identitySnapshot.name || "Cliente"}{identitySnapshot.email ? ` · ${identitySnapshot.email}` : ""}{identitySnapshot.phone ? ` · ${identitySnapshot.phone}` : ""}</small>
                <em>{hasCanonicalLoopProfile ? "Este cliente se sincronizará con el nombre, email y WhatsApp de su perfil Loop." : hasSoftFieldConflict ? "Loop mantendrá el WhatsApp/email que acabas de escribir y actualizará este cliente." : "Este contacto ya está registrado en este negocio. No se creará duplicado."}</em>
                {hasSoftFieldConflict ? (
                  <ul className="loop-identity-field-conflicts">
                    {visibleIdentityFieldConflicts.map((item, idx) => (
                      <li key={`${item.field}-${idx}`}><span>{item.label || item.field}</span><strong>{item.typed || item.canonical || "—"}</strong><small>{t("antes:") + " "}{item.existing || "—"}</small></li>
                    ))}
                  </ul>
                ) : null}
              </div>
              <button type="button" onClick={applyIdentitySnapshotToDraft}>{hasCanonicalLoopProfile ? "Usar perfil Loop" : "Completar faltantes"}</button>
            </div>
          ) : null}

          {hasPlatformUser ? (
            <div className={`loop-identity-resolve-card is-platform ${hasSoftFieldConflict ? "has-soft-conflict" : ""}`}>
              <span className="loop-identity-dot">∞</span>
              <div>
                <strong>{t("Usuario de Loop encontrado")}</strong>
                <small>{identitySnapshot.name || "Usuario Loop"}{identitySnapshot.email ? ` · ${identitySnapshot.email}` : ""}{identitySnapshot.phone ? ` · ${identitySnapshot.phone}` : ""}</small>
                <em>{hasCanonicalLoopProfile ? "Se usará el nombre, email y WhatsApp guardados en ProfilePage." : hasSoftFieldConflict ? "Se vinculará la cuenta, pero se conservarán los datos que escribiste en este cliente." : "Se vinculará este usuario con el cliente de esta empresa."}</em>
              </div>
              <button type="button" onClick={applyIdentitySnapshotToDraft}>{hasCanonicalLoopProfile ? "Usar perfil Loop" : "Completar faltantes"}</button>
            </div>
          ) : null}

          {hasMissingProfilePhone ? (
            <div className="loop-identity-resolve-card is-conflict">
              <span className="loop-identity-dot">!</span>
              <div>
                <strong>{t("Usuario Loop sin WhatsApp")}</strong>
                <small>{identityResolution?.conflict?.message || "Este email pertenece a un usuario Loop, pero su perfil no tiene WhatsApp."}</small>
                <em>{t("El cliente debe actualizar su ProfilePage antes de crear la reserva.")}</em>
              </div>
            </div>
          ) : null}

          {hasConflict ? (
            <div className="loop-identity-resolve-card is-conflict">
              <span className="loop-identity-dot">!</span>
              <div>
                <strong>{t("Revisa los datos")}</strong>
                <small>{identityConflict.message || "El WhatsApp y el email parecen pertenecer a personas diferentes."}</small>
                <em>{t("Cambia el email o el WhatsApp antes de guardar.")}</em>
              </div>
            </div>
          ) : null}
        </div>
        <div className="loop-settings-modal-foot">
          <button type="submit" disabled={savingCustomer || hasBlockingIdentityIssue}>{submitLabel}</button>
        </div>
      </form>
    </AccessibleModal>
  );
}

export default function BookingCreateModal({
  open,
  onClose,
  date,
  defaultResourceId,
  employees = [],
  operationalEmployees = null,
  services = [],
  existingEvents = [],
  existingBookings = [],
  onCreate,
  initialBooking = null,
  initialTime = null,
  businessId = null,
  businessDoc = null,
  isEventBusiness = false,
  permissions = null,
  isOwnerOrAdmin = false,
  businessSettings = null,
  calendarSettings = null,
}) {
  useLanguage();
  const editMode = Boolean(initialBooking && initialBooking.id);

  const staffEmployees = useMemo(() => {
    const list = Array.isArray(operationalEmployees) ? operationalEmployees : employees;
    return Array.isArray(list) ? list : [];
  }, [operationalEmployees, employees]);
  const pad2 = (n) => String(n).padStart(2, "0");
  const eventMode = isEventBusinessDoc(businessDoc, isEventBusiness);
  const soloBusinessMode = isSoloBookingBusiness(businessDoc || {}, staffEmployees);
  const workerNotRequired = eventMode || soloBusinessMode;
  // LOOP WORKSPACE V4.1 — only show event package/persona UI for explicit event businesses.
  // Some normal service businesses can have noWorkerMode/metadata that made eventMode true.
  // eventMode may still be used for scheduling behavior, but this flag controls the visual event package flow.
  const showEventPackageFlow = isEventBusiness === true || ["event", "events", "evento", "eventos"].includes(String(
    businessDoc?.businessMode ||
    businessDoc?.businessModel ||
    businessDoc?.businessType ||
    businessDoc?.vertical ||
    ""
  ).trim().toLowerCase());
  // LOOP PHASE 2.0.4 — reservation creation respects role permissions + Business OS settings.
  const modalPermissions = useMemo(() => normalizeWorkerPermissions(permissions), [permissions]);
  const loopSettings = useMemo(() => normalizeLoopBusinessSettings(businessSettings || businessDoc || {}), [businessSettings, businessDoc]);

  // LOOP BOOKING MOBILE WORKER CUSTOMER + COMPACT UX V13
  // Native keyboards shrink visualViewport without changing layout viewport. Keep a CSS
  // variable updated so the sticky submit footer and result lists can leave enough room.
  useEffect(() => {
    if (!open || typeof window === "undefined" || typeof document === "undefined") return undefined;
    const root = document.documentElement;
    const body = document.body;
    const vv = window.visualViewport || null;
    const updateKeyboardOffset = () => {
      try {
        const viewportHeight = vv && Number(vv.height) ? Number(vv.height) : window.innerHeight;
        const viewportTop = vv && Number(vv.offsetTop) ? Number(vv.offsetTop) : 0;
        const layoutHeight = window.innerHeight || viewportHeight;
        const keyboardOffset = Math.max(0, Math.round(layoutHeight - viewportHeight - viewportTop));
        root.style.setProperty("--loop-booking-keyboard-offset", `${keyboardOffset}px`);
        body.classList.toggle("loop-booking-keyboard-active", keyboardOffset > 80);
      } catch (_) {}
    };
    updateKeyboardOffset();
    vv?.addEventListener?.("resize", updateKeyboardOffset);
    vv?.addEventListener?.("scroll", updateKeyboardOffset);
    window.addEventListener("resize", updateKeyboardOffset);
    return () => {
      vv?.removeEventListener?.("resize", updateKeyboardOffset);
      vv?.removeEventListener?.("scroll", updateKeyboardOffset);
      window.removeEventListener("resize", updateKeyboardOffset);
      try {
        root.style.removeProperty("--loop-booking-keyboard-offset");
        body.classList.remove("loop-booking-keyboard-active");
      } catch (_) {}
    };
  }, [open]);

  const bookingTimeFormat = useMemo(() => {
    const candidates = [
      calendarSettings?.timeFormat,
      businessSettings?.calendarSettings?.timeFormat,
      businessSettings?.timeFormat,
      businessDoc?.calendarSettings?.timeFormat,
      businessDoc?.timeFormat,
    ];
    return candidates.some((value) => String(value || "").toLowerCase() === "12h") ? "12h" : "24h";
  }, [calendarSettings, businessSettings, businessDoc]);
  const inventoryModuleEnabled = loopSettings.enabledModules.inventory !== false && loopSettings.inventorySettings.enabled !== false && loopSettings.inventorySettings.plan !== "disabled";
  const canViewPrices = isOwnerOrAdmin || modalPermissions.canViewPrices;
  const canViewFinancials = isOwnerOrAdmin || modalPermissions.canViewFinancials;
  const canViewPaymentInfo = isOwnerOrAdmin || modalPermissions.canViewFinancials || modalPermissions.canViewPaymentMethods || modalPermissions.canEditPayments;
  const canViewCommissions = Boolean(loopSettings.financeSettings?.showWorkerPayouts !== false) && (isOwnerOrAdmin || modalPermissions.canViewCommissions);
  const canEditPayments = isOwnerOrAdmin || modalPermissions.canEditPayments;
  const canEditCommissions = isOwnerOrAdmin || modalPermissions.canEditCommissions;
  const canCompleteBookings = isOwnerOrAdmin || modalPermissions.canCompleteBookings;
  const canCancelBookings = isOwnerOrAdmin || modalPermissions.canCancelBookings;
  const canViewPaymentSection = canViewPaymentInfo || canViewCommissions;
  // LOOP BOOKING UX V2 A2.1 — manual booking-payment tracking stays enabled by
  // default. This intentionally does NOT reuse enabledModules.payments because
  // that switch currently governs online/deposit payment capability, not manual
  // booking collection tracking. A future Business OS toggle can set
  // financeSettings.trackBookingPayments = false to remove booking collection UI.
  const bookingPaymentTrackingEnabled = loopSettings.financeSettings?.trackBookingPayments !== false;
  const canShowBookingPaymentUi = bookingPaymentTrackingEnabled && canViewPaymentInfo;
  const canViewInventory = inventoryModuleEnabled && (isOwnerOrAdmin || modalPermissions.canViewInventory);
  const canEditInventory = inventoryModuleEnabled && (isOwnerOrAdmin || modalPermissions.canEditInventory);
  // LOOP PHASE 2.1.1 — product sales obey inventory module, stock policy and payment permissions.
  const canEditProductSales = inventoryModuleEnabled && (canEditInventory || canEditPayments);
  const allowNegativeProductStock = Boolean(loopSettings.inventorySettings?.allowNegativeStock);
  const productStockAutoDeductEnabled = Boolean(loopSettings.inventorySettings?.autoDeduct !== false && loopSettings.inventoryAutoDeduct !== false);
  const productStockDeductTrigger = loopSettings.inventorySettings?.deductTrigger || loopSettings.inventoryDeductTrigger || "completed";

  const [customerId, setCustomerId] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerSnapshot, setCustomerSnapshot] = useState(null);
  const [customerWhatsappOptOut, setCustomerWhatsappOptOut] = useState(false);

  // LOOP BOOKING CUSTOMER NAME SCOPE HOTFIX V6B
  // Render-level labels need a safe customer name too; submit still recalculates
  // with the DOM fallback inside handleSubmit.
  const effectiveCustomerName = useMemo(
    () => bookingModalCustomerDisplayName(customerName, customerSnapshot, ""),
    [customerName, customerSnapshot]
  );
  const [time, setTime] = useState("10:00");
  const bookingTimeLabel = useMemo(() => bookingModalFormatTimeLabel(time, bookingTimeFormat), [time, bookingTimeFormat]);
  const [bookingDateValue, setBookingDateValue] = useState("");
  const [bookingStatus, setBookingStatus] = useState("confirmed");
  const [resourceId, setResourceId] = useState("");
  const [duration, setDuration] = useState(60);
  const [serviceId, setServiceId] = useState("");
  const [visitTypeId, setVisitTypeId] = useState("");
  const [selectedExtraIds, setSelectedExtraIds] = useState([]);
  const [assignedStaffIds, setAssignedStaffIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [whatsappManualSending, setWhatsappManualSending] = useState(false);
  const [whatsappManualStatus, setWhatsappManualStatus] = useState("");
  const [loyaltyReprocessSending, setLoyaltyReprocessSending] = useState(false);
  const [loyaltyReprocessStatus, setLoyaltyReprocessStatus] = useState("");
  const [loyaltyReverseSending, setLoyaltyReverseSending] = useState(false);
  const [loyaltyReverseStatus, setLoyaltyReverseStatus] = useState("");
  const [loyaltyReverseDone, setLoyaltyReverseDone] = useState(false);
  const [membershipReprocessSending, setMembershipReprocessSending] = useState(false);
  const [membershipReprocessStatus, setMembershipReprocessStatus] = useState("");
  const [membershipBenefits, setMembershipBenefits] = useState([]);
  const [membershipBenefitsLoading, setMembershipBenefitsLoading] = useState(false);
  const [selectedMembershipBenefitId, setSelectedMembershipBenefitId] = useState("");
  const [membershipBenefitsError, setMembershipBenefitsError] = useState("");
  const [wompiSessionLoading, setWompiSessionLoading] = useState(false);
  const [wompiSessionStatus, setWompiSessionStatus] = useState("");
  const [wompiSession, setWompiSession] = useState(null);
  const [whatsappLogRows, setWhatsappLogRows] = useState([]);
  const [whatsappLogLoading, setWhatsappLogLoading] = useState(false);
  const [customerLocation, setCustomerLocation] = useState(null);
  const [customerExactAddress, setCustomerExactAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("pending");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [amountCharged, setAmountCharged] = useState("");
  const [verifiedOnlinePaidCents, setVerifiedOnlinePaidCents] = useState(null);
  const [paymentHistoryEntries,setPaymentHistoryEntries]=useState([]);
  const [collectionApplied, setCollectionApplied] = useState(false);
  useEffect(() => {
    setCollectionApplied(false);
    setVerifiedOnlinePaidCents(null);
    setPaymentHistoryEntries([]);
    if (!open || !businessId || !initialBooking?.id) return;
    let active=true;
    getDocs(collection(db, "businesses", businessId, "bookings", initialBooking.id, "paymentLedger")).then(snap=>{
      const entries=snap.docs.map(d=>({...d.data(),id:d.id}));
      if(active)setPaymentHistoryEntries(entries);
      if(active && entries.length) setVerifiedOnlinePaidCents(Math.max(0,entries.filter(e=>e.provider==="wompi"&&e.status==="approved"&&e.providerMode==="production").reduce((sum,e)=>sum+(e.direction==="refund"?-1:1)*Number(e.serviceCents||0),0)));
    }).catch(()=>{});
    return ()=>{active=false;};
  },[open,businessId,initialBooking?.id]);
  const [discountAmount, setDiscountAmount] = useState("");
  const [tipAmount, setTipAmount] = useState("");
  const [tipPaymentMethod, setTipPaymentMethod] = useState("cash");
  const [workerCommissionType, setWorkerCommissionType] = useState("none");
  const [workerCommissionValue, setWorkerCommissionValue] = useState("");
  const [commissionIncludesTips, setCommissionIncludesTips] = useState(false);
  const [commissionOnlyCompleted, setCommissionOnlyCompleted] = useState(true);
  const [workerPayoutStatus, setWorkerPayoutStatus] = useState("pending");
  const [workerPaymentNotes, setWorkerPaymentNotes] = useState("");
  const [commissionManuallyEdited, setCommissionManuallyEdited] = useState(false);
  const [showPaymentAdvanced, setShowPaymentAdvanced] = useState(false);
  const [showPaymentSettingsModal, setShowPaymentSettingsModal] = useState(false);
  const [showTipPaymentMethodOverride, setShowTipPaymentMethodOverride] = useState(false);
  const [showCommissionSettingsModal, setShowCommissionSettingsModal] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const moreActionsRef = useRef(null);
  const [showWhatsAppDetails, setShowWhatsAppDetails] = useState(false);
  const [showMembershipDetails, setShowMembershipDetails] = useState(false);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [customerProfileEditMode, setCustomerProfileEditMode] = useState(false);
  const [customerProfileEditDraft, setCustomerProfileEditDraft] = useState({ name: "", phone: "", email: "" });
  const [customerProfileSaving, setCustomerProfileSaving] = useState(false);
  const [customerProfileEditError, setCustomerProfileEditError] = useState("");
  const [showReservationSummary, setShowReservationSummary] = useState(false);
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
  // LOOP PHASE 2.5D POLISH — professional lifecycle confirmation, no browser confirm().
  const [bookingLifecycleDecision, setBookingLifecycleDecision] = useState(null);
  const [bookingDeleteDecision, setBookingDeleteDecision] = useState(null);
  // LOOP PHASE 2.5D POLISH V2 — replace browser alerts with branded Loop guardrail modal.
  const [bookingGuardrailNotice, setBookingGuardrailNotice] = useState(null);
  const showBookingGuardrail = (notice = {}) => {
    setBookingGuardrailNotice({
      tone: notice.tone || "warning",
      eyebrow: notice.eyebrow || "Revisa esta reserva",
      title: notice.title || "Falta información",
      message: notice.message || "Completa la información necesaria para continuar.",
      primaryLabel: notice.primaryLabel || "Entendido",
      summary: Array.isArray(notice.summary) ? notice.summary : [],
    });
    return false;
  };
  const [serviceSearch, setServiceSearch] = useState("");
  const [showAllServices, setShowAllServices] = useState(false);
  const [showAvailabilitySlotsModal, setShowAvailabilitySlotsModal] = useState(false);
  const [showDateTimeModal, setShowDateTimeModal] = useState(false);
  const [showCustomerLocationPicker, setShowCustomerLocationPicker] = useState(false);
  const [showRetailAddons, setShowRetailAddons] = useState(false);
  const [showInternalNotes, setShowInternalNotes] = useState(false);
  const [mobileEditExpanded, setMobileEditExpanded] = useState(false);
  // LOOP BOOKING UX V2 A2.2B — edit mode opens as a compact management
  // workspace. The original full editor remains available on demand so we
  // preserve every existing field, validation rule and save payload.
  const [inlineCustomerEditing, setInlineCustomerEditing] = useState(false);
  const [inlineContactEditing, setInlineContactEditing] = useState(false);
  const [priceOverride,setPriceOverride]=useState(null);
  const [priceAdjustmentDraft,setPriceAdjustmentDraft]=useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [retailRows, setRetailRows] = useState([]);
  const [retailSearch, setRetailSearch] = useState("");
  const [bookingModalLoadedServices, setBookingModalLoadedServices] = useState([]);
  const [bookingModalServicesLoading, setBookingModalServicesLoading] = useState(false);
  const [bookingModalServicesLoadError, setBookingModalServicesLoadError] = useState("");
  const customerEmailInputRef = useRef(null);
  const selectedCustomerDraftRef = useRef(null);
  const bookingModalHydratedSignatureRef = useRef("");
  const autoAmountChargedRef = useRef("");
  const bookingModalPropServicesCount = Array.isArray(services) ? services.length : 0;

  // LOOP BOOKING SERVICE ASSIGNMENT FIX V2
  // Some businesses store worker-service assignments in different shapes:
  // services, assignedServiceIds, serviceMap, commissions by service, etc.
  // Normalize all possible shapes so the service picker does not show a false
  // "0 servicios disponibles" state when the worker actually has services.
  function normalizeKey(value) {
    return String(value || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }

  const serviceKeyCandidates = (value) => {
    const out = [];
    const add = (raw) => {
      const clean = String(raw || "").trim();
      if (!clean) return;
      const normalized = normalizeKey(clean);
      if (normalized) out.push(normalized);
      clean.split(/[\/]/).filter(Boolean).slice(-2).forEach((part) => {
        const partKey = normalizeKey(part);
        if (partKey) out.push(partKey);
      });
    };

    if (value == null) return out;

    if (typeof value === "string" || typeof value === "number") {
      add(value);
      return Array.from(new Set(out.filter(Boolean)));
    }

    if (typeof value === "object" && !Array.isArray(value)) {
      [
        value.id,
        value.serviceId,
        value.serviceID,
        value.service_id,
        value.uid,
        value.docId,
        value.refId,
        value.value,
        value.key,
        value.slug,
        value.path,
        value.name,
        value.title,
        value.label,
        value.serviceName,
      ].forEach(add);
    }

    return Array.from(new Set(out.filter(Boolean)));
  };

  const serviceValueList = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === "object") {
      return Object.entries(value).flatMap(([key, item]) => {
        if (item === false || item === "false" || item?.enabled === false || item?.active === false) return [key];
        return [key, item];
      });
    }
    return [value];
  };

  const collectEmployeeServiceObjects = (emp) => {
    const out = [];
    const pushMaybeService = (item) => {
      if (!item || typeof item !== "object" || Array.isArray(item)) return;
      const id = item.id || item.serviceId || item.serviceID || item.service_id || item.value || item.key || item.docId || item.uid || item.path;
      const name = item.name || item.title || item.label || item.serviceName;
      const looksLikeService = Boolean(id || name || item.price != null || item.duration != null || item.durationMinutes != null || item.defaultDuration != null);
      if (!looksLikeService) return;
      out.push({
        ...item,
        id: String(id || name || `service-${out.length}`).trim(),
        name: name || item.title || item.label || String(id || "Servicio"),
      });
    };

    [
      emp?.services,
      emp?.assignedServices,
      emp?.servicesAssigned,
      emp?.serviceAssignments,
      emp?.serviceOptions,
      emp?.allowedServices,
    ].forEach((list) => {
      if (Array.isArray(list)) list.forEach(pushMaybeService);
    });

    return out;
  };

  const employeeDerivedServices = useMemo(() => {
    const map = new Map();
    (Array.isArray(staffEmployees) ? staffEmployees : []).forEach((emp) => {
      collectEmployeeServiceObjects(emp).forEach((service) => {
        const key = serviceKeyCandidates(service)[0] || normalizeKey(service.name);
        if (!key || map.has(key)) return;
        map.set(key, service);
      });
    });
    return Array.from(map.values());
  }, [staffEmployees]);

  // LOOP BOOKING SERVICE SOURCE FALLBACK V3
  // AdminBookingCalendar normally passes services as a prop, but some runtime
  // paths can reach the modal before that array is hydrated. In that case the
  // service picker showed 0 services even though Firestore had business services.
  // Load the business service collection directly as a safe fallback.
  useEffect(() => {
    let alive = true;
    const shouldLoad = open && businessId && bookingModalPropServicesCount <= 0;

    if (!shouldLoad) {
      setBookingModalServicesLoading(false);
      setBookingModalServicesLoadError("");
      if (bookingModalPropServicesCount > 0) setBookingModalLoadedServices([]);
      return () => { alive = false; };
    }

    setBookingModalServicesLoading(true);
    setBookingModalServicesLoadError("");

    (async () => {
      try {
        let rows = [];
        const path = `businesses/${String(businessId)}/services`;

        if (isIosCapacitor() && fsClient && typeof fsClient.listCollectionByPath === "function") {
          rows = await bookingModalWithTimeout(
            fsClient.listCollectionByPath(path, { limit: 300 }, { db }),
            6500,
            []
          );
        } else {
          const snap = await getDocs(collection(db, "businesses", String(businessId), "services"));
          rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) }));
        }

        const normalized = (Array.isArray(rows) ? rows : [])
          .map((row, index) => ({
            ...(row || {}),
            id: String(row?.id || row?.serviceId || row?.docId || `service-${index}`).trim(),
          }))
          .filter((row) => row.id || row.name || row.title || row.serviceName);

        if (!alive) return;
        setBookingModalLoadedServices(normalized);
      } catch (error) {
        console.warn("[BookingCreateModal] fallback service load failed", error);
        if (!alive) return;
        setBookingModalLoadedServices([]);
        setBookingModalServicesLoadError(error?.message || "No pudimos cargar servicios.");
      } finally {
        if (alive) setBookingModalServicesLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [open, businessId, bookingModalPropServicesCount]);

  const bookingModalServiceSource = useMemo(() => {
    const base = Array.isArray(services) ? services : [];
    if (base.length) return base;
    if (bookingModalLoadedServices.length) return bookingModalLoadedServices;
    return employeeDerivedServices;
  }, [services, bookingModalLoadedServices, employeeDerivedServices]);


  const selectedService = useMemo(() => {
    return (bookingModalServiceSource || []).find((s) => String(s.id || s.serviceId || s.name) === String(serviceId)) || null;
  }, [bookingModalServiceSource, serviceId]);

  const sessionPrograms = useSessionPrograms(businessId, customerId, open && !!selectedService?.sessionPackage?.enabled && !editMode);
  const [sessionAction,setSessionAction] = useState('continue');
  const [sessionPrevious,setSessionPrevious] = useState(0);
  const [sessionNewConfirmed,setSessionNewConfirmed] = useState(false);
  const collectionAttempt=useRef(null);
  const [sessionPaymentsOpen,setSessionPaymentsOpen]=useState(false);
  useEffect(()=>setSessionNewConfirmed(false),[customerId,serviceId,open,sessionAction]);
  useEffect(()=>{setSessionAction('continue');setSessionPrevious(0);},[customerId,serviceId,open]);
  const sessionProgram = selectSessionProgram(sessionPrograms.programs,serviceId,initialBooking?.requestedSessionProgramId);
  const continuingSession = !editMode && !!selectedService?.sessionPackage?.enabled && !!sessionProgram && sessionAction!=='new';
  const loadMembershipBenefitsForBooking = async () => {
    if (!LOOP_BOOKING_MEMBERSHIPS_ENABLED || !open || !businessId || !customerId) {
      setMembershipBenefits([]);
      setSelectedMembershipBenefitId("");
      setMembershipBenefitsError("");
      setMembershipBenefitsLoading(false);
      return;
    }
    setMembershipBenefitsLoading(true);
    setMembershipBenefitsError("");
    try {
      const callable = httpsCallable(getFunctions(), "getApplicableMembershipBenefits");
      const res = await callable({
        businessId,
        customerId,
        booking: {
          customerId,
          serviceId: serviceId || null,
          serviceName: selectedService?.name || selectedService?.title || selectedService?.serviceName || null,
        },
      });
      const rawItems = Array.isArray(res?.data?.items) ? res.data.items : [];
      const items = [...rawItems];
      const existingMemberId = initialBooking?.customerMembershipId || initialBooking?.membershipId;
      if (existingMemberId && (initialBooking?.membershipApplied === true || initialBooking?.membershipBenefitApplied === true)) {
        const exists = items.some((item) => String(item?.membership?.id || item?.membershipId || "") === String(existingMemberId));
        if (!exists) {
          const type = initialBooking?.membershipBenefitType || (Number(initialBooking?.membershipCreditsUsed || 0) > 0 ? "credit" : "discount");
          items.unshift({
            id: `${existingMemberId}:${type}:existing`,
            type,
            membership: { id: existingMemberId, planId: initialBooking?.membershipPlanId || null, planName: initialBooking?.membershipPlanName || "Membresía" },
            plan: { id: initialBooking?.membershipPlanId || null, name: initialBooking?.membershipPlanName || "Membresía" },
            remainingCredits: Math.max(0, Number(initialBooking?.membershipCreditsAfter ?? 0) || 0),
            discountPercent: Math.max(0, Number(initialBooking?.membershipDiscountPercent || 0) || 0),
            label: initialBooking?.membershipBenefitLabel || (type === "credit" ? `Usar 1 crédito · ${initialBooking?.membershipPlanName || "Membresía"}` : `Aplicar descuento · ${initialBooking?.membershipPlanName || "Membresía"}`),
            preservedFromBooking: true,
          });
        }
      }
      setMembershipBenefits(items);
      setSelectedMembershipBenefitId((prev) => {
        if (prev && items.some((item) => String(item.id) === String(prev))) return prev;
        if (existingMemberId) {
          const existing = items.find((item) => String(item?.membership?.id || item?.membershipId || "") === String(existingMemberId));
          if (existing?.id) return existing.id;
        }
        return items[0]?.id || "";
      });
    } catch (e) {
      console.error("[BookingCreateModal] membership benefits failed", e);
      setMembershipBenefits([]);
      setSelectedMembershipBenefitId("");
      setMembershipBenefitsError("No pudimos cargar beneficios de membresía.");
    } finally {
      setMembershipBenefitsLoading(false);
    }
  };

  useEffect(() => {
    let alive = true;
    if (!LOOP_BOOKING_MEMBERSHIPS_ENABLED || !open || !businessId || !customerId) {
      setMembershipBenefits([]);
      setSelectedMembershipBenefitId("");
      setMembershipBenefitsError("");
      setMembershipBenefitsLoading(false);
      return undefined;
    }
    (async () => {
      if (!alive) return;
      await loadMembershipBenefitsForBooking();
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, businessId, customerId, serviceId]);

  const selectedMembershipBenefit = useMemo(() => {
    if (!LOOP_BOOKING_MEMBERSHIPS_ENABLED) return null;
    return (membershipBenefits || []).find((item) => String(item.id) === String(selectedMembershipBenefitId)) || null;
  }, [membershipBenefits, selectedMembershipBenefitId]);

  const existingMembershipBenefit = useMemo(() => {
    if (!LOOP_BOOKING_MEMBERSHIPS_ENABLED) return null;
    if (!initialBooking || (initialBooking.membershipApplied !== true && initialBooking.membershipBenefitApplied !== true)) return null;
    const memberId = initialBooking.customerMembershipId || initialBooking.membershipId;
    if (!memberId) return null;
    const type = initialBooking.membershipBenefitType || (Number(initialBooking.membershipCreditsUsed || 0) > 0 ? "credit" : "discount");
    return {
      id: `${memberId}:${type}:existing`,
      type,
      membership: { id: memberId, planId: initialBooking.membershipPlanId || null, planName: initialBooking.membershipPlanName || "Membresía" },
      plan: { id: initialBooking.membershipPlanId || null, name: initialBooking.membershipPlanName || "Membresía" },
      remainingCredits: Math.max(0, Number(initialBooking.membershipCreditsAfter ?? 0) || 0),
      discountPercent: Math.max(0, Number(initialBooking.membershipDiscountPercent || 0) || 0),
      label: initialBooking.membershipBenefitLabel || (type === "credit" ? `Usar 1 crédito · ${initialBooking.membershipPlanName || "Membresía"}` : `Aplicar descuento · ${initialBooking.membershipPlanName || "Membresía"}`),
      preservedFromBooking: true,
    };
  }, [initialBooking]);

  const effectiveSelectedMembershipBenefit = LOOP_BOOKING_MEMBERSHIPS_ENABLED ? (selectedMembershipBenefit || existingMembershipBenefit) : null;
  const membershipSummary = LOOP_BOOKING_MEMBERSHIPS_ENABLED ? bookingModalMembershipSummary(initialBooking || {}, effectiveSelectedMembershipBenefit) : null;
  const membershipCreditManualOnlyNotice = LOOP_BOOKING_MEMBERSHIPS_ENABLED && editMode
    && (initialBooking?.membershipApplied === true || initialBooking?.membershipBenefitApplied === true || Boolean(effectiveSelectedMembershipBenefit))
    && LOOP_BOOKING_CANCELLED_STATUSES.has(String(bookingStatus || "").trim().toLowerCase())
    && (String(initialBooking?.membershipBenefitType || effectiveSelectedMembershipBenefit?.type || "").toLowerCase() === "credit" || Number(initialBooking?.membershipCreditsUsed || 0) > 0)
      ? `Esta reserva usó ${Math.max(1, Number(initialBooking?.membershipCreditsUsed || 1))} crédito de membresía. Cancelarla/no-show no restaura el crédito automáticamente; ajuste manual desde CRM si aplica.`
      : "";

  const selectedServiceInventoryUsageRows = useMemo(() => bookingModalInventoryUsageRows(selectedService), [selectedService]);
  const selectedServiceHasInventoryUsage = selectedServiceInventoryUsageRows.length > 0;

  const visitOptions = useMemo(() => normalizeVisitOptions(selectedService, eventMode), [selectedService, eventMode]);
  const extraOptions = useMemo(() => normalizeExtraOptions(selectedService), [selectedService]);

  const selectedVisit = useMemo(() => {
    return visitOptions.find((v) => String(v.id) === String(visitTypeId)) || visitOptions[0] || null;
  }, [visitOptions, visitTypeId]);

  const selectedExtras = useMemo(() => {
    const set = new Set((selectedExtraIds || []).map(String));
    return extraOptions.filter((x) => set.has(String(x.id)));
  }, [extraOptions, selectedExtraIds]);

  const selectedStaff = useMemo(() => {
    const set = new Set((assignedStaffIds || []).map(String));
    return (employees || []).filter((emp) => set.has(String(emp.id)));
  }, [employees, assignedStaffIds]);

  const selectedEmployee = useMemo(() => {
    if (workerNotRequired || !resourceId) return null;
    const pools = [staffEmployees, employees];
    for (const pool of pools) {
      const found = (Array.isArray(pool) ? pool : []).find((emp) => {
        const ids = [emp?.id, emp?.employeeId, emp?.uid, emp?.userUid, emp?.userId, emp?.workerId, emp?.resourceId]
          .map((x) => String(x || "").trim())
          .filter(Boolean);
        return ids.includes(String(resourceId));
      });
      if (found) return found;
    }
    return null;
  }, [staffEmployees, employees, resourceId, workerNotRequired]);

  useEffect(() => {
    let alive = true;
    async function loadInventoryForRetail() {
      if (!open || !businessId) {
        if (alive) setInventoryItems([]);
        return;
      }
      setInventoryLoading(true);
      try {
        const snap = await getDocs(collection(db, `businesses/${businessId}/inventoryItems`));
        const rows = [];
        snap.forEach((d) => {
          const data = d.data() || {};
          rows.push({
            id: d.id,
            ...data,
            name: data.name || data.title || data.itemName || "Producto",
            salePrice: data.salePrice ?? data.price ?? data.unitPrice ?? "",
            unitCost: data.unitCost ?? data.cost ?? "",
            currentStock: data.currentStock ?? data.stock ?? data.quantity ?? null,
          });
        });
        rows.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
        if (alive) setInventoryItems(rows);
      } catch (err) {
        console.warn("[BookingCreateModal] inventory load failed", err);
        if (alive) setInventoryItems([]);
      } finally {
        if (alive) setInventoryLoading(false);
      }
    }
    loadInventoryForRetail();
    return () => { alive = false; };
  }, [open, businessId]);

  const retailSearchNormalized = normalizeText(retailSearch);
  const retailSearchResults = useMemo(() => {
    const list = Array.isArray(inventoryItems) ? inventoryItems : [];
    const selectedIds = new Set((retailRows || []).map((r) => String(r.itemId)));
    return list
      .filter((item) => !selectedIds.has(String(item.id)))
      .filter((item) => {
        if (!retailSearchNormalized) return true;
        const haystack = normalizeText([item.name, item.title, item.category, item.sku, item.barcode].filter(Boolean).join(" "));
        return haystack.includes(retailSearchNormalized);
      })
      .slice(0, 8);
  }, [inventoryItems, retailRows, retailSearchNormalized]);

  const getRetailItemStock = (itemOrRow = {}) => {
    const stockRaw = itemOrRow.currentStock ?? itemOrRow.stock ?? itemOrRow.quantityAvailable ?? itemOrRow.availableStockAtSale ?? null;
    if (stockRaw === null || stockRaw === undefined || stockRaw === "") return null;
    const stock = asMoneyNumber(stockRaw, NaN);
    return Number.isFinite(stock) ? stock : null;
  };

  const retailProductIsBlocked = (item = {}) => {
    if (!canEditProductSales) return true;
    if (allowNegativeProductStock) return false;
    const stock = getRetailItemStock(item);
    return stock !== null && stock <= 0;
  };

  const retailStockLabel = (itemOrRow = {}) => {
    const stock = getRetailItemStock(itemOrRow);
    if (stock === null) return "Sin stock registrado";
    if (stock <= 0) return allowNegativeProductStock ? `Stock ${stock} · permite negativo` : "Sin stock";
    return `Stock ${stock}`;
  };

  const addRetailProduct = (item) => {
    if (!item?.id || retailProductIsBlocked(item)) return;
    const unitPrice = asMoneyNumber(item.salePrice ?? item.price ?? item.unitPrice, 0);
    const unitCost = asMoneyNumber(item.unitCost ?? item.cost, 0);
    setRetailRows((prev) => ([
      ...(Array.isArray(prev) ? prev : []),
      {
        id: `retail-${item.id}-${Date.now()}`,
        itemId: String(item.id),
        itemName: item.name || item.title || "Producto",
        quantity: 1,
        unitPrice,
        unitCost,
        unit: item.unit || item.measureUnit || "unidad",
        availableStockAtSale: getRetailItemStock(item),
        trackStock: item.trackStock !== false,
        workerCommissionType: "none",
        workerCommissionValue: 0,
      }
    ]));
    setRetailSearch("");
  };

  const updateRetailRow = (rowId, key, value) => {
    setRetailRows((prev) => (Array.isArray(prev) ? prev : []).map((row) => (
      row.id === rowId ? { ...row, [key]: value } : row
    )));
  };

  const removeRetailRow = (rowId) => {
    setRetailRows((prev) => (Array.isArray(prev) ? prev : []).filter((row) => row.id !== rowId));
  };

  const normalizedRetailRows = useMemo(() => {
    return (Array.isArray(retailRows) ? retailRows : [])
      .map((row, idx) => {
        const quantity = asMoneyNumber(row.quantity, 0);
        const unitPrice = asMoneyNumber(row.unitPrice, 0);
        const unitCost = asMoneyNumber(row.unitCost, 0);
        if (!row.itemId || quantity <= 0) return null;
        const lineTotal = Math.max(0, quantity * unitPrice);
        const costTotal = Math.max(0, quantity * unitCost);
        const workerCommissionTypeRow = normalizeCommissionType(row.workerCommissionType || row.commissionType || "none");
        const workerCommissionValueRow = asMoneyNumber(row.workerCommissionValue ?? row.commissionValue, 0);
        const workerPayoutAmount = calculateRetailWorkerPayout({
          quantity,
          unitPrice,
          workerCommissionType: workerCommissionTypeRow,
          workerCommissionValue: workerCommissionValueRow,
        });
        return {
          id: row.id || `retail-${idx}`,
          itemId: row.itemId,
          itemName: row.itemName || "Producto",
          quantity,
          unitPrice,
          unitCost,
          unit: row.unit || "unidad",
          availableStockAtSale: getRetailItemStock(row),
          trackStock: row.trackStock !== false,
          stockWarning: !allowNegativeProductStock && getRetailItemStock(row) !== null && quantity > getRetailItemStock(row),
          lineTotal,
          costTotal,
          workerCommissionType: workerCommissionTypeRow,
          workerCommissionValue: workerCommissionValueRow,
          workerPayoutAmount,
        };
      })
      .filter(Boolean);
  }, [retailRows]);

  const retailSalesTotalAmount = normalizedRetailRows.reduce((sum, row) => sum + asMoneyNumber(row.lineTotal, 0), 0);
  const retailSalesCostAmount = normalizedRetailRows.reduce((sum, row) => sum + asMoneyNumber(row.costTotal, 0), 0);
  const retailSalesPayoutAmount = normalizedRetailRows.reduce((sum, row) => sum + asMoneyNumber(row.workerPayoutAmount, 0), 0);
  const retailSalesNetAmount = Math.max(0, retailSalesTotalAmount - retailSalesCostAmount - retailSalesPayoutAmount);
  const retailStockIssues = normalizedRetailRows.filter((row) => row.stockWarning);
  const retailCanSave = canEditProductSales && retailStockIssues.length === 0;
  const retailDeductionHint = productStockAutoDeductEnabled
    ? productStockDeductTrigger === "paid"
      ? "Se descontará al marcar como cobrada."
      : "Se descontará al completar la reserva."
    : "No se descontará stock automáticamente.";

  const applyDefaultCommissionFromEmployee = (employee, force = false) => {
    if (!employee) return;
    if (!force && commissionManuallyEdited) return;
    const commission = getEmployeeDefaultCommission(employee);
    setWorkerCommissionType(commission.type);
    setWorkerCommissionValue(commission.value ? String(commission.value) : "");
    setCommissionIncludesTips(Boolean(commission.includesTips));
    setCommissionOnlyCompleted(commission.onlyCompleted !== false);
    setCommissionManuallyEdited(false);
  };

  useEffect(() => {
    if (!open || eventMode || !selectedEmployee) return;
    if (commissionManuallyEdited) return;
    const commission = getEmployeeDefaultCommission(selectedEmployee);
    setWorkerCommissionType(commission.type);
    setWorkerCommissionValue(commission.value ? String(commission.value) : "");
    setCommissionIncludesTips(Boolean(commission.includesTips));
    setCommissionOnlyCompleted(commission.onlyCompleted !== false);
  }, [open, eventMode, selectedEmployee, commissionManuallyEdited]);

  const getEmployeeServiceKeys = (emp) => {
    const keys = [];
    const collect = (value) => serviceValueList(value).forEach((item) => keys.push(...serviceKeyCandidates(item)));

    [
      emp?.services,
      emp?.serviceIds,
      emp?.serviceIDs,
      emp?.assignedServiceIds,
      emp?.assignedServices,
      emp?.servicesAssigned,
      emp?.serviceAssignments,
      emp?.serviceOptions,
      emp?.allowedServiceIds,
      emp?.allowedServices,
      emp?.publicServiceIds,
      emp?.serviceRefs,
      emp?.serviceMap,
      emp?.servicesMap,
      emp?.serviceCommissions,
      emp?.commissionByService,
      emp?.workerServiceCommissions,
      emp?.serviceRates,
      emp?.serviceSettings,
    ].forEach(collect);

    return Array.from(new Set(keys.filter(Boolean)));
  };

  const getServiceEmployeeKeys = (service) => {
    const keys = [];
    const collect = (value) => serviceValueList(value).forEach((item) => keys.push(...serviceKeyCandidates(item)));

    [
      service?.employeeIds,
      service?.employeeIDs,
      service?.workerIds,
      service?.workerIDs,
      service?.assignedEmployeeIds,
      service?.assignedWorkerIds,
      service?.employees,
      service?.workers,
      service?.staff,
      service?.staffIds,
      service?.team,
    ].forEach(collect);

    return Array.from(new Set(keys.filter(Boolean)));
  };

  const employeeIdentityKeys = (emp) => {
    return serviceKeyCandidates({
      id: emp?.id,
      serviceId: emp?.employeeId,
      uid: emp?.uid,
      docId: emp?.userUid,
      refId: emp?.userId,
      value: emp?.workerId,
      key: emp?.resourceId,
      name: emp?.name,
      title: emp?.displayName,
      label: emp?.role,
    });
  };

  const serviceMatchesEmployee = (service, emp) => {
    if (!emp) return true;

    const assigned = getEmployeeServiceKeys(emp);
    const serviceCandidates = serviceKeyCandidates(service);
    if (assigned.length > 0 && serviceCandidates.some((k) => assigned.includes(k))) return true;

    const serviceEmployees = getServiceEmployeeKeys(service);
    if (serviceEmployees.length > 0) {
      const empKeys = employeeIdentityKeys(emp);
      return empKeys.some((k) => serviceEmployees.includes(k));
    }

    // If the worker does not expose a recognizable service-assignment shape,
    // do not hide all services. This avoids a false "0 servicios disponibles"
    // in businesses whose worker data uses a different schema.
    if (assigned.length === 0) return true;

    return false;
  };

  const filteredServices = useMemo(() => {
    const list = (Array.isArray(bookingModalServiceSource) ? bookingModalServiceSource : []).filter((s) => s?.disabled !== true && s?.active !== false && s?.visible !== false);
    if (eventMode) return list;
    if (!selectedEmployee) return list;
    const matched = list.filter((service) => serviceMatchesEmployee(service, selectedEmployee));
    return matched.length ? matched : list;
  }, [bookingModalServiceSource, selectedEmployee, eventMode]);

  const serviceSearchQuery = normalizeText(serviceSearch);
  const serviceSearchResults = useMemo(() => {
    const list = Array.isArray(filteredServices) ? filteredServices : [];
    if (!serviceSearchQuery) return list;
    return list.filter((service) => {
      const haystack = normalizeText([
        service?.name,
        service?.title,
        service?.description,
        service?.category,
        service?.serviceType,
        service?.duration,
        service?.price,
      ].filter(Boolean).join(" "));
      return haystack.includes(serviceSearchQuery);
    });
  }, [filteredServices, serviceSearchQuery]);

  useEffect(() => {
    if (!open) return undefined;
    try {
      window.__LOOP_BOOKING_SERVICE_DEBUG__ = {
        at: new Date().toISOString(),
        businessId: String(businessId || ""),
        propServicesCount: bookingModalPropServicesCount,
        fallbackLoadedServicesCount: bookingModalLoadedServices.length,
        employeeDerivedServicesCount: employeeDerivedServices.length,
        sourceServicesCount: bookingModalServiceSource.length,
        filteredServicesCount: filteredServices.length,
        serviceSearchResultsCount: serviceSearchResults.length,
        selectedEmployeeId: selectedEmployee?.id || selectedEmployee?.employeeId || selectedEmployee?.uid || "",
        serviceSearch: serviceSearchQuery,
        loading: bookingModalServicesLoading,
        error: bookingModalServicesLoadError,
      };
    } catch (_) {}
    return undefined;
  }, [open, businessId, bookingModalPropServicesCount, bookingModalLoadedServices.length, employeeDerivedServices.length, bookingModalServiceSource.length, filteredServices.length, serviceSearchResults.length, selectedEmployee, serviceSearchQuery, bookingModalServicesLoading, bookingModalServicesLoadError]);

  const serviceQuickResults = useMemo(() => {
    const list = Array.isArray(serviceSearchResults) ? serviceSearchResults : [];
    const selected = list.find((service) => String(service.id) === String(serviceId));
    const base = list.slice(0, 5);
    if (selected && !base.some((service) => String(service.id) === String(selected.id))) {
      return [selected, ...base].slice(0, 6);
    }
    return base.slice(0, 6);
  }, [serviceSearchResults, serviceId]);

  const hhmmFromBooking = (bk) => {
    try {
      const d = bookingScheduledAtToDate(bk);
      if (!d || isNaN(d.getTime())) return null;
      return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    } catch {
      return null;
    }
  };

  const floorMinutesTo15 = (d) => {
    const dd = new Date(d);
    if (isNaN(dd.getTime())) return null;
    dd.setMinutes(Math.floor(dd.getMinutes() / 15) * 15, 0, 0);
    return dd;
  };

  useEffect(() => {
    // LOOP BOOKING IOS WORKER CREATE STABILITY V17
    // This effect seeds the modal only once per open/edit/date/resource identity.
    // iOS worker routes hydrate employees/services in layers; if we let those array
    // updates rerun the whole initializer, the selected customer gets erased after
    // a picker selection or after returning from the quick-create customer modal.
    if (!open) {
      bookingModalHydratedSignatureRef.current = "";
      return;
    }
    const modalSeedDateMs = (() => {
      try {
        const d = date ? new Date(date) : null;
        return d && !Number.isNaN(d.getTime()) ? d.getTime() : "";
      } catch (_) {
        return "";
      }
    })();
    const hydrationSignature = [
      initialBooking?.id || "create",
      modalSeedDateMs,
      initialTime || "",
      defaultResourceId || "",
      eventMode ? "event" : "service",
    ].join("|");
    if (bookingModalHydratedSignatureRef.current === hydrationSignature) return;
    bookingModalHydratedSignatureRef.current = hydrationSignature;

    setCustomerId(initialBooking ? initialBooking.customerId || null : null);
    setCustomerName(initialBooking ? initialBooking.customerName || "" : "");
    setCustomerEmail(initialBooking ? initialBooking.customerEmail || "" : "");
    setCustomerPhone(initialBooking ? bookingModalPhoneForStorage(initialBooking.customerPhone || initialBooking.clientPhone || initialBooking.phone || initialBooking.whatsapp || "") : "");
    setCustomerSnapshot(initialBooking ? initialBooking.customerSnapshot || null : null);
    setCustomerWhatsappOptOut(initialBooking ? bookingModalCustomerWhatsappOptOut(initialBooking) : false);
    setCustomerLocation(initialBooking ? initialBooking.customerLocation || null : null);
    setCustomerExactAddress(initialBooking ? (initialBooking.customerExactAddress || initialBooking.customerAddressDetails || "") : "");
    setNotes(initialBooking ? (initialBooking.notes || initialBooking.internalNotes || initialBooking.eventDetails?.notes || "") : "");
    setRetailRows(Array.isArray(initialBooking?.productSales) ? initialBooking.productSales : Array.isArray(initialBooking?.retailSales) ? initialBooking.retailSales : []);
    setRetailSearch("");
    setShowRetailAddons(Boolean(initialBooking && ((Array.isArray(initialBooking.productSales) && initialBooking.productSales.length) || (Array.isArray(initialBooking.retailSales) && initialBooking.retailSales.length))));
    setBookingStatus(initialBooking ? (() => {
      const rawStatus = String(initialBooking.status || initialBooking.confirmationStatus || "confirmed").trim().toLowerCase();
      return ["in_progress", "in-progress", "en_progreso", "started"].includes(rawStatus) ? "confirmed" : rawStatus;
    })() : "confirmed");
    setBookingDateValue(bookingModalDateInputValue(initialBooking ? (bookingScheduledAtToDate(initialBooking) || initialBooking.startAt || initialBooking.scheduledAt || initialBooking.date) : (date || new Date())));
    setPaymentStatus(initialBooking ? (initialBooking.paymentStatus || initialBooking.payment?.status || "pending") : "pending");
    setPaymentMethod(initialBooking ? (initialBooking.paymentMethod || initialBooking.payment?.method || "cash") : "cash");
    setAmountCharged(initialBooking ? String(initialBooking.amountCharged ?? initialBooking.paidAmount ?? initialBooking.paymentAmount ?? "") : "");
    setDiscountAmount(initialBooking ? String(initialBooking.discountAmount ?? initialBooking.discount ?? "") : "");
    setTipAmount(initialBooking ? String(initialBooking.tipAmount ?? initialBooking.tip ?? "") : "");
    setTipPaymentMethod(initialBooking ? (initialBooking.tipPaymentMethod || initialBooking.payment?.tipPaymentMethod || initialBooking.workerTipPaymentMethod || initialBooking.payment?.workerTipPaymentMethod || "cash") : "cash");
    setWorkerCommissionType(initialBooking ? normalizeCommissionType(initialBooking.workerCommissionType || initialBooking.commissionType || initialBooking.payment?.workerCommissionType || "none") : "none");
    setWorkerCommissionValue(initialBooking ? String(initialBooking.workerCommissionValue ?? initialBooking.commissionValue ?? initialBooking.payment?.workerCommissionValue ?? "") : "");
    setCommissionIncludesTips(false);
    setCommissionOnlyCompleted(initialBooking ? initialBooking.commissionOnlyCompleted !== false : true);
    setWorkerPayoutStatus(initialBooking ? (initialBooking.workerPayoutStatus || initialBooking.payment?.workerPayoutStatus || "pending") : "pending");
    setWorkerPaymentNotes(initialBooking ? String(initialBooking.workerPaymentNotes || initialBooking.payment?.workerPaymentNotes || "") : "");
    setCommissionManuallyEdited(Boolean(initialBooking && (initialBooking.workerCommissionType || initialBooking.commissionType || initialBooking.workerCommissionValue != null || initialBooking.commissionValue != null)));
    setShowPaymentAdvanced(false);
    setShowTipPaymentMethodOverride(false);

    const modalSeedDate = date ? floorMinutesTo15(date) : null;
    const modalSeedTime = modalSeedDate ? `${pad2(modalSeedDate.getHours())}:${pad2(modalSeedDate.getMinutes())}` : "";
    if (initialBooking) setTime(hhmmFromBooking(initialBooking) || modalSeedTime || "10:00");
    else if (modalSeedTime) setTime(modalSeedTime);
    else if (initialTime && /^\d{1,2}:\d{2}$/.test(initialTime)) {
      const [hh, mm] = initialTime.split(":").map((p) => Number(p));
      setTime(`${pad2(hh)}:${pad2(mm)}`);
    } else setTime("10:00");

    const employeePool = Array.isArray(staffEmployees) && staffEmployees.length ? staffEmployees : (Array.isArray(employees) ? employees : []);
    const nextResourceId = workerNotRequired ? "" : (initialBooking ? (initialBooking.employeeId || initialBooking.resourceId || initialBooking.workerId || "") : (defaultResourceId ? String(defaultResourceId) : ""));
    setResourceId(nextResourceId);

    const nextEmployee = nextResourceId ? employeePool.find((emp) => {
      const ids = [emp?.id, emp?.employeeId, emp?.uid, emp?.userUid, emp?.userId, emp?.workerId, emp?.resourceId]
        .map((x) => String(x || "").trim())
        .filter(Boolean);
      return ids.includes(String(nextResourceId));
    }) || null : null;
    if (!initialBooking && nextEmployee) {
      const commission = getEmployeeDefaultCommission(nextEmployee);
      setWorkerCommissionType(commission.type);
      setWorkerCommissionValue(commission.value ? String(commission.value) : "");
      setCommissionIncludesTips(Boolean(commission.includesTips));
      setCommissionOnlyCompleted(commission.onlyCompleted !== false);
    }
    const allServiceRows = bookingModalServiceSource || [];
    const matchedAllowedServices = eventMode ? allServiceRows : (nextEmployee ? allServiceRows.filter((service) => serviceMatchesEmployee(service, nextEmployee)) : allServiceRows);
    const allowedServices = matchedAllowedServices.length ? matchedAllowedServices : allServiceRows;
    const firstAllowed = allowedServices && allowedServices[0] ? allowedServices[0] : null;

    const initialServiceStillAllowed = initialBooking && initialBooking.serviceId && allowedServices.some((s) => String(s.id) === String(initialBooking.serviceId));
    const nextServiceId = initialBooking?.serviceId || "";
    setServiceId(nextServiceId);
    setShowAllServices(false);
    setServiceSearch("");

    const sourceService = allowedServices.find((s) => String(s.id) === String(nextServiceId)) || null;
    const options = normalizeVisitOptions(sourceService);
    const initialVisitType = initialBooking?.visitType || initialBooking?.eventDetails?.visitType || initialBooking?.eventOptions?.visitType || "";
    const nextVisit = options.find((v) => String(v.id) === String(initialVisitType)) || options[0] || null;
    setVisitTypeId(nextVisit ? nextVisit.id : "");

    const initialExtras = initialBooking?.selectedCharacterIds || initialBooking?.eventDetails?.selectedCharacterIds || initialBooking?.eventOptions?.selectedCharacterIds || [];
    setSelectedExtraIds(Array.isArray(initialExtras) ? initialExtras.map(String) : []);

    const initialStaffIds = initialBooking?.assignedEmployeeIds || (Array.isArray(initialBooking?.assignedEmployees) ? initialBooking.assignedEmployees.map((x) => x?.id || x?.employeeId).filter(Boolean) : []);
    setAssignedStaffIds(Array.isArray(initialStaffIds) ? initialStaffIds.map(String) : []);

    const nextDuration = nextVisit?.durationMinutes || sourceService?.duration || sourceService?.durationMinutes || sourceService?.defaultDuration || 60;
    setDuration(initialBooking ? Number(initialBooking.duration || nextDuration) : Number(nextDuration || 60));
    setSaving(false);
    setMobileEditExpanded(false);
    setInlineCustomerEditing(false);
    setInlineContactEditing(false);
    setPriceOverride(initialBooking?.bookingPriceOverride ?? null);
    setPriceAdjustmentDraft(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialBooking?.id, date, initialTime, defaultResourceId, eventMode]);

  useEffect(() => {
    if (!serviceId) return;
    const svc = filteredServices.find((s) => String(s.id) === String(serviceId));
    const opts = normalizeVisitOptions(svc, eventMode);
    const first = opts[0] || null;
    if (!opts.some((v) => String(v.id) === String(visitTypeId))) {
      setVisitTypeId(first ? first.id : "");
    }
    if (!eventMode && svc) {
      const serviceDuration = Number(svc.duration || svc.durationMinutes || svc.defaultDuration || 0);
      if (serviceDuration > 0) setDuration(serviceDuration);
    }
  }, [serviceId, filteredServices, visitTypeId, eventMode]);

  useEffect(() => {
    if (!selectedVisit) return;
    if (eventMode) setDuration(Number(selectedVisit.durationMinutes || 60));
  }, [selectedVisit, eventMode]);

  // A refresh or worker change must never erase the user's selection.
  // Keep it visible and validate against the latest allowed catalog before saving.
  const serviceSelectionUnavailable = Boolean(serviceId) && !bookingModalServicesLoading &&
    !filteredServices.some(s => String(s.id || s.serviceId || "") === String(serviceId));

  const selectedCustomer = Boolean(customerId);
  const activeBookingDate = useMemo(() => bookingModalDateFromYmd(bookingDateValue, date || new Date()), [bookingDateValue, date]);
  const humanDateLabel = activeBookingDate
    ? new Date(activeBookingDate).toLocaleDateString(interfaceLocale(), { weekday: "long", day: "numeric", month: "long", year: "numeric" })
    : "";
  const bookingWorkerOptions = useMemo(() => ([
    { value: "", label: "Sin asignar" },
    ...employees.map((emp) => ({ value: String(emp.id), label: employeeLabel(emp) })),
  ]), [employees]);
  const basePrice = selectedVisit ? asMoneyNumber(selectedVisit.price, 0) : 0;
  const extrasPrice = selectedExtras.reduce((sum, x) => sum + asMoneyNumber(x.price, 0), 0);
  const locationRequired = bookingNeedsCustomerLocation(businessDoc);
  const matchedServiceArea = customerLocation ? findMatchingCoverageArea(businessDoc, customerLocation) : null;
  const hasCoverageOptions = getCoverageAreasForBooking(businessDoc).length > 0;
  const deliveryFee = (locationRequired || eventMode) && matchedServiceArea ? asMoneyNumber(matchedServiceArea.extraFee, 0) : 0;
  const isOutsideCoverage = (locationRequired || eventMode) && customerLocation && hasCoverageOptions && !matchedServiceArea;
  const totalPrice = editMode && initialBooking?.sessionProgramId ? Number(initialBooking.totalPrice ?? 0) : continuingSession ? 0 : priceOverride ?? (basePrice + extrasPrice + deliveryFee);
  const membershipBenefitType = effectiveSelectedMembershipBenefit?.type || "";
  const membershipBenefitDiscountPercent = membershipBenefitType === "discount" ? Math.max(0, Number(effectiveSelectedMembershipBenefit?.discountPercent || 0)) : 0;
  const membershipBenefitDiscountAmount = effectiveSelectedMembershipBenefit
    ? (membershipBenefitType === "credit"
        ? Math.min(totalPrice, Math.max(0, basePrice || totalPrice || 0))
        : Math.max(0, Number(((totalPrice || 0) * membershipBenefitDiscountPercent / 100).toFixed(2))))
    : 0;
  // HOTFIX PRODUCCIÓN: Cobrado representa solo el servicio/base cobrada.
  // La propina vive aparte y no debe autollenarse dentro de Cobrado.
  const expectedChargedAmount = Math.max(0, totalPrice - asMoneyNumber(discountAmount, 0) - membershipBenefitDiscountAmount);
  const bookingCollectedAmount = Math.max(0, asMoneyNumber(amountCharged, 0));
  const bookingOutstandingAmount = Math.max(0, expectedChargedAmount - bookingCollectedAmount);
  const bookingCompactMoney = (value) => {
    const amount = Math.max(0, asMoneyNumber(value, 0));
    return `$${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`;
  };
  const bookingPaymentCompactLabel = (() => {
    if(initialBooking?.sessionProgramId && initialBooking.sessionBillingBookingId!==initialBooking.id || continuingSession)return "Cita incluida · Consulta el saldo del paquete";
    const status = String(paymentStatus || "pending").trim().toLowerCase();
    if (status === "covered") return "Cubierto por beneficio";
    if (status === "refunded") return "Reembolsado";
    if (status === "paid") return `${bookingCompactMoney(bookingCollectedAmount || expectedChargedAmount)} cobrado`;
    if (status === "partial") {
      return bookingOutstandingAmount > 0
        ? `${bookingCompactMoney(bookingCollectedAmount)} cobrado · ${bookingCompactMoney(bookingOutstandingAmount)} pendiente`
        : `${bookingCompactMoney(bookingCollectedAmount)} cobrado`;
    }
    return expectedChargedAmount > 0
      ? `${bookingCompactMoney(expectedChargedAmount)} por cobrar`
      : !serviceId ? "Elige un servicio para calcular el cobro" : "Sin saldo pendiente";
  })();

  useEffect(() => {
    if (!open || priceOverride !== null) return;
    if (paymentStatus !== "paid") {
      autoAmountChargedRef.current = "";
      return;
    }
    const nextAuto = formatMoneyInputValue(expectedChargedAmount);
    const currentRaw = String(amountCharged ?? "").trim();
    const lastAuto = String(autoAmountChargedRef.current || "");
    const currentN = Number(currentRaw);
    const lastAutoN = Number(lastAuto);
    const currentLooksAuto =
      !currentRaw ||
      currentRaw === lastAuto ||
      (!Number.isFinite(currentN)) ||
      (lastAuto && Number.isFinite(currentN) && Number.isFinite(lastAutoN) && Math.abs(currentN - lastAutoN) < 0.005);

    if (currentLooksAuto && currentRaw !== nextAuto) {
      autoAmountChargedRef.current = nextAuto;
      setAmountCharged(nextAuto);
      return;
    }
    if (currentRaw === nextAuto) {
      autoAmountChargedRef.current = nextAuto;
    }
  }, [open, paymentStatus, expectedChargedAmount, amountCharged, priceOverride]);

  const approvalMode = (() => {
    const settings = businessDoc?.calendarSettings && typeof businessDoc.calendarSettings === "object" ? businessDoc.calendarSettings : {};
    const raw = String(settings.approvalMode || settings.onlineApprovalMode || businessDoc?.bookingApprovalMode || businessDoc?.approvalMode || "manual").trim().toLowerCase();
    return raw === "auto" || raw === "automatic" || raw === "automatico" || raw === "automático" ? "auto" : "manual";
  })();
  const autoConfirm = approvalMode === "auto";
  const selectedServiceLabel = selectedService?.name || selectedService?.title || selectedService?.label || "Servicio";
  const selectedWorkerLabel = soloBusinessMode ? "Atención individual" : workerNotRequired ? "No requerido" : (selectedEmployee ? employeeLabel(selectedEmployee) : "Sin asignar");
  const selectedModeLabel = locationRequired ? "A domicilio" : "En local";

  const selectedVisitLabel = selectedVisit?.label || (eventMode ? "Paquete estándar" : "Servicio estándar");


  const editStatus = useMemo(
    () => bookingEditStatusInfo(initialBooking?.status || initialBooking?.confirmationStatus),
    [initialBooking]
  );

  const originalEditSnapshot = useMemo(() => {
    if (!editMode || !initialBooking) return null;
    const originalService = bookingModalServiceSource.find((s) => String(s.id || s.serviceId || s.name) === String(initialBooking.serviceId));
    const originalWorker = employees.find((emp) => String(emp.id) === String(initialBooking.employeeId || initialBooking.resourceId));
    const originalVisit = initialBooking?.visitTypeLabel || initialBooking?.eventDetails?.visitTypeLabel || initialBooking?.eventOptions?.visitTypeLabel || "";
    const originalTotal = initialBooking?.totalPrice ?? initialBooking?.eventDetails?.totalPrice ?? initialBooking?.eventOptions?.totalPrice ?? null;
    return {
      customerName: initialBooking.customerName || "",
      serviceLabel: originalService?.name || originalService?.title || initialBooking.serviceName || initialBooking.service || "Servicio",
      workerLabel: eventMode ? "No requerido" : (originalWorker ? employeeLabel(originalWorker) : (initialBooking.employeeName || initialBooking.workerName || "Sin asignar")),
      time: bookingEditDisplayTime(initialBooking),
      duration: Number(initialBooking.duration || initialBooking.durationMinutes || 60) || 60,
      visitLabel: originalVisit,
      total: originalTotal,
      dateLabel: bookingEditDisplayDate(initialBooking),
    };
  }, [editMode, initialBooking, bookingModalServiceSource, employees, eventMode]);

  const editChangeSummary = useMemo(() => {
    if (!editMode || !originalEditSnapshot) return [];
    const changes = [];
    const push = (label, beforeValue, afterValue, mode = "text") => {
      const beforeText = beforeValue === null || beforeValue === undefined || beforeValue === "" ? "—" : String(beforeValue);
      const afterText = afterValue === null || afterValue === undefined || afterValue === "" ? "—" : String(afterValue);
      if (mode === "number") {
        if (Number(beforeValue || 0) === Number(afterValue || 0)) return;
      } else if (bookingEditNormalizeComparable(beforeText) === bookingEditNormalizeComparable(afterText)) {
        return;
      }
      changes.push({ label, before: beforeText, after: afterText });
    };

    push("Cliente", originalEditSnapshot.customerName, customerName || "Cliente nuevo");
    push("Servicio", originalEditSnapshot.serviceLabel, selectedServiceLabel);
    if (eventMode) push("Paquete", originalEditSnapshot.visitLabel, selectedVisitLabel);
    push("Hora", originalEditSnapshot.time, time);
    push("Duración", originalEditSnapshot.duration, Number(duration || 60), "number");
    push(eventMode ? "Equipo" : "Trabajador", originalEditSnapshot.workerLabel, selectedWorkerLabel);
    if (originalEditSnapshot.total !== null || totalPrice) push("Total", originalEditSnapshot.total ?? 0, totalPrice ?? 0, "number");
    return changes.slice(0, 6);
  }, [editMode, originalEditSnapshot, customerName, selectedServiceLabel, eventMode, selectedVisitLabel, time, duration, selectedWorkerLabel, totalPrice]);

  const existingReservations = useMemo(() => {
    const fromEvents = bookingModalNormalizeExisting(existingEvents, "events");
    const fromBookings = bookingModalNormalizeExisting(existingBookings, "bookings");
    const seen = new Set();
    return [...fromEvents, ...fromBookings].filter((item) => {
      const key = item.id || `${item.start?.getTime()}-${item.end?.getTime()}-${item.title}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [existingEvents, existingBookings]);

  const proposedStart = useMemo(() => bookingModalBuildDateAtTime(activeBookingDate, time), [activeBookingDate, time]);
  const proposedEnd = useMemo(() => {
    if (!proposedStart) return null;
    const safeDuration = Math.max(15, Number(duration || 60));
    return new Date(proposedStart.getTime() + safeDuration * 60000);
  }, [proposedStart, duration]);

  const selectedWorkerHasExplicitAvailability = useMemo(() => {
    return !eventMode && selectedEmployee && bookingModalExplicitAvailabilityRanges(selectedEmployee, activeBookingDate).length > 0;
  }, [eventMode, selectedEmployee, activeBookingDate]);

  const isOutsideWorkerAvailability = useMemo(() => {
    if (eventMode || !selectedEmployee || !selectedWorkerHasExplicitAvailability) return false;
    const ranges = bookingModalExplicitAvailabilityRanges(selectedEmployee, activeBookingDate);
    return !bookingModalSlotInsideRanges(proposedStart, proposedEnd, ranges);
  }, [eventMode, selectedEmployee, selectedWorkerHasExplicitAvailability, activeBookingDate, proposedStart, proposedEnd]);

  const bookingConflict = useMemo(() => {
    if (eventMode) return null;
    return bookingModalFindConflict({
      existingReservations,
      resourceId: soloBusinessMode ? "__loop_company_agenda__" : resourceId,
      start: proposedStart,
      end: proposedEnd,
      initialBookingId: initialBooking?.id,
      initialBooking,
    });
  }, [eventMode, soloBusinessMode, existingReservations, resourceId, proposedStart, proposedEnd, initialBooking]);

  const timeSuggestions = useMemo(() => {
    if (eventMode) return [];
    return bookingModalTimeSuggestions({
      date: activeBookingDate,
      duration,
      resourceId: soloBusinessMode ? "__loop_company_agenda__" : resourceId,
      employee: soloBusinessMode ? {} : selectedEmployee,
      businessDoc,
      existingReservations,
      initialBookingId: initialBooking?.id,
      initialBooking,
      max: 96,
    });
  }, [eventMode, soloBusinessMode, activeBookingDate, duration, resourceId, selectedEmployee, businessDoc, existingReservations, initialBooking]);

  const availabilitySource = useMemo(() => {
    if (eventMode || (!soloBusinessMode && !selectedEmployee)) return null;
    return bookingModalAvailabilitySource(soloBusinessMode ? {} : selectedEmployee, activeBookingDate, businessDoc);
  }, [eventMode, soloBusinessMode, selectedEmployee, activeBookingDate, businessDoc]);
  const availabilityRangeLabel = availabilitySource?.ranges?.length
    ? availabilitySource.ranges.map(bookingModalFormatRangeMinutes).filter(Boolean).join(", ")
    : "";
  const groupedTimeSuggestions = useMemo(() => bookingModalGroupSlots(timeSuggestions), [timeSuggestions]);

  const missingWorker = !workerNotRequired && !resourceId;
  const invalidBookingTime = !proposedStart || Number.isNaN(proposedStart.getTime());
  // Conflicts and hours are reviewed against fresh server data at save time.
  // The local preview must not disable an authorized exception before that review.
  const hasBlockingScheduleIssue = Boolean(missingWorker || invalidBookingTime);
  const isDeleteReservationStatus = Boolean(editMode && bookingStatus === BOOKING_MODAL_DELETE_SENTINEL);
  const hasBlockingScheduleIssueForSave = Boolean(hasBlockingScheduleIssue && !isDeleteReservationStatus);
  const availabilityTone = invalidBookingTime || missingWorker || bookingConflict || isOutsideWorkerAvailability ? "is-error" : "is-ok";
  const availabilityTitle = invalidBookingTime ? "Selecciona una hora válida" : missingWorker
    ? "Falta asignar trabajador"
    : bookingConflict
      ? "Conflicto de horario"
      : isOutsideWorkerAvailability
        ? "Fuera del horario del trabajador"
        : eventMode
          ? "Agenda flexible"
          : "Horario disponible";
  const availabilityDescription = invalidBookingTime ? "Completa la hora antes de guardar la reserva." : missingWorker
    ? "Selecciona quién atenderá esta reserva antes de crearla."
    : bookingConflict
      ? `${bookingConflict.customerName || "Otra reserva"} ya ocupa ${bookingModalFormatRange(bookingConflict.start, bookingConflict.end, bookingTimeFormat)}. Al guardar se revisará la autorización.`
      : isOutsideWorkerAvailability
        ? `${selectedWorkerLabel} no tiene disponibilidad guardada para ese horario. Al guardar se revisará la autorización.`
        : eventMode
          ? "Este tipo de reserva no bloquea un trabajador específico."
          : proposedStart && proposedEnd
            ? `${selectedWorkerLabel} está libre de ${bookingModalFormatRange(proposedStart, proposedEnd, bookingTimeFormat)}.`
            : "Completa hora y duración para validar disponibilidad.";

  const selectedStaffNames = selectedStaff.map((emp) => employeeLabel(emp)).filter(Boolean);
  const showLocationSection = locationRequired || eventMode;
  const locationSectionTitle = locationRequired ? "Ubicación de atención" : "Ubicación del evento";
  const locationSectionCopy = locationRequired
    ? "Selecciona dónde se realizará el servicio para validar cobertura y traslado."
    : "Opcional: guarda el lugar del evento, dirección o referencia para operación.";
  const packageTotalLabel = selectedVisit ? `$${Number(basePrice || 0).toFixed(Number(basePrice || 0) % 1 === 0 ? 0 : 2)}` : "—";
  const extrasTotalLabel = `$${Number(extrasPrice || 0).toFixed(Number(extrasPrice || 0) % 1 === 0 ? 0 : 2)}`;
  const deliveryTotalLabel = `$${Number(deliveryFee || 0).toFixed(Number(deliveryFee || 0) % 1 === 0 ? 0 : 2)}`;
  const normalizedPaymentStatus = String(paymentStatus || "pending").trim().toLowerCase();
  const normalizedPaymentMethod = String(paymentMethod || "cash").trim().toLowerCase();
  const isCoveredPayment = normalizedPaymentStatus === "covered" || LOOP_BOOKING_COVERED_PAYMENT_METHODS.has(normalizedPaymentMethod);
  const fullMembershipCoverage = LOOP_BOOKING_MEMBERSHIPS_ENABLED && Boolean(effectiveSelectedMembershipBenefit) && expectedChargedAmount <= 0.005;
  const paymentCoverageType = isCoveredPayment
    ? (normalizedPaymentMethod === "courtesy" ? "courtesy" : normalizedPaymentMethod === "membership" && LOOP_BOOKING_MEMBERSHIPS_ENABLED ? "membership" : "loyalty_reward")
    : "cash";

  useEffect(() => {
    if (!open || !fullMembershipCoverage) return;
    if (String(paymentStatus || "").toLowerCase() !== "covered") setPaymentStatus("covered");
    if (String(paymentMethod || "").toLowerCase() !== "membership") setPaymentMethod("membership");
    if (String(amountCharged || "").trim()) setAmountCharged("");
  }, [open, fullMembershipCoverage, paymentStatus, paymentMethod, amountCharged]);

  const normalizedAmountCharged = isCoveredPayment ? 0 : asMoneyNumber(amountCharged, paymentStatus === "paid" ? expectedChargedAmount : 0);
  const normalizedDiscount = asMoneyNumber(discountAmount, 0) + membershipBenefitDiscountAmount;
  const normalizedTip = asMoneyNumber(tipAmount, 0);
  const normalizedTipPaymentMethod = ["cash", "yappy", "card", "transfer", "other"].includes(String(tipPaymentMethod || "").toLowerCase())
    ? String(tipPaymentMethod || "cash").toLowerCase()
    : "cash";
  const normalizedCommissionValue = asMoneyNumber(workerCommissionValue, 0);

  // HOTFIX PRODUCCIÓN: la comisión se calcula sobre el monto realmente cobrado.
  // Si el negocio cambia Cobrado de $30 a $50, una comisión 50% debe ser $25.
  // Si Cobrado está vacío, usamos el total del servicio menos descuento como fallback.
  // La propina nunca entra en la base de comisión; se suma aparte al payout.
  const coveredAmount = isCoveredPayment ? Math.max(0, asMoneyNumber(totalPrice, 0)) : 0;
  const fallbackCommissionBaseAmount = isCoveredPayment
    ? Math.max(0, asMoneyNumber(totalPrice, 0))
    : Math.max(0, asMoneyNumber(totalPrice, 0) - normalizedDiscount);
  const commissionBaseAmount = Math.max(0, normalizedAmountCharged > 0 ? normalizedAmountCharged : fallbackCommissionBaseAmount);
  const workerCommissionAmount = workerCommissionType === "percentage"
    ? Math.max(0, commissionBaseAmount * (normalizedCommissionValue / 100))
    : workerCommissionType === "fixed"
      ? Math.max(0, normalizedCommissionValue)
      : 0;
  const workerTipAmount = Math.max(0, normalizedTip);
  const workerPayoutEstimatedAmount = Math.max(0, workerCommissionAmount + workerTipAmount + retailSalesPayoutAmount);
  const workerPayoutAmount = workerPayoutEstimatedAmount;
  // Cobrado y propina son entradas separadas.
  // Total recibido = Cobrado + Propina.
  // Neto negocio = Total recibido - pago trabajador - costo productos.
  // Como el pago trabajador ya incluye propina, el negocio conserva Cobrado - comisión - costos.
  const totalCollectedAmount = Math.max(0, normalizedAmountCharged + normalizedTip);
  const businessNetAmount = totalCollectedAmount - workerPayoutAmount - retailSalesCostAmount;
  const commissionReadable = workerCommissionType === "percentage"
    ? `${normalizedCommissionValue || 0}%`
    : workerCommissionType === "fixed"
      ? `$${(normalizedCommissionValue || 0).toFixed((normalizedCommissionValue || 0) % 1 === 0 ? 0 : 2)}`
      : "Sin comisión";
  const calculatedPayoutReadable = `$${workerPayoutAmount.toFixed(workerPayoutAmount % 1 === 0 ? 0 : 2)}`;

  const toggleExtra = (id) => {
    setSelectedExtraIds((prev) => {
      const set = new Set((prev || []).map(String));
      if (set.has(String(id))) set.delete(String(id));
      else set.add(String(id));
      return Array.from(set);
    });
  };

  const toggleStaff = (id) => {
    setAssignedStaffIds((prev) => {
      const set = new Set((prev || []).map(String));
      if (set.has(String(id))) set.delete(String(id));
      else set.add(String(id));
      return Array.from(set);
    });
  };

  const serviceInventoryDeductionStatus = bookingModalInventoryDeductionStatusForDraft({
    selectedService,
    initialBooking,
    autoDeductEnabled: productStockAutoDeductEnabled,
  });

  const bookingNotificationSettings = (businessDoc && businessDoc.notificationSettings) || (businessSettings && businessSettings.notificationSettings) || {};
  const whatsappFeatureEnabled = bookingNotificationSettings.whatsappEnabled !== false;
  const whatsappManualMessagesEnabled = whatsappFeatureEnabled && bookingNotificationSettings.whatsappManualMessagesEnabled !== false;
  const canManageWhatsAppMessages = Boolean(isOwnerOrAdmin || modalPermissions.canManageBusinessSettings || modalPermissions.canManageWorkers);
  const effectiveCustomerWhatsappOptOut = Boolean(customerWhatsappOptOut || bookingModalCustomerWhatsappOptOut(initialBooking || {}));
  const whatsappReady = bookingModalWhatsAppReadiness(customerPhone || initialBooking?.customerPhone || initialBooking?.clientPhone || initialBooking?.phone || initialBooking?.whatsapp, effectiveCustomerWhatsappOptOut);
  const customerHasLoopAccount = Boolean(
    (customerId && String(customerId) === String(initialBooking?.customerId || "") && (
      initialBooking?.hasUserAccount === true || initialBooking?.customerUserUid ||
      initialBooking?.customerUserId || initialBooking?.linkedUserUid || initialBooking?.userUid || initialBooking?.userId
    )) ||
    customerSnapshot?.identityCanonicalProfile === true ||
    customerSnapshot?.identityContactSource === "platform_user_profile" ||
    customerSnapshot?.hasUserAccount === true ||
    customerSnapshot?.isPlatformUserCandidate === true ||
    customerSnapshot?.isSyncedFromPlatformUser === true ||
    customerSnapshot?.linkedUserUid ||
    customerSnapshot?.linkedUserId ||
    customerSnapshot?.userUid ||
    customerSnapshot?.userId ||
    customerSnapshot?.platformUserUid ||
    customerSnapshot?.platformUserId || customerSnapshot?.customerUserUid || customerSnapshot?.customerUserId
  );
  const customerHasWhatsApp = whatsappReady.tone === "ok";
  const customerHasEmail = Boolean(String(customerEmail || customerSnapshot?.email || customerSnapshot?.customerEmail || customerSnapshot?.emailNormalized || "").trim().includes("@"));
  const customerLoopBadgeLabel = customerHasLoopAccount ? "Loop" : (customerId ? "Sin Loop" : "Por registrar");
  const manageCustomerContactSummary = [
    customerPhone ? "WhatsApp" : "",
    customerEmail ? "Email" : "",
    customerHasLoopAccount ? "Loop" : "",
  ].filter(Boolean);
  const manageCustomerMeta = manageCustomerContactSummary.length
    ? manageCustomerContactSummary.join(" · ")
    : "Sin contacto adicional";
  const manageServiceMeta = [
    duration ? `${duration} min` : "",
    totalPrice > 0 ? bookingCompactMoney(totalPrice) : "",
  ].filter(Boolean).join(" · ");
  const manageAgendaMeta = [
    bookingTimeLabel || "",
    selectedModeLabel || "",
  ].filter(Boolean).join(" · ");

  const canSendManualWhatsApp = Boolean(editMode && businessId && initialBooking?.id && whatsappManualMessagesEnabled && canManageWhatsAppMessages && !effectiveCustomerWhatsappOptOut);
  const smartWhatsAppAction = bookingModalSmartWhatsAppAction({ rows: whatsappLogRows, status: bookingStatus, canSend: canSendManualWhatsApp, phone: customerPhone || initialBooking?.customerPhone || initialBooking?.clientPhone || initialBooking?.phone || initialBooking?.whatsapp, optOut: effectiveCustomerWhatsappOptOut });

  const bookingStatusOptionsForModal = useMemo(() => {
    const base = BOOKING_MODAL_STATUS_OPTIONS.filter((opt) => {
      if (opt.value === "completed" && !canCompleteBookings) return false;
      if (opt.value === "cancelled" && !canCancelBookings) return false;
      return true;
    });
    if (!editMode) return base;
    return [
      ...base.filter((opt) => opt.value !== "cancelled"),
      ...(canCancelBookings ? [{ value: "cancelled", label: "Cancelada" }, { value: BOOKING_MODAL_DELETE_SENTINEL, label: "Quitar de agenda" }] : []),
    ];
  }, [editMode, canCompleteBookings, canCancelBookings]);

  const bookingIdForWompi = String(initialBooking?.id || initialBooking?.bookingId || "").trim();
  const loyaltyRewardHistoryForEdit = Boolean(
    initialBooking?.loyaltyRedemptionEventId ||
    initialBooking?.loyaltyRedemptionToken ||
    initialBooking?.loyaltyProgramId
  );
  const loyaltyRewardFieldsStillAppliedForEdit = Boolean(
    initialBooking?.loyaltyRewardApplied === true ||
    String(paymentMethod || initialBooking?.paymentMethod || initialBooking?.payment?.method || initialBooking?.paymentCoverageType || initialBooking?.payment?.coverageType || "").toLowerCase() === "loyalty_reward" ||
    String(initialBooking?.workerPayoutSource || initialBooking?.payment?.workerPayoutSource || initialBooking?.loyaltyWorkerPayoutSource || "").toLowerCase() === "loyalty_reward" ||
    Number(initialBooking?.loyaltyBusinessSubsidyAmount || initialBooking?.payment?.loyaltyBusinessSubsidyAmount || 0) > 0 ||
    Number(initialBooking?.loyaltyWorkerPayoutAmount || initialBooking?.payment?.loyaltyWorkerPayoutAmount || 0) > 0 ||
    Number(initialBooking?.loyaltyBusinessCashImpactAmount || initialBooking?.payment?.loyaltyBusinessCashImpactAmount || 0) !== 0
  );
  const loyaltyRewardAppliedForEdit = Boolean(loyaltyRewardFieldsStillAppliedForEdit || loyaltyRewardHistoryForEdit);
  const loyaltyRewardAlreadyReversedForEdit = Boolean(
    loyaltyReverseDone ||
    initialBooking?.loyaltyRewardReversed === true ||
    initialBooking?.loyaltyRedemptionReversed === true
  );
  const loyaltyWorkerPayoutPaidForEdit = ["paid", "pagado", "paid_worker", "worker_paid"].includes(String(
    initialBooking?.loyaltyWorkerPayoutStatus ||
    initialBooking?.workerPayoutStatus ||
    initialBooking?.payment?.workerPayoutStatus ||
    workerPayoutStatus ||
    ""
  ).toLowerCase());
  const canReverseLoyaltyForEdit = Boolean(
    editMode &&
    businessId &&
    bookingIdForWompi &&
    canEditPayments &&
    !loyaltyWorkerPayoutPaidForEdit &&
    (
      (!loyaltyRewardAlreadyReversedForEdit && loyaltyRewardAppliedForEdit) ||
      (loyaltyRewardAlreadyReversedForEdit && loyaltyRewardFieldsStillAppliedForEdit)
    )
  );
  const paymentProvider = wompiSession?.provider || initialBooking?.paymentProvider || initialBooking?.payment?.provider || "wompi";
  const wompiReference = wompiSession?.reference || initialBooking?.paymentReference || initialBooking?.wompiReference || initialBooking?.payment?.reference || "";
  const wompiCheckoutUrl = wompiSession?.checkoutUrl || initialBooking?.paymentUrl || initialBooking?.wompiCheckoutUrl || initialBooking?.payment?.checkoutUrl || "";
  const wompiSessionLabel = wompiSession?.sessionStatus || initialBooking?.paymentSessionStatus || initialBooking?.payment?.sessionStatus || "no creado";

  useEffect(() => {
    let mounted = true;
    async function loadWhatsAppLogs() {
      if (!editMode || !businessId || !initialBooking?.id || !db) {
        setWhatsappLogRows([]);
        return;
      }
      setWhatsappLogLoading(true);
      try {
        const snap = await getDocs(collection(db, "businesses", businessId, "bookings", initialBooking.id, "notificationLogs"));
        const rows = [];
        snap.forEach((docSnap) => {
          const data = docSnap.data() || {};
          if (String(data.channel || "").toLowerCase() !== "whatsapp") return;
          rows.push({ id: docSnap.id, ...data });
        });
        rows.sort((a, b) => {
          const da = a.createdAt && typeof a.createdAt.toDate === "function" ? a.createdAt.toDate() : a.createdAt ? new Date(a.createdAt) : new Date(0);
          const dbb = b.createdAt && typeof b.createdAt.toDate === "function" ? b.createdAt.toDate() : b.createdAt ? new Date(b.createdAt) : new Date(0);
          return dbb.getTime() - da.getTime();
        });
        if (mounted) setWhatsappLogRows(rows.slice(0, 8));
      } catch (err) {
        console.warn("[BookingCreateModal] load WhatsApp logs failed", err);
        if (mounted) setWhatsappLogRows([]);
      } finally {
        if (mounted) setWhatsappLogLoading(false);
      }
    }
    loadWhatsAppLogs();
    return () => { mounted = false; };
  }, [editMode, businessId, initialBooking?.id]);

  useEffect(() => {
    setWompiSession(null);
    setWompiSessionStatus("");
  }, [editMode, businessId, initialBooking?.id, open]);

  const loadWompiWidgetScript = () => new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("Widget no disponible fuera del navegador"));
    if (window.WidgetCheckout) return resolve(window.WidgetCheckout);
    const existing = document.querySelector('script[data-loop-wompi-widget="true"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(window.WidgetCheckout));
      existing.addEventListener("error", () => reject(new Error("No se pudo cargar el widget de Wompi")));
      return;
    }
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = "https://checkout.wompi.pa/widget.js";
    script.async = true;
    script.setAttribute("data-loop-wompi-widget", "true");
    script.onload = () => window.WidgetCheckout ? resolve(window.WidgetCheckout) : reject(new Error("WidgetCheckout no quedó disponible"));
    script.onerror = () => reject(new Error("No se pudo cargar el widget de Wompi"));
    document.head.appendChild(script);
  });

  const handleOpenWompiWidget = async () => {
    const session = wompiSession;
    const widget = session?.widget || null;
    if (!widget || !widget.publicKey || !widget.reference || !widget.signatureIntegrity) {
      setWompiSessionStatus("Primero prepara un checkout de abono o pago total.");
      return;
    }
    setWompiSessionLoading(true);
    setWompiSessionStatus("Abriendo widget seguro de Wompi...");
    try {
      const WidgetCheckout = await loadWompiWidgetScript();
      const checkout = new WidgetCheckout({
        currency: widget.currency || "USD",
        amountInCents: Number(widget.amountInCents || session.amountInCents || 0),
        reference: widget.reference,
        publicKey: widget.publicKey,
        signature: { integrity: widget.signatureIntegrity },
        redirectUrl: widget.redirectUrl,
        expirationTime: widget.expirationTime,
        defaultLanguage: widget.defaultLanguage || "es",
        customerData: widget.customerData || undefined,
      });
      checkout.open(async (result) => {
        const transaction = result?.transaction || {};
        const status = String(transaction.status || "").toUpperCase();
        setWompiSessionStatus(status ? `Wompi respondió: ${status}. Guardando resultado sandbox...` : "Wompi respondió. Esperando confirmación.");
        try {
          const { getFunctions, httpsCallable } = await import("firebase/functions");
          const firebaseApp = db?.app || db?._app;
          const functions = firebaseApp ? getFunctions(firebaseApp) : getFunctions();
          const syncResult = httpsCallable(functions, "syncWompiSandboxWidgetResult");
          const res = await syncResult({ businessId, bookingId: bookingIdForWompi, transaction });
          const nextStatus = res?.data?.status || "pending";
          const paidAmount = Number(res?.data?.amount || session.amount || 0);
          if (nextStatus === "paid" || nextStatus === "partial") {
            setPaymentStatus(nextStatus);
            setPaymentMethod("card");
            setAmountCharged(formatMoneyInputValue(paidAmount));
          }
          setWompiSessionStatus(`Pago sandbox actualizado: ${nextStatus}. El webhook real también podrá confirmar esta reserva.`);
        } catch (syncErr) {
          console.error("[BookingCreateModal] syncWompiSandboxWidgetResult failed", syncErr);
          setWompiSessionStatus("Wompi respondió, pero no pudimos sincronizar automáticamente. Revisa el webhook o refresca la reserva.");
        }
      });
    } catch (err) {
      console.error("[BookingCreateModal] open Wompi widget failed", err);
      setWompiSessionStatus(err?.message || "No pudimos abrir el widget de Wompi.");
    } finally {
      setWompiSessionLoading(false);
    }
  };

  const handleCreateWompiSession = async (mode = "deposit") => {
    if (!editMode || !businessId || !bookingIdForWompi) {
      setWompiSessionStatus("Primero guarda la reserva para poder preparar el checkout.");
      return;
    }
    if (!canEditPayments) {
      setWompiSessionStatus("Tu rol no tiene permiso para crear pagos online.");
      return;
    }
    if (!totalPrice || totalPrice <= 0) {
      setWompiSessionStatus("Esta reserva no tiene total mayor a $0.");
      return;
    }

    const deposit = Math.min(totalPrice, Math.max(5, Number((totalPrice * 0.25).toFixed(2))));
    const amount = mode === "deposit" ? deposit : totalPrice;

    setWompiSessionLoading(true);
    setWompiSessionStatus("");
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const firebaseApp = db?.app || db?._app;
      const functions = firebaseApp ? getFunctions(firebaseApp) : getFunctions();
      const createPayment = httpsCallable(functions, "createWompiPaymentSession");
      const res = await createPayment({
        businessId,
        bookingId: bookingIdForWompi,
        mode,
        amount,
      });
      setWompiSession(res?.data || null);
      setWompiSessionStatus("Checkout sandbox preparado. Ahora puedes abrir el widget real de Wompi dentro de Loop.");
    } catch (err) {
      console.error("[BookingCreateModal] createWompiPaymentSession failed", err);
      setWompiSessionStatus(err?.message || err?.details?.message || "No pudimos preparar el checkout de Wompi.");
    } finally {
      setWompiSessionLoading(false);
    }
  };

  const handleMarkWompiMockPaid = async () => {
    if (!editMode || !businessId || !bookingIdForWompi) {
      setWompiSessionStatus("Primero guarda la reserva para poder simular el pago.");
      return;
    }

    const deposit = Math.min(totalPrice, Math.max(5, Number((totalPrice * 0.25).toFixed(2))));
    const amount = wompiSession?.amount || deposit || totalPrice;

    setWompiSessionLoading(true);
    setWompiSessionStatus("");
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const firebaseApp = db?.app || db?._app;
      const functions = firebaseApp ? getFunctions(firebaseApp) : getFunctions();
      const markPaid = httpsCallable(functions, "markWompiPaymentMockPaid");
      const res = await markPaid({
        businessId,
        bookingId: bookingIdForWompi,
        reference: wompiReference || wompiSession?.reference || "",
        amount,
      });
      const nextStatus = res?.data?.status || (amount >= totalPrice ? "paid" : "partial");
      setPaymentStatus(nextStatus);
      setPaymentMethod("card");
      setAmountCharged(formatMoneyInputValue(amount));
      setWompiSessionStatus(`Pago mock registrado como ${nextStatus === "paid" ? "pagado" : "parcial"}. Puedes guardar cambios o refrescar para ver el backend.`);
    } catch (err) {
      console.error("[BookingCreateModal] markWompiPaymentMockPaid failed", err);
      setWompiSessionStatus(err?.message || err?.details?.message || "No pudimos simular el pago.");
    } finally {
      setWompiSessionLoading(false);
    }
  };

  const handleSendManualWhatsApp = async () => {
    if (!canSendManualWhatsApp) {
      setWhatsappManualStatus("Solo owner/admin/manager puede enviar o reintentar WhatsApp desde esta reserva.");
      return;
    }
    if (smartWhatsAppAction?.disabled) {
      setWhatsappManualStatus(smartWhatsAppAction.label === "Ya enviado" ? "Este WhatsApp ya fue enviado." : "WhatsApp no está listo para esta reserva.");
      return;
    }
    setWhatsappManualSending(true);
    setWhatsappManualStatus("");
    try {
      const { getFunctions, httpsCallable } = await import("firebase/functions");
      const firebaseApp = db?.app || db?._app;
      const functions = firebaseApp ? getFunctions(firebaseApp) : getFunctions();
      const callableName = smartWhatsAppAction?.type && smartWhatsAppAction.type !== "manual" ? "retryBookingWhatsAppTemplate" : "sendBookingWhatsAppManual";
      const sendManual = httpsCallable(functions, callableName);
      const result = await sendManual({
        businessId,
        bookingId: initialBooking.id,
        messageType: smartWhatsAppAction?.type,
      });
      const skipped = result?.data?.skipped;
      setWhatsappManualStatus(skipped ? `WhatsApp no enviado: ${skipped}.` : "WhatsApp enviado correctamente.");
      setWhatsappLogRows((prev) => [{
        id: `local-${Date.now()}`,
        channel: "whatsapp",
        type: smartWhatsAppAction?.type === "manual" ? "text" : smartWhatsAppAction?.type,
        reason: smartWhatsAppAction?.type === "manual" ? "booking_manual_message" : "manual_retry",
        status: skipped ? "skipped" : "sent",
        ok: !skipped,
        to: result?.data?.to || customerPhone || initialBooking?.customerPhone || initialBooking?.phone || "",
        metaMessageId: result?.data?.messageId || "",
        meta: { reason: skipped || "manual_action", templateName: smartWhatsAppAction?.type },
        createdAt: new Date(),
      }, ...(Array.isArray(prev) ? prev : [])].slice(0, 8));
    } catch (err) {
      console.error("[BookingCreateModal] send/retry WhatsApp failed", err);
      const msg = err?.message || err?.details?.message || "No pude enviar el WhatsApp.";
      setWhatsappManualStatus(`No pude enviar WhatsApp: ${msg}`);
    } finally {
      setWhatsappManualSending(false);
    }
  };

  const handleReprocessLoyalty = async () => {
    if (!editMode || !businessId || !initialBooking?.id) {
      setLoyaltyReprocessStatus("Primero guarda la reserva antes de reprocesar fidelidad.");
      return;
    }
    setLoyaltyReprocessSending(true);
    setLoyaltyReprocessStatus("");
    try {
      const result = await reprocessBookingLoyaltyAction({
        db,
        businessId,
        bookingId: initialBooking.id,
      });
      setLoyaltyReprocessStatus(summarizeReprocessResult(result));
    } catch (err) {
      console.error("[BookingCreateModal] reprocess loyalty failed", err);
      const msg = err?.message || err?.details?.message || "No pude reprocesar fidelidad.";
      setLoyaltyReprocessStatus(`No pude reprocesar fidelidad: ${msg}`);
    } finally {
      setLoyaltyReprocessSending(false);
    }
  };

  const handleReverseLoyaltyRedemption = async () => {
    const bookingId = String(initialBooking?.id || initialBooking?.bookingId || "").trim();
    if (!editMode || !businessId || !bookingId) {
      setLoyaltyReverseStatus("Primero guarda la reserva antes de deshacer fidelidad.");
      return;
    }

    if (loyaltyWorkerPayoutPaidForEdit) {
      setLoyaltyReverseStatus("No puedes deshacer fidelidad porque la comisión del trabajador ya fue marcada como pagada.");
      return;
    }

    const confirmed = typeof window === "undefined"
      ? true
      : window.confirm("¿Deshacer la redención de fidelidad? La recompensa volverá al cliente y la reserva quedará pendiente de pago.");
    if (!confirmed) return;

    const reason = typeof window === "undefined"
      ? "Corrección operativa"
      : window.prompt("Motivo del reverso", "Corrección operativa");
    if (reason === null) return;

    setLoyaltyReverseSending(true);
    setLoyaltyReverseStatus("");
    try {
      const result = await reverseLoyaltyRedemptionAction({
        db,
        businessId,
        bookingId,
        reason: String(reason || "Corrección operativa").trim(),
      });

      setPaymentStatus("pending");
      setPaymentMethod("cash");
      setAmountCharged("");
      setWorkerPayoutStatus("pending");
      setWorkerCommissionType("none");
      setWorkerCommissionValue("");
      setCommissionManuallyEdited(false);
      setLoyaltyReverseDone(true);
      setLoyaltyReverseStatus(summarizeReverseLoyaltyRedemptionResult(result));
    } catch (err) {
      console.error("[BookingCreateModal] reverse loyalty redemption failed", err);
      const msg = err?.message || err?.details?.message || "No pude deshacer fidelidad.";
      setLoyaltyReverseStatus(`No pude deshacer fidelidad: ${msg}`);
    } finally {
      setLoyaltyReverseSending(false);
    }
  };

  const handleReprocessMembershipBenefit = async () => {
    if (!LOOP_BOOKING_MEMBERSHIPS_ENABLED) {
      setMembershipReprocessStatus("");
      return;
    }
    if (!editMode || !businessId || !bookingIdForWompi) {
      setMembershipReprocessStatus("Primero guarda la reserva para poder procesar la membresía.");
      return;
    }
    setMembershipReprocessSending(true);
    setMembershipReprocessStatus("");
    try {
      const result = await reprocessBookingMembershipBenefitAction({ businessId, bookingId: bookingIdForWompi });
      setMembershipReprocessStatus(summarizeMembershipBenefitResult(result));
    } catch (err) {
      console.error("[BookingCreateModal] reprocess membership failed", err);
      setMembershipReprocessStatus(err?.message || "No pude procesar la membresía.");
    } finally {
      setMembershipReprocessSending(false);
    }
  };

  const handleSubmit = async (e, lifecycleOptions = {}) => {
    e && e.preventDefault && e.preventDefault();
    if (editMode && bookingStatus === BOOKING_MODAL_DELETE_SENTINEL && lifecycleOptions.confirmDeleteReservation !== true) {
      setBookingDeleteDecision({ type: "delete_reservation" });
      return;
    }

    const domCustomerNameFallback = bookingModalReadCustomerInputDomValue();
    const selectedCustomerDraft = bookingModalNormalizeSelectedCustomerDraft(selectedCustomerDraftRef.current, domCustomerNameFallback || customerName);
    const selectedDraftMatchesInput = Boolean(
      selectedCustomerDraft?.customerName &&
      normalizeText(selectedCustomerDraft.customerName) === normalizeText(domCustomerNameFallback || customerName || selectedCustomerDraft.customerName)
    );
    const effectiveCustomerId = String(customerId || (selectedDraftMatchesInput ? selectedCustomerDraft?.customerId : "") || "").trim();
    const effectiveCustomerName = bookingModalCustomerDisplayName(customerName, customerSnapshot, selectedDraftMatchesInput ? selectedCustomerDraft : null, domCustomerNameFallback);
    const effectiveCustomerEmail = String(customerEmail || (selectedDraftMatchesInput ? selectedCustomerDraft?.customerEmail : "") || "").trim().toLowerCase();
    const effectiveCustomerPhone = bookingModalPhoneForStorage(customerPhone || (selectedDraftMatchesInput ? selectedCustomerDraft?.customerPhone : "") || "");
    const effectiveCustomerSnapshot = bookingModalNormalizeCustomerSnapshot(
      customerSnapshot || (selectedDraftMatchesInput ? selectedCustomerDraft?.customerSnapshot : null),
      effectiveCustomerName
    );
    bookingModalRecordCustomerDebug("submit-start", {
      customerId,
      effectiveCustomerId: effectiveCustomerId || null,
      customerName,
      effectiveCustomerName,
      domCustomerNameFallback,
      hasCustomerSnapshot: Boolean(customerSnapshot),
      hasSelectedCustomerDraft: Boolean(selectedCustomerDraft),
      selectedDraftMatchesInput,
      selectedDraftCustomerId: selectedCustomerDraft?.customerId || null,
      snapshotName: customerSnapshot?.name || customerSnapshot?.customerName || customerSnapshot?.displayName || "",
      effectiveSnapshotName: effectiveCustomerSnapshot?.name || effectiveCustomerSnapshot?.customerName || effectiveCustomerSnapshot?.displayName || "",
      serviceId,
      resourceId,
    });

    if (!effectiveCustomerName) {
      bookingModalRecordCustomerDebug("submit-missing-customer-name", {
        customerId,
        customerName,
        effectiveCustomerName,
        domCustomerNameFallback,
        hasCustomerSnapshot: Boolean(customerSnapshot),
        snapshotName: customerSnapshot?.name || customerSnapshot?.customerName || customerSnapshot?.displayName || "",
      });
      return showBookingGuardrail({
        tone: "warning",
        eyebrow: "Cliente pendiente",
        title: "Agrega el nombre del cliente",
        message: "Necesitamos un cliente para poder guardar esta reserva correctamente.",
        summary: [{ label: "Campo requerido", value: "Cliente" }],
      });
    }
    if (invalidBookingTime && !isDeleteReservationStatus) return showBookingGuardrail({
      tone: "warning",
      eyebrow: "Hora inválida",
      title: "Revisa la hora de la reserva",
      message: "La hora debe tener formato HH:MM para poder agendarla correctamente.",
      summary: [{ label: "Formato esperado", value: "09:30" }],
    });
    if (!serviceId && !isDeleteReservationStatus) return showBookingGuardrail({
      tone: "warning",
      eyebrow: "Servicio pendiente",
      title: "Selecciona un servicio",
      message: "Elige el servicio o paquete que el cliente va a reservar.",
      summary: [{ label: "Campo requerido", value: "Servicio" }],
    });
    if (serviceId && (bookingModalServicesLoading || serviceSelectionUnavailable) && !isDeleteReservationStatus) return showBookingGuardrail({
      tone: "warning",
      eyebrow: "Revisar servicio",
      title: bookingModalServicesLoading ? "Estamos actualizando los servicios" : "Revisa el servicio y el trabajador",
      message: bookingModalServicesLoading ? "Espera un momento y vuelve a guardar. Tu selección se conserva." : "El servicio seleccionado no está disponible en la lista actual de este trabajador. Revisa la asignación o elige otro servicio; no se ha borrado tu selección.",
      summary: [{ label: "Servicio", value: selectedServiceLabel || String(serviceId) }],
    });
    if (missingWorker && !isDeleteReservationStatus) return showBookingGuardrail({
      tone: "warning",
      eyebrow: "Trabajador pendiente",
      title: "Asigna un trabajador",
      message: "Selecciona quién atenderá esta reserva antes de guardarla.",
      summary: [{ label: "Campo requerido", value: "Trabajador" }],
    });
    if (locationRequired && !customerLocation && !isDeleteReservationStatus) return showBookingGuardrail({
      tone: "warning",
      eyebrow: "Ubicación pendiente",
      title: "Selecciona la zona del servicio",
      message: "Para servicios a domicilio necesitamos la ubicación donde se atenderá al cliente.",
      summary: [{ label: "Campo requerido", value: "Ubicación" }],
    });
    if (locationRequired && !String(customerExactAddress || "").trim() && !isDeleteReservationStatus) return showBookingGuardrail({
      tone: "warning",
      eyebrow: "Dirección pendiente",
      title: "Agrega la dirección exacta",
      message: "Incluye una referencia o dirección específica para que el negocio pueda atender correctamente.",
      summary: [{ label: "Campo requerido", value: "Dirección exacta" }],
    });
    if (isOutsideCoverage && !isDeleteReservationStatus) return showBookingGuardrail({
      tone: "danger",
      eyebrow: "Zona no cubierta",
      title: "El negocio no atiende esta zona",
      message: "Selecciona una zona dentro del área de cobertura configurada para este negocio.",
      summary: [{ label: "Acción sugerida", value: "Cambiar ubicación" }],
    });
    const requestedBookingStatus = lifecycleOptions.forceComplete === true ? "completed" : (bookingStatus === BOOKING_MODAL_DELETE_SENTINEL ? "deleted" : bookingStatus);
    if (requestedBookingStatus === "completed" && !canCompleteBookings) {
      return showBookingGuardrail({
        tone: "danger",
        eyebrow: "Permiso requerido",
        title: "No puedes completar esta reserva",
        message: "Tu rol no tiene permiso para marcar reservas como completadas.",
        summary: [{ label: "Permiso", value: "Marcar reservas completadas" }],
      });
    }
    if (["paid", "partial", "covered", "refunded"].includes(String(paymentStatus || "").toLowerCase()) && !canEditPayments && !isDeleteReservationStatus && editMode) {
      const originalPaymentStatus = String(initialBooking?.paymentStatus || initialBooking?.payment?.status || "pending").toLowerCase();
      if (String(paymentStatus || "pending").toLowerCase() !== originalPaymentStatus) {
        return showBookingGuardrail({
          tone: "danger",
          eyebrow: "Permiso requerido",
          title: "No puedes editar pagos",
          message: "Tu rol no tiene permiso para cambiar estado, método o cobros de pago.",
          summary: [{ label: "Permiso", value: "Editar pagos" }],
        });
      }
    }
    if (["paid", "partial"].includes(String(paymentStatus || "").toLowerCase()) && normalizedAmountCharged <= 0 && !isDeleteReservationStatus) {
      return showBookingGuardrail({
        tone: "warning",
        eyebrow: "Pago incompleto",
        title: "Ingresa el monto cobrado",
        message: "Para marcar esta reserva como pagada o parcial, registra primero cuánto se cobró al cliente.",
        summary: [
          { label: "Estado de pago", value: paymentStatus === "paid" ? "Pagado" : "Parcial" },
          { label: "Monto cobrado", value: "$0" },
          { label: "Resultado", value: "Falta cobro" },
        ],
      });
    }

    let effectiveBookingStatus = requestedBookingStatus;
    if (editMode && bookingStatus === "completed" && !["paid", "covered"].includes(String(paymentStatus || "pending").toLowerCase()) && normalizedAmountCharged <= 0 && lifecycleOptions.allowCompleteWithoutPayment !== true) {
      setBookingLifecycleDecision({ type: "complete_without_payment" });
      return;
    }
    if (editMode && String(paymentStatus || "").toLowerCase() === "paid" && !bookingEditIsClosedStatus(bookingStatus) && lifecycleOptions.skipPaidCompletionPrompt !== true) {
      setBookingLifecycleDecision({ type: "paid_suggest_complete" });
      return;
    }
    const commissionLifecycleStatus = bookingEditCommissionLifecycleStatus({
      bookingStatus: editMode ? effectiveBookingStatus : bookingStatus,
      paymentStatus,
      commissionOnlyCompleted,
    });
    const commissionLifecycleLabel = bookingEditCommissionLifecycleLabel(commissionLifecycleStatus);

    setSaving(true);
    try {
      const eventDetails = eventMode ? {
        visitType: selectedVisit?.id || null,
        visitTypeLabel: selectedVisit?.label || null,
        visitHours: selectedVisit?.visitHours || Math.max(1, Math.round((Number(duration) || 60) / 60)),
        selectedCharacterIds: selectedExtras.map((x) => x.id),
        selectedCharacters: selectedExtras,
        durationPrice: basePrice,
        charactersPrice: extrasPrice,
        totalPrice,
        pricingMode: selectedVisit?.pricingMode || selectedService?.pricingMode || null,
        durationPricingTierId: selectedVisit?.pricingMode === "duration_tiers" ? selectedVisit.id : null,
        notes: String(notes || "").trim() || null,
      } : null;

      // Do not preserve generic initialBooking.source as bookingSource.
      // In historical data, source often means CRM/customer source, not reservation origin.
      const preservedBookingSource = initialBooking?.bookingSource || null;
      const preservedCreatedFrom = initialBooking?.createdFrom || initialBooking?.origin || null;
      const preservedCreatedByRole = initialBooking?.createdByRole || null;

      let canonicalCustomerRecord = null;
      let canonicalCustomerSnapshot = effectiveCustomerSnapshot && typeof effectiveCustomerSnapshot === "object" ? { ...effectiveCustomerSnapshot } : null;

      const hasStrongCustomerIdentity = bookingModalStrongCustomerIdentity({
        id: effectiveCustomerId,
        customerId: effectiveCustomerId,
        clientId: effectiveCustomerId,
        phone: effectiveCustomerPhone,
        customerPhone: effectiveCustomerPhone,
        email: effectiveCustomerEmail,
        customerEmail: effectiveCustomerEmail,
        customerSnapshot: canonicalCustomerSnapshot,
      });

      const shouldSyncCustomerClientSide = Boolean(isOwnerOrAdmin);

      if (!shouldSyncCustomerClientSide) {
        // LOOP WORKER BOOKING CUSTOMER IDENTITY V8C
        // Workers should not run client-side CRM identity resolution. That path can
        // require owner/admin Firestore permissions and can expose hidden email/phone
        // in a guardrail. Keep the selected customer id/snapshot in the booking
        // payload and let createWorkerBooking hydrate sensitive identity server-side
        // with Admin SDK before writing the booking.
        const deferredCustomerId = String(
          effectiveCustomerId ||
          canonicalCustomerSnapshot?.customerId ||
          canonicalCustomerSnapshot?.id ||
          canonicalCustomerSnapshot?.clientId ||
          ""
        ).trim();
        const deferredCustomerName = String(
          effectiveCustomerName ||
          canonicalCustomerSnapshot?.name ||
          canonicalCustomerSnapshot?.customerName ||
          canonicalCustomerSnapshot?.displayName ||
          "Cliente"
        ).trim() || "Cliente";

        canonicalCustomerRecord = {
          ...(canonicalCustomerSnapshot || {}),
          ...(deferredCustomerId ? { id: deferredCustomerId, customerId: deferredCustomerId, clientId: deferredCustomerId } : {}),
          name: deferredCustomerName,
          customerName: deferredCustomerName,
          displayName: canonicalCustomerSnapshot?.displayName || deferredCustomerName,
          phone: effectiveCustomerPhone || canonicalCustomerSnapshot?.phone || canonicalCustomerSnapshot?.phoneNormalized || canonicalCustomerSnapshot?.phoneE164 || canonicalCustomerSnapshot?.customerPhone || "",
          customerPhone: effectiveCustomerPhone || canonicalCustomerSnapshot?.customerPhone || canonicalCustomerSnapshot?.phone || canonicalCustomerSnapshot?.phoneNormalized || "",
          phoneNormalized: bookingModalNormalizePhone(effectiveCustomerPhone || canonicalCustomerSnapshot?.phoneNormalized || canonicalCustomerSnapshot?.phoneE164 || canonicalCustomerSnapshot?.phone || "") || canonicalCustomerSnapshot?.phoneNormalized || "",
          email: effectiveCustomerEmail || canonicalCustomerSnapshot?.emailNormalized || canonicalCustomerSnapshot?.email || canonicalCustomerSnapshot?.customerEmail || "",
          customerEmail: effectiveCustomerEmail || canonicalCustomerSnapshot?.customerEmail || canonicalCustomerSnapshot?.emailNormalized || canonicalCustomerSnapshot?.email || "",
          emailNormalized: String(effectiveCustomerEmail || canonicalCustomerSnapshot?.emailNormalized || canonicalCustomerSnapshot?.email || "").trim().toLowerCase(),
          linkedUserId: canonicalCustomerSnapshot?.linkedUserId || canonicalCustomerSnapshot?.linkedUserUid || canonicalCustomerSnapshot?.userId || canonicalCustomerSnapshot?.userUid || null,
          linkedUserUid: canonicalCustomerSnapshot?.linkedUserUid || canonicalCustomerSnapshot?.linkedUserId || canonicalCustomerSnapshot?.userUid || canonicalCustomerSnapshot?.userId || null,
          userId: canonicalCustomerSnapshot?.userId || canonicalCustomerSnapshot?.linkedUserId || null,
          userUid: canonicalCustomerSnapshot?.userUid || canonicalCustomerSnapshot?.linkedUserUid || null,
          workerClientSideIdentityDeferred: true,
        };
      } else if (!hasStrongCustomerIdentity) {
        // Name-only / walk-in booking. Do not run conflict de-dupe because there is
        // no phone/email/Loop uid to prove a unique identity. Create a lightweight
        // CRM customer when possible, but never block the booking if CRM write fails.
        canonicalCustomerRecord = await bookingModalCreateNameOnlyCustomerRecord(db, businessId, {
          name: effectiveCustomerName,
          customerSnapshot: canonicalCustomerSnapshot,
          createdByRole: editMode ? "booking_edit_name_only" : "booking_create_name_only",
        });
      } else {
        try {
          canonicalCustomerRecord = await bookingModalWithTimeout(
            findOrCreateCustomerForBusiness(db, businessId, {
              id: effectiveCustomerId || canonicalCustomerSnapshot?.id || canonicalCustomerSnapshot?.customerId || null,
              customerId: effectiveCustomerId || canonicalCustomerSnapshot?.customerId || canonicalCustomerSnapshot?.id || null,
              clientId: effectiveCustomerId || canonicalCustomerSnapshot?.customerId || canonicalCustomerSnapshot?.id || null,
              name: effectiveCustomerName,
              customerName: effectiveCustomerName,
              phone: effectiveCustomerPhone,
              customerPhone: effectiveCustomerPhone,
              email: effectiveCustomerEmail,
              customerEmail: effectiveCustomerEmail,
              customerSnapshot: canonicalCustomerSnapshot,
              linkedUserId: canonicalCustomerSnapshot?.linkedUserId || canonicalCustomerSnapshot?.linkedUserUid || canonicalCustomerSnapshot?.userId || canonicalCustomerSnapshot?.userUid || null,
              linkedUserUid: canonicalCustomerSnapshot?.linkedUserUid || canonicalCustomerSnapshot?.linkedUserId || canonicalCustomerSnapshot?.userUid || canonicalCustomerSnapshot?.userId || null,
              userId: canonicalCustomerSnapshot?.userId || canonicalCustomerSnapshot?.linkedUserId || null,
              userUid: canonicalCustomerSnapshot?.userUid || canonicalCustomerSnapshot?.linkedUserUid || null,
            }, {
              incrementBookings: false,
              linkPlatformUser: true,
              allowIdentityConflict: false,
              trustExplicitCustomerId: Boolean(
                effectiveCustomerId &&
                canonicalCustomerSnapshot &&
                (
                  canonicalCustomerSnapshot.id ||
                  canonicalCustomerSnapshot.customerId ||
                  canonicalCustomerSnapshot.clientId
                )
              ),
              allowNameFallbackMatch: true,
              updateExistingCustomerName: false,
              bookingDate: activeBookingDate,
              createdByRole: editMode ? "booking_edit" : "booking_create",
            }),
            9500,
            { __timeout: true }
          );
          if (canonicalCustomerRecord?.__timeout) {
            throw new Error("La sincronización del cliente tardó demasiado. Intenta guardar nuevamente en unos segundos.");
          }
        } catch (identityErr) {
          console.warn("[BookingCreateModal] canonical customer save resolution failed", identityErr);
          showBookingGuardrail({
            tone: "danger",
            eyebrow: "Identidad del cliente",
            title: "No pudimos confirmar el cliente único",
            message: identityErr?.message || "Revisa el email o WhatsApp del cliente antes de guardar.",
            summary: [
              { label: "Cliente", value: effectiveCustomerName || "—" },
              { label: "Email", value: effectiveCustomerEmail || "—" },
              { label: "WhatsApp", value: effectiveCustomerPhone || "—" },
            ],
          });
          return;
        }
      }

      const canonicalCustomerId = String(canonicalCustomerRecord?.id || canonicalCustomerRecord?.customerId || effectiveCustomerId || canonicalCustomerSnapshot?.customerId || canonicalCustomerSnapshot?.id || "").trim();
      canonicalCustomerSnapshot = canonicalCustomerRecord ? {
        ...(canonicalCustomerSnapshot || {}),
        ...canonicalCustomerRecord,
        id: canonicalCustomerId || canonicalCustomerRecord.id,
        customerId: canonicalCustomerId || canonicalCustomerRecord.customerId || canonicalCustomerRecord.id,
        clientId: canonicalCustomerId || canonicalCustomerRecord.clientId || canonicalCustomerRecord.id,
      } : canonicalCustomerSnapshot;

      const canonicalCustomerName = String(canonicalCustomerSnapshot?.name || canonicalCustomerSnapshot?.customerName || effectiveCustomerName || customerName || "Cliente").trim();
      const canonicalCustomerEmail = String(canonicalCustomerSnapshot?.emailNormalized || canonicalCustomerSnapshot?.email || canonicalCustomerSnapshot?.customerEmail || effectiveCustomerEmail || "").trim().toLowerCase();
      const canonicalCustomerPhone = bookingModalPhoneForStorage(canonicalCustomerSnapshot?.phoneDisplay || canonicalCustomerSnapshot?.phone || canonicalCustomerSnapshot?.phoneNormalized || canonicalCustomerSnapshot?.phoneE164 || canonicalCustomerSnapshot?.customerPhone || effectiveCustomerPhone || "");
      const canonicalCustomerPhoneNormalized = bookingModalNormalizePhone(canonicalCustomerPhone || canonicalCustomerSnapshot?.phoneNormalized || canonicalCustomerSnapshot?.phoneE164 || effectiveCustomerPhone || "") || null;
      const canonicalWhatsappOptOut = canonicalCustomerSnapshot?.whatsappOptOut === true || canonicalCustomerSnapshot?.whatsappOptIn === false || canonicalCustomerSnapshot?.customerWhatsappConsent === false || effectiveCustomerWhatsappOptOut;

      if (canonicalCustomerId && canonicalCustomerId !== customerId) setCustomerId(canonicalCustomerId);
      if (canonicalCustomerName && canonicalCustomerName !== customerName) setCustomerName(canonicalCustomerName);
      if (canonicalCustomerEmail && canonicalCustomerEmail !== customerEmail) setCustomerEmail(canonicalCustomerEmail);
      if (canonicalCustomerPhone && canonicalCustomerPhone !== customerPhone) setCustomerPhone(canonicalCustomerPhone);
      if (canonicalCustomerSnapshot) setCustomerSnapshot(canonicalCustomerSnapshot);

      if (selectedService?.sessionPackage?.enabled && !editMode) {
        if(canonicalCustomerId !== customerId){
          const response=await callCallableNativeSafe({functionName:'getCustomerSessionPrograms',data:{businessId,customerId:canonicalCustomerId},timeoutMs:15000});
          const programs=(response.data || response).programs || [];
          if(programs.some(p=>p.serviceId===serviceId && p.completed<p.totalSessions))throw new Error('Encontramos un paquete activo en la ficha del cliente. Revisa el paquete seleccionado antes de guardar.');
        } else if(sessionPrograms.loading || sessionPrograms.error || sessionPrograms.refreshError)throw new Error('Espera a que se cargue el programa del cliente.');
      }
      if (selectedService?.sessionPackage?.enabled && !editMode && sessionProgram && sessionAction==='new' && !sessionNewConfirmed) throw new Error('Confirma que deseas iniciar otro paquete para este cliente.');
      await onCreate({
        sessionProgramAction: sessionProgram ? sessionAction : 'new',
        requestedSessionProgramId: continuingSession ? sessionProgram?.id : null,
        sessionPreviousCompleted: sessionPrevious,
        sessionExpectedActiveIds: sessionPrograms.programs.filter(p=>p.serviceId===serviceId && p.completed<p.totalSessions).map(p=>p.id),
        collectionEventId: (()=>{const signature=JSON.stringify([initialBooking?.id,amountCharged,paymentMethod,priceAdjustmentDraft]);if(collectionAttempt.current?.signature!==signature)collectionAttempt.current={signature,id:`manual-${Date.now()}-${Math.random().toString(36).slice(2)}`};return collectionAttempt.current.id;})(),
        expectedSchedule: editMode ? scheduleSnapshot(initialBooking) : undefined,
        id: initialBooking ? initialBooking.id : null,

        // Loop analytics source classification.
        bookingSource: preservedBookingSource || "admin_manual",
        createdFrom: preservedCreatedFrom || "admin_calendar",
        // LOOP PHASE 1 HOTFIX V9 — role-aware manual booking metadata.
        // Worker-created reservations must not be sent as owner/admin-created.
        createdByRole: preservedCreatedByRole || (isOwnerOrAdmin ? "owner" : "worker"),
        channel: initialBooking?.channel || "admin_calendar",
        origin: initialBooking?.origin || "booking_create_modal",
        manual: preservedBookingSource ? initialBooking?.manual === true : true,
        isManual: preservedBookingSource ? initialBooking?.isManual === true : true,
        adminCreated: preservedBookingSource ? initialBooking?.adminCreated === true : Boolean(isOwnerOrAdmin),
        onlineBooking: preservedBookingSource ? initialBooking?.onlineBooking === true : false,
        isOnlineBooking: preservedBookingSource ? initialBooking?.isOnlineBooking === true : false,

        customerId: canonicalCustomerId || null,
        clientId: canonicalCustomerId || null,
        customerName: canonicalCustomerName,
        customerEmail: canonicalCustomerEmail || null,
        customerEmailNormalized: canonicalCustomerEmail || null,
        userEmail: canonicalCustomerEmail || null,
        clientEmail: canonicalCustomerEmail || null,
        customerPhone: canonicalCustomerPhone || null,
        customerPhoneNormalized: canonicalCustomerPhoneNormalized,
        customerWhatsappOptOut: canonicalWhatsappOptOut,
        whatsappOptOut: canonicalWhatsappOptOut,
        whatsappOptIn: !canonicalWhatsappOptOut,
        customerWhatsappConsent: !canonicalWhatsappOptOut,
        whatsappPhoneStatus: canonicalCustomerPhoneNormalized ? "valid" : "missing",
        customerSnapshot: canonicalCustomerSnapshot ? { ...canonicalCustomerSnapshot, id: canonicalCustomerId || canonicalCustomerSnapshot.id || null, customerId: canonicalCustomerId || canonicalCustomerSnapshot.customerId || null, whatsappOptOut: canonicalWhatsappOptOut, whatsappOptIn: !canonicalWhatsappOptOut, customerWhatsappConsent: !canonicalWhatsappOptOut } : null,
        hasUserAccount: canonicalCustomerSnapshot?.hasUserAccount === true || Boolean(canonicalCustomerSnapshot?.linkedUserId || canonicalCustomerSnapshot?.linkedUserUid || canonicalCustomerSnapshot?.userId || canonicalCustomerSnapshot?.userUid),
        linkedUserId: canonicalCustomerSnapshot?.linkedUserId || canonicalCustomerSnapshot?.linkedUserUid || canonicalCustomerSnapshot?.userId || canonicalCustomerSnapshot?.userUid || null,
        linkedUserUid: canonicalCustomerSnapshot?.linkedUserUid || canonicalCustomerSnapshot?.linkedUserId || canonicalCustomerSnapshot?.userUid || canonicalCustomerSnapshot?.userId || null,
        customerUserId: canonicalCustomerSnapshot?.linkedUserId || canonicalCustomerSnapshot?.userId || null,
        customerUserUid: canonicalCustomerSnapshot?.linkedUserUid || canonicalCustomerSnapshot?.userUid || null,
        identityCanonicalizedAtClient: new Date().toISOString(),
        identityCanonicalSource: canonicalCustomerRecord?.identityStatus || canonicalCustomerRecord?.identityMatchedBy || "booking_modal_save",
        time: time.trim(),
        smartBaseDate: activeBookingDate,
        status: effectiveBookingStatus,
        confirmationStatus: effectiveBookingStatus,
        deleteReservationIntent: editMode && bookingStatus === BOOKING_MODAL_DELETE_SENTINEL,
        cancellationSource: editMode && bookingStatus === BOOKING_MODAL_DELETE_SENTINEL ? "admin_booking_modal_delete_state" : null,
        cancellationReason: editMode && bookingStatus === BOOKING_MODAL_DELETE_SENTINEL ? "Quitada de agenda sin notificar cliente" : null,
        notifyCustomerCancellation: false,
        resourceId: workerNotRequired ? null : (resourceId || null),
        duration: Number(duration) || 60,
        serviceId: serviceId || null,
        requestedWorkerMode: workerNotRequired ? "none" : (resourceId ? "specific" : "any"),
        assignmentStatus: workerNotRequired ? "not_required" : (resourceId ? "assigned" : "unassigned"),
        employeeNotRequired: workerNotRequired,
        eventOptions: eventDetails,
        eventDetails,
        visitType: eventDetails?.visitType || null,
        visitTypeLabel: eventDetails?.visitTypeLabel || null,
        visitHours: eventDetails?.visitHours || null,
        selectedCharacterIds: eventDetails?.selectedCharacterIds || [],
        selectedCharacters: eventDetails?.selectedCharacters || [],
        durationPrice: eventDetails?.durationPrice ?? null,
        charactersPrice: eventDetails?.charactersPrice ?? null,
        totalPrice: eventDetails?.totalPrice ?? totalPrice,
        membershipApplied: LOOP_BOOKING_MEMBERSHIPS_ENABLED && Boolean(effectiveSelectedMembershipBenefit),
        membershipBenefitApplied: LOOP_BOOKING_MEMBERSHIPS_ENABLED && Boolean(effectiveSelectedMembershipBenefit),
        customerMembershipId: LOOP_BOOKING_MEMBERSHIPS_ENABLED ? (effectiveSelectedMembershipBenefit?.membership?.id || null) : null,
        membershipId: LOOP_BOOKING_MEMBERSHIPS_ENABLED ? (effectiveSelectedMembershipBenefit?.membership?.id || null) : null,
        membershipPlanId: LOOP_BOOKING_MEMBERSHIPS_ENABLED ? (effectiveSelectedMembershipBenefit?.plan?.id || effectiveSelectedMembershipBenefit?.membership?.planId || null) : null,
        membershipPlanName: LOOP_BOOKING_MEMBERSHIPS_ENABLED ? (effectiveSelectedMembershipBenefit?.plan?.name || effectiveSelectedMembershipBenefit?.membership?.planName || null) : null,
        membershipBenefitType: LOOP_BOOKING_MEMBERSHIPS_ENABLED && effectiveSelectedMembershipBenefit ? membershipBenefitType : null,
        membershipCreditsUsed: LOOP_BOOKING_MEMBERSHIPS_ENABLED && effectiveSelectedMembershipBenefit && membershipBenefitType === "credit" ? 1 : 0,
        membershipDiscountPercent: LOOP_BOOKING_MEMBERSHIPS_ENABLED && effectiveSelectedMembershipBenefit && membershipBenefitType === "discount" ? membershipBenefitDiscountPercent : 0,
        membershipDiscountAmount: LOOP_BOOKING_MEMBERSHIPS_ENABLED && effectiveSelectedMembershipBenefit ? membershipBenefitDiscountAmount : 0,
        membershipBenefitLabel: LOOP_BOOKING_MEMBERSHIPS_ENABLED ? (effectiveSelectedMembershipBenefit?.label || null) : null,
        paymentStatus,
        paymentMethod,
        paymentCoverageType,
        coverageType: paymentCoverageType,
        coveredAmount,
        benefitCoveredAmount: coveredAmount,
        commissionSource: isCoveredPayment ? paymentCoverageType : "cash",
        amountCharged: normalizedAmountCharged,
        paidAmount: normalizedAmountCharged,
        collectionApplied,
        priceAdjustmentDraft,
        bookingPriceOverride:priceOverride,
        collectionBasePaidAmount: Number(initialBooking?.paidAmount ?? initialBooking?.amountCharged ?? 0),
        balanceAmount: Math.max(0, expectedChargedAmount-normalizedAmountCharged),
        discountAmount: normalizedDiscount,
        tipAmount: normalizedTip,
        tipPaymentMethod: normalizedTipPaymentMethod,
        workerTipPaymentMethod: normalizedTipPaymentMethod,
        totalCollectedAmount,
        grossCollectedAmount: totalCollectedAmount,
        serviceCollectedAmount: normalizedAmountCharged,
        workerCommissionType,
        commissionType: workerCommissionType,
        workerCommissionValue: normalizedCommissionValue,
        commissionValue: normalizedCommissionValue,
        commissionBaseAmount,
        workerCommissionAmount,
        workerTipAmount,
        commissionIncludesTips: false,
        commissionOnlyCompleted,
        commissionLifecycleStatus,
        commissionLifecycleLabel,
        workerPayoutEstimatedAmount,
        workerPayoutAmount,
        workerPayoutStatus,
        workerPaymentNotes: String(workerPaymentNotes || "").trim() || null,
        businessNetAmount,
        payment: {
          status: paymentStatus,
          method: paymentMethod,
          coverageType: paymentCoverageType,
          paymentCoverageType,
          coveredAmount,
          benefitCoveredAmount: coveredAmount,
          commissionSource: isCoveredPayment ? paymentCoverageType : "cash",
          amountCharged: normalizedAmountCharged,
          discountAmount: normalizedDiscount,
          membershipDiscountAmount: LOOP_BOOKING_MEMBERSHIPS_ENABLED && effectiveSelectedMembershipBenefit ? membershipBenefitDiscountAmount : 0,
          membershipBenefitType: LOOP_BOOKING_MEMBERSHIPS_ENABLED && effectiveSelectedMembershipBenefit ? membershipBenefitType : null,
          tipAmount: normalizedTip,
          tipPaymentMethod: normalizedTipPaymentMethod,
          workerTipPaymentMethod: normalizedTipPaymentMethod,
          totalCollectedAmount,
          grossCollectedAmount: totalCollectedAmount,
          serviceCollectedAmount: normalizedAmountCharged,
          workerCommissionType,
          commissionType: workerCommissionType,
          workerCommissionValue: normalizedCommissionValue,
          commissionValue: normalizedCommissionValue,
          commissionBaseAmount,
          workerCommissionAmount,
          workerTipAmount,
          commissionIncludesTips: false,
          commissionOnlyCompleted,
          commissionLifecycleStatus,
          commissionLifecycleLabel,
          workerPayoutEstimatedAmount,
          workerPayoutAmount,
          workerPayoutStatus,
          workerPaymentNotes: String(workerPaymentNotes || "").trim() || null,
          businessNetAmount,
        },
        visitMode: (locationRequired || customerLocation) ? "delivery" : "onsite",
        visitTypeLocation: (locationRequired || customerLocation) ? "delivery" : "onsite",
        customerLocation: customerLocation || null,
        serviceAreaMatched: matchedServiceArea || null,
        customerExactAddress: String(customerExactAddress || "").trim() || null,
        customerAddressDetails: String(customerExactAddress || "").trim() || null,
        deliveryFee,
        locationFee: deliveryFee,
        assignedEmployeeIds: selectedStaff.map((emp) => emp.id),
        assignedEmployees: selectedStaff.map((emp) => ({
          id: emp.id,
          name: employeeLabel(emp),
          email: emp.email || emp.employeeEmail || null,
        })),
        notes: String(notes || "").trim() || null,
        internalNotes: String(notes || "").trim() || null,
        productSales: normalizedRetailRows,
        retailSales: normalizedRetailRows,
        productSalesTotalAmount: retailSalesTotalAmount,
        retailSalesTotalAmount,
        productSalesCostAmount: retailSalesCostAmount,
        retailCostAmount: retailSalesCostAmount,
        productSalesPayoutAmount: retailSalesPayoutAmount,
        retailPayoutAmount: retailSalesPayoutAmount,
        productSalesNetAmount: retailSalesNetAmount,
        retailNetAmount: retailSalesNetAmount,
        productSalesDeductionStatus: normalizedRetailRows.length ? (productStockAutoDeductEnabled ? (initialBooking?.productSalesDeductionStatus || "pending") : "not_required") : "not_required",
        productSalesDeductTrigger: productStockDeductTrigger,
        productSalesHasStockWarning: retailStockIssues.length > 0,
        inventoryDeductionStatus: serviceInventoryDeductionStatus,
        inventoryDeductTrigger: productStockDeductTrigger,
        inventoryAutoDeductTrigger: productStockDeductTrigger,
        inventoryAutoDeductStatus: productStockAutoDeductEnabled ? (selectedServiceHasInventoryUsage ? (initialBooking?.inventoryAutoDeductStatus || "waiting_trigger") : "not_required") : "disabled",
        inventoryMaterialsConfigured: selectedServiceHasInventoryUsage,
        inventoryUsagePreview: selectedServiceInventoryUsageRows,
      });
      setBookingLifecycleDecision(null);
      onClose();
    } catch (err) {
      console.error("BookingCreateModal create/edit error", err);
      if (err?.code === 'schedule-exception-cancelled') return;
      showBookingGuardrail({
        tone: "danger",
        eyebrow: "No se pudo guardar",
        title: "Error creando o actualizando la reserva",
        message: err && err.message ? err.message : String(err),
        primaryLabel: "Cerrar",
      });
    } finally {
      setSaving(false);
    }
  };


  const liveCustomerProfile = useMemo(() => bookingModalBuildLiveCustomerProfile({
    customerId,
    customerName,
    customerEmail,
    customerPhone,
    customerSnapshot,
  }), [customerId, customerName, customerEmail, customerPhone, customerSnapshot]);

  const liveCustomerIsLoopUser = useMemo(() => {
    const profile = liveCustomerProfile || {};
    return profile.identityCanonicalProfile === true ||
      profile.identityContactSource === "platform_user_profile" ||
      profile.hasUserAccount === true ||
      Boolean(profile.linkedUserUid || profile.linkedUserId || profile.userUid || profile.userId || profile.platformUserUid || profile.platformUserId);
  }, [liveCustomerProfile]);

  const editableCustomerProfileId = String(liveCustomerProfile?.customerId || liveCustomerProfile?.id || customerId || "").trim();
  const canEditLiveCustomerProfile = Boolean(businessId && editableCustomerProfileId && !liveCustomerIsLoopUser);

  const resetCustomerProfileEditor = () => {
    setCustomerProfileEditMode(false);
    setCustomerProfileEditError("");
    setCustomerProfileSaving(false);
  };

  const openCustomerProfileEditor = () => {
    setCustomerProfileEditDraft({
      name: liveCustomerProfile?.name || customerName || "",
      phone: bookingModalPhoneForStorage(liveCustomerProfile?.phoneNormalized || liveCustomerProfile?.phoneE164 || liveCustomerProfile?.phoneDisplay || liveCustomerProfile?.phone || customerPhone || ""),
      email: liveCustomerProfile?.email || customerEmail || "",
    });
    setCustomerProfileEditError("");
    setCustomerProfileEditMode(true);
  };

  const closeCustomerProfileModal = () => {
    setShowCustomerDetails(false);
    resetCustomerProfileEditor();
  };

  const saveCustomerProfileEdit = async () => {
    if (!canEditLiveCustomerProfile) return;
    const name = String(customerProfileEditDraft.name || "").trim();
    const email = String(customerProfileEditDraft.email || "").trim().toLowerCase();
    const localPhone = bookingModalPhoneForStorage(customerProfileEditDraft.phone || "");
    const phoneE164 = bookingModalNormalizePhone(localPhone) || "";
    const phoneDisplay = phoneE164 ? formatPhoneDisplay(phoneE164, "PA") : localPhone;

    if (!name) {
      setCustomerProfileEditError("Agrega el nombre del cliente.");
      return;
    }

    setCustomerProfileSaving(true);
    setCustomerProfileEditError("");
    try {
      let identity = null;
      try {
        identity = await bookingModalResolveCustomerIdentitySafe(db, businessId, {
          name,
          customerName: name,
          phone: localPhone,
          customerPhone: localPhone,
          email,
          customerEmail: email,
        });
      } catch (identityErr) {
        console.warn("[BookingCreateModal] customer profile edit identity check failed", identityErr);
      }

      const identitySnapshot = identity?.customerSnapshot || null;
      const resolvedCustomerId = String(identity?.existingCustomer?.id || identity?.customerId || identitySnapshot?.customerId || identitySnapshot?.id || "").trim();
      const resolvedIsLoopUser = identitySnapshot?.identityCanonicalProfile === true ||
        identitySnapshot?.identityContactSource === "platform_user_profile" ||
        identity?.status === "platform_user" ||
        identity?.status === "platform_user_missing_phone" ||
        Boolean(identitySnapshot?.linkedUserUid || identitySnapshot?.linkedUserId || identitySnapshot?.platformUserUid || identitySnapshot?.platformUserId);

      if (identity?.status === "conflict") {
        setCustomerProfileEditError(identity?.conflict?.message || "El email y el WhatsApp parecen pertenecer a personas diferentes.");
        return;
      }

      if (resolvedIsLoopUser) {
        setCustomerProfileEditError("Este email o WhatsApp pertenece a un usuario Loop. Sus datos deben venir de ProfilePage y sincronizarse, no editarse manualmente desde el negocio.");
        return;
      }

      if (resolvedCustomerId && resolvedCustomerId !== editableCustomerProfileId) {
        setCustomerProfileEditError("Este email o WhatsApp ya pertenece a otro cliente de este negocio. Selecciona ese cliente o usa otros datos.");
        return;
      }

      const updatedAtMs = Date.now();
      const normalizedName = normalizeText(name);
      const searchableText = normalizeText([name, email, phoneDisplay, phoneE164, localPhone].filter(Boolean).join(" "));
      const payload = {
        name,
        customerName: name,
        displayName: name,
        fullName: name,
        nameKey: normalizedName,
        searchableName: searchableText,
        searchText: searchableText,
        email,
        customerEmail: email,
        emailNormalized: email,
        phone: phoneDisplay,
        customerPhone: phoneDisplay,
        phoneDisplay,
        phoneNormalized: phoneE164,
        phoneE164,
        customerPhoneNormalized: phoneE164,
        whatsapp: phoneDisplay,
        whatsApp: phoneDisplay,
        updatedAt: serverTimestamp(),
        updatedAtMs,
        identityContactSource: "business_customer_manual_edit",
        identityNeedsUserProfileSync: false,
        isSyncedFromPlatformUser: false,
      };

      await updateDoc(doc(db, "businesses", String(businessId), "customers", editableCustomerProfileId), payload);

      const nextSnapshot = {
        ...(customerSnapshot && typeof customerSnapshot === "object" ? customerSnapshot : {}),
        ...payload,
        id: editableCustomerProfileId,
        customerId: editableCustomerProfileId,
        updatedAtMs,
        totalBookings: Number(liveCustomerProfile?.totalBookings || customerSnapshot?.totalBookings || customerSnapshot?.importedTotalBookings || 0) || 0,
        importedTotalBookings: Number(liveCustomerProfile?.importedTotalBookings || customerSnapshot?.importedTotalBookings || customerSnapshot?.totalBookings || 0) || 0,
      };

      setCustomerName(name);
      setCustomerEmail(email);
      setCustomerPhone(localPhone);
      setCustomerSnapshot(nextSnapshot);
      setCustomerProfileEditMode(false);
    } catch (err) {
      console.error("[BookingCreateModal] customer profile edit failed", err);
      setCustomerProfileEditError(err?.message || "No pudimos actualizar este cliente. Intenta nuevamente.");
    } finally {
      setCustomerProfileSaving(false);
    }
  };

  if (!open) return null;

  return (
    <AccessibleModal open={open} onClose={onClose} labelledBy="modal-title" width={1160} dialogClassName="loop-create-modal-host" dialogStyle={{ padding: 0, background: "transparent", boxShadow: "none", borderRadius: 0 }}>
      <style>{`${styles}

/* LOOP D2.9.1A.11 — customer note cleanup + ops order */
.loop-booking-ops-strip.loop-booking-ops-minimal{grid-template-columns:minmax(180px,.95fr) minmax(190px,1fr) minmax(210px,1.05fr) minmax(210px,1.05fr)!important;}
.loop-customer-one-line-note:not(.is-new-customer-only){display:none!important;}
.loop-customer-one-line-note.is-new-customer-only{margin-top:8px!important;padding:9px 11px!important;min-height:auto!important;border-radius:12px!important;background:#f8fafc!important;}
.loop-customer-one-line-note.is-new-customer-only span{font-size:10px!important;letter-spacing:.08em!important;text-transform:uppercase!important;color:#64748b!important;}
.loop-customer-one-line-note.is-new-customer-only strong{font-size:11px!important;line-height:1.35!important;color:#64748b!important;font-weight:800!important;}
.loop-customer-minimal-section .loop-customer-minimal-search{margin-bottom:0!important;}
@media (max-width: 980px){.loop-booking-ops-strip.loop-booking-ops-minimal{grid-template-columns:1fr!important;}}

/* LOOP D2.9.3B.0 — create/edit modal unification */
.loop-booking-ops-strip.loop-booking-ops-minimal{grid-template-columns:minmax(180px,.9fr) minmax(220px,1fr) minmax(210px,1fr) minmax(210px,1fr)!important;margin:12px 20px 0!important;}
.loop-ops-item.is-muted{opacity:.68!important;cursor:not-allowed!important;background:#f8fafc!important;}
.loop-create-pro-form.is-create-mode,.loop-create-pro-form.is-edit-mode{grid-template-columns:1fr!important;padding:14px 20px 26px!important;}
.loop-create-pro-form.is-create-mode .loop-create-pro-left,.loop-create-pro-form.is-edit-mode .loop-create-pro-left{width:min(100%,980px)!important;max-width:980px!important;margin:0 auto!important;}
.loop-create-pro-form .loop-create-summary-card{display:none!important;}
.loop-create-pro-form.is-create-mode .loop-payment-section .booking-pro-section-title,.loop-create-pro-form.is-edit-mode .loop-payment-section .booking-pro-section-title{margin-bottom:8px!important;}
@media (max-width: 980px){.loop-booking-ops-strip.loop-booking-ops-minimal{grid-template-columns:1fr!important;margin:10px 12px 0!important}.loop-create-pro-form.is-create-mode,.loop-create-pro-form.is-edit-mode{padding:12px!important}.loop-create-pro-form.is-create-mode .loop-create-pro-left,.loop-create-pro-form.is-edit-mode .loop-create-pro-left{max-width:none!important}}

/* LOOP D2.9.3J.3 — header hero + smart chips exact polish */
.loop-create-pro-modal{background:#fff!important;border:1px solid rgba(203,213,225,.78)!important;box-shadow:0 24px 80px rgba(15,23,42,.22)!important;border-radius:26px!important;overflow:hidden!important;}
.loop-booking-minimal-head{display:grid!important;grid-template-columns:auto minmax(0,1fr) auto!important;align-items:center!important;gap:16px!important;padding:28px 32px 26px!important;border-bottom:1px solid #edf2f7!important;background:linear-gradient(180deg,#ffffff 0%,#fbfdff 100%)!important;}
.loop-booking-hero-icon{width:52px!important;height:52px!important;border-radius:999px!important;background:#ecfdf5!important;color:#007568!important;display:flex!important;align-items:center!important;justify-content:center!important;border:1px solid rgba(0,103,95,.10)!important;box-shadow:0 14px 28px rgba(0,103,95,.08)!important;}
.loop-booking-hero-icon svg{width:22px!important;height:22px!important;stroke:currentColor!important;stroke-width:1.9!important;stroke-linecap:round!important;stroke-linejoin:round!important;}
.loop-booking-minimal-head .loop-booking-head-copy{min-width:0!important;}
.loop-booking-minimal-head .booking-pro-kicker{margin:0!important;color:#00675f!important;font-size:10px!important;font-weight:950!important;letter-spacing:.18em!important;text-transform:uppercase!important;line-height:1!important;}
.loop-booking-minimal-head .booking-edit-title-row{display:flex!important;align-items:center!important;gap:10px!important;min-width:0!important;margin-top:8px!important;}
.loop-booking-minimal-head .booking-edit-title-row h2{margin:0!important;font-size:24px!important;line-height:1.05!important;font-weight:950!important;letter-spacing:-.04em!important;color:#0f172a!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;}
.loop-booking-minimal-head .booking-edit-status-pill{margin:0!important;border-radius:999px!important;padding:7px 12px!important;font-size:11px!important;font-weight:950!important;line-height:1!important;border:1px solid rgba(34,197,94,.22)!important;background:#dcfce7!important;color:#047857!important;}
.loop-booking-minimal-head .loop-booking-head-copy p{margin:6px 0 0!important;color:#64748b!important;font-size:12px!important;font-weight:850!important;text-transform:none!important;line-height:1.25!important;}
.loop-booking-head-actions-minimal{display:flex!important;align-items:center!important;justify-content:flex-end!important;gap:10px!important;min-width:max-content!important;}
.loop-booking-head-actions-minimal .booking-pro-primary{height:46px!important;border-radius:13px!important;padding:0 24px!important;background:linear-gradient(135deg,#007568,#00675f)!important;color:#fff!important;font-size:13px!important;font-weight:950!important;box-shadow:0 16px 34px rgba(0,103,95,.22)!important;min-width:128px!important;}
.loop-header-summary-btn{height:42px!important;border-radius:13px!important;padding:0 16px!important;border:1px solid #e2e8f0!important;background:#fff!important;color:#334155!important;font-size:13px!important;font-weight:950!important;display:inline-flex!important;align-items:center!important;gap:8px!important;box-shadow:0 10px 22px rgba(15,23,42,.045)!important;}
.loop-header-summary-icon{font-size:15px!important;line-height:1!important;color:#0f172a!important;}
.loop-booking-head-actions-minimal .booking-pro-close{width:42px!important;height:42px!important;border-radius:999px!important;border:1px solid #dbe4ef!important;background:#fff!important;color:#0f172a!important;font-size:20px!important;box-shadow:0 10px 22px rgba(15,23,42,.055)!important;}
.loop-more-actions-wrap{order:3!important;}
.loop-more-actions-btn{height:42px!important;border-radius:13px!important;padding:0 14px!important;border:1px solid #e2e8f0!important;background:#fff!important;color:#334155!important;font-size:13px!important;font-weight:950!important;}
.loop-booking-ops-minimal{display:grid!important;grid-template-columns:repeat(4,minmax(0,1fr))!important;gap:10px!important;margin:18px 32px 18px!important;padding:0!important;border:0!important;background:transparent!important;overflow:visible!important;}
.loop-booking-ops-minimal>div,.loop-booking-ops-minimal>button{position:relative!important;min-height:58px!important;border:1px solid #e2e8f0!important;background:#fff!important;border-radius:999px!important;padding:10px 14px 10px 48px!important;text-align:left!important;box-shadow:0 10px 24px rgba(15,23,42,.04)!important;overflow:visible!important;transition:transform .16s ease,box-shadow .16s ease,border-color .16s ease!important;}
.loop-booking-ops-minimal>button:not(:disabled):hover,.loop-booking-ops-minimal>div:hover{transform:translateY(-1px)!important;border-color:rgba(0,103,95,.20)!important;box-shadow:0 14px 30px rgba(15,23,42,.07)!important;}
.loop-booking-ops-minimal>div::before,.loop-booking-ops-minimal>button::before{content:""!important;position:absolute!important;left:14px!important;top:50%!important;width:25px!important;height:25px!important;border-radius:999px!important;transform:translateY(-50%)!important;background:#ecfdf5!important;border:1px solid rgba(0,103,95,.12)!important;color:#007568!important;display:flex!important;align-items:center!important;justify-content:center!important;font-size:13px!important;font-weight:950!important;}
.loop-booking-ops-minimal>div:nth-child(1)::before{content:"✓"!important;}
.loop-booking-ops-minimal>button:nth-child(2)::before{content:"▣"!important;}
.loop-booking-ops-minimal>button:nth-child(3)::before{content:"◉"!important;}
.loop-booking-ops-minimal>button:nth-child(4)::before{content:"▤"!important;}
.loop-booking-ops-minimal span{display:block!important;margin:0 0 3px!important;color:#64748b!important;font-size:10px!important;font-weight:950!important;letter-spacing:0!important;text-transform:none!important;line-height:1.1!important;}
.loop-booking-ops-minimal strong{display:block!important;color:#0f172a!important;font-size:12px!important;font-weight:950!important;line-height:1.15!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;}
.loop-booking-ops-minimal em{position:absolute!important;top:8px!important;right:8px!important;border-radius:999px!important;background:#ecfdf5!important;color:#007568!important;border:1px solid rgba(0,103,95,.16)!important;font-size:9.5px!important;line-height:1!important;font-style:normal!important;font-weight:950!important;padding:5px 7px!important;}
.loop-booking-ops-minimal .loop-ops-summary-btn{display:none!important;}
.loop-booking-ops-minimal .loop-mini-select{width:100%!important;position:static!important;}
.loop-booking-ops-minimal .loop-mini-select-trigger{height:auto!important;min-height:0!important;border:0!important;background:transparent!important;padding:0 18px 0 0!important;box-shadow:none!important;color:#0f172a!important;font-size:12px!important;font-weight:950!important;line-height:1.15!important;}
.loop-booking-ops-minimal .loop-mini-select-trigger i{position:absolute!important;right:12px!important;bottom:10px!important;color:#007568!important;font-size:14px!important;}
.loop-booking-ops-minimal .loop-mini-select-menu{top:calc(100% + 8px)!important;left:0!important;right:auto!important;min-width:210px!important;z-index:999999!important;border-radius:16px!important;border:1px solid #dbe4ef!important;box-shadow:0 22px 60px rgba(15,23,42,.18)!important;padding:7px!important;background:#fff!important;}
.loop-booking-ops-minimal .loop-mini-select-menu button{border-radius:11px!important;min-height:36px!important;padding:9px 10px!important;font-size:13px!important;font-weight:900!important;}
@media(max-width:980px){.loop-booking-minimal-head{grid-template-columns:auto 1fr!important;padding:22px 18px 16px!important}.loop-booking-head-actions-minimal{grid-column:1/-1!important;justify-content:flex-start!important;flex-wrap:wrap!important}.loop-booking-ops-minimal{grid-template-columns:1fr 1fr!important;margin:14px 18px!important}.loop-header-summary-btn{order:0!important}.loop-booking-head-actions-minimal .booking-pro-primary{order:1!important}.loop-more-actions-wrap{order:2!important}.loop-booking-head-actions-minimal .booking-pro-close{margin-left:auto!important;order:3!important}}
@media(max-width:620px){.loop-booking-minimal-head{grid-template-columns:1fr auto!important}.loop-booking-hero-icon{display:none!important}.loop-booking-minimal-head .booking-edit-title-row h2{font-size:22px!important;white-space:normal!important}.loop-booking-head-actions-minimal{grid-column:1/-1!important}.loop-booking-ops-minimal{grid-template-columns:1fr!important}.loop-header-summary-btn,.loop-booking-head-actions-minimal .booking-pro-primary{flex:1!important;justify-content:center!important}}
`}</style>

      <style>{`
/* LOOP D2.9.4K — booking modal stacking lock.
   Keeps the create/edit booking surface above the global header and keeps
   dropdown/submodals above the smart summary chips. */
.loop-accessible-modal-layer {
  z-index: 2147483641 !important;
  isolation: isolate !important;
}
.loop-accessible-modal-layer.is-nested-modal {
  z-index: 2147483645 !important;
}
.loop-create-modal-host {
  position: relative !important;
  z-index: 2147483642 !important;
}
.loop-nested-settings-modal,
.loop-new-customer-stable-modal {
  position: relative !important;
  z-index: 2147483646 !important;
}
.booking-pro-modal.loop-create-pro-modal,
.loop-create-pro-modal {
  position: relative !important;
  z-index: 1 !important;
  isolation: isolate !important;
}
.loop-booking-minimal-head,
.loop-create-pro-head,
.booking-pro-head.loop-create-pro-head {
  position: relative !important;
  z-index: 9000 !important;
  overflow: visible !important;
  isolation: isolate !important;
}
.loop-booking-head-actions,
.loop-booking-head-actions-minimal,
.loop-more-actions-wrap {
  position: relative !important;
  z-index: 9010 !important;
  overflow: visible !important;
}
.loop-more-actions-menu {
  position: absolute !important;
  z-index: 9020 !important;
  isolation: isolate !important;
  transform: translateZ(0) !important;
}
.loop-booking-ops-strip,
.loop-booking-ops-minimal,
.loop-ops-item,
.loop-create-pro-form,
.loop-create-pro-left,
.loop-payment-section,
.loop-payment-slim-card,
.loop-payment-slim-main {
  position: relative !important;
}
.loop-booking-ops-strip,
.loop-booking-ops-minimal {
  z-index: 10 !important;
}
.loop-ops-item,
.loop-payment-slim-card,
.loop-payment-slim-main {
  z-index: 1 !important;
}
.loop-availability-modal-backdrop {
  position: fixed !important;
  inset: 0 !important;
  z-index: 2147483645 !important;
  isolation: isolate !important;
}
.loop-availability-modal {
  position: relative !important;
  z-index: 2147483646 !important;
}

/* LOOP D2.9.4K.1 — More actions must float above smart chips.
   The ops strip has older high-specificity z-index rules for its internal
   dropdowns. Keep those dropdowns safe, but lift only the booking header/menu
   above that strip so Más acciones never goes behind WhatsApp/Membresía/Pago. */
.booking-pro-modal.loop-create-pro-modal .loop-booking-minimal-head,
.booking-pro-modal.loop-create-pro-modal .loop-create-pro-head,
.booking-pro-modal.loop-create-pro-modal .booking-pro-head.loop-create-pro-head {
  position: relative !important;
  z-index: 2147483200 !important;
  overflow: visible !important;
  isolation: isolate !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-booking-head-actions,
.booking-pro-modal.loop-create-pro-modal .loop-booking-head-actions-minimal,
.booking-pro-modal.loop-create-pro-modal .loop-more-actions-wrap {
  position: relative !important;
  z-index: 2147483300 !important;
  overflow: visible !important;
  isolation: isolate !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-more-actions-menu {
  position: absolute !important;
  z-index: 2147483400 !important;
  background: #ffffff !important;
  isolation: isolate !important;
  transform: translateZ(0) !important;
  will-change: transform !important;
}


/* LOOP D2.9.4K.4 — Date/time editor respects agenda 12h/24h setting. */
.booking-pro-modal.loop-create-pro-modal .loop-time-format-field{
  position:relative!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time-format-field>small{
  display:block!important;
  margin-top:6px!important;
  color:#64748b!important;
  font-size:10.5px!important;
  font-weight:750!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control{
  width:100%!important;
  min-height:42px!important;
  border:1px solid #dbe7ef!important;
  background:#fff!important;
  border-radius:13px!important;
  display:grid!important;
  grid-template-columns:minmax(0,1fr) auto minmax(0,1fr) minmax(74px,.85fr)!important;
  align-items:center!important;
  gap:4px!important;
  padding:4px 6px!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control span{
  color:#64748b!important;
  font-size:16px!important;
  font-weight:950!important;
  line-height:1!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control select{
  height:34px!important;
  min-width:0!important;
  width:100%!important;
  border:0!important;
  border-radius:10px!important;
  background:#f8fafc!important;
  color:#0f172a!important;
  font-size:13px!important;
  font-weight:950!important;
  text-align:center!important;
  padding:0 8px!important;
  outline:none!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control select:focus{
  background:#ecfdf5!important;
  box-shadow:0 0 0 2px rgba(0,117,104,.12)!important;
}

/* LOOP D2.9.4K.3 — Fecha/hora modal above chips + duration belongs to service. */
.booking-pro-modal.loop-create-pro-modal .loop-datetime-modal-backdrop {
  z-index: 2147483646 !important;
  isolation: isolate !important;
  pointer-events: auto !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-datetime-modal {
  position: relative !important;
  z-index: 2147483647 !important;
  isolation: isolate !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-duration-row {
  margin-top: 10px !important;
  border: 1px solid #dbe7ef !important;
  background: linear-gradient(135deg, #f8fffd 0%, #ffffff 100%) !important;
  border-radius: 16px !important;
  padding: 11px 12px !important;
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) auto !important;
  align-items: center !important;
  gap: 12px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-duration-copy {
  display: grid !important;
  gap: 2px !important;
  min-width: 0 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-duration-copy span {
  color: #00675f !important;
  font-size: 10px !important;
  font-weight: 950 !important;
  text-transform: uppercase !important;
  letter-spacing: .08em !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-duration-copy strong {
  color: #0f172a !important;
  font-size: 14px !important;
  font-weight: 950 !important;
  line-height: 1.1 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-duration-copy small {
  color: #64748b !important;
  font-size: 11px !important;
  font-weight: 750 !important;
  line-height: 1.25 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-duration-control {
  width: 124px !important;
  height: 42px !important;
  border: 1px solid #cfe8e4 !important;
  border-radius: 999px !important;
  background: #ffffff !important;
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) auto !important;
  align-items: center !important;
  gap: 4px !important;
  padding: 0 12px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-duration-control input {
  width: 100% !important;
  min-width: 0 !important;
  height: 38px !important;
  border: 0 !important;
  outline: 0 !important;
  background: transparent !important;
  color: #0f172a !important;
  font-size: 14px !important;
  font-weight: 950 !important;
  text-align: right !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-duration-control span {
  color: #00675f !important;
  font-size: 12px !important;
  font-weight: 950 !important;
}
@media (max-width: 640px) {
  .booking-pro-modal.loop-create-pro-modal .loop-service-duration-row {
    grid-template-columns: 1fr !important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-service-duration-control {
    width: 100% !important;
  }
}


/* LOOP RESERVATION WORKSPACE V1 — first shell pass
   Goal: make create/edit feel like a clean reservation workspace, not a long form. */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 {
  width: min(1160px, calc(100vw - 28px)) !important;
  max-height: min(92vh, 940px) !important;
  overflow: auto !important;
  border-radius: 26px !important;
  background: #ffffff !important;
  border: 1px solid rgba(203,213,225,.82) !important;
  box-shadow: 0 28px 95px rgba(15,23,42,.24) !important;
}
.booking-pro-modal.loop-reservation-workspace-v1::-webkit-scrollbar { width: 10px; }
.booking-pro-modal.loop-reservation-workspace-v1::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 999px; border: 3px solid #fff; }
.booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-minimal-head {
  position: sticky !important;
  top: 0 !important;
  z-index: 80 !important;
  grid-template-columns: minmax(0,1fr) auto !important;
  padding: 24px 30px 22px !important;
  background: rgba(255,255,255,.985) !important;
  backdrop-filter: blur(16px) !important;
  -webkit-backdrop-filter: blur(16px) !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-hero-icon { display: none !important; }
.booking-pro-modal.loop-reservation-workspace-v1 .booking-pro-kicker {
  font-size: 10.5px !important;
  letter-spacing: .22em !important;
  color: #00675f !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .booking-edit-title-row h2 {
  font-size: clamp(22px, 2.8vw, 30px) !important;
  letter-spacing: -.055em !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .booking-edit-status-pill {
  min-height: 30px !important;
  display: inline-flex !important;
  align-items: center !important;
  border-radius: 999px !important;
  padding: 0 13px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-copy p {
  font-size: 13px !important;
  font-weight: 850 !important;
  color: #64748b !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-header-summary-btn { display: none !important; }
.booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-actions-minimal {
  gap: 10px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-actions-minimal .booking-pro-primary {
  min-width: 126px !important;
  height: 50px !important;
  border-radius: 15px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-more-actions-btn,
.booking-pro-modal.loop-reservation-workspace-v1 .booking-pro-close {
  height: 48px !important;
  border-radius: 15px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .booking-pro-close {
  width: 48px !important;
  border-radius: 999px !important;
}

/* Compact top status strip */
.booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal {
  grid-template-columns: repeat(4, minmax(0,1fr)) !important;
  margin: 16px 28px 16px !important;
  gap: 10px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > :nth-child(3) { display: none !important; }
.booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > div,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > button {
  min-height: 62px !important;
  border-radius: 999px !important;
  background: #ffffff !important;
  border: 1px solid #dfeaf2 !important;
  box-shadow: 0 10px 24px rgba(15,23,42,.045) !important;
  padding: 11px 15px 11px 50px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > div::before,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > button::before {
  width: 28px !important;
  height: 28px !important;
  left: 14px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal span {
  font-size: 10px !important;
  letter-spacing: .08em !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal strong {
  font-size: 13px !important;
  line-height: 1.18 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal em {
  right: 12px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  border-radius: 999px !important;
  padding: 6px 10px !important;
  background: #f0fdfa !important;
  color: #00675f !important;
  border: 1px solid #b8efe6 !important;
}

/* Workspace grid */
.booking-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form.is-create-mode,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form.is-edit-mode {
  padding: 0 28px 30px !important;
  background: #ffffff !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-left {
  width: 100% !important;
  max-width: none !important;
  display: grid !important;
  grid-template-columns: minmax(360px,1fr) minmax(460px,1fr) !important;
  grid-template-areas:
    "client details"
    "payment payment" !important;
  gap: 16px !important;
  align-items: start !important;
  margin: 0 auto !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section { grid-area: client !important; }
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section { grid-area: details !important; }
.booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-section { grid-area: payment !important; }
.booking-pro-modal.loop-reservation-workspace-v1 .booking-pro-section.loop-create-section {
  border: 1px solid #dfeaf2 !important;
  border-radius: 22px !important;
  background: #ffffff !important;
  box-shadow: 0 12px 34px rgba(15,23,42,.048) !important;
  padding: 20px !important;
  margin: 0 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .booking-pro-section.loop-create-section:hover {
  border-color: rgba(0,103,95,.18) !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .booking-pro-section-title.is-quiet-title,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-head,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-section-headline {
  margin: 0 0 14px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .booking-pro-section-title.is-quiet-title span,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-datetime-step,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-workspace-step {
  width: 28px !important;
  height: 28px !important;
  min-width: 28px !important;
  border-radius: 999px !important;
  background: #ecfdf5 !important;
  border: 1px solid #b8efe6 !important;
  color: #00675f !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 11px !important;
  font-weight: 950 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-workspace-title-inline {
  display: inline-flex !important;
  align-items: center !important;
  gap: 10px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-head strong,
.booking-pro-modal.loop-reservation-workspace-v1 .booking-pro-section-title strong,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-section-headline strong {
  font-size: 18px !important;
  letter-spacing: -.035em !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-head-actions {
  gap: 8px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-new-customer-action,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-datetime-change-btn,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-action,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-head-action {
  border-radius: 999px !important;
  border: 1px solid #b8efe6 !important;
  background: #ffffff !important;
  color: #00675f !important;
  font-weight: 950 !important;
}

/* Clean customer block */
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search { margin-top: 12px !important; }
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-fields {
  grid-template-columns: 1fr 1fr !important;
  gap: 12px !important;
  margin-top: 12px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-smart-footer {
  margin-top: 12px !important;
  display: flex !important;
  align-items: center !important;
  flex-wrap: wrap !important;
  gap: 7px !important;
  border: 0 !important;
  padding: 0 !important;
  background: transparent !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-smart-footer span,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-smart-footer strong,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-smart-footer em {
  display: inline-flex !important;
  align-items: center !important;
  min-height: 26px !important;
  padding: 0 9px !important;
  border-radius: 999px !important;
  background: #f8fafc !important;
  border: 1px solid #e2e8f0 !important;
  color: #64748b !important;
  font-style: normal !important;
  font-size: 11px !important;
  font-weight: 900 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-smart-footer span:first-child {
  background: #ecfdf5 !important;
  border-color: #bbf7d0 !important;
  color: #047857 !important;
}

/* Service and date blocks */
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-combo-row {
  grid-template-columns: minmax(0,1fr) auto !important;
  gap: 12px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-trigger,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-datetime-minimal-card {
  min-height: 76px !important;
  border-radius: 18px !important;
  background: linear-gradient(180deg,#ffffff 0%,#fbfdff 100%) !important;
  border: 1px solid #dfeaf2 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-datetime-minimal-card {
  min-height: 136px !important;
  align-items: center !important;
  padding: 22px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-datetime-minimal-main strong {
  font-size: 19px !important;
  line-height: 1.18 !important;
  letter-spacing: -.035em !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-datetime-minimal-main small {
  font-size: 13px !important;
  font-weight: 850 !important;
  color: #64748b !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-datetime-inline-warning,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-create-location-block,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-create-staff-block {
  margin-top: 14px !important;
}


/* LOOP WORKSPACE V4 — service + date unified bubble */
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section {
  min-height: 232px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section {
  padding: 20px !important;
  overflow: visible !important;
  position: relative !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-shell {
  display: grid !important;
  gap: 14px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-top,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-bottom {
  position: relative !important;
  min-width: 0 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-title-row {
  display: inline-flex !important;
  align-items: center !important;
  gap: 10px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-title-row strong {
  font-size: 18px !important;
  line-height: 1.1 !important;
  letter-spacing: -.035em !important;
  color: #0f172a !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-title-row.is-sub strong {
  font-size: 12px !important;
  letter-spacing: .08em !important;
  text-transform: uppercase !important;
  color: #64748b !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-title-row.is-sub .loop-workspace-step {
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  font-size: 10px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-detail-row {
  display: grid !important;
  grid-template-columns: minmax(0,1fr) minmax(132px,150px) !important;
  gap: 12px !important;
  align-items: stretch !important;
  margin-top: 18px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-service-picker {
  position: relative !important;
  min-width: 0 !important;
  z-index: 20 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-service-picker.is-open {
  z-index: 120 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-service-trigger {
  width: 100% !important;
  min-height: 60px !important;
  border: 1px solid #dfeaf2 !important;
  border-radius: 16px !important;
  background: linear-gradient(180deg,#ffffff 0%,#fbfdff 100%) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 12px !important;
  padding: 10px 12px !important;
  cursor: pointer !important;
  text-align: left !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-service-trigger span {
  display: grid !important;
  gap: 3px !important;
  min-width: 0 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-service-trigger small,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-duration-box > span,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-date-grid span {
  font-size: 10px !important;
  letter-spacing: .10em !important;
  text-transform: uppercase !important;
  color: #64748b !important;
  font-weight: 950 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-service-trigger strong {
  display: block !important;
  max-width: 100% !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  color: #0f172a !important;
  font-size: 14px !important;
  font-weight: 950 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-service-trigger em,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-date-change {
  flex: 0 0 auto !important;
  border-radius: 999px !important;
  border: 1px solid #b8efe6 !important;
  background: #ffffff !important;
  color: #00675f !important;
  font-size: 12px !important;
  font-weight: 950 !important;
  font-style: normal !important;
  padding: 9px 13px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-duration-box {
  border: 1px solid #dfeaf2 !important;
  border-radius: 16px !important;
  background: #fbfdff !important;
  padding: 10px 11px !important;
  display: grid !important;
  align-content: center !important;
  gap: 6px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-duration-box label {
  height: 36px !important;
  border: 1px solid #b8efe6 !important;
  background: #ffffff !important;
  border-radius: 999px !important;
  display: flex !important;
  align-items: center !important;
  gap: 6px !important;
  padding: 0 10px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-duration-box input {
  border: 0 !important;
  outline: 0 !important;
  background: transparent !important;
  width: 54px !important;
  min-width: 0 !important;
  text-align: right !important;
  font-size: 14px !important;
  font-weight: 950 !important;
  color: #0f172a !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-duration-box em {
  font-style: normal !important;
  color: #00675f !important;
  font-size: 12px !important;
  font-weight: 950 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-divider {
  height: 1px !important;
  background: linear-gradient(90deg,transparent,#dfeaf2 12%,#dfeaf2 88%,transparent) !important;
  margin: 0 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-date-title-line {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 10px !important;
  margin-bottom: 12px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-availability-mini-pill {
  display: inline-flex !important;
  align-items: center !important;
  min-height: 26px !important;
  padding: 0 10px !important;
  border-radius: 999px !important;
  background: #ecfdf5 !important;
  border: 1px solid #bbf7d0 !important;
  color: #047857 !important;
  font-size: 11px !important;
  font-weight: 950 !important;
  font-style: normal !important;
  white-space: nowrap !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-availability-mini-pill.warning,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-availability-mini-pill.is-warning {
  background: #fffbeb !important;
  border-color: #fde68a !important;
  color: #b45309 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-date-grid {
  display: grid !important;
  grid-template-columns: 1.25fr .7fr .95fr .8fr !important;
  gap: 12px !important;
  align-items: start !important;
  padding-right: 96px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-date-grid div {
  min-width: 0 !important;
  display: grid !important;
  gap: 4px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-date-grid strong {
  font-size: 14px !important;
  line-height: 1.2 !important;
  letter-spacing: -.02em !important;
  color: #0f172a !important;
  font-weight: 950 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-date-change {
  position: absolute !important;
  right: 0 !important;
  bottom: 0 !important;
  min-height: 40px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-service-menu {
  position: absolute !important;
  top: calc(100% + 8px) !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 9999 !important;
  max-height: 300px !important;
  overflow: hidden !important;
  border-radius: 18px !important;
  box-shadow: 0 20px 54px rgba(15,23,42,.18) !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .booking-event-options,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-create-staff-block,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-create-location-block,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-datetime-inline-warning {
  margin-top: 14px !important;
}
@media (max-width: 980px) {
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-service-detail-row,
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-service-date-grid {
    grid-template-columns: 1fr !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-service-date-grid {
    padding-right: 0 !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-date-change {
    position: static !important;
    margin-top: 12px !important;
    width: max-content !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section,
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section {
    min-height: auto !important;
  }
}


/* LOOP WORKSPACE V4.3 — compact service/date rows */
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-shell {
  gap: 12px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-head {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 12px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-rows {
  display: grid !important;
  gap: 8px !important;
  margin-top: 8px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-row,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-info-row {
  display: grid !important;
  grid-template-columns: 98px minmax(0,1fr) !important;
  gap: 12px !important;
  align-items: center !important;
  min-width: 0 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-row > span,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-info-row > span {
  font-size: 10px !important;
  letter-spacing: .10em !important;
  text-transform: uppercase !important;
  color: #64748b !important;
  font-weight: 950 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-service-picker {
  position: relative !important;
  min-width: 0 !important;
  z-index: 30 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-service-picker.is-open {
  z-index: 180 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-service-trigger {
  width: 100% !important;
  min-height: 42px !important;
  border: 1px solid #dfeaf2 !important;
  background: #ffffff !important;
  border-radius: 14px !important;
  padding: 7px 8px 7px 12px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 10px !important;
  cursor: pointer !important;
  text-align: left !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-service-trigger strong {
  display: block !important;
  min-width: 0 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  color: #0f172a !important;
  font-size: 14px !important;
  font-weight: 950 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-service-trigger em {
  flex: 0 0 auto !important;
  border: 1px solid #b8efe6 !important;
  background: #ffffff !important;
  color: #00675f !important;
  border-radius: 999px !important;
  padding: 7px 11px !important;
  font-size: 12px !important;
  font-weight: 950 !important;
  font-style: normal !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-duration-control {
  width: max-content !important;
  min-width: 104px !important;
  height: 36px !important;
  border: 1px solid #b8efe6 !important;
  background: #ffffff !important;
  border-radius: 999px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 6px !important;
  padding: 0 12px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-duration-control input {
  border: 0 !important;
  outline: 0 !important;
  background: transparent !important;
  width: 50px !important;
  text-align: right !important;
  color: #0f172a !important;
  font-size: 14px !important;
  font-weight: 950 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-duration-control em {
  color: #00675f !important;
  font-size: 12px !important;
  font-weight: 950 !important;
  font-style: normal !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-date-block {
  display: grid !important;
  grid-template-columns: minmax(0,1fr) auto !important;
  align-items: center !important;
  gap: 14px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-date-rows {
  display: grid !important;
  gap: 8px !important;
  min-width: 0 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-info-row strong {
  min-width: 0 !important;
  color: #0f172a !important;
  font-size: 14px !important;
  font-weight: 950 !important;
  line-height: 1.2 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-mode-mini-pill {
  display: inline-flex !important;
  align-items: center !important;
  width: max-content !important;
  min-height: 26px !important;
  border-radius: 999px !important;
  border: 1px solid #dbe4ef !important;
  background: #f8fafc !important;
  color: #334155 !important;
  padding: 0 10px !important;
  font-size: 11px !important;
  font-weight: 950 !important;
  font-style: normal !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-date-change {
  min-height: 40px !important;
  border-radius: 999px !important;
  border: 1px solid #b8efe6 !important;
  background: #ffffff !important;
  color: #00675f !important;
  font-size: 12px !important;
  font-weight: 950 !important;
  padding: 0 16px !important;
  white-space: nowrap !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-service-menu {
  position: absolute !important;
  top: calc(100% + 8px) !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 9999 !important;
  max-height: 300px !important;
  overflow: hidden !important;
  border-radius: 18px !important;
  box-shadow: 0 20px 54px rgba(15,23,42,.18) !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-duration-box,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-detail-row,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-date-grid,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-date-title-line,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-unified-date-change {
  display: none !important;
}
@media (max-width: 980px) {
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-row,
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-info-row,
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-date-block {
    grid-template-columns: 1fr !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-head {
    align-items: flex-start !important;
    flex-direction: column !important;
  }
}


/* LOOP WORKSPACE V4.4 — raise/compact service-date bubble */
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section {
  padding: 14px 18px 16px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-head-compact {
  min-height: 30px !important;
  margin-bottom: 2px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-step-only {
  min-height: 28px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-step-only strong {
  display: none !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-rows {
  margin-top: 2px !important;
  gap: 6px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-row,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-info-row {
  grid-template-columns: 94px minmax(0,1fr) !important;
  gap: 10px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-service-trigger {
  min-height: 38px !important;
  border-radius: 13px !important;
  padding: 6px 7px 6px 10px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-service-trigger strong {
  font-size: 13px !important;
  letter-spacing: -.01em !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-service-trigger em {
  padding: 6px 10px !important;
  font-size: 11px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-duration-control {
  height: 32px !important;
  min-width: 94px !important;
  padding: 0 10px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-duration-control input {
  width: 42px !important;
  font-size: 13px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-inline-duration-control em {
  font-size: 11px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-divider {
  margin: 8px 0 10px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-date-block {
  align-items: start !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-date-rows {
  gap: 6px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-info-row strong {
  font-size: 13px !important;
  font-weight: 900 !important;
  line-height: 1.18 !important;
  letter-spacing: -.01em !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-info-row > span,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-row > span {
  font-size: 9.5px !important;
  letter-spacing: .095em !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-mode-mini-pill {
  min-height: 22px !important;
  padding: 0 8px !important;
  font-size: 10.5px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-date-change {
  min-height: 36px !important;
  padding: 0 14px !important;
  font-size: 11.5px !important;
  align-self: center !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-availability-mini-pill {
  min-height: 24px !important;
  padding: 0 9px !important;
  font-size: 10.5px !important;
}


/* LOOP WORKSPACE V4.5 — ordered compact service/date card */
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section {
  padding: 14px 20px 16px !important;
  overflow: visible !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-ordered-shell {
  display: grid !important;
  gap: 8px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-rows {
  display: grid !important;
  gap: 6px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-row,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-info-row {
  display: grid !important;
  grid-template-columns: 30px 88px minmax(0,1fr) !important;
  align-items: center !important;
  column-gap: 10px !important;
  min-width: 0 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-label {
  color: #64748b !important;
  font-size: 10px !important;
  font-weight: 950 !important;
  letter-spacing: .105em !important;
  text-transform: uppercase !important;
  white-space: nowrap !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-step-spacer {
  width: 26px !important;
  height: 1px !important;
  display: block !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-picker {
  position: relative !important;
  min-width: 0 !important;
  z-index: 40 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-picker.is-open {
  z-index: 220 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-trigger {
  width: 100% !important;
  min-height: 36px !important;
  border: 1px solid #dfeaf2 !important;
  background: #fff !important;
  border-radius: 13px !important;
  padding: 5px 7px 5px 11px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 8px !important;
  cursor: pointer !important;
  text-align: left !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-trigger strong {
  min-width: 0 !important;
  color: #0f172a !important;
  font-size: 13px !important;
  font-weight: 900 !important;
  line-height: 1.1 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-trigger em {
  flex: 0 0 auto !important;
  border: 1px solid #b8efe6 !important;
  background: #fff !important;
  color: #00675f !important;
  border-radius: 999px !important;
  padding: 6px 10px !important;
  font-size: 11px !important;
  font-weight: 950 !important;
  font-style: normal !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-duration-control {
  justify-self: start !important;
  width: max-content !important;
  min-width: 94px !important;
  height: 32px !important;
  border: 1px solid #b8efe6 !important;
  background: #fff !important;
  border-radius: 999px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 6px !important;
  padding: 0 10px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-duration-control input {
  border: 0 !important;
  outline: 0 !important;
  background: transparent !important;
  width: 42px !important;
  text-align: right !important;
  color: #0f172a !important;
  font-size: 13px !important;
  font-weight: 950 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-duration-control em {
  color: #00675f !important;
  font-size: 11px !important;
  font-weight: 950 !important;
  font-style: normal !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-divider {
  height: 1px !important;
  margin: 7px 0 6px !important;
  background: linear-gradient(90deg, transparent, #dfeaf2 10%, #dfeaf2 92%, transparent) !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block {
  position: relative !important;
  display: grid !important;
  gap: 4px !important;
  min-width: 0 !important;
  padding-right: 118px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-info-row strong {
  min-width: 0 !important;
  color: #0f172a !important;
  font-size: 13px !important;
  font-weight: 900 !important;
  line-height: 1.14 !important;
  letter-spacing: -.01em !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-mode-mini-pill {
  display: inline-flex !important;
  align-items: center !important;
  justify-self: start !important;
  width: max-content !important;
  min-height: 22px !important;
  border-radius: 999px !important;
  border: 1px solid #dbe4ef !important;
  background: #f8fafc !important;
  color: #334155 !important;
  padding: 0 8px !important;
  font-size: 10.5px !important;
  font-weight: 950 !important;
  font-style: normal !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-availability {
  position: absolute !important;
  top: 0 !important;
  right: 0 !important;
  min-height: 24px !important;
  padding: 0 9px !important;
  font-size: 10.5px !important;
  white-space: nowrap !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-change {
  position: absolute !important;
  right: 0 !important;
  top: 34px !important;
  min-height: 36px !important;
  border-radius: 999px !important;
  border: 1px solid #b8efe6 !important;
  background: #fff !important;
  color: #00675f !important;
  font-size: 11.5px !important;
  font-weight: 950 !important;
  padding: 0 14px !important;
  white-space: nowrap !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-menu {
  position: absolute !important;
  top: calc(100% + 8px) !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 9999 !important;
  max-height: 300px !important;
  overflow: hidden !important;
  border-radius: 18px !important;
  box-shadow: 0 20px 54px rgba(15,23,42,.18) !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-head,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-rows,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-simple-date-block,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-detail-row,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-date-grid,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-date-title-line {
  display: none !important;
}
@media (max-width: 980px) {
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-row,
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-info-row {
    grid-template-columns: 28px 82px minmax(0,1fr) !important;
    column-gap: 8px !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block {
    padding-right: 0 !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-availability,
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-change {
    position: static !important;
    justify-self: start !important;
    margin-top: 6px !important;
  }
}


/* LOOP WORKSPACE V4.6 — balanced equal cards + calmer typography */
.booking-pro-modal.loop-reservation-workspace-v1 .loop-create-main-grid {
  align-items: stretch !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section {
  min-height: 232px !important;
  height: 100% !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section {
  display: flex !important;
  flex-direction: column !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section .loop-customer-body,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section .loop-customer-minimal-body {
  flex: 1 1 auto !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section {
  padding: 16px 20px 18px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-ordered-shell {
  height: 100% !important;
  align-content: start !important;
  gap: 9px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-row,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-info-row {
  grid-template-columns: 30px 96px minmax(0,1fr) !important;
  column-gap: 10px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-label {
  color: #53637a !important;
  font-size: 10px !important;
  font-weight: 950 !important;
  letter-spacing: .115em !important;
  text-transform: uppercase !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-trigger {
  min-height: 35px !important;
  border-radius: 13px !important;
  background: #ffffff !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-trigger strong,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-info-row strong {
  color: #111827 !important;
  font-size: 12.75px !important;
  font-weight: 760 !important;
  line-height: 1.18 !important;
  letter-spacing: -.005em !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-trigger em {
  font-size: 10.75px !important;
  font-weight: 900 !important;
  padding: 5px 9px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-duration-control {
  height: 31px !important;
  min-width: 90px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-duration-control input {
  font-size: 12.75px !important;
  font-weight: 760 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-duration-control em {
  font-size: 10.75px !important;
  font-weight: 900 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-divider {
  margin: 8px 0 7px !important;
  opacity: .82 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block {
  gap: 5px !important;
  padding-right: 112px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-mode-mini-pill {
  min-height: 21px !important;
  padding: 0 8px !important;
  font-size: 10.25px !important;
  font-weight: 850 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-availability {
  min-height: 23px !important;
  padding: 0 9px !important;
  font-size: 10.25px !important;
  font-weight: 900 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-change {
  top: 35px !important;
  min-height: 34px !important;
  padding: 0 13px !important;
  font-size: 11px !important;
  font-weight: 900 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section .loop-workspace-step,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-workspace-step {
  width: 26px !important;
  height: 26px !important;
  min-width: 26px !important;
  font-size: 11px !important;
}
@media (min-width: 981px) {
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-create-main-grid {
    grid-auto-rows: minmax(232px, auto) !important;
  }
}
@media (max-width: 980px) {
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section,
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section {
    min-height: auto !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block {
    padding-right: 0 !important;
  }
}


/* LOOP WORKSPACE V4.7 — cleaner date/time worker modal */
.loop-datetime-modal-grid{
  padding:14px 18px 0;
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:14px;
  align-items:start;
}
.loop-datetime-modal-grid .booking-pro-field:nth-child(3){grid-column:1 / -1;}
.loop-date-field-modern,
.loop-time-format-field-modern,
.loop-worker-field-modern{
  gap:8px!important;
}
.loop-datetime-date-row{
  display:grid!important;
  grid-template-columns:84px minmax(0,1fr) 92px!important;
  gap:8px!important;
  align-items:center!important;
}
.loop-date-mini-select,
.loop-worker-mini-select{
  width:100%!important;
}
.loop-date-mini-select .loop-mini-select-trigger,
.loop-worker-mini-select .loop-mini-select-trigger{
  min-height:42px!important;
  height:42px!important;
  border-radius:13px!important;
  background:#fff!important;
  border:1px solid #dbe7ef!important;
  box-shadow:none!important;
  padding:0 36px 0 12px!important;
  font-size:13px!important;
  font-weight:900!important;
}
.loop-date-mini-select .loop-mini-select-trigger span,
.loop-worker-mini-select .loop-mini-select-trigger span{
  color:#0f172a!important;
  font-size:13px!important;
  font-weight:900!important;
  white-space:nowrap!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
}
.loop-date-mini-select .loop-mini-select-trigger i,
.loop-worker-mini-select .loop-mini-select-trigger i{
  right:12px!important;
  color:#00675f!important;
  font-size:14px!important;
}
.loop-date-mini-select .loop-mini-select-menu,
.loop-worker-mini-select .loop-mini-select-menu{
  min-width:100%!important;
  right:0!important;
  border-radius:16px!important;
}
.loop-time-format-field-modern .loop-time-field-top{
  display:flex!important;
  align-items:flex-start!important;
  justify-content:space-between!important;
  gap:10px!important;
  margin-bottom:4px!important;
}
.loop-datetime-availability-inline{
  display:inline-flex!important;
  align-items:center!important;
  flex-wrap:wrap!important;
  justify-content:flex-end!important;
  gap:7px!important;
  min-width:0!important;
  padding-top:1px!important;
}
.loop-datetime-availability-inline i{
  color:#0f766e!important;
  font-size:9px!important;
  line-height:1!important;
}
.loop-datetime-availability-inline strong{
  color:#0f766e!important;
  font-size:11px!important;
  font-weight:900!important;
}
.loop-datetime-availability-inline small{
  color:#64748b!important;
  font-size:10.5px!important;
  font-weight:750!important;
}
.loop-datetime-availability-link{
  border:0!important;
  background:transparent!important;
  color:#00675f!important;
  font-size:10.75px!important;
  font-weight:900!important;
  padding:0!important;
  display:inline-flex!important;
  align-items:center!important;
  gap:6px!important;
  cursor:pointer!important;
}
.loop-datetime-availability-link em{
  min-width:18px!important;
  height:18px!important;
  border-radius:999px!important;
  background:#f0fdfa!important;
  border:1px solid #ccfbf1!important;
  color:#00675f!important;
  display:grid!important;
  place-items:center!important;
  font-style:normal!important;
  font-size:10px!important;
  font-weight:950!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control{
  min-height:42px!important;
  height:42px!important;
  grid-template-columns:minmax(0,1fr) 14px minmax(0,1fr) 72px!important;
  gap:6px!important;
  padding:4px 6px!important;
  border-radius:13px!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control span{
  font-size:13px!important;
  text-align:center!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control select{
  height:32px!important;
  border-radius:10px!important;
  font-size:13px!important;
  font-weight:900!important;
}
.loop-time-format-field-modern > small{
  margin-top:6px!important;
  font-size:10.5px!important;
}
.loop-datetime-modal-mode-minimal{
  margin:12px 18px 0!important;
  padding:12px 13px!important;
  border-radius:14px!important;
  display:grid!important;
  grid-template-columns:72px minmax(0,1fr)!important;
  gap:10px!important;
  align-items:start!important;
}
.loop-datetime-modal-mode-minimal > span{
  padding-top:2px!important;
  text-transform:uppercase!important;
  letter-spacing:.09em!important;
  font-size:10px!important;
  font-weight:950!important;
}
.loop-datetime-modal-mode-minimal > div{display:grid!important;gap:3px!important;}
.loop-datetime-modal-mode-minimal strong{font-size:13px!important;font-weight:900!important;}
.loop-datetime-modal-mode-minimal small{font-size:11px!important;line-height:1.35!important;}
@media(max-width:760px){
  .loop-datetime-modal-grid{grid-template-columns:1fr!important;gap:12px!important;}
  .loop-time-format-field-modern .loop-time-field-top{display:grid!important;gap:6px!important;}
  .loop-datetime-availability-inline{justify-content:flex-start!important;}
  .loop-datetime-date-row{grid-template-columns:74px minmax(0,1fr) 82px!important;}
  .loop-datetime-modal-mode-minimal{grid-template-columns:1fr!important;gap:6px!important;}
}


/* LOOP WORKSPACE V4.8 — date + time one-line modal layout */
.loop-datetime-modal-grid{
  padding:14px 18px 0!important;
  display:grid!important;
  grid-template-columns:minmax(0,1fr) minmax(0,1fr)!important;
  gap:14px!important;
  align-items:start!important;
}
.loop-date-field-modern,
.loop-time-format-field-modern{
  min-width:0!important;
}
.loop-date-field-modern > span,
.loop-time-format-field-modern .loop-time-field-top > span,
.loop-worker-field-modern > span{
  margin-bottom:7px!important;
}
.loop-datetime-date-row{
  display:grid!important;
  grid-template-columns:64px minmax(98px,1fr) 78px!important;
  gap:8px!important;
  align-items:center!important;
}
.loop-date-mini-select .loop-mini-select-trigger{
  min-height:42px!important;
  height:42px!important;
  padding:0 24px 0 12px!important;
  border-radius:13px!important;
}
.loop-date-mini-select .loop-mini-select-trigger span{
  font-size:13px!important;
  font-weight:900!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
}
.loop-date-mini-select.is-month .loop-mini-select-trigger{
  padding-left:11px!important;
  padding-right:22px!important;
}
.loop-date-mini-select.is-month .loop-mini-select-trigger span{
  text-transform:none!important;
}
.loop-date-mini-select .loop-mini-select-trigger i{
  right:9px!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control{
  min-height:42px!important;
  height:42px!important;
  border-radius:13px!important;
  grid-template-columns:minmax(0,1fr) 12px minmax(0,1fr) 64px!important;
  gap:5px!important;
  padding:4px 6px!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control select{
  height:32px!important;
  border-radius:10px!important;
  font-size:13px!important;
  font-weight:900!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control span{
  font-size:13px!important;
  font-weight:900!important;
}
.loop-time-format-helper{
  display:none!important;
}
.loop-time-field-top{
  display:block!important;
  margin:0!important;
}
.loop-datetime-availability-below{
  margin-top:8px!important;
  min-height:36px!important;
  border:1px solid #d6f5ec!important;
  background:#f3fffb!important;
  border-radius:13px!important;
  padding:7px 8px!important;
  display:grid!important;
  grid-template-columns:22px minmax(0,1fr) auto!important;
  gap:8px!important;
  align-items:center!important;
}
.loop-datetime-availability-below > span{
  width:22px!important;
  height:22px!important;
  border-radius:999px!important;
  background:#0f766e!important;
  color:#fff!important;
  display:grid!important;
  place-items:center!important;
  font-size:11px!important;
  font-weight:950!important;
}
.loop-datetime-availability-below div{
  display:grid!important;
  gap:1px!important;
  min-width:0!important;
}
.loop-datetime-availability-below strong{
  color:#0f766e!important;
  font-size:11.5px!important;
  line-height:1.1!important;
  font-weight:950!important;
}
.loop-datetime-availability-below small{
  color:#64748b!important;
  font-size:10.25px!important;
  line-height:1.15!important;
  font-weight:750!important;
  white-space:nowrap!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
}
.loop-datetime-availability-below .loop-datetime-availability-link{
  border:0!important;
  background:transparent!important;
  color:#00675f!important;
  font-size:10.75px!important;
  font-weight:950!important;
  padding:0!important;
  display:inline-flex!important;
  align-items:center!important;
  gap:6px!important;
  cursor:pointer!important;
  white-space:nowrap!important;
}
.loop-datetime-availability-below .loop-datetime-availability-link em{
  min-width:18px!important;
  height:18px!important;
  border-radius:999px!important;
  background:#fff!important;
  border:1px solid #ccfbf1!important;
  color:#00675f!important;
  display:grid!important;
  place-items:center!important;
  font-size:10px!important;
  font-style:normal!important;
  font-weight:950!important;
}
.loop-datetime-availability-inline{
  display:none!important;
}
.loop-worker-field-modern{
  margin-top:2px!important;
}
.loop-worker-mini-select .loop-mini-select-trigger{
  min-height:42px!important;
  height:42px!important;
  border-radius:13px!important;
}
.loop-datetime-modal-mode-minimal{
  margin-top:12px!important;
}
@media(max-width:760px){
  .loop-datetime-modal-grid{
    grid-template-columns:1fr!important;
    gap:12px!important;
  }
  .loop-datetime-availability-below{
    grid-template-columns:22px minmax(0,1fr)!important;
  }
  .loop-datetime-availability-below .loop-datetime-availability-link{
    grid-column:2!important;
    justify-self:start!important;
    margin-top:2px!important;
  }
}


/* LOOP WORKSPACE V5.0 — premium compact calendar + calmer time controls */
.loop-datetime-first-row{
  grid-template-columns:minmax(0,1fr) minmax(0,1fr)!important;
  gap:14px!important;
  align-items:start!important;
}
.loop-date-calendar-picker{
  position:relative!important;
  min-width:0!important;
  z-index:40!important;
}
.loop-date-calendar-picker:has(.loop-booking-date-calendar){
  z-index:999999!important;
}
.loop-date-calendar-trigger{
  width:100%!important;
  min-height:42px!important;
  height:42px!important;
  border:1px solid #dbe7ef!important;
  background:#ffffff!important;
  border-radius:13px!important;
  display:grid!important;
  grid-template-columns:54px minmax(0,1fr) 70px 16px!important;
  align-items:center!important;
  gap:6px!important;
  padding:0 10px!important;
  color:#0f172a!important;
  cursor:pointer!important;
  text-align:left!important;
  box-shadow:none!important;
}
.loop-date-calendar-trigger span,
.loop-date-calendar-trigger strong,
.loop-date-calendar-trigger em{
  min-width:0!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
  line-height:1!important;
}
.loop-date-calendar-trigger span{
  font-size:13px!important;
  font-weight:950!important;
  text-align:center!important;
}
.loop-date-calendar-trigger strong{
  font-size:13px!important;
  font-weight:900!important;
  text-transform:capitalize!important;
}
.loop-date-calendar-trigger em{
  font-style:normal!important;
  font-size:12px!important;
  font-weight:850!important;
  color:#334155!important;
  text-align:right!important;
}
.loop-date-calendar-trigger i,
.loop-time-mini-select .loop-mini-select-trigger i,
.loop-worker-mini-select .loop-mini-select-trigger i{
  font-size:0!important;
  width:7px!important;
  height:7px!important;
  border-right:1.7px solid #00675f!important;
  border-bottom:1.7px solid #00675f!important;
  transform:rotate(45deg)!important;
  display:block!important;
  justify-self:center!important;
  margin-top:-3px!important;
}
.loop-booking-date-calendar{
  position:absolute!important;
  top:calc(100% + 8px)!important;
  left:0!important;
  width:min(292px,calc(100vw - 48px))!important;
  border:1px solid #dbe7ef!important;
  border-radius:18px!important;
  background:#ffffff!important;
  box-shadow:0 24px 64px rgba(15,23,42,.18)!important;
  padding:12px!important;
  z-index:999999!important;
}
.loop-booking-date-calendar-head{
  display:grid!important;
  grid-template-columns:34px minmax(0,1fr) 34px!important;
  align-items:center!important;
  gap:8px!important;
  margin-bottom:10px!important;
}
.loop-booking-date-calendar-head strong{
  text-align:center!important;
  text-transform:capitalize!important;
  color:#0f172a!important;
  font-size:13px!important;
  font-weight:950!important;
  white-space:nowrap!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
}
.loop-booking-date-calendar-head button{
  width:34px!important;
  height:34px!important;
  border-radius:999px!important;
  border:1px solid #dbe7ef!important;
  background:#ffffff!important;
  color:#00675f!important;
  font-size:20px!important;
  font-weight:900!important;
  cursor:pointer!important;
}
.loop-booking-date-calendar-head button:hover{
  background:#f0fdfa!important;
  border-color:#b8efe6!important;
}
.loop-booking-date-weekdays,
.loop-booking-date-days{
  display:grid!important;
  grid-template-columns:repeat(7,1fr)!important;
  gap:4px!important;
}
.loop-booking-date-weekdays{
  margin-bottom:5px!important;
}
.loop-booking-date-weekdays span{
  display:grid!important;
  place-items:center!important;
  height:24px!important;
  color:#64748b!important;
  font-size:10px!important;
  font-weight:950!important;
}
.loop-booking-date-days button,
.loop-booking-date-days span{
  height:34px!important;
  border-radius:11px!important;
}
.loop-booking-date-days button{
  border:0!important;
  background:transparent!important;
  color:#0f172a!important;
  font-size:12.5px!important;
  font-weight:850!important;
  cursor:pointer!important;
}
.loop-booking-date-days button:hover{
  background:#f0fdfa!important;
  color:#00675f!important;
}
.loop-booking-date-days button.is-selected{
  background:#00675f!important;
  color:#ffffff!important;
  font-weight:950!important;
  box-shadow:0 10px 22px rgba(0,103,95,.16)!important;
}
.loop-datetime-date-row{
  display:none!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern{
  width:max-content!important;
  min-width:222px!important;
  max-width:100%!important;
  height:42px!important;
  grid-template-columns:54px 12px 54px 62px!important;
  gap:5px!important;
  justify-content:start!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select-trigger{
  padding:0 16px 0 8px!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select-trigger span{
  text-align:center!important;
  font-size:13px!important;
  font-weight:900!important;
}
.loop-time-mini-select .loop-mini-select-menu{
  min-width:74px!important;
}
.loop-time-mini-select.is-period .loop-mini-select-menu{
  min-width:84px!important;
}
.loop-datetime-availability-below{
  width:min(100%,286px)!important;
}
@media(max-width:760px){
  .loop-date-calendar-trigger{
    grid-template-columns:48px minmax(0,1fr) 62px 14px!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern{
    width:100%!important;
    grid-template-columns:54px 12px 54px 62px!important;
  }
  .loop-booking-date-calendar{
    width:min(292px,calc(100vw - 36px))!important;
  }
}


/* LOOP WORKSPACE V5.1 — elegant date trigger + non-cutting time fields */
.loop-date-calendar-trigger{
  grid-template-columns:minmax(0,1fr) auto 16px!important;
  gap:9px!important;
  padding:0 12px 0 14px!important;
}
.loop-date-calendar-trigger .loop-date-trigger-main{
  display:block!important;
  min-width:0!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
  color:#0f172a!important;
  font-size:13px!important;
  font-weight:900!important;
  line-height:1!important;
  letter-spacing:-.01em!important;
  text-transform:capitalize!important;
}
.loop-date-calendar-trigger .loop-date-trigger-year{
  display:block!important;
  font-style:normal!important;
  color:#334155!important;
  font-size:12px!important;
  font-weight:850!important;
  line-height:1!important;
  white-space:nowrap!important;
}
.loop-date-calendar-trigger > span:not(.loop-date-trigger-main),
.loop-date-calendar-trigger > strong:not(.loop-date-trigger-main),
.loop-date-calendar-trigger > em:not(.loop-date-trigger-year){
  min-width:0!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern{
  width:min(100%,252px)!important;
  min-width:238px!important;
  height:42px!important;
  grid-template-columns:52px 10px 66px 72px!important;
  gap:5px!important;
  justify-content:start!important;
  overflow:visible!important;
  background:#fff!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select{
  min-width:0!important;
  width:100%!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select-trigger{
  min-height:40px!important;
  height:40px!important;
  width:100%!important;
  padding:0 8px!important;
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  background:#fff!important;
  border-radius:11px!important;
  overflow:visible!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select-trigger:hover{
  background:#f8fafc!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select-trigger span{
  display:block!important;
  width:auto!important;
  max-width:none!important;
  min-width:0!important;
  overflow:visible!important;
  text-overflow:clip!important;
  white-space:nowrap!important;
  text-align:center!important;
  color:#0f172a!important;
  font-size:13px!important;
  font-weight:900!important;
  line-height:1!important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select-trigger i{
  display:none!important;
}
.loop-time12-separator{
  width:10px!important;
  color:#64748b!important;
  font-size:13px!important;
  font-weight:950!important;
  text-align:center!important;
}
.loop-time-mini-select.is-hour .loop-mini-select-menu,
.loop-time-mini-select.is-minute .loop-mini-select-menu{
  min-width:82px!important;
}
.loop-time-mini-select.is-period .loop-mini-select-menu{
  min-width:88px!important;
}
.loop-time-format-field-modern > small,
.loop-time-format-field-modern .loop-time-format-helper,
.booking-pro-modal.loop-create-pro-modal .loop-time-format-field-modern > small{
  display:none!important;
}
.loop-datetime-availability-below{
  margin-top:8px!important;
}
@media(max-width:760px){
  .loop-date-calendar-trigger{
    grid-template-columns:minmax(0,1fr) auto 14px!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern{
    width:100%!important;
    min-width:0!important;
    grid-template-columns:minmax(46px,.78fr) 10px minmax(54px,.95fr) minmax(62px,1fr)!important;
  }
}


/* LOOP WORKSPACE V5.2 — fixed calendar popover + single-piece time capsule */
.booking-pro-modal.loop-create-pro-modal .loop-datetime-modal:has(.loop-booking-date-calendar),
.booking-pro-modal.loop-create-pro-modal .loop-datetime-modal-backdrop:has(.loop-booking-date-calendar) {
  overflow: visible !important;
}
.loop-booking-date-calendar{
  position: fixed !important;
  top: clamp(176px, 29vh, 224px) !important;
  left: max(18px, calc(50vw - 260px)) !important;
  width: 292px !important;
  max-height: min(340px, calc(100vh - 210px)) !important;
  overflow-y: auto !important;
  z-index: 2147483647 !important;
  transform: none !important;
}
.loop-date-calendar-picker{
  position: relative !important;
  z-index: 50 !important;
}
.loop-date-calendar-picker:has(.loop-booking-date-calendar){
  z-index: 2147483647 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern{
  width: max-content !important;
  min-width: 0 !important;
  max-width: 100% !important;
  height: 42px !important;
  min-height: 42px !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  gap: 4px !important;
  padding: 0 7px !important;
  border: 1px solid #dbe7ef !important;
  border-radius: 14px !important;
  background: #ffffff !important;
  overflow: visible !important;
  box-shadow: none !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select{
  width: auto !important;
  min-width: 0 !important;
  height: 100% !important;
  flex: 0 0 auto !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select-trigger{
  width: auto !important;
  min-width: 0 !important;
  height: 38px !important;
  min-height: 38px !important;
  border: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
  border-radius: 11px !important;
  padding: 0 !important;
  display: grid !important;
  place-items: center !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select-trigger:hover{
  background: #f8fafc !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select-trigger span{
  display: block !important;
  width: auto !important;
  max-width: none !important;
  min-width: 0 !important;
  overflow: visible !important;
  text-overflow: clip !important;
  white-space: nowrap !important;
  color: #0f172a !important;
  font-size: 13px !important;
  font-weight: 950 !important;
  line-height: 1 !important;
  text-align: center !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-time-mini-select.is-hour .loop-mini-select-trigger{
  width: 42px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-time-mini-select.is-minute .loop-mini-select-trigger{
  width: 50px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-time-mini-select.is-period .loop-mini-select-trigger{
  width: 56px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select-trigger i{
  display: none !important;
}
.loop-time12-separator{
  width: 9px !important;
  height: 38px !important;
  display: grid !important;
  place-items: center !important;
  color: #64748b !important;
  font-size: 13px !important;
  font-weight: 950 !important;
  flex: 0 0 9px !important;
}
.loop-time-mini-select .loop-mini-select-menu{
  top: calc(100% + 7px) !important;
  border-radius: 14px !important;
  box-shadow: 0 18px 44px rgba(15,23,42,.16) !important;
}
.loop-time-mini-select.is-hour .loop-mini-select-menu,
.loop-time-mini-select.is-minute .loop-mini-select-menu{
  min-width: 76px !important;
}
.loop-time-mini-select.is-period .loop-mini-select-menu{
  min-width: 86px !important;
}
@media(max-width:760px){
  .loop-booking-date-calendar{
    left: 18px !important;
    right: 18px !important;
    width: auto !important;
    top: 168px !important;
    max-height: min(330px, calc(100vh - 190px)) !important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern{
    width: max-content !important;
  }
}


/* LOOP WORKSPACE V5.3B — worker dropdown opens upward without breaking click */
.booking-pro-modal.loop-create-pro-modal .loop-datetime-modal,
.booking-pro-modal.loop-create-pro-modal .loop-datetime-modal-grid,
.booking-pro-modal.loop-create-pro-modal .loop-worker-field-modern,
.booking-pro-modal.loop-create-pro-modal .loop-worker-mini-select {
  overflow: visible !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-worker-field-modern {
  position: relative !important;
  z-index: 500000 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-worker-mini-select {
  position: relative !important;
  z-index: 500001 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-worker-mini-select.is-open {
  z-index: 2147483000 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-worker-mini-select .loop-mini-select-menu {
  position: absolute !important;
  top: auto !important;
  bottom: calc(100% + 8px) !important;
  left: 0 !important;
  right: 0 !important;
  min-width: 100% !important;
  max-width: 100% !important;
  max-height: 190px !important;
  overflow-y: auto !important;
  overscroll-behavior: contain !important;
  border: 1px solid #dbe7ef !important;
  border-radius: 16px !important;
  background: #ffffff !important;
  box-shadow: 0 22px 58px rgba(15,23,42,.18) !important;
  padding: 7px !important;
  z-index: 2147483000 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-worker-mini-select .loop-mini-select-menu button {
  min-height: 38px !important;
  border: 0 !important;
  border-radius: 11px !important;
  background: transparent !important;
  color: #0f172a !important;
  font-size: 13px !important;
  font-weight: 850 !important;
  text-align: left !important;
  padding: 0 11px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-worker-mini-select .loop-mini-select-menu button:hover {
  background: #f0fdfa !important;
  color: #00675f !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-worker-mini-select .loop-mini-select-menu button.is-selected {
  background: #e6fbf5 !important;
  color: #00675f !important;
}
@media(max-width:760px) {
  .booking-pro-modal.loop-create-pro-modal .loop-worker-mini-select .loop-mini-select-menu {
    bottom: auto !important;
    top: calc(100% + 8px) !important;
    max-height: 210px !important;
  }
}


/* LOOP WORKSPACE V5.4 — payment row white editable fields + clean chevrons + better tip input */
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-card {
  overflow: visible !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-main {
  overflow: visible !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-field {
  overflow: visible !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-field input,
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-field .loop-mini-select-trigger {
  width: 100% !important;
  height: 42px !important;
  min-height: 42px !important;
  border-radius: 13px !important;
  border: 1px solid #dbe7ef !important;
  background: #ffffff !important;
  color: #0f172a !important;
  box-shadow: none !important;
  font-size: 13px !important;
  font-weight: 900 !important;
  transition: border-color .14s ease, box-shadow .14s ease, background .14s ease !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-field input:hover,
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-field .loop-mini-select-trigger:hover {
  border-color: #b8d8d2 !important;
  background: #ffffff !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-field input:focus,
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-field .loop-mini-select.is-open .loop-mini-select-trigger {
  border-color: #66cfc2 !important;
  box-shadow: 0 0 0 3px rgba(0,103,95,.09) !important;
  background: #ffffff !important;
  outline: none !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-field input:disabled,
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-field .loop-mini-select.is-disabled .loop-mini-select-trigger {
  background: #f8fafc !important;
  color: #94a3b8 !important;
  cursor: not-allowed !important;
}

/* Payment dropdown chevron: replace the text glyph with a clean Loop chevron */
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-field .loop-mini-select-trigger {
  position: relative !important;
  padding: 0 34px 0 12px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-field .loop-mini-select-trigger i {
  position: absolute !important;
  right: 13px !important;
  top: 50% !important;
  width: 7px !important;
  height: 7px !important;
  margin-top: -5px !important;
  border-right: 1.8px solid #00675f !important;
  border-bottom: 1.8px solid #00675f !important;
  transform: rotate(45deg) !important;
  font-size: 0 !important;
  line-height: 0 !important;
  color: transparent !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-field .loop-mini-select.is-open .loop-mini-select-trigger i {
  margin-top: -1px !important;
  transform: rotate(225deg) !important;
}

/* Tip amount: no more tiny circle. Make it a real editable money field. */
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-tip-payment-field .loop-tip-payment-inline {
  position: relative !important;
  width: 100% !important;
  min-width: 0 !important;
  display: grid !important;
  grid-template-columns: minmax(126px, 1fr) !important;
  gap: 8px !important;
  align-items: center !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-tip-payment-field .loop-tip-payment-inline:has(.loop-mini-select) {
  grid-template-columns: minmax(112px, 1fr) minmax(106px, .58fr) !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-tip-payment-field .loop-tip-payment-inline::before {
  content: "$" !important;
  position: absolute !important;
  left: 12px !important;
  top: 21px !important;
  transform: translateY(-50%) !important;
  z-index: 2 !important;
  color: #64748b !important;
  font-size: 12px !important;
  font-weight: 950 !important;
  pointer-events: none !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-tip-payment-field .loop-tip-payment-inline > input {
  width: 100% !important;
  min-width: 0 !important;
  max-width: none !important;
  border-radius: 13px !important;
  padding-left: 27px !important;
  padding-right: 10px !important;
  text-align: left !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-tip-payment-field .loop-tip-payment-inline > input::placeholder {
  color: #94a3b8 !important;
  opacity: 1 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-tip-payment-field .loop-mini-select-trigger {
  height: 42px !important;
  min-height: 42px !important;
  border-radius: 13px !important;
  background: #ffffff !important;
  font-size: 12px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-field .loop-mini-select-menu {
  border-radius: 16px !important;
  border: 1px solid #dbe7ef !important;
  box-shadow: 0 22px 55px rgba(15,23,42,.18) !important;
}
@media(max-width: 980px) {
  .booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-main {
    grid-template-columns: 1fr 1fr !important;
  }
}
@media(max-width: 620px) {
  .booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-slim-main,
  .booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-tip-payment-field .loop-tip-payment-inline,
  .booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-tip-payment-field .loop-tip-payment-inline:has(.loop-mini-select) {
    grid-template-columns: 1fr !important;
  }
}


/* LOOP WORKSPACE V5.5 — money charged field + single simplified commission popup */
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-charged-payment-field .loop-money-payment-inline {
  position: relative !important;
  width: 100% !important;
  min-width: 0 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-charged-payment-field .loop-money-payment-inline::before {
  content: "$" !important;
  position: absolute !important;
  left: 12px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  z-index: 2 !important;
  color: #64748b !important;
  font-size: 12px !important;
  font-weight: 950 !important;
  pointer-events: none !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-charged-payment-field .loop-money-payment-inline > input {
  padding-left: 27px !important;
  text-align: left !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-one-popup-action {
  min-width: 104px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-simple-section .loop-payment-advanced-inline {
  display: none !important;
}

/* Simplified commission modal */
.loop-commission-editor-modal-simple {
  overflow: visible !important;
}
.loop-commission-editor-modal-simple .loop-settings-modal-head {
  padding-bottom: 14px !important;
}
.loop-commission-editor-modal-simple .loop-settings-modal-head span {
  max-width: 340px !important;
}
.loop-commission-simple-body {
  padding: 18px !important;
  overflow: visible !important;
}
.loop-commission-simple-grid {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) minmax(150px, .72fr) !important;
  gap: 12px !important;
  align-items: start !important;
  overflow: visible !important;
}
.loop-commission-simple-grid .booking-pro-field {
  min-width: 0 !important;
  gap: 7px !important;
}
.loop-commission-simple-grid .booking-pro-field > span {
  font-size: 10.5px !important;
  letter-spacing: .09em !important;
  text-transform: uppercase !important;
  color: #64748b !important;
  font-weight: 950 !important;
}
.loop-commission-simple-grid .loop-mini-select,
.loop-commission-simple-grid .loop-mini-select-trigger,
.loop-commission-simple-grid input,
.loop-commission-empty-control {
  width: 100% !important;
  min-height: 42px !important;
  height: 42px !important;
  border-radius: 13px !important;
}
.loop-commission-simple-grid .loop-mini-select {
  position: relative !important;
  z-index: 5 !important;
}
.loop-commission-simple-grid .loop-mini-select.is-open {
  z-index: 999999 !important;
}
.loop-commission-simple-grid .loop-mini-select-trigger,
.loop-commission-simple-grid input,
.loop-commission-empty-control {
  border: 1px solid #dbe7ef !important;
  background: #ffffff !important;
  color: #0f172a !important;
  box-shadow: none !important;
  font-size: 13px !important;
  font-weight: 900 !important;
}
.loop-commission-simple-grid input {
  padding: 0 12px !important;
  outline: none !important;
}
.loop-commission-simple-grid input:focus {
  border-color: #66cfc2 !important;
  box-shadow: 0 0 0 3px rgba(0,103,95,.09) !important;
}
.loop-commission-value-control {
  position: relative !important;
}
.loop-commission-value-field.is-money .loop-commission-value-control::before {
  content: "$" !important;
  position: absolute !important;
  left: 12px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  color: #64748b !important;
  font-size: 12px !important;
  font-weight: 950 !important;
  z-index: 2 !important;
  pointer-events: none !important;
}
.loop-commission-value-field.is-money input {
  padding-left: 27px !important;
}
.loop-commission-value-field.is-percent .loop-commission-value-control::after {
  content: "%" !important;
  position: absolute !important;
  right: 12px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  color: #64748b !important;
  font-size: 12px !important;
  font-weight: 950 !important;
  pointer-events: none !important;
}
.loop-commission-value-field.is-percent input {
  padding-right: 28px !important;
}
.loop-commission-empty-control {
  display: flex !important;
  align-items: center !important;
  padding: 0 12px !important;
  color: #94a3b8 !important;
  background: #f8fafc !important;
}
.loop-commission-simple-grid .loop-mini-select-menu {
  border-radius: 16px !important;
  border: 1px solid #dbe7ef !important;
  background: #ffffff !important;
  box-shadow: 0 22px 55px rgba(15,23,42,.18) !important;
  padding: 7px !important;
}
.loop-commission-simple-grid .loop-mini-select-menu button {
  min-height: 38px !important;
  border-radius: 11px !important;
  font-size: 13px !important;
  font-weight: 850 !important;
}
@media(max-width: 620px) {
  .loop-commission-simple-grid {
    grid-template-columns: 1fr !important;
  }
}


/* LOOP WORKSPACE V5.6 — side-by-side retail/notes + remove horizontal modal scroll */
.booking-pro-modal.loop-create-pro-modal,
.booking-pro-modal.loop-create-pro-modal .booking-pro-body,
.booking-pro-modal.loop-create-pro-modal .loop-create-pro-form,
.booking-pro-modal.loop-create-pro-modal .loop-create-main-grid,
.booking-pro-modal.loop-create-pro-modal .loop-create-section,
.booking-pro-modal.loop-create-pro-modal .booking-pro-section {
  max-width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}
.booking-pro-modal.loop-create-pro-modal,
.booking-pro-modal.loop-create-pro-modal .booking-pro-body,
.booking-pro-modal.loop-create-pro-modal .loop-create-pro-form {
  overflow-x: hidden !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-create-main-grid {
  width: 100% !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row {
  grid-column: 1 / -1 !important;
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
  gap: 12px !important;
  align-items: start !important;
  overflow: visible !important;
  box-sizing: border-box !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row .loop-clean-accordion {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  margin: 0 !important;
  overflow: visible !important;
  border-radius: 16px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row .loop-clean-accordion-row {
  min-height: 52px !important;
  padding: 10px 12px !important;
  gap: 10px !important;
  box-sizing: border-box !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row .loop-accordion-left {
  min-width: 0 !important;
  display: grid !important;
  grid-template-columns: auto minmax(0, auto) minmax(0, 1fr) !important;
  align-items: center !important;
  column-gap: 8px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row .loop-accordion-left strong {
  white-space: nowrap !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row .loop-accordion-left small {
  min-width: 0 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row .loop-clean-accordion-row > em {
  flex: 0 0 auto !important;
  white-space: nowrap !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row .loop-clean-accordion-panel {
  max-width: 100% !important;
  overflow-x: hidden !important;
  box-sizing: border-box !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row .loop-retail-search-box,
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row .loop-retail-results,
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row .loop-retail-selected-list,
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row .loop-notes-compact-field,
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row textarea {
  max-width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row .loop-retail-selected-row {
  max-width: 100% !important;
  min-width: 0 !important;
  overflow-x: hidden !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row .loop-retail-selected-row > * {
  min-width: 0 !important;
}
@media (max-width: 980px) {
  .booking-pro-modal.loop-create-pro-modal .loop-post-payment-actions-row {
    grid-template-columns: 1fr !important;
  }
}


/* LOOP WORKSPACE V5.7 — full-width bottom actions + no horizontal modal scroll + clean accordion chevron */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 {
  overflow-y: auto !important;
  overflow-x: hidden !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1::-webkit-scrollbar:horizontal {
  height: 0 !important;
  display: none !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-left,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-main-grid,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-accordion-stack {
  max-width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-left,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-main-grid {
  overflow-x: hidden !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-accordion-stack {
  grid-column: 1 / -1 !important;
  width: 100% !important;
  display: block !important;
  margin-top: 12px !important;
  overflow: visible !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-post-payment-actions-row {
  width: 100% !important;
  max-width: 100% !important;
  min-width: 0 !important;
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) !important;
  gap: 12px !important;
  align-items: stretch !important;
  overflow: visible !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-post-payment-actions-row .loop-clean-accordion {
  width: 100% !important;
  min-width: 0 !important;
  max-width: none !important;
  margin: 0 !important;
  border-radius: 16px !important;
  background: #ffffff !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-post-payment-actions-row .loop-clean-accordion-row {
  width: 100% !important;
  min-height: 54px !important;
  padding: 10px 13px !important;
  box-sizing: border-box !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-post-payment-actions-row .loop-accordion-left {
  min-width: 0 !important;
  display: grid !important;
  grid-template-columns: 20px auto minmax(0, 1fr) !important;
  align-items: center !important;
  gap: 8px !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-post-payment-actions-row .loop-accordion-left strong {
  white-space: nowrap !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-post-payment-actions-row .loop-accordion-left small {
  min-width: 0 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
}

/* Cleaner accordion chevron */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-post-payment-actions-row .loop-accordion-left i {
  width: 20px !important;
  height: 20px !important;
  flex: 0 0 20px !important;
  border-radius: 999px !important;
  background: #eefaf7 !important;
  border: 1px solid #d7f3ec !important;
  color: transparent !important;
  font-size: 0 !important;
  line-height: 0 !important;
  transform: none !important;
  position: relative !important;
  display: inline-grid !important;
  place-items: center !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-post-payment-actions-row .loop-accordion-left i::before {
  content: "" !important;
  width: 6px !important;
  height: 6px !important;
  border-right: 1.7px solid #00675f !important;
  border-bottom: 1.7px solid #00675f !important;
  transform: rotate(-45deg) !important;
  transition: transform 140ms ease !important;
  margin-left: -2px !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-post-payment-actions-row .loop-clean-accordion.is-open .loop-accordion-left i::before {
  transform: rotate(45deg) !important;
  margin-left: 0 !important;
  margin-top: -2px !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-post-payment-actions-row .loop-clean-accordion-row > em {
  flex: 0 0 auto !important;
  white-space: nowrap !important;
  margin-left: 8px !important;
}
@media (max-width: 980px) {
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-post-payment-actions-row {
    grid-template-columns: 1fr !important;
  }
}


/* LOOP WORKSPACE V5.8 — status dropdown simplified + state-aware icon */
.booking-pro-modal.loop-create-pro-modal .loop-status-ops-item::before {
  content: "•" !important;
  width: 26px !important;
  height: 26px !important;
  border-radius: 999px !important;
  font-size: 13px !important;
  font-weight: 950 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow: 0 8px 18px rgba(15,23,42,.045) !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-ops-item.is-status-pending::before {
  content: "!" !important;
  background: #fffbeb !important;
  border-color: #fde68a !important;
  color: #b45309 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-ops-item.is-status-confirmed::before {
  content: "✓" !important;
  background: #ecfdf5 !important;
  border-color: #bbf7d0 !important;
  color: #047857 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-ops-item.is-status-completed::before {
  content: "✓" !important;
  background: #eff6ff !important;
  border-color: #bfdbfe !important;
  color: #2563eb !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-ops-item.is-status-no-show::before,
.booking-pro-modal.loop-create-pro-modal .loop-status-ops-item.is-status-cancelled::before,
.booking-pro-modal.loop-create-pro-modal .loop-status-ops-item.is-status-canceled::before,
.booking-pro-modal.loop-create-pro-modal .loop-status-ops-item.is-status-deleted::before {
  content: "×" !important;
  background: #fef2f2 !important;
  border-color: #fecaca !important;
  color: #dc2626 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-ops-item.is-status-deleted::before {
  content: "−" !important;
}

/* Cleaner status dropdown rows */
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu {
  min-width: 220px !important;
  max-height: 272px !important;
  border-radius: 16px !important;
  border: 1px solid #dbe7ef !important;
  background: #ffffff !important;
  box-shadow: 0 22px 58px rgba(15,23,42,.18) !important;
  padding: 7px !important;
  overflow-y: auto !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-status-option {
  min-height: 38px !important;
  border-radius: 11px !important;
  padding: 0 11px 0 36px !important;
  position: relative !important;
  font-size: 13px !important;
  font-weight: 900 !important;
  color: #0f172a !important;
  background: transparent !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-status-option::before {
  content: "" !important;
  position: absolute !important;
  left: 11px !important;
  top: 50% !important;
  width: 14px !important;
  height: 14px !important;
  border-radius: 999px !important;
  transform: translateY(-50%) !important;
  border: 1px solid transparent !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-status-pending::before {
  background: #fef3c7 !important;
  border-color: #fde68a !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-status-confirmed::before {
  background: #bbf7d0 !important;
  border-color: #86efac !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-status-completed::before {
  background: #bfdbfe !important;
  border-color: #93c5fd !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-status-no-show::before,
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-status-cancelled::before,
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-status-deleted::before {
  background: #fecaca !important;
  border-color: #fca5a5 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-selected {
  background: #f8fafc !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-status-pending.is-selected {
  background: #fffbeb !important;
  color: #92400e !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-status-confirmed.is-selected {
  background: #ecfdf5 !important;
  color: #047857 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-status-completed.is-selected {
  background: #eff6ff !important;
  color: #2563eb !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-status-no-show.is-selected,
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-status-cancelled.is-selected,
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-menu button.is-status-deleted.is-selected {
  background: #fef2f2 !important;
  color: #dc2626 !important;
}

/* Status trigger chevron polish */
.booking-pro-modal.loop-create-pro-modal .loop-status-select .loop-mini-select-trigger i {
  font-size: 0 !important;
  width: 7px !important;
  height: 7px !important;
  border-right: 1.7px solid #00675f !important;
  border-bottom: 1.7px solid #00675f !important;
  transform: rotate(45deg) !important;
  color: transparent !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-status-select.is-open .loop-mini-select-trigger i {
  transform: rotate(225deg) !important;
  margin-top: 4px !important;
}


/* LOOP WORKSPACE V5.9 — payment modal equal money fields + colored payment status */
.loop-payment-editor-modal .loop-payment-editor-body-compact,
.loop-payment-editor-modal .loop-payment-editor-grid-simple {
  overflow: visible !important;
}
.loop-payment-editor-modal .loop-payment-editor-grid-simple {
  align-items: start !important;
}
.loop-payment-editor-modal .loop-payment-modal-money-field {
  min-width: 0 !important;
}
.loop-payment-editor-modal .loop-payment-modal-money-input {
  position: relative !important;
  width: 100% !important;
  min-width: 0 !important;
}
.loop-payment-editor-modal .loop-payment-modal-money-input::before {
  content: "$" !important;
  position: absolute !important;
  left: 13px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  z-index: 2 !important;
  color: #64748b !important;
  font-size: 13px !important;
  font-weight: 950 !important;
  pointer-events: none !important;
}
.loop-payment-editor-modal .loop-payment-modal-money-input > input {
  width: 100% !important;
  height: 46px !important;
  min-height: 46px !important;
  border-radius: 13px !important;
  border: 1px solid #dbe7ef !important;
  background: #ffffff !important;
  color: #0f172a !important;
  box-shadow: none !important;
  padding: 0 13px 0 31px !important;
  font-size: 14px !important;
  font-weight: 900 !important;
  line-height: 1 !important;
}
.loop-payment-editor-modal .loop-payment-modal-money-input > input::placeholder {
  color: #94a3b8 !important;
  opacity: 1 !important;
}
.loop-payment-editor-modal .loop-payment-modal-money-input > input:hover {
  border-color: #b8d8d2 !important;
}
.loop-payment-editor-modal .loop-payment-modal-money-input > input:focus {
  outline: none !important;
  border-color: #66cfc2 !important;
  box-shadow: 0 0 0 3px rgba(0,103,95,.09) !important;
}
.loop-payment-editor-modal .loop-payment-modal-money-input > input:disabled {
  background: #f8fafc !important;
  color: #94a3b8 !important;
  cursor: not-allowed !important;
}
.loop-payment-editor-modal .loop-tip-method-discreet,
.loop-payment-editor-modal .loop-tip-method-inline {
  display: none !important;
}
.loop-payment-editor-modal .loop-tip-method-override-row {
  margin-top: 8px !important;
}

/* Payment status trigger tones */
.loop-payment-status-select .loop-mini-select-trigger {
  position: relative !important;
  border-color: #dbe7ef !important;
  background: #ffffff !important;
}
.loop-payment-status-select .loop-mini-select-trigger::before {
  content: "" !important;
  width: 10px !important;
  height: 10px !important;
  border-radius: 999px !important;
  position: absolute !important;
  left: 12px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  background: #cbd5e1 !important;
}
.loop-payment-status-select .loop-mini-select-trigger {
  padding-left: 30px !important;
}
.loop-payment-status-select.is-payment-status-pending .loop-mini-select-trigger,
.loop-payment-status-select.is-payment-status-partial .loop-mini-select-trigger {
  background: #fffbeb !important;
  border-color: #fde68a !important;
}
.loop-payment-status-select.is-payment-status-pending .loop-mini-select-trigger::before,
.loop-payment-status-select.is-payment-status-partial .loop-mini-select-trigger::before {
  background: #f59e0b !important;
}
.loop-payment-status-select.is-payment-status-paid .loop-mini-select-trigger,
.loop-payment-status-select.is-payment-status-covered .loop-mini-select-trigger {
  background: #ecfdf5 !important;
  border-color: #bbf7d0 !important;
}
.loop-payment-status-select.is-payment-status-paid .loop-mini-select-trigger::before,
.loop-payment-status-select.is-payment-status-covered .loop-mini-select-trigger::before {
  background: #10b981 !important;
}
.loop-payment-status-select.is-payment-status-refunded .loop-mini-select-trigger {
  background: #f8fafc !important;
  border-color: #e2e8f0 !important;
}
.loop-payment-status-select.is-payment-status-refunded .loop-mini-select-trigger::before {
  background: #64748b !important;
}

/* Payment status option colors */
.loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option {
  position: relative !important;
  min-height: 38px !important;
  border-radius: 11px !important;
  padding-left: 36px !important;
  font-size: 13px !important;
  font-weight: 900 !important;
}
.loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option::before {
  content: "" !important;
  position: absolute !important;
  left: 12px !important;
  top: 50% !important;
  width: 12px !important;
  height: 12px !important;
  border-radius: 999px !important;
  transform: translateY(-50%) !important;
  background: #cbd5e1 !important;
}
.loop-payment-status-select .loop-mini-select-menu button.is-payment-pending::before,
.loop-payment-status-select .loop-mini-select-menu button.is-payment-partial::before {
  background: #f59e0b !important;
}
.loop-payment-status-select .loop-mini-select-menu button.is-payment-paid::before,
.loop-payment-status-select .loop-mini-select-menu button.is-payment-covered::before {
  background: #10b981 !important;
}
.loop-payment-status-select .loop-mini-select-menu button.is-payment-refunded::before {
  background: #64748b !important;
}
.loop-payment-status-select .loop-mini-select-menu button.is-payment-pending.is-selected,
.loop-payment-status-select .loop-mini-select-menu button.is-payment-partial.is-selected {
  background: #fffbeb !important;
  color: #92400e !important;
}
.loop-payment-status-select .loop-mini-select-menu button.is-payment-paid.is-selected,
.loop-payment-status-select .loop-mini-select-menu button.is-payment-covered.is-selected {
  background: #ecfdf5 !important;
  color: #047857 !important;
}
.loop-payment-status-select .loop-mini-select-menu button.is-payment-refunded.is-selected {
  background: #f8fafc !important;
  color: #334155 !important;
}


/* LOOP WORKSPACE V6.0 — payment status visual system */
.booking-pro-modal.loop-create-pro-modal .loop-payment-ops-item {
  padding-left: 48px !important;
  overflow: visible !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-ops-item::before {
  content: "!" !important;
  position: absolute !important;
  left: 14px !important;
  top: 50% !important;
  width: 28px !important;
  height: 28px !important;
  transform: translateY(-50%) !important;
  border-radius: 999px !important;
  display: grid !important;
  place-items: center !important;
  font-size: 14px !important;
  font-weight: 950 !important;
  line-height: 1 !important;
  box-shadow: 0 8px 18px rgba(15,23,42,.05) !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-ops-item.is-payment-status-pending::before,
.booking-pro-modal.loop-create-pro-modal .loop-payment-ops-item.is-payment-status-partial::before {
  content: "!" !important;
  background: #fffbeb !important;
  border: 1px solid #fde68a !important;
  color: #b45309 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-ops-item.is-payment-status-paid::before,
.booking-pro-modal.loop-create-pro-modal .loop-payment-ops-item.is-payment-status-covered::before {
  content: "✓" !important;
  background: #ecfdf5 !important;
  border: 1px solid #bbf7d0 !important;
  color: #047857 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-ops-item.is-payment-status-refunded::before {
  content: "↩" !important;
  background: #f8fafc !important;
  border: 1px solid #cbd5e1 !important;
  color: #64748b !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-ops-item.is-payment-status-cancelled::before,
.booking-pro-modal.loop-create-pro-modal .loop-payment-ops-item.is-payment-status-canceled::before {
  content: "×" !important;
  background: #fef2f2 !important;
  border: 1px solid #fecaca !important;
  color: #dc2626 !important;
}

/* Payment status trigger: dot no longer overlaps text */
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-trigger {
  position: relative !important;
  display: flex !important;
  align-items: center !important;
  min-height: 48px !important;
  height: 48px !important;
  padding: 0 38px 0 42px !important;
  border-radius: 13px !important;
  overflow: hidden !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-trigger span,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-trigger span {
  position: relative !important;
  z-index: 2 !important;
  display: block !important;
  min-width: 0 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  padding: 0 !important;
  margin: 0 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-trigger::before,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-trigger::before {
  content: "" !important;
  position: absolute !important;
  left: 15px !important;
  top: 50% !important;
  width: 12px !important;
  height: 12px !important;
  border-radius: 999px !important;
  transform: translateY(-50%) !important;
  z-index: 1 !important;
  background: #cbd5e1 !important;
  pointer-events: none !important;
}

/* More intentional status tones */
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-pending .loop-mini-select-trigger,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-partial .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-pending .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-partial .loop-mini-select-trigger {
  background: #fffaf0 !important;
  border-color: #fde68a !important;
  color: #78350f !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-pending .loop-mini-select-trigger::before,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-partial .loop-mini-select-trigger::before,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-pending .loop-mini-select-trigger::before,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-partial .loop-mini-select-trigger::before {
  background: #f59e0b !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-paid .loop-mini-select-trigger,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-covered .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-paid .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-covered .loop-mini-select-trigger {
  background: #ecfdf5 !important;
  border-color: #bbf7d0 !important;
  color: #064e3b !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-paid .loop-mini-select-trigger::before,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-covered .loop-mini-select-trigger::before,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-paid .loop-mini-select-trigger::before,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-covered .loop-mini-select-trigger::before {
  background: #10b981 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-refunded .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-refunded .loop-mini-select-trigger {
  background: #f8fafc !important;
  border-color: #cbd5e1 !important;
  color: #334155 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-refunded .loop-mini-select-trigger::before,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-refunded .loop-mini-select-trigger::before {
  background: #64748b !important;
}

/* Chevron stays on the right and does not compete with text */
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-trigger i,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-trigger i {
  position: absolute !important;
  right: 15px !important;
  top: 50% !important;
  width: 7px !important;
  height: 7px !important;
  margin-top: -5px !important;
  border-right: 1.8px solid #00675f !important;
  border-bottom: 1.8px solid #00675f !important;
  transform: rotate(45deg) !important;
  font-size: 0 !important;
  line-height: 0 !important;
  color: transparent !important;
  z-index: 2 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-open .loop-mini-select-trigger i,
.loop-payment-editor-modal .loop-payment-status-select.is-open .loop-mini-select-trigger i {
  margin-top: -1px !important;
  transform: rotate(225deg) !important;
}

/* Payment dropdown rows: cleaner, no icon on top of the word */
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu {
  min-width: max(100%, 230px) !important;
  border-radius: 16px !important;
  padding: 7px !important;
  background: #ffffff !important;
  border: 1px solid #dbe7ef !important;
  box-shadow: 0 22px 58px rgba(15,23,42,.18) !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option {
  position: relative !important;
  min-height: 40px !important;
  border-radius: 12px !important;
  padding: 0 12px 0 40px !important;
  color: #0f172a !important;
  background: transparent !important;
  font-size: 13px !important;
  font-weight: 900 !important;
  text-align: left !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option::before,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option::before {
  content: "" !important;
  position: absolute !important;
  left: 15px !important;
  top: 50% !important;
  width: 12px !important;
  height: 12px !important;
  border-radius: 999px !important;
  transform: translateY(-50%) !important;
  background: #cbd5e1 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option:hover,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option:hover {
  background: #f8fafc !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-pending::before,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-partial::before,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-pending::before,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-partial::before {
  background: #f59e0b !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-paid::before,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-covered::before,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-paid::before,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-covered::before {
  background: #10b981 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-refunded::before,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-refunded::before {
  background: #64748b !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-pending.is-selected,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-partial.is-selected,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-pending.is-selected,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-partial.is-selected {
  background: #fff7db !important;
  color: #92400e !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-paid.is-selected,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-covered.is-selected,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-paid.is-selected,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-covered.is-selected {
  background: #e6fbf5 !important;
  color: #047857 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-refunded.is-selected,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-refunded.is-selected {
  background: #f1f5f9 !important;
  color: #334155 !important;
}


/* LOOP WORKSPACE V6.1 — payment status dropdown neutral below, summary keeps colored icon */
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-trigger {
  min-height: 44px !important;
  height: 44px !important;
  border-radius: 13px !important;
  border: 1px solid #dbe7ef !important;
  background: #ffffff !important;
  color: #0f172a !important;
  padding: 0 36px 0 12px !important;
  box-shadow: none !important;
  display: flex !important;
  align-items: center !important;
  overflow: hidden !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-trigger:hover,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-trigger:hover {
  border-color: #b8d8d2 !important;
  background: #ffffff !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-open .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-open .loop-mini-select-trigger {
  border-color: #66cfc2 !important;
  background: #ffffff !important;
  box-shadow: 0 0 0 3px rgba(0,103,95,.09) !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-trigger::before,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-trigger::before {
  display: none !important;
  content: none !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-trigger span,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-trigger span {
  color: #0f172a !important;
  font-size: 13px !important;
  font-weight: 900 !important;
  min-width: 0 !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  white-space: nowrap !important;
  padding: 0 !important;
  margin: 0 !important;
}

/* Neutralize all payment-status tones only inside dropdown inputs/menus */
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-pending .loop-mini-select-trigger,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-partial .loop-mini-select-trigger,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-paid .loop-mini-select-trigger,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-covered .loop-mini-select-trigger,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-payment-status-refunded .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-pending .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-partial .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-paid .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-covered .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-payment-status-refunded .loop-mini-select-trigger {
  background: #ffffff !important;
  border-color: #dbe7ef !important;
  color: #0f172a !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-open.is-payment-status-pending .loop-mini-select-trigger,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-open.is-payment-status-partial .loop-mini-select-trigger,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-open.is-payment-status-paid .loop-mini-select-trigger,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-open.is-payment-status-covered .loop-mini-select-trigger,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-open.is-payment-status-refunded .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-open.is-payment-status-pending .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-open.is-payment-status-partial .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-open.is-payment-status-paid .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-open.is-payment-status-covered .loop-mini-select-trigger,
.loop-payment-editor-modal .loop-payment-status-select.is-open.is-payment-status-refunded .loop-mini-select-trigger {
  border-color: #66cfc2 !important;
  background: #ffffff !important;
}

/* Normal dropdown menu: no dots, no color coding, clean selected row */
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu {
  min-width: max(100%, 220px) !important;
  border-radius: 16px !important;
  border: 1px solid #dbe7ef !important;
  background: #ffffff !important;
  box-shadow: 0 22px 58px rgba(15,23,42,.18) !important;
  padding: 7px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option {
  min-height: 40px !important;
  border-radius: 12px !important;
  padding: 0 12px !important;
  color: #0f172a !important;
  background: transparent !important;
  font-size: 13px !important;
  font-weight: 900 !important;
  text-align: left !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option::before,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option::before {
  display: none !important;
  content: none !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option:hover,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option:hover {
  background: #f8fafc !important;
  color: #0f172a !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option.is-selected,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option.is-selected,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-pending.is-selected,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-partial.is-selected,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-paid.is-selected,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-covered.is-selected,
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-refunded.is-selected,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-pending.is-selected,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-partial.is-selected,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-paid.is-selected,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-covered.is-selected,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-refunded.is-selected {
  background: #f1f5f9 !important;
  color: #0f172a !important;
}

/* Keep only the clean chevron on the right */
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select .loop-mini-select-trigger i,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-trigger i {
  position: absolute !important;
  right: 14px !important;
  top: 50% !important;
  width: 7px !important;
  height: 7px !important;
  margin-top: -5px !important;
  border-right: 1.8px solid #00675f !important;
  border-bottom: 1.8px solid #00675f !important;
  transform: rotate(45deg) !important;
  font-size: 0 !important;
  line-height: 0 !important;
  color: transparent !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-payment-status-select.is-open .loop-mini-select-trigger i,
.loop-payment-editor-modal .loop-payment-status-select.is-open .loop-mini-select-trigger i {
  margin-top: -1px !important;
  transform: rotate(225deg) !important;
}


/* LOOP WORKSPACE V6.2 — payment/commission dropdowns float without pushing modal */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-main-grid,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-card,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-main,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field {
  overflow-x: hidden !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-card,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-main,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field {
  overflow-y: visible !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field {
  position: relative !important;
  z-index: 1 !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field:has(.loop-mini-select.is-open) {
  z-index: 2147482000 !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field .loop-payment-status-select,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field .loop-payment-method-select {
  position: relative !important;
  z-index: 20 !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field .loop-payment-status-select.is-open,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field .loop-payment-method-select.is-open {
  z-index: 2147482500 !important;
}

/* Main payment row: open upward so it doesn't extend the modal's scroll height */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-slim-field .loop-payment-status-select .loop-mini-select-menu,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-slim-field .loop-payment-method-select .loop-mini-select-menu {
  position: absolute !important;
  top: auto !important;
  bottom: calc(100% + 8px) !important;
  left: 0 !important;
  right: auto !important;
  width: max(100%, 224px) !important;
  min-width: 100% !important;
  max-width: min(284px, calc(100vw - 56px)) !important;
  max-height: 218px !important;
  overflow-y: auto !important;
  overscroll-behavior: contain !important;
  border-radius: 16px !important;
  border: 1px solid #dbe7ef !important;
  background: #ffffff !important;
  box-shadow: 0 24px 62px rgba(15,23,42,.20) !important;
  padding: 7px !important;
  z-index: 2147483000 !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-slim-field .loop-payment-method-select .loop-mini-select-menu {
  width: max(100%, 242px) !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-slim-field .loop-payment-status-select .loop-mini-select-menu button,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-slim-field .loop-payment-method-select .loop-mini-select-menu button {
  min-height: 38px !important;
  border-radius: 11px !important;
  padding: 0 11px !important;
  font-size: 13px !important;
  font-weight: 900 !important;
  text-align: left !important;
  white-space: nowrap !important;
}

/* Payment modal: keep the menu overlayed, but open downward because fields are at the top */
.loop-payment-editor-modal,
.loop-payment-editor-modal .loop-settings-modal-body,
.loop-payment-editor-modal .loop-payment-editor-body-compact,
.loop-payment-editor-modal .loop-payment-editor-grid-simple,
.loop-payment-editor-modal .booking-pro-field {
  overflow: visible !important;
}
.loop-payment-editor-modal .booking-pro-field:has(.loop-mini-select.is-open) {
  position: relative !important;
  z-index: 2147482000 !important;
}
.loop-payment-editor-modal .loop-payment-status-select,
.loop-payment-editor-modal .loop-payment-method-select {
  position: relative !important;
  z-index: 20 !important;
}
.loop-payment-editor-modal .loop-payment-status-select.is-open,
.loop-payment-editor-modal .loop-payment-method-select.is-open {
  z-index: 2147482500 !important;
}
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu,
.loop-payment-editor-modal .loop-payment-method-select .loop-mini-select-menu {
  position: absolute !important;
  top: calc(100% + 8px) !important;
  bottom: auto !important;
  left: 0 !important;
  right: auto !important;
  width: max(100%, 224px) !important;
  max-width: min(292px, calc(100vw - 56px)) !important;
  max-height: 218px !important;
  overflow-y: auto !important;
  overscroll-behavior: contain !important;
  border-radius: 16px !important;
  border: 1px solid #dbe7ef !important;
  background: #ffffff !important;
  box-shadow: 0 24px 62px rgba(15,23,42,.20) !important;
  padding: 7px !important;
  z-index: 2147483000 !important;
}

/* Commission modal: tiny list, overlay it without reflow */
.loop-commission-editor-modal-simple,
.loop-commission-editor-modal-simple .loop-settings-modal-body,
.loop-commission-editor-modal-simple .loop-commission-simple-body,
.loop-commission-editor-modal-simple .loop-commission-simple-grid,
.loop-commission-editor-modal-simple .loop-commission-type-field {
  overflow: visible !important;
}
.loop-commission-editor-modal-simple .loop-commission-type-field {
  position: relative !important;
  z-index: 20 !important;
}
.loop-commission-editor-modal-simple .loop-commission-type-field:has(.loop-mini-select.is-open) {
  z-index: 2147482500 !important;
}
.loop-commission-editor-modal-simple .loop-commission-type-select {
  position: relative !important;
  z-index: 20 !important;
}
.loop-commission-editor-modal-simple .loop-commission-type-select.is-open {
  z-index: 2147482500 !important;
}
.loop-commission-editor-modal-simple .loop-commission-type-select .loop-mini-select-menu {
  position: absolute !important;
  top: calc(100% + 8px) !important;
  bottom: auto !important;
  left: 0 !important;
  right: auto !important;
  width: max(100%, 220px) !important;
  max-height: 172px !important;
  overflow-y: auto !important;
  border-radius: 16px !important;
  border: 1px solid #dbe7ef !important;
  background: #ffffff !important;
  box-shadow: 0 24px 62px rgba(15,23,42,.18) !important;
  padding: 7px !important;
  z-index: 2147483000 !important;
}

/* Small screens: if space is tight, dropdowns can open downward in a controlled internal menu */
@media(max-width: 760px) {
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-slim-field .loop-payment-status-select .loop-mini-select-menu,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-slim-field .loop-payment-method-select .loop-mini-select-menu {
    top: calc(100% + 8px) !important;
    bottom: auto !important;
    max-height: 210px !important;
    width: min(280px, calc(100vw - 44px)) !important;
  }
}


/* LOOP WORKSPACE V6.2B — dropdown menus portaled to body, no clipping/no reflow */
.loop-mini-select-menu.is-portaled-menu {
  position: fixed !important;
  right: auto !important;
  bottom: auto !important;
  min-width: 0 !important;
  overflow-y: auto !important;
  overscroll-behavior: contain !important;
  border-radius: 16px !important;
  border: 1px solid #dbe7ef !important;
  background: #ffffff !important;
  box-shadow: 0 24px 62px rgba(15,23,42,.20) !important;
  padding: 7px !important;
  z-index: 2147483647 !important;
  box-sizing: border-box !important;
}
.loop-mini-select-menu.is-portaled-menu button {
  width: 100% !important;
  min-height: 38px !important;
  border: 0 !important;
  border-radius: 11px !important;
  background: transparent !important;
  color: #0f172a !important;
  font-size: 13px !important;
  font-weight: 900 !important;
  text-align: left !important;
  padding: 0 11px !important;
  white-space: nowrap !important;
  cursor: pointer !important;
}
.loop-mini-select-menu.is-portaled-menu button:hover {
  background: #f8fafc !important;
  color: #0f172a !important;
}
.loop-mini-select-menu.is-portaled-menu button.is-selected {
  background: #f1f5f9 !important;
  color: #0f172a !important;
}
.loop-mini-select-menu.is-portaled-menu button.is-payment-status-option::before {
  display: none !important;
  content: none !important;
}

/* While portaled, the trigger stays normal; the menu no longer belongs to the card layout. */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-slim-field .loop-mini-select.uses-portal-menu .loop-mini-select-menu,
.loop-payment-editor-modal .loop-mini-select.uses-portal-menu .loop-mini-select-menu,
.loop-commission-editor-modal-simple .loop-mini-select.uses-portal-menu .loop-mini-select-menu {
  display: none !important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-card,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-main,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field {
  overflow: visible !important;
}


/* LOOP WORKSPACE V6.2C — visible local overlay dropdowns for payment/commission */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-card,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-main,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field,
.loop-payment-editor-modal,
.loop-payment-editor-modal .loop-settings-modal-body,
.loop-payment-editor-modal .loop-payment-editor-body-compact,
.loop-payment-editor-modal .loop-payment-editor-grid-simple,
.loop-payment-editor-modal .booking-pro-field,
.loop-commission-editor-modal-simple,
.loop-commission-editor-modal-simple .loop-settings-modal-body,
.loop-commission-editor-modal-simple .loop-commission-simple-body,
.loop-commission-editor-modal-simple .loop-commission-simple-grid,
.loop-commission-editor-modal-simple .booking-pro-field {
  overflow: visible !important;
}

.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field,
.loop-payment-editor-modal .booking-pro-field,
.loop-commission-editor-modal-simple .booking-pro-field {
  position: relative !important;
  z-index: 1 !important;
}

.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field:has(.loop-mini-select.is-open),
.loop-payment-editor-modal .booking-pro-field:has(.loop-mini-select.is-open),
.loop-commission-editor-modal-simple .booking-pro-field:has(.loop-mini-select.is-open) {
  z-index: 2147483000 !important;
}

.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-status-select,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-method-select,
.loop-payment-editor-modal .loop-payment-status-select,
.loop-payment-editor-modal .loop-payment-method-select,
.loop-commission-editor-modal-simple .loop-commission-type-select {
  position: relative !important;
  z-index: 20 !important;
}

.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-status-select.is-open,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-method-select.is-open,
.loop-payment-editor-modal .loop-payment-status-select.is-open,
.loop-payment-editor-modal .loop-payment-method-select.is-open,
.loop-commission-editor-modal-simple .loop-commission-type-select.is-open {
  z-index: 2147483000 !important;
}

/* Main payment row: open down as an overlay so it is visible and does not push content. */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-status-select .loop-mini-select-menu,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-method-select .loop-mini-select-menu {
  display: block !important;
  position: absolute !important;
  top: calc(100% + 8px) !important;
  bottom: auto !important;
  left: 0 !important;
  right: auto !important;
  width: max(100%, 224px) !important;
  min-width: 100% !important;
  max-width: min(292px, calc(100vw - 56px)) !important;
  max-height: 230px !important;
  overflow-y: auto !important;
  overscroll-behavior: contain !important;
  border-radius: 16px !important;
  border: 1px solid #dbe7ef !important;
  background: #ffffff !important;
  box-shadow: 0 24px 62px rgba(15,23,42,.20) !important;
  padding: 7px !important;
  z-index: 2147483600 !important;
  box-sizing: border-box !important;
}

.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-method-select .loop-mini-select-menu {
  width: max(100%, 246px) !important;
}

.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-status-select .loop-mini-select-menu button,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-method-select .loop-mini-select-menu button,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button,
.loop-payment-editor-modal .loop-payment-method-select .loop-mini-select-menu button,
.loop-commission-editor-modal-simple .loop-commission-type-select .loop-mini-select-menu button {
  min-height: 38px !important;
  width: 100% !important;
  border: 0 !important;
  border-radius: 11px !important;
  background: transparent !important;
  color: #0f172a !important;
  font-size: 13px !important;
  font-weight: 900 !important;
  text-align: left !important;
  white-space: nowrap !important;
  padding: 0 11px !important;
  cursor: pointer !important;
}

.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-status-select .loop-mini-select-menu button:hover,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-method-select .loop-mini-select-menu button:hover,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button:hover,
.loop-payment-editor-modal .loop-payment-method-select .loop-mini-select-menu button:hover,
.loop-commission-editor-modal-simple .loop-commission-type-select .loop-mini-select-menu button:hover {
  background: #f8fafc !important;
  color: #0f172a !important;
}

.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-status-select .loop-mini-select-menu button.is-selected,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-section .loop-payment-method-select .loop-mini-select-menu button.is-selected,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-selected,
.loop-payment-editor-modal .loop-payment-method-select .loop-mini-select-menu button.is-selected,
.loop-commission-editor-modal-simple .loop-commission-type-select .loop-mini-select-menu button.is-selected {
  background: #f1f5f9 !important;
  color: #0f172a !important;
}

/* Remove the color dots from payment-status option rows in this neutral selector. */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option::before,
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu button.is-payment-status-option::before {
  display: none !important;
  content: none !important;
}

/* Payment popup and commission popup can still open down; they sit high enough. */
.loop-payment-editor-modal .loop-payment-status-select .loop-mini-select-menu,
.loop-payment-editor-modal .loop-payment-method-select .loop-mini-select-menu,
.loop-commission-editor-modal-simple .loop-commission-type-select .loop-mini-select-menu {
  display: block !important;
  position: absolute !important;
  top: calc(100% + 8px) !important;
  bottom: auto !important;
  left: 0 !important;
  right: auto !important;
  width: max(100%, 224px) !important;
  max-width: min(292px, calc(100vw - 56px)) !important;
  max-height: 220px !important;
  overflow-y: auto !important;
  overscroll-behavior: contain !important;
  border-radius: 16px !important;
  border: 1px solid #dbe7ef !important;
  background: #ffffff !important;
  box-shadow: 0 24px 62px rgba(15,23,42,.20) !important;
  padding: 7px !important;
  z-index: 2147483600 !important;
  box-sizing: border-box !important;
}

/* Neutralize the previous portal hiding rules. */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-mini-select.uses-portal-menu .loop-mini-select-menu,
.loop-payment-editor-modal .loop-mini-select.uses-portal-menu .loop-mini-select-menu,
.loop-commission-editor-modal-simple .loop-mini-select.uses-portal-menu .loop-mini-select-menu {
  display: block !important;
}


/* LOOP IDENTITY RESOLUTION V1 — new customer modal feedback */
.loop-new-customer-stable-modal .loop-identity-resolve-card{
  margin-top:12px!important;
  display:grid!important;
  grid-template-columns:30px minmax(0,1fr) auto!important;
  gap:10px!important;
  align-items:center!important;
  padding:11px 12px!important;
  border-radius:16px!important;
  border:1px solid #dbe7ef!important;
  background:#ffffff!important;
  box-shadow:0 10px 28px rgba(15,23,42,.045)!important;
}
.loop-new-customer-stable-modal .loop-identity-dot{
  width:28px!important;
  height:28px!important;
  border-radius:999px!important;
  display:grid!important;
  place-items:center!important;
  font-size:12px!important;
  font-weight:950!important;
  background:#eefaf7!important;
  border:1px solid #ccfbf1!important;
  color:#00675f!important;
}
.loop-new-customer-stable-modal .loop-identity-resolve-card>div{
  min-width:0!important;
  display:grid!important;
  gap:2px!important;
}
.loop-new-customer-stable-modal .loop-identity-resolve-card strong{
  color:#0f172a!important;
  font-size:12.5px!important;
  font-weight:950!important;
}
.loop-new-customer-stable-modal .loop-identity-resolve-card small,
.loop-new-customer-stable-modal .loop-identity-resolve-card em{
  min-width:0!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
  color:#64748b!important;
  font-size:11px!important;
  font-weight:750!important;
  font-style:normal!important;
}
.loop-new-customer-stable-modal .loop-identity-resolve-card button{
  height:32px!important;
  border-radius:999px!important;
  border:1px solid #b8efe6!important;
  background:#ffffff!important;
  color:#00675f!important;
  padding:0 12px!important;
  font-size:11px!important;
  font-weight:950!important;
  cursor:pointer!important;
}
.loop-new-customer-stable-modal .loop-identity-resolve-card.is-existing{
  background:#f3fffb!important;
  border-color:#b8efe6!important;
}
.loop-new-customer-stable-modal .loop-identity-resolve-card.is-platform{
  background:#f8fffe!important;
  border-color:#ccfbf1!important;
}
.loop-new-customer-stable-modal .loop-identity-resolve-card.is-conflict{
  background:#fff7ed!important;
  border-color:#fed7aa!important;
}
.loop-new-customer-stable-modal .loop-identity-resolve-card.is-conflict .loop-identity-dot{
  background:#fffbeb!important;
  border-color:#fde68a!important;
  color:#b45309!important;
}
.loop-new-customer-stable-modal .loop-identity-resolve-card.is-warning{
  background:#f8fafc!important;
}
.loop-new-customer-stable-modal .loop-identity-resolve-card.is-loading .loop-identity-dot{
  font-size:0!important;
  border:2px solid #dbe7ef!important;
  border-top-color:#00675f!important;
  animation:loopIdentitySpin .75s linear infinite!important;
}
@keyframes loopIdentitySpin{to{transform:rotate(360deg)}}
@media(max-width:560px){
  .loop-new-customer-stable-modal .loop-identity-resolve-card{grid-template-columns:28px minmax(0,1fr)!important;}
  .loop-new-customer-stable-modal .loop-identity-resolve-card button{grid-column:2!important;width:max-content!important;}
}


/* LOOP IDENTITY V3 — keep typed contact data and show safe mismatch actions */
.loop-new-customer-stable-modal .loop-identity-resolve-card.has-soft-conflict{
  border-color:#99f6e4!important;
  background:linear-gradient(135deg,#f0fdfa,#ffffff)!important;
}
.loop-new-customer-stable-modal .loop-identity-field-conflicts{
  list-style:none!important;
  margin:7px 0 0!important;
  padding:0!important;
  display:grid!important;
  gap:5px!important;
}
.loop-new-customer-stable-modal .loop-identity-field-conflicts li{
  display:grid!important;
  grid-template-columns:auto minmax(0,1fr)!important;
  gap:4px 8px!important;
  align-items:center!important;
  padding:6px 8px!important;
  border-radius:12px!important;
  background:#ffffff!important;
  border:1px solid #dbe7ef!important;
}
.loop-new-customer-stable-modal .loop-identity-field-conflicts li span{
  color:#64748b!important;
  font-size:10px!important;
  font-weight:950!important;
  letter-spacing:.08em!important;
  text-transform:uppercase!important;
}
.loop-new-customer-stable-modal .loop-identity-field-conflicts li strong{
  color:#0f172a!important;
  font-size:11px!important;
  font-weight:950!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
}
.loop-new-customer-stable-modal .loop-identity-field-conflicts li small{
  grid-column:2!important;
  color:#94a3b8!important;
  font-size:10.5px!important;
  font-weight:800!important;
}

/* Payment row as a calm operational strip */
.booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-section {
  padding: 18px 20px 20px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-section-headline {
  display: flex !important;
  align-items: center !important;
  gap: 12px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-title-actions {
  margin-left: auto !important;
  display: flex !important;
  gap: 8px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-card {
  border-radius: 20px !important;
  border: 1px solid #dfeaf2 !important;
  background: #ffffff !important;
  box-shadow: none !important;
  padding: 12px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-main {
  grid-template-columns: 1.05fr 1.05fr 1.05fr .7fr !important;
  gap: 12px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field {
  border: 0 !important;
  border-right: 1px solid #eef2f7 !important;
  background: transparent !important;
  border-radius: 0 !important;
  padding: 0 10px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field:last-child { border-right: 0 !important; }
.booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field > span {
  font-size: 11px !important;
  letter-spacing: .08em !important;
  text-transform: uppercase !important;
  color: #64748b !important;
  font-weight: 950 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field input,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field .loop-mini-select-trigger {
  min-height: 42px !important;
  border-radius: 14px !important;
  background: #f8fafc !important;
}

/* Keep the old aside hidden in this workspace shell. */
.booking-pro-modal.loop-reservation-workspace-v1 .loop-create-summary-card { display: none !important; }

@media (max-width: 980px) {
  .booking-pro-modal.loop-reservation-workspace-v1 {
    width: min(100vw - 12px, 720px) !important;
    max-height: 94vh !important;
    border-radius: 22px !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-minimal-head {
    grid-template-columns: 1fr !important;
    gap: 14px !important;
    padding: 20px 16px !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-actions-minimal {
    width: 100% !important;
    display: grid !important;
    grid-template-columns: 1fr auto auto !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal {
    grid-template-columns: 1fr 1fr !important;
    margin: 12px 14px !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form.is-create-mode,
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form.is-edit-mode {
    padding: 0 14px 22px !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-left {
    grid-template-columns: 1fr !important;
    grid-template-areas:
      "client"
      "details"
      "payment" !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-fields,
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-main,
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-service-combo-row {
    grid-template-columns: 1fr !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field {
    border-right: 0 !important;
    border-bottom: 1px solid #eef2f7 !important;
    padding: 8px 0 !important;
  }
  .booking-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field:last-child { border-bottom: 0 !important; }
}



/* LOOP WORKSPACE V3 — cleaner customer + true service dropdown overlay */
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-one-line-note.is-new-customer-only,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-compact-insight.is-new-customer-only {
  display: none !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-workspace-section,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-combo-row,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox {
  overflow: visible !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox {
  position: relative !important;
  z-index: 40 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox.is-open {
  z-index: 2147483644 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-main {
  gap: 0 !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-main small {
  display: none !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-trigger {
  min-height: 58px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-panel {
  position: absolute !important;
  top: calc(100% + 8px) !important;
  left: 0 !important;
  right: 0 !important;
  width: 100% !important;
  margin-top: 0 !important;
  z-index: 2147483645 !important;
  border-radius: 18px !important;
  box-shadow: 0 26px 70px rgba(15,23,42,.22) !important;
  max-height: min(370px, calc(100dvh - 220px)) !important;
  display: flex !important;
  flex-direction: column !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-list {
  max-height: 250px !important;
}
.booking-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-search input {
  scroll-margin-top: 120px !important;
}

/* LOOP BOOKING MODAL V43 — TRUE MOBILE SHEET FIT
   Goal: one mobile scroll owner, compact header, compact chips, and dropdowns as sheets. */
@media (max-width: 760px) {
  .loop-accessible-modal-layer:not(.is-nested-modal) {
    align-items: stretch !important;
    justify-content: stretch !important;
    padding: 0 !important;
    overflow: hidden !important;
    background: rgba(15, 23, 42, 0.56) !important;
  }

  .loop-create-modal-host {
    width: 100vw !important;
    max-width: 100vw !important;
    height: 100dvh !important;
    max-height: 100dvh !important;
    min-height: 100dvh !important;
    inset: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    border-radius: 0 !important;
    overflow: hidden !important;
    display: flex !important;
    align-items: stretch !important;
    justify-content: stretch !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 {
    --loop-booking-mobile-safe-bottom: env(safe-area-inset-bottom, 0px);
    --loop-booking-mobile-safe-top: env(safe-area-inset-top, 0px);
    width: 100vw !important;
    max-width: 100vw !important;
    height: 100dvh !important;
    max-height: 100dvh !important;
    min-height: 0 !important;
    margin: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    display: flex !important;
    flex-direction: column !important;
    overflow: hidden !important;
    background: #f8fafc !important;
    box-shadow: none !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-minimal-head {
    flex: 0 0 auto !important;
    position: sticky !important;
    top: 0 !important;
    z-index: 2147483200 !important;
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) 42px !important;
    grid-template-areas: "copy close" !important;
    align-items: start !important;
    gap: 10px !important;
    min-height: 0 !important;
    height: auto !important;
    padding: calc(10px + var(--loop-booking-mobile-safe-top)) 12px 10px !important;
    background: rgba(255, 255, 255, 0.98) !important;
    border-bottom: 1px solid #e2e8f0 !important;
    box-shadow: 0 8px 22px rgba(15, 23, 42, 0.06) !important;
    overflow: visible !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-hero-icon,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-header-summary-btn {
    display: none !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-copy {
    grid-area: copy !important;
    min-width: 0 !important;
    width: 100% !important;
    overflow: hidden !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-kicker {
    margin: 0 0 6px !important;
    font-size: 9px !important;
    line-height: 1 !important;
    letter-spacing: .16em !important;
    color: #00675f !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-edit-title-row {
    margin: 0 !important;
    display: flex !important;
    align-items: center !important;
    gap: 8px !important;
    min-width: 0 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-edit-title-row h2 {
    margin: 0 !important;
    font-size: clamp(18px, 5.2vw, 22px) !important;
    line-height: 1.02 !important;
    letter-spacing: -0.045em !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-edit-status-pill {
    flex: 0 0 auto !important;
    max-width: 118px !important;
    padding: 5px 8px !important;
    font-size: 9.5px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-copy p {
    margin: 5px 0 0 !important;
    max-width: calc(100vw - 78px) !important;
    font-size: 11.5px !important;
    line-height: 1.2 !important;
    font-weight: 850 !important;
    color: #64748b !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-actions-minimal {
    display: contents !important;
    min-width: 0 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-close {
    grid-area: close !important;
    position: static !important;
    width: 40px !important;
    height: 40px !important;
    min-width: 40px !important;
    border-radius: 999px !important;
    align-self: start !important;
    justify-self: end !important;
    border: 1px solid #dbe7ef !important;
    background: #ffffff !important;
    color: #0f172a !important;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08) !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-primary {
    position: fixed !important;
    left: 12px !important;
    right: 12px !important;
    bottom: calc(10px + var(--loop-booking-mobile-safe-bottom)) !important;
    z-index: 2147483400 !important;
    width: auto !important;
    min-width: 0 !important;
    height: 50px !important;
    border-radius: 16px !important;
    padding: 0 16px !important;
    box-shadow: 0 18px 38px rgba(0, 103, 95, 0.28) !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-more-actions-wrap {
    position: fixed !important;
    right: 14px !important;
    bottom: calc(70px + var(--loop-booking-mobile-safe-bottom)) !important;
    z-index: 2147483400 !important;
    order: 0 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-more-actions-btn {
    width: 44px !important;
    height: 44px !important;
    padding: 0 !important;
    border-radius: 999px !important;
    font-size: 0 !important;
    background: #ffffff !important;
    color: #00675f !important;
    box-shadow: 0 16px 34px rgba(15, 23, 42, 0.18) !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-more-actions-btn::after {
    content: "•••" !important;
    font-size: 18px !important;
    line-height: 1 !important;
    font-weight: 950 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal {
    flex: 0 0 auto !important;
    display: flex !important;
    grid-template-columns: none !important;
    gap: 8px !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 10px 12px !important;
    overflow-x: auto !important;
    overflow-y: visible !important;
    background: #ffffff !important;
    border-bottom: 1px solid #e2e8f0 !important;
    scrollbar-width: none !important;
    -webkit-overflow-scrolling: touch !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal::-webkit-scrollbar {
    display: none !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > div,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > button {
    flex: 0 0 auto !important;
    width: auto !important;
    min-width: 142px !important;
    max-width: 176px !important;
    min-height: 42px !important;
    height: 42px !important;
    border-radius: 14px !important;
    padding: 7px 10px 7px 34px !important;
    box-shadow: none !important;
    transform: none !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > .is-muted {
    display: none !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > div::before,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > button::before {
    left: 9px !important;
    width: 20px !important;
    height: 20px !important;
    font-size: 11px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal span {
    margin: 0 0 2px !important;
    font-size: 8.8px !important;
    line-height: 1 !important;
    letter-spacing: .06em !important;
    text-transform: uppercase !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-mini-select-trigger {
    font-size: 11px !important;
    line-height: 1.1 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal em {
    top: 6px !important;
    right: 6px !important;
    padding: 4px 6px !important;
    font-size: 8.5px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-whatsapp-manual-status {
    flex: 0 0 auto !important;
    margin: 8px 12px 0 !important;
    border-radius: 14px !important;
    padding: 9px 11px !important;
    font-size: 12px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-mobile-edit-summary {
    flex: 0 0 auto !important;
    padding: 10px 12px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form.is-create-mode,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form.is-edit-mode {
    flex: 1 1 auto !important;
    min-height: 0 !important;
    height: auto !important;
    display: block !important;
    padding: 12px 12px calc(92px + var(--loop-booking-mobile-safe-bottom)) !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    background: #f8fafc !important;
    -webkit-overflow-scrolling: touch !important;
    overscroll-behavior: contain !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-left {
    width: 100% !important;
    max-width: none !important;
    height: auto !important;
    min-height: 0 !important;
    display: grid !important;
    grid-template-columns: 1fr !important;
    grid-template-areas: "client" "details" "payment" !important;
    gap: 12px !important;
    margin: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-section.loop-create-section,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-section,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-accordion-stack,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-post-payment-actions-row {
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    padding: 14px !important;
    border-radius: 20px !important;
    border: 1px solid #dfeaf2 !important;
    background: #ffffff !important;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.035) !important;
    overflow: visible !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-head,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-section-headline,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-section-title.is-quiet-title {
    display: grid !important;
    grid-template-columns: minmax(0, 1fr) !important;
    gap: 10px !important;
    align-items: start !important;
    margin-bottom: 10px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-head-actions,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-title-actions {
    margin: 0 !important;
    width: 100% !important;
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 8px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-new-customer-action,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-datetime-change-btn,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-action,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-head-action {
    min-height: 38px !important;
    height: 38px !important;
    border-radius: 13px !important;
    padding: 0 10px !important;
    font-size: 11.5px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search {
    margin: 10px 0 0 !important;
    position: relative !important;
    z-index: 80 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 input,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 textarea,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 select,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-mini-select-trigger,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-trigger,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-date-calendar-trigger,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-time12-control-modern {
    min-height: 44px !important;
    border-radius: 14px !important;
    font-size: 14px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-fields,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-main,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-combo-row,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-row,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-datetime-editor-grid,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-simple-grid {
    display: grid !important;
    grid-template-columns: 1fr !important;
    gap: 10px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-row {
    align-items: stretch !important;
    padding: 10px 0 !important;
    border-bottom: 1px solid #edf2f7 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-row:last-child {
    border-bottom: 0 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-workspace-step {
    width: 24px !important;
    height: 24px !important;
    min-width: 24px !important;
    font-size: 11px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-label,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-field > span,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field > span {
    font-size: 9px !important;
    letter-spacing: .12em !important;
    line-height: 1 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-card,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-main,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field {
    padding: 0 !important;
    border: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    overflow: visible !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field {
    padding: 8px 0 !important;
    border-bottom: 1px solid #eef2f7 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field:last-child {
    border-bottom: 0 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badges {
    margin-top: 10px !important;
    gap: 6px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badges .loop-customer-status-badge {
    min-height: 24px !important;
    padding: 0 9px !important;
    font-size: 10px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .lcp-menu,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-panel,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-unified-service-menu,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-menu,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-mini-select-menu,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-more-actions-menu {
    position: fixed !important;
    left: 10px !important;
    right: 10px !important;
    top: auto !important;
    bottom: calc(10px + var(--loop-booking-mobile-safe-bottom)) !important;
    width: auto !important;
    min-width: 0 !important;
    max-width: none !important;
    max-height: min(62dvh, 480px) !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    z-index: 2147483647 !important;
    border-radius: 22px !important;
    background: #ffffff !important;
    border: 1px solid #dbe7ef !important;
    box-shadow: 0 -20px 60px rgba(15, 23, 42, 0.24) !important;
    padding: 10px !important;
    transform: translateZ(0) !important;
    -webkit-overflow-scrolling: touch !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-list,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-menu .loop-service-combobox-list {
    max-height: none !important;
    overflow: visible !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-mini-select-menu button,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-panel button,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-unified-service-menu button,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .lcp-menu button {
    min-height: 42px !important;
    border-radius: 14px !important;
    font-size: 14px !important;
  }
}

/* LOOP BOOKING MODAL V44 — MOBILE POPUP POLISH
   Fixes clipped title, submit button in header, over-dense customer fields,
   service/date layout, and payment editor visibility on mobile. */
@media (max-width: 760px) {
  .loop-accessible-modal-layer:not(.is-nested-modal) {
    align-items: center !important;
    justify-content: center !important;
    padding: 8px !important;
    overflow: hidden !important;
    background: rgba(15, 23, 42, 0.48) !important;
  }

  .loop-create-modal-host {
    width: 100% !important;
    max-width: calc(100vw - 16px) !important;
    height: auto !important;
    min-height: 0 !important;
    max-height: calc(100dvh - 16px) !important;
    margin: 0 auto !important;
    inset: auto !important;
    border-radius: 26px !important;
    overflow: hidden !important;
    background: transparent !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 {
    width: 100% !important;
    max-width: calc(100vw - 16px) !important;
    height: min(92dvh, calc(100dvh - 16px)) !important;
    min-height: 0 !important;
    max-height: calc(100dvh - 16px) !important;
    border-radius: 26px !important;
    border: 1px solid rgba(203, 213, 225, 0.88) !important;
    box-shadow: 0 22px 70px rgba(15, 23, 42, 0.24) !important;
    overflow: hidden !important;
    background: #f8fafc !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-minimal-head {
    grid-template-columns: minmax(0, 1fr) 40px !important;
    grid-template-areas: "copy close" !important;
    align-items: start !important;
    gap: 8px !important;
    padding: 12px 12px 10px !important;
    min-height: 78px !important;
    max-height: 96px !important;
    overflow: hidden !important;
    background: rgba(255, 255, 255, 0.985) !important;
    border-bottom: 1px solid #e2e8f0 !important;
    box-shadow: 0 8px 18px rgba(15, 23, 42, 0.055) !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-copy,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-edit-title-row {
    min-width: 0 !important;
    width: 100% !important;
    overflow: hidden !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-kicker {
    margin: 0 0 5px !important;
    font-size: 9px !important;
    line-height: 1 !important;
    letter-spacing: .14em !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-edit-title-row h2 {
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    margin: 0 !important;
    font-size: clamp(19px, 5.3vw, 22px) !important;
    line-height: 1.04 !important;
    letter-spacing: -0.05em !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-copy p {
    max-width: 100% !important;
    margin: 5px 0 0 !important;
    font-size: 11px !important;
    line-height: 1.15 !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-actions-minimal {
    display: contents !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-close {
    grid-area: close !important;
    position: relative !important;
    width: 38px !important;
    height: 38px !important;
    min-width: 38px !important;
    align-self: start !important;
    justify-self: end !important;
    border-radius: 999px !important;
    z-index: 2147483500 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-primary {
    position: fixed !important;
    left: 18px !important;
    right: 18px !important;
    bottom: calc(16px + env(safe-area-inset-bottom, 0px)) !important;
    z-index: 2147483500 !important;
    width: auto !important;
    min-width: 0 !important;
    max-width: none !important;
    height: 48px !important;
    min-height: 48px !important;
    padding: 0 16px !important;
    border-radius: 16px !important;
    font-size: 14px !important;
    font-weight: 950 !important;
    box-shadow: 0 18px 36px rgba(0, 103, 95, 0.30) !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-primary:disabled {
    opacity: .78 !important;
    background: #73b8ad !important;
    border-color: #73b8ad !important;
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.10) !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal {
    padding: 8px 10px !important;
    gap: 7px !important;
    min-height: 58px !important;
    background: #ffffff !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > div,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > button {
    min-width: 132px !important;
    max-width: 154px !important;
    height: 42px !important;
    min-height: 42px !important;
    padding: 7px 8px 7px 32px !important;
    border-radius: 14px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-ops-item:disabled {
    display: none !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-mini-select-trigger {
    font-size: 10.5px !important;
    white-space: nowrap !important;
    overflow: hidden !important;
    text-overflow: ellipsis !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form.is-create-mode,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form.is-edit-mode {
    padding: 12px 10px calc(84px + env(safe-area-inset-bottom, 0px)) !important;
    background: #f8fafc !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-section.loop-create-section,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-section,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-accordion-stack,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-post-payment-actions-row {
    padding: 13px 14px !important;
    border-radius: 20px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-head small,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-fields,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-smart-footer,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-note {
    display: none !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-head-actions {
    grid-template-columns: 1fr 1fr !important;
    gap: 8px !important;
    width: 100% !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-new-customer-action {
    height: 38px !important;
    min-height: 38px !important;
    border-radius: 13px !important;
    font-size: 12px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search {
    margin-top: 12px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section {
    padding: 13px 14px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-shell {
    display: grid !important;
    gap: 12px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-rows,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block {
    border: 1px solid #e2e8f0 !important;
    border-radius: 18px !important;
    background: #ffffff !important;
    overflow: hidden !important;
    padding: 0 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-row,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-info-row {
    display: grid !important;
    grid-template-columns: 28px minmax(82px, .7fr) minmax(0, 1fr) !important;
    align-items: center !important;
    gap: 10px !important;
    min-height: 52px !important;
    padding: 10px 12px !important;
    border-bottom: 1px solid #eef2f7 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-row:last-child,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-info-row:last-of-type {
    border-bottom: 0 !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-step-spacer {
    width: 28px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-label {
    font-size: 9px !important;
    letter-spacing: .12em !important;
    color: #64748b !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-trigger {
    height: 42px !important;
    min-height: 42px !important;
    padding: 0 10px !important;
    border-radius: 13px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-trigger strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-info-row strong {
    font-size: 12.5px !important;
    line-height: 1.15 !important;
    min-width: 0 !important;
    white-space: normal !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-duration-control {
    justify-self: start !important;
    width: auto !important;
    min-width: 82px !important;
    height: 38px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-divider {
    display: none !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-availability-mini-pill {
    margin: 10px 12px 0 !important;
    justify-self: start !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-change {
    width: calc(100% - 24px) !important;
    margin: 10px 12px 12px !important;
    height: 38px !important;
    border-radius: 13px !important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-datetime-inline-warning {
    margin: 0 !important;
    padding: 10px 11px !important;
    border-radius: 15px !important;
  }

  .loop-accessible-modal-layer.is-nested-modal:has(.loop-payment-editor-modal) {
    align-items: flex-end !important;
    justify-content: center !important;
    padding: 0 10px 10px !important;
    background: rgba(15, 23, 42, 0.42) !important;
  }

  .loop-payment-editor-modal {
    width: calc(100vw - 20px) !important;
    max-width: calc(100vw - 20px) !important;
    max-height: min(78dvh, 680px) !important;
    border-radius: 24px !important;
    overflow: hidden !important;
    box-shadow: 0 -22px 70px rgba(15, 23, 42, 0.28) !important;
  }

  .loop-payment-editor-modal .loop-settings-modal-body {
    max-height: calc(78dvh - 82px) !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
  }
}



      `}</style>
      <style>{`
/* LOOP BOOKING MOBILE POPUP V46 — compact mobile/tablet action bar + payment chip restored */
.loop-mobile-submit-footer{display:none!important;}

@media(max-width:980px){
  .loop-accessible-modal-layer:has(.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1){
    align-items:center!important;
    justify-content:center!important;
    padding:10px!important;
    background:rgba(15,23,42,.42)!important;
  }

  .loop-create-modal-host:has(.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1){
    width:min(100%,760px)!important;
    max-width:calc(100vw - 20px)!important;
    height:auto!important;
    max-height:calc(100dvh - 20px)!important;
    margin:0 auto!important;
    border-radius:26px!important;
    overflow:hidden!important;
    background:transparent!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1{
    width:100%!important;
    max-width:760px!important;
    height:min(88dvh,820px)!important;
    max-height:calc(100dvh - 20px)!important;
    min-height:0!important;
    border-radius:26px!important;
    overflow:hidden!important;
    background:#f8fafc!important;
    border:1px solid rgba(203,213,225,.88)!important;
    box-shadow:0 22px 70px rgba(15,23,42,.24)!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-minimal-head{
    grid-template-columns:minmax(0,1fr) 40px!important;
    grid-template-areas:"copy close"!important;
    min-height:auto!important;
    max-height:none!important;
    padding:12px 12px 10px!important;
    gap:8px!important;
    overflow:visible!important;
    box-shadow:none!important;
    border-bottom:1px solid #e6edf3!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-actions-minimal{display:contents!important;}
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-header-summary-btn,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-more-actions-wrap{display:none!important;}
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-header-submit-btn{display:none!important;}

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-close{
    grid-area:close!important;
    position:relative!important;
    width:38px!important;
    height:38px!important;
    min-width:38px!important;
    align-self:start!important;
    justify-self:end!important;
    border-radius:999px!important;
    z-index:20!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-edit-title-row h2{
    font-size:clamp(20px,4.4vw,24px)!important;
    line-height:1.05!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    margin:0!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-copy p{
    font-size:11px!important;
    line-height:1.2!important;
    margin:4px 0 0!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal{
    grid-template-columns:repeat(3,minmax(0,1fr))!important;
    display:grid!important;
    gap:7px!important;
    padding:8px 10px!important;
    min-height:58px!important;
    background:#fff!important;
    border-bottom:1px solid #e6edf3!important;
    overflow-x:auto!important;
    overflow-y:visible!important;
    scrollbar-width:none!important;
    -webkit-overflow-scrolling:touch!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal::-webkit-scrollbar{display:none!important;}

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal>div,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal>button{
    display:grid!important;
    min-width:0!important;
    max-width:none!important;
    width:100%!important;
    height:42px!important;
    min-height:42px!important;
    padding:7px 8px 7px 32px!important;
    border-radius:14px!important;
    border:1px solid #dbe7ef!important;
    background:#fff!important;
    box-shadow:none!important;
    opacity:1!important;
    pointer-events:auto!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-ops-item,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-ops-item:disabled{
    display:grid!important;
    opacity:1!important;
    pointer-events:auto!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-ops-item:nth-child(3),
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ops-summary-btn{
    display:none!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal span{
    font-size:9px!important;
    letter-spacing:.08em!important;
    line-height:1!important;
    margin:0!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-mini-select-trigger{
    font-size:10.5px!important;
    line-height:1.1!important;
    min-width:0!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal em{
    top:-7px!important;
    right:7px!important;
    font-size:8.5px!important;
    padding:3px 6px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form.is-create-mode,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form.is-edit-mode{
    padding:12px 10px calc(74px + env(safe-area-inset-bottom,0px))!important;
    background:#f8fafc!important;
    overflow-y:auto!important;
    -webkit-overflow-scrolling:touch!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-section.loop-create-section{
    padding:14px!important;
    border-radius:20px!important;
    border:1px solid #dbe7ef!important;
    background:#fff!important;
    box-shadow:none!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section{
    padding:14px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-shell{
    display:grid!important;
    gap:10px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-rows,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block{
    display:grid!important;
    gap:8px!important;
    border:0!important;
    border-radius:0!important;
    background:transparent!important;
    padding:0!important;
    overflow:visible!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-row,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-info-row{
    display:grid!important;
    grid-template-columns:26px minmax(78px,.58fr) minmax(0,1fr)!important;
    align-items:center!important;
    gap:8px!important;
    min-height:46px!important;
    padding:10px 0!important;
    border:0!important;
    border-bottom:1px solid #edf2f7!important;
    background:transparent!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-info-row:last-of-type,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-row:last-child{
    border-bottom:0!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-label{
    font-size:9px!important;
    letter-spacing:.10em!important;
    color:#64748b!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-trigger{
    height:38px!important;
    min-height:38px!important;
    border-radius:13px!important;
    padding:0 9px!important;
    box-shadow:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-trigger strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-info-row strong{
    font-size:12px!important;
    line-height:1.15!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-duration-control{
    height:36px!important;
    min-width:78px!important;
    border-radius:999px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-availability-mini-pill{
    margin:8px 0 0!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-change{
    width:auto!important;
    margin:8px 0 0!important;
    height:36px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-datetime-inline-warning{
    margin:8px 0 0!important;
    padding:10px 11px!important;
    border-radius:15px!important;
  }

  .loop-mobile-submit-footer{
    position:absolute!important;
    left:0!important;
    right:0!important;
    bottom:0!important;
    z-index:35!important;
    display:block!important;
    padding:10px 12px calc(10px + env(safe-area-inset-bottom,0px))!important;
    background:rgba(255,255,255,.96)!important;
    border-top:1px solid #e6edf3!important;
    box-shadow:0 -12px 28px rgba(15,23,42,.08)!important;
  }
  .loop-mobile-submit-footer .loop-mobile-submit-btn{
    position:relative!important;
    left:auto!important;
    right:auto!important;
    bottom:auto!important;
    width:100%!important;
    height:44px!important;
    min-height:44px!important;
    border-radius:14px!important;
    font-size:13px!important;
    box-shadow:0 12px 24px rgba(0,103,95,.22)!important;
  }

  .loop-accessible-modal-layer.is-nested-modal:has(.loop-payment-editor-modal){
    align-items:flex-end!important;
    justify-content:center!important;
    padding:0 10px 10px!important;
    background:rgba(15,23,42,.42)!important;
  }
}

@media(min-width:981px){.loop-mobile-submit-footer{display:none!important;}}
      `}</style>
      <style>{`
/* LOOP BOOKING MOBILE HEADER V47 — clean header + three compact chips.
   Scope: mobile/tablet create/edit modal only. No booking/Firebase logic changes. */
@media(max-width:980px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-minimal-head{
    grid-template-columns:minmax(0,1fr) 40px!important;
    grid-template-areas:"copy close"!important;
    padding:16px 54px 12px 20px!important;
    min-height:86px!important;
    background:#ffffff!important;
    border-bottom:1px solid #e6edf3!important;
    overflow:visible!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-copy{
    grid-area:copy!important;
    min-width:0!important;
    width:100%!important;
    padding-left:0!important;
    margin-left:0!important;
    overflow:visible!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-kicker{
    display:block!important;
    margin:0 0 6px!important;
    padding:0!important;
    font-size:10px!important;
    line-height:1!important;
    letter-spacing:.15em!important;
    color:#00675f!important;
    white-space:nowrap!important;
    overflow:visible!important;
    text-overflow:clip!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-edit-title-row{
    display:flex!important;
    align-items:center!important;
    min-width:0!important;
    margin:0!important;
    padding:0!important;
    overflow:visible!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-edit-title-row h2{
    display:block!important;
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
    margin:0!important;
    color:#0f172a!important;
    font-size:clamp(19px,4.35vw,23px)!important;
    line-height:1.05!important;
    font-weight:950!important;
    letter-spacing:-.04em!important;
    white-space:nowrap!important;
    overflow:visible!important;
    text-overflow:clip!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-copy p{
    margin:5px 0 0!important;
    padding:0!important;
    font-size:11px!important;
    line-height:1.2!important;
    font-weight:850!important;
    color:#64748b!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-close{
    grid-area:close!important;
    position:absolute!important;
    top:10px!important;
    right:10px!important;
    width:38px!important;
    height:38px!important;
    min-width:38px!important;
    border-radius:999px!important;
    z-index:2147483400!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal{
    grid-template-columns:minmax(104px,.88fr) minmax(132px,1.16fr) minmax(104px,.88fr)!important;
    grid-auto-rows:46px!important;
    align-items:stretch!important;
    gap:8px!important;
    padding:10px 12px!important;
    min-height:66px!important;
    height:auto!important;
    margin:0!important;
    background:#ffffff!important;
    border-bottom:1px solid #e6edf3!important;
    overflow:visible!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > .loop-ops-summary-btn,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > button.loop-ops-summary-btn{
    display:none!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > .loop-ops-item:nth-child(3){
    display:none!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > div,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > button{
    width:100%!important;
    min-width:0!important;
    height:46px!important;
    min-height:46px!important;
    max-height:46px!important;
    padding:7px 8px 7px 31px!important;
    border-radius:14px!important;
    overflow:hidden!important;
    box-shadow:none!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > div::before,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > button::before{
    left:9px!important;
    width:18px!important;
    height:18px!important;
    font-size:10px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal span{
    display:block!important;
    margin:0 0 3px!important;
    font-size:9px!important;
    line-height:1!important;
    letter-spacing:.10em!important;
    font-weight:950!important;
    color:#64748b!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-mini-select-trigger,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-mini-select-trigger span{
    display:block!important;
    width:100%!important;
    max-width:100%!important;
    min-width:0!important;
    font-size:11px!important;
    line-height:1.12!important;
    font-weight:950!important;
    color:#0f172a!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    text-transform:none!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-status-select,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-status-select .loop-mini-select-trigger{
    position:relative!important;
    width:100%!important;
    min-width:0!important;
    padding-right:12px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-status-select .loop-mini-select-trigger i{
    right:0!important;
    bottom:3px!important;
    transform:rotate(45deg) scale(.82)!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-ops-item strong{
    font-size:10.6px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal em,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-ops-item em,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > button:nth-child(4) em{
    display:none!important;
  }
}

@media(max-width:380px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal{
    grid-template-columns:minmax(96px,.9fr) minmax(122px,1.2fr) minmax(96px,.9fr)!important;
    gap:6px!important;
    padding-left:9px!important;
    padding-right:9px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-mini-select-trigger,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-mini-select-trigger span{
    font-size:10.2px!important;
  }
}
      `}</style>

      <style>{`
/* LOOP BOOKING MOBILE HEADER V48 — status chip cleanup + centered picker.
   Scope: mobile/tablet booking create/edit modal only. */
@media(max-width:980px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal{
    grid-template-columns:minmax(0,1fr) minmax(0,1.18fr) minmax(0,1fr)!important;
    gap:8px!important;
  }

  /* Status chip: remove decorative circles/chevrons so the actual state fits. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > .loop-status-ops-item{
    display:flex!important;
    flex-direction:column!important;
    align-items:flex-start!important;
    justify-content:center!important;
    gap:2px!important;
    padding:7px 10px!important;
    overflow:hidden!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-status-ops-item::before{
    display:none!important;
    content:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-status-ops-item > span{
    position:static!important;
    left:auto!important;
    top:auto!important;
    right:auto!important;
    bottom:auto!important;
    margin:0!important;
    width:100%!important;
    max-width:100%!important;
    display:block!important;
    color:#64748b!important;
    font-size:9px!important;
    line-height:1!important;
    font-weight:950!important;
    letter-spacing:.10em!important;
    text-transform:uppercase!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    z-index:1!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-status-ops-item .loop-mini-select{
    position:static!important;
    inset:auto!important;
    width:100%!important;
    min-width:0!important;
    height:auto!important;
    min-height:0!important;
    z-index:1!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-status-ops-item .loop-mini-select-trigger{
    position:static!important;
    inset:auto!important;
    width:100%!important;
    height:auto!important;
    min-height:0!important;
    display:block!important;
    padding:0!important;
    margin:0!important;
    border:0!important;
    background:transparent!important;
    box-shadow:none!important;
    text-align:left!important;
    overflow:visible!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-status-ops-item .loop-mini-select-trigger span{
    position:static!important;
    left:auto!important;
    top:auto!important;
    right:auto!important;
    bottom:auto!important;
    width:100%!important;
    max-width:100%!important;
    display:block!important;
    color:#0f172a!important;
    font-size:11.4px!important;
    line-height:1.12!important;
    font-weight:950!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    text-transform:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-status-ops-item .loop-mini-select-trigger i{
    display:none!important;
  }

  /* Status options: show as a centered mobile picker instead of dropping below the chip. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-status-ops-item .loop-mini-select-menu{
    position:fixed!important;
    left:50%!important;
    top:50%!important;
    right:auto!important;
    bottom:auto!important;
    transform:translate(-50%,-50%)!important;
    width:min(360px,calc(100vw - 28px))!important;
    min-width:0!important;
    max-width:calc(100vw - 28px)!important;
    max-height:min(420px,calc(100dvh - 120px))!important;
    overflow:auto!important;
    z-index:2147483647!important;
    padding:10px!important;
    border:1px solid #dbe7ef!important;
    border-radius:24px!important;
    background:#ffffff!important;
    box-shadow:0 28px 80px rgba(15,23,42,.24)!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-status-ops-item .loop-mini-select-menu button{
    min-height:44px!important;
    border-radius:14px!important;
    padding:0 13px!important;
    display:flex!important;
    align-items:center!important;
    gap:10px!important;
    color:#0f172a!important;
    font-size:13px!important;
    line-height:1!important;
    font-weight:950!important;
    background:#ffffff!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-status-ops-item .loop-mini-select-menu button.is-selected{
    background:#ecfdf5!important;
    color:#00675f!important;
  }

  /* Keep the other top chips readable and calm. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-ops-item,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > button:nth-child(4){
    padding-left:31px!important;
    padding-right:9px!important;
  }
}

@media(max-width:380px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal{
    grid-template-columns:minmax(0,.95fr) minmax(0,1.22fr) minmax(0,.95fr)!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-status-ops-item .loop-mini-select-trigger span{
    font-size:10.8px!important;
  }
}
      `}</style>


      <style>{`
/* LOOP BOOKING MOBILE SERVICE DATE V49 — app/web parity for service + fecha card.
   Goal: compact rows, one divider only between Servicio and Fecha, matching web minimal layout. */
@media(max-width:980px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section{
    padding:12px 14px 13px!important;
    border-radius:20px!important;
    min-height:0!important;
    height:auto!important;
    overflow:visible!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-ordered-shell,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-shell{
    display:grid!important;
    gap:0!important;
    height:auto!important;
    align-content:start!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-rows,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block{
    display:grid!important;
    gap:0!important;
    padding:0!important;
    margin:0!important;
    border:0!important;
    background:transparent!important;
    min-height:0!important;
  }

  /* Only visible separator between service/duration and date/time/worker. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-divider,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-divider{
    display:block!important;
    height:1px!important;
    min-height:1px!important;
    margin:8px 0 9px!important;
    border:0!important;
    background:#e8eef4!important;
    opacity:1!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-row,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-info-row{
    display:grid!important;
    grid-template-columns:26px 82px minmax(0,1fr)!important;
    align-items:center!important;
    column-gap:8px!important;
    min-height:34px!important;
    height:auto!important;
    padding:3px 0!important;
    margin:0!important;
    border:0!important;
    border-bottom:0!important;
    background:transparent!important;
    box-shadow:none!important;
  }

  /* Remove every internal divider inside Fecha; the card should breathe like web. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block .loop-ordered-info-row,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-simple-date-rows .loop-simple-info-row,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-row,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-simple-info-row{
    border:0!important;
    border-bottom:0!important;
    min-height:31px!important;
    padding-top:2px!important;
    padding-bottom:2px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-workspace-step,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-workspace-step{
    width:24px!important;
    height:24px!important;
    min-width:24px!important;
    font-size:10.5px!important;
    align-self:center!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-label,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-simple-row > span,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-simple-info-row > span{
    font-size:9px!important;
    line-height:1!important;
    letter-spacing:.105em!important;
    font-weight:950!important;
    color:#64748b!important;
    text-transform:uppercase!important;
    white-space:nowrap!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-trigger{
    min-height:36px!important;
    height:36px!important;
    border-radius:13px!important;
    padding:0 7px 0 10px!important;
    box-shadow:none!important;
    background:#fff!important;
    border:1px solid #dbe7ef!important;
    display:flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:8px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-trigger strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-info-row strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-simple-info-row strong{
    font-size:12.6px!important;
    line-height:1.12!important;
    font-weight:900!important;
    letter-spacing:-.01em!important;
    color:#0f172a!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-trigger em{
    height:26px!important;
    min-height:26px!important;
    padding:0 10px!important;
    border-radius:999px!important;
    font-size:10.5px!important;
    line-height:1!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    flex:0 0 auto!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-duration-control,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-inline-duration-control{
    height:34px!important;
    min-height:34px!important;
    min-width:86px!important;
    width:max-content!important;
    padding:0 12px!important;
    border-radius:999px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    gap:6px!important;
    justify-self:start!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-duration-control input,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-inline-duration-control input{
    width:34px!important;
    min-width:34px!important;
    font-size:13px!important;
    line-height:1!important;
    text-align:center!important;
    font-weight:900!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-duration-control em,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-inline-duration-control em{
    font-size:10.5px!important;
    font-weight:950!important;
    line-height:1!important;
    color:#00675f!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-mode-mini-pill{
    min-height:20px!important;
    height:20px!important;
    padding:0 8px!important;
    border-radius:999px!important;
    font-size:9.5px!important;
    line-height:1!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    justify-self:start!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-availability,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-availability-mini-pill{
    position:static!important;
    justify-self:start!important;
    width:max-content!important;
    min-height:22px!important;
    height:22px!important;
    margin:6px 0 0 2px!important;
    padding:0 9px!important;
    border-radius:999px!important;
    font-size:9.6px!important;
    line-height:1!important;
    font-weight:950!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-change,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-simple-date-change{
    position:static!important;
    justify-self:start!important;
    min-height:34px!important;
    height:34px!important;
    width:max-content!important;
    margin:8px 0 0 2px!important;
    padding:0 13px!important;
    border-radius:12px!important;
    font-size:11px!important;
    line-height:1!important;
    font-weight:950!important;
  }

  /* Phone-sized version should not expand the date card vertically. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block{
    padding-right:0!important;
    gap:0!important;
  }
}

@media(min-width:600px) and (max-width:980px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section{
    padding:13px 18px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-service-row,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-info-row{
    grid-template-columns:26px 96px minmax(0,1fr)!important;
    min-height:32px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-availability{
    margin-top:4px!important;
  }
}
      `}</style>
      <div className={`booking-pro-modal loop-create-pro-modal loop-reservation-workspace-v1 ${!editMode ? "is-create-workspace-v3 is-create-workspace-v4" : ""} is-manage-overview-v2`}>
        <header className="booking-pro-head loop-create-pro-head loop-booking-minimal-head">
          <div className="loop-booking-hero-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M7 3v4M17 3v4M4.5 8.5h15" />
              <rect x="4" y="5" width="16" height="15.5" rx="3" />
              <path d="M8 12h.01M12 12h.01M16 12h.01M8 16h.01M12 16h.01" />
            </svg>
          </div>
          <div className="loop-booking-head-copy">
            <div className="booking-pro-kicker">{editMode ? "Gestión de reserva" : eventMode ? "Nueva reserva de evento" : "Nueva reserva"}</div>
            <div className="booking-edit-title-row">
              <h2 id="modal-title">{editMode ? `${effectiveCustomerName || customerName || "Cliente"} · ${selectedServiceLabel || "Reserva"}` : t("Crear reserva")}</h2>
              {editMode ? <span className={`booking-edit-status-pill is-${bookingEditStatusInfo(bookingStatus).tone}`}>{bookingEditStatusInfo(bookingStatus).label}</span> : null}
            </div>
            <p>{editMode ? [humanDateLabel || "Fecha pendiente", bookingTimeLabel || "", selectedWorkerLabel || ""].filter(Boolean).join(" · ") : (humanDateLabel || "Completa los datos para agendar una nueva reserva.")}</p>
          </div>

          <div className="loop-booking-head-actions loop-booking-head-actions-minimal">
            {!editMode ? (
              <button type="button" className="booking-pro-secondary loop-header-summary-btn" onClick={() => setShowReservationSummary(true)}>
                <span className="loop-header-summary-icon" aria-hidden="true">☷</span>{t("Ver resumen")}</button>
            ) : null}
            <button type="submit" form="loop-booking-create-form" disabled={saving || hasBlockingScheduleIssueForSave || (!serviceId && !isDeleteReservationStatus)} className="booking-pro-primary loop-header-submit-btn">
              {saving ? (isDeleteReservationStatus ? "Quitando..." : editMode ? t("Guardando...") : "Creando...") : isDeleteReservationStatus ? t("Quitar de agenda") : hasBlockingScheduleIssueForSave ? "Corrige disponibilidad" : (editMode ? "Guardar cambios" : t("Crear reserva"))}
            </button>
            {editMode ? (
              <div className="loop-more-actions-wrap" ref={moreActionsRef}>
                <button type="button" className="booking-pro-secondary loop-more-actions-btn" onClick={() => setShowMoreActions((v) => !v)} aria-expanded={showMoreActions}>{t("Más acciones")}</button>
                {showMoreActions ? (
                  <div className="loop-more-actions-menu" role="menu">
                    {canSendManualWhatsApp ? (
                      <button type="button" disabled={whatsappManualSending || smartWhatsAppAction.disabled} onClick={() => { setShowMoreActions(false); handleSendManualWhatsApp(); }}>
                        {whatsappManualSending ? "Enviando WhatsApp..." : smartWhatsAppAction.label}
                      </button>
                    ) : null}
                    <button type="button" onClick={() => { setShowMoreActions(false); setShowWhatsAppDetails(true); }}>{t("Ver historial WhatsApp")}</button>
                    {businessId && initialBooking?.id ? (
                      <button type="button" disabled={loyaltyReprocessSending || saving} onClick={() => { setShowMoreActions(false); handleReprocessLoyalty(); }}>
                        {loyaltyReprocessSending ? "Reprocesando fidelidad..." : "Reprocesar fidelidad"}
                      </button>
                    ) : null}
                    {canReverseLoyaltyForEdit ? (
                      <button type="button" className="is-danger" disabled={loyaltyReverseSending || saving} onClick={() => { setShowMoreActions(false); handleReverseLoyaltyRedemption(); }}>
                        {loyaltyReverseSending ? "Deshaciendo fidelidad..." : "Deshacer fidelidad"}
                      </button>
                    ) : null}
                    {editMode && loyaltyRewardAppliedForEdit && loyaltyWorkerPayoutPaidForEdit ? (
                      <button type="button" disabled title={t("La comisión del trabajador ya fue marcada como pagada")}>{t("Fidelidad no reversible")}</button>
                    ) : null}
                    {LOOP_BOOKING_MEMBERSHIPS_ENABLED && businessId && initialBooking?.id ? (
                      <button type="button" disabled={membershipReprocessSending || saving} onClick={() => { setShowMoreActions(false); handleReprocessMembershipBenefit(); }}>
                        {membershipReprocessSending ? "Procesando membresía..." : "Procesar membresía"}
                      </button>
                    ) : null}
                    <button type="button" className="is-danger" onClick={() => { setShowMoreActions(false); setBookingStatus(BOOKING_MODAL_DELETE_SENTINEL); }}>{t("Quitar de agenda")}</button>
                  </div>
                ) : null}
              </div>
            ) : null}
            <button onClick={onClose} className="booking-pro-close" type="button" aria-label={t("Cerrar")}>✕</button>
          </div>
        </header>

        {whatsappManualStatus ? (
          <div className={`loop-whatsapp-manual-status ${/rechaz|error|no pude|falta|permiso/i.test(whatsappManualStatus) ? "is-error" : "is-ok"}`} role="status">
            {whatsappManualStatus}
          </div>
        ) : null}

        {loyaltyReprocessStatus ? (
          <div className={`loop-whatsapp-manual-status ${/no pude|no se aplic|error|falta/i.test(loyaltyReprocessStatus) ? "is-error" : "is-ok"}`} role="status">
            {loyaltyReprocessStatus}
          </div>
        ) : null}

        {loyaltyReverseStatus ? (
          <div className={`loop-whatsapp-manual-status ${/no pude|no puedes|error|falta/i.test(loyaltyReverseStatus) ? "is-error" : "is-ok"}`} role="status">
            {loyaltyReverseStatus}
          </div>
        ) : null}

        {LOOP_BOOKING_MEMBERSHIPS_ENABLED && membershipReprocessStatus ? (
          <div className={`loop-whatsapp-manual-status ${/no pude|no se aplic|error|falta/i.test(membershipReprocessStatus) ? "is-error" : "is-ok"}`} role="status">
            {membershipReprocessStatus}
          </div>
        ) : null}

        {LOOP_BOOKING_MEMBERSHIPS_ENABLED && membershipCreditManualOnlyNotice ? (
          <div className="loop-whatsapp-manual-status is-error" role="status">
            {membershipCreditManualOnlyNotice}
          </div>
        ) : null}

        {/* LOOP BOOKING UX V2 A2.1B — create-mode quickbar.
            The previous A2.1 reused legacy smart-chip CSS and collapsed visually.
            Create mode now has its own flat, full-width status / collection bar.
            Edit mode keeps the existing operational strip until A2.2. */}
        {!editMode ? (
          <section
            className={`loop-booking-create-quickbar loop-booking-create-quickbar-v3 ${LOOP_BOOKING_MEMBERSHIPS_ENABLED ? "has-membership" : ""}`}
            aria-label={t("Estado y cobro de la reserva")}
          >
            <div className="loop-booking-create-quick-status">
              <span>{t("Estado")}</span>
              <LoopMiniSelect
                value={bookingStatus}
                options={bookingStatusOptionsForModal}
                onChange={setBookingStatus}
                ariaLabel="Cambiar estado"
                className="loop-create-status-select"
              />
            </div>

            {canShowBookingPaymentUi ? (
              <button
                type="button"
                className={`loop-booking-create-quick-payment is-payment-status-${String(paymentStatus || "pending").replace(/_/g, "-")}`}
                onClick={() => initialBooking?.sessionProgramId ? setSessionPaymentsOpen(true) : setShowPaymentSettingsModal(true)}
              >
                <div>
                  <span>{t("Cobro")}</span>
                  <strong>{bookingPaymentCompactLabel}</strong>
                </div>
                <em>{initialBooking?.sessionProgramId ? "Ver cobros del paquete" : canEditPayments ? (String(paymentStatus || "pending").toLowerCase() === "pending" ? "Registrar cobro" : "Editar cobro") : "Ver cobro"}</em>
              </button>
            ) : null}

            {LOOP_BOOKING_MEMBERSHIPS_ENABLED ? (
              <button
                type="button"
                className="loop-booking-create-quick-membership"
                onClick={() => setShowMembershipDetails(true)}
              >
                <div>
                  <span>{t("Membresía")}</span>
                  <strong>{membershipSummary ? membershipSummary.stateLabel : effectiveSelectedMembershipBenefit ? "Beneficio seleccionado" : "No aplicada"}</strong>
                </div>
                <em>{t("Ver")}</em>
              </button>
            ) : null}
          </section>
        ) : (
          /* LOOP BOOKING UX V2 A2.1D — edit/manage mode gets its own dedicated
             quickbar too. This removes the legacy smart-strip CSS collision while
             preserving every existing action and permission gate. */
          <section className="loop-booking-manage-quickbar" aria-label={t("Gestión rápida de la reserva")}>
            <div className="loop-booking-manage-quick-status">
              <span>{t("Estado")}</span>
              <LoopMiniSelect
                value={bookingStatus}
                options={bookingStatusOptionsForModal}
                onChange={setBookingStatus}
                ariaLabel="Cambiar estado"
                className="loop-manage-status-select"
              />
            </div>

            {canShowBookingPaymentUi ? (
              <button
                type="button"
                className={`loop-booking-manage-quick-payment is-payment-status-${String(paymentStatus || "pending").replace(/_/g, "-")}`}
                onClick={() => initialBooking?.sessionProgramId ? setSessionPaymentsOpen(true) : setShowPaymentSettingsModal(true)}
              >
                <div>
                  <span>{t("Cobro")}</span>
                  <strong>{bookingPaymentCompactLabel}</strong>
                </div>
                <em>{initialBooking?.sessionProgramId ? "Ver cobros del paquete" : canEditPayments ? (String(paymentStatus || "pending").toLowerCase() === "pending" ? "Registrar cobro" : "Editar cobro") : "Ver cobro"}</em>
              </button>
            ) : null}

            <div className="loop-booking-manage-quick-actions">
              <button type="button" onClick={() => setShowWhatsAppDetails(true)}>
                <span>WhatsApp</span>
                <strong>{whatsappReady.label}{whatsappLogRows.length ? ` · ${whatsappLogRows.length}` : ""}</strong>
              </button>

              {LOOP_BOOKING_MEMBERSHIPS_ENABLED ? (
                <button type="button" onClick={() => setShowMembershipDetails(true)}>
                  <span>{t("Membresía")}</span>
                  <strong>{membershipSummary ? membershipSummary.stateLabel : effectiveSelectedMembershipBenefit ? "Beneficio seleccionado" : "No aplicada"}</strong>
                </button>
              ) : null}

              <button type="button" onClick={() => setShowReservationSummary(true)}>
                <span>{t("Resumen")}</span>
                <strong>{t("Ver detalles")}</strong>
              </button>
            </div>
          </section>
        )}

        <form id="loop-booking-create-form" onSubmit={handleSubmit} className="loop-inline-reservation-form">
          <section className="loop-manage-overview-v2" aria-label={editMode ? "Gestión de reserva" : t("Crear reserva")}>

            <div className="loop-manage-overview-grid">
              <article className="loop-manage-overview-card is-customer">
                <div className="loop-manage-overview-card-head"><span>Cliente</span><button type="button" onClick={() => {if (editMode) {setInlineCustomerEditing(true);setInlineContactEditing(false);return;}setInlineCustomerEditing(true);setInlineContactEditing(true);selectedCustomerDraftRef.current=null;setCustomerId(null);setCustomerSnapshot(null);setCustomerName("");setCustomerEmail("");setCustomerPhone("");setCustomerWhatsappOptOut(false);}}>{editMode ? "Cambiar cliente" : "+ Nuevo cliente"}</button></div>
                {customerName && !inlineCustomerEditing ? <><strong>{effectiveCustomerName || customerName}</strong><small>{manageCustomerMeta}</small></> : null}
                {inlineCustomerEditing || !customerName ? <div className="loop-inline-fields">
                {!inlineContactEditing ? <div className="loop-customer-minimal-search">
                <CustomerPicker
                  businessId={businessId}
                  value={customerName}
                  email={customerEmail}
                  phone={customerPhone}
                  maxVisibleResults={5}
                  canViewCustomerPhone={isOwnerOrAdmin || modalPermissions.canViewCustomerPhone}
                  canViewCustomerEmail={isOwnerOrAdmin || modalPermissions.canViewCustomerEmail}
                  onUseNewCustomer={({ customerName: typedCustomerName }) => {
                    const nextName = String(typedCustomerName || customerName || "").trim();
                    if (nextName) setCustomerName(nextName);
                    selectedCustomerDraftRef.current = null;
                    setCustomerId(null);
                    setCustomerEmail("");
                    setCustomerPhone("");
                    setCustomerSnapshot(null);
                    setCustomerWhatsappOptOut(false);
                    setCustomerProfileEditMode(false);
                    setCustomerProfileEditError("");
                    setShowCustomerDetails(false);
                    setInlineCustomerEditing(true);
                    setInlineContactEditing(true);
                  }}
                  onChange={(next) => {
                    setInlineCustomerEditing(true);
                    const rawSnapshot = next.customerSnapshot && typeof next.customerSnapshot === "object" ? next.customerSnapshot : null;
                    const nextName = bookingModalCustomerDisplayName(next, rawSnapshot, customerName);
                    const incomingCustomerId = String(next.customerId || rawSnapshot?.id || rawSnapshot?.customerId || rawSnapshot?.clientId || "").trim();
                    const pickedExistingCustomer = Boolean(incomingCustomerId || rawSnapshot);
                    const nextSnapshot = pickedExistingCustomer
                      ? bookingModalNormalizeCustomerSnapshot(rawSnapshot || (incomingCustomerId ? { id: incomingCustomerId, customerId: incomingCustomerId } : null), nextName)
                      : null;
                    const previousDraft = bookingModalNormalizeSelectedCustomerDraft(selectedCustomerDraftRef.current, customerName);
                    const previousSnapshot = customerSnapshot && typeof customerSnapshot === "object" ? customerSnapshot : {};
                    const previousName = bookingModalCustomerDisplayName(previousDraft, previousSnapshot, customerName);
                    const typedMatchesPreviousSelection = Boolean(previousDraft?.customerName && nextName && normalizeText(nextName) === normalizeText(previousDraft.customerName));
                    const typedDifferentCustomer = !pickedExistingCustomer && Boolean(customerId || customerSnapshot || previousDraft) && !typedMatchesPreviousSelection && normalizeText(nextName) !== normalizeText(previousName);

                    bookingModalRecordCustomerDebug("picker-change", {
                      incomingCustomerId: incomingCustomerId || null,
                      incomingCustomerName: next.customerName || "",
                      resolvedCustomerName: nextName,
                      snapshotName: rawSnapshot?.name || rawSnapshot?.customerName || rawSnapshot?.displayName || "",
                      pickedExistingCustomer,
                      preservedPreviousSelection: !pickedExistingCustomer && typedMatchesPreviousSelection,
                    });

                    if (pickedExistingCustomer) {
                      setInlineCustomerEditing(false);
                      setInlineContactEditing(false);
                      const selectedDraft = bookingModalNormalizeSelectedCustomerDraft({
                        customerId: incomingCustomerId || nextSnapshot?.id || nextSnapshot?.customerId || null,
                        customerName: nextName,
                        customerEmail: next.customerEmail || nextSnapshot?.emailNormalized || nextSnapshot?.email || nextSnapshot?.customerEmail || "",
                        customerPhone: next.customerPhone || nextSnapshot?.phoneDisplay || nextSnapshot?.phone || nextSnapshot?.phoneNormalized || nextSnapshot?.phoneE164 || "",
                        customerSnapshot: nextSnapshot,
                      }, nextName);
                      selectedCustomerDraftRef.current = selectedDraft;
                      setCustomerId(selectedDraft?.customerId || null);
                      setCustomerName(selectedDraft?.customerName || nextName);
                      setCustomerEmail(selectedDraft?.customerEmail || "");
                      setCustomerPhone(bookingModalPhoneForStorage(selectedDraft?.customerPhone || ""));
                      setCustomerSnapshot(selectedDraft?.customerSnapshot || nextSnapshot);
                      setCustomerWhatsappOptOut(nextSnapshot?.whatsappOptOut === true || nextSnapshot?.whatsappOptIn === false || nextSnapshot?.customerWhatsappConsent === false);
                      return;
                    }

                    setCustomerName(nextName);
                    if (typedMatchesPreviousSelection && previousDraft) {
                      // Some mobile/browser flows emit a plain input change immediately after
                      // picking a result. Keep the selected customer's hidden identity intact.
                      setCustomerId(previousDraft.customerId || null);
                      setCustomerEmail(previousDraft.customerEmail || "");
                      setCustomerPhone(bookingModalPhoneForStorage(previousDraft.customerPhone || ""));
                      setCustomerSnapshot(previousDraft.customerSnapshot || null);
                      setCustomerWhatsappOptOut(previousDraft.customerSnapshot?.whatsappOptOut === true || previousDraft.customerSnapshot?.whatsappOptIn === false || previousDraft.customerSnapshot?.customerWhatsappConsent === false);
                      return;
                    }

                    // Free-typing after selecting a saved customer means the draft is a new
                    // customer candidate. Do not keep the previous customer's email/WhatsApp
                    // in "Ver perfil" or in the booking payload by accident.
                    if (typedDifferentCustomer) selectedCustomerDraftRef.current = null;
                    setCustomerId(null);
                    setCustomerEmail(typedDifferentCustomer ? "" : (next.customerEmail || customerEmail || ""));
                    setCustomerPhone(typedDifferentCustomer ? "" : bookingModalPhoneForStorage(next.customerPhone || customerPhone || ""));
                    setCustomerSnapshot(null);
                    setCustomerWhatsappOptOut(false);
                  }}
                />
              </div> : null}
              {inlineContactEditing && !customerHasLoopAccount ? <div className="loop-customer-minimal-fields">
                <label className="booking-pro-field"><span>{t("Nombre del cliente")}</span><input aria-label={t("Nombre del cliente")} value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder={t("Nombre y apellido")} /></label>
                <label className="booking-pro-field">
                  <span>Email</span>
                  <input ref={customerEmailInputRef} value={customerEmail} onPointerDown={stopBookingInputPointerPropagation} onTouchStart={stopBookingInputPointerPropagation} onFocus={stabilizeBookingMobileInput} onChange={(e) => setCustomerEmail(e.target.value)} placeholder="cliente@ejemplo.com" inputMode="email" autoComplete="email" enterKeyHint="next" />
                </label>
                <label className="booking-pro-field loop-phone-field">
                  <span>WhatsApp</span>
                  <LoopPhoneInput
                    value={customerPhone}
                    onPointerDown={stopBookingInputPointerPropagation}
                    onTouchStart={stopBookingInputPointerPropagation}
                    onFocus={stabilizeBookingMobileInput}
                    onChange={setCustomerPhone}
                  />
                  <small className={`booking-whatsapp-readiness ${whatsappReady.tone}`}>{whatsappReady.label} · {whatsappReady.helper}</small>
                </label>
              <button type="button" className="loop-contact-done" onClick={() => {setInlineContactEditing(false);setInlineCustomerEditing(false);}}>{t("Listo")}</button>
              </div> : null}

</div> : null}
                {customerName && !inlineCustomerEditing ? <div className="loop-manage-overview-card-actions">{customerId ? <button type="button" onClick={() => setShowCustomerDetails(true)}>{t("Ver perfil")}</button> : null}{!customerHasLoopAccount ? <button type="button" onClick={() => {setInlineCustomerEditing(true);setInlineContactEditing(true);}}>{t("Editar contacto")}</button> : null}</div> : null}
              </article>
              <article className="loop-manage-overview-card is-service" onKeyDown={e => {if(e.key === "Escape") {e.stopPropagation();setShowAllServices(false);}}} onBlur={e => {if(!e.currentTarget.contains(e.relatedTarget)) setShowAllServices(false);}}>
                <div className="loop-manage-overview-card-head"><span>Servicio</span><button type="button" aria-expanded={showAllServices} onClick={() => {setServiceSearch("");setShowAllServices(v => !v);}}>{showAllServices ? t("Listo") : serviceId ? t("Cambiar") : "Elegir servicio"}</button></div>
                {serviceId ? <strong>{selectedServiceLabel}</strong> : <p className="loop-service-empty-prompt">{t("Elige el servicio para esta reserva")}</p>}{showEventPackageFlow ? <small>{manageServiceMeta}</small> : null}
                {selectedService?.sessionPackage?.enabled && !editMode && <SessionProgramPicker currency={businessDoc?.currency} state={sessionPrograms} program={sessionProgram} action={sessionAction} onAction={setSessionAction} previous={sessionPrevious} onPrevious={setSessionPrevious} total={continuingSession ? sessionProgram.totalSessions : selectedService.sessionPackage.totalSessions} price={selectedService.price} confirmed={sessionNewConfirmed} onConfirm={setSessionNewConfirmed}/> }
                {initialBooking?.sessionProgramId && <><p>{t("Sesión") + " "}{initialBooking.sessionNumber} de {initialBooking.sessionTotal} · {initialBooking.sessionBillingBookingId!==initialBooking.id?'Incluida en el paquete':'Reserva inicial del paquete'}</p><button type="button" onClick={()=>setSessionPaymentsOpen(true)}>{t("Ver saldo y cobros del paquete")}</button></>}
                {sessionPaymentsOpen && initialBooking?.sessionProgramId && <AccessibleModal open={sessionPaymentsOpen} onClose={()=>setSessionPaymentsOpen(false)} labelledBy="session-payments-title" width={650}><h3 id="session-payments-title">{t("Cobros del paquete")}</h3><button type="button" onClick={()=>setSessionPaymentsOpen(false)}>{t("Cerrar")}</button><CustomerSessionPrograms businessId={businessId} customerId={customerId} programId={initialBooking.sessionProgramId}/></AccessibleModal>}
                {serviceSelectionUnavailable && <p role="status" className="loop-service-empty-prompt">{t("El servicio seleccionado requiere revisión. Comprueba el trabajador o elige otro servicio.")}</p>}
                <div className="loop-inline-fields">                      {showAllServices ? (
                        <div className="loop-service-combobox-panel loop-unified-service-menu loop-ordered-service-menu loop-summary-service-menu">
                          <div className="loop-service-combobox-search">
                            <span>⌕</span>
                            <input
                              value={serviceSearch}
                              onChange={(e) => setServiceSearch(e.target.value)}
                              placeholder={t("Buscar servicio")}
                            />
                          </div>
                          <div className="loop-service-combobox-list" role="listbox" aria-label={t("Servicios disponibles")}>
                            {bookingModalServicesLoading ? (
                              <div className="loop-service-empty-state">
                                <strong>{t("Cargando servicios...")}</strong>
                                <span>{t("Estamos sincronizando los servicios del negocio.")}</span>
                              </div>
                            ) : serviceSearchResults.length ? serviceSearchResults.map((s) => {
                              const active = String(serviceId) === String(s.id);
                              const price = asMoneyNumber(s.price ?? s.basePrice ?? s.durationPrice, 0);
                              const dur = Number(s.duration || s.durationMinutes || s.defaultDuration || 0);
                              return (
                                <button
                                  key={s.id}
                                  type="button"
                                  className={`loop-service-combobox-option ${active ? "is-active" : ""}`}
                                  onClick={() => {
                                    setServiceId(s.id);
                                    setPriceOverride(null);setPriceAdjustmentDraft(null);
                                    if (!showEventPackageFlow && dur > 0) setDuration(dur);
                                    setServiceSearch("");
                                    setShowAllServices(false);
                                    if (paymentStatus === "paid") {
                                      autoAmountChargedRef.current = "";
                                    }
                                  }}
                                >
                                  <span className="loop-service-option-check">{active ? "✓" : ""}</span>
                                  <strong>{s.name || s.title || s.id}</strong>
                                  <small>{dur ? `${dur} min` : "Flexible"}{price ? ` · $${price}` : ""}</small>
                                </button>
                              );
                            }) : (
                              <div className="loop-service-empty-state">
                                <strong>{t("No encontré servicios")}</strong>
                                <span>{t("Prueba con otro nombre o revisa los servicios asignados al trabajador.")}</span>
                              </div>
                            )}
                          </div>
                          <div className="loop-service-combobox-foot">
                            {bookingModalServicesLoading
                              ? t("Cargando servicios...")
                              : `${serviceSearchResults.length} servicio${serviceSearchResults.length === 1 ? "" : "s"} disponible${serviceSearchResults.length === 1 ? "" : "s"}`}
                          </div>
                        </div>
                      ) : null}
</div>
                {serviceId && !showEventPackageFlow ? <label className="loop-inline-duration"><span>{t("Duración")}</span><span className="loop-duration-value"><input aria-label={t("Duración en minutos")} type="number" min="5" step="5" value={duration} onChange={e => setDuration(Number(e.target.value)||60)} /><span>min</span></span></label> : null}
                <div className="loop-manage-overview-service-note"><span>{t("Total estimado")}</span><b>{bookingCompactMoney(totalPrice)}</b></div>
              </article>
              <article className="loop-manage-overview-card is-agenda">
                <div className="loop-manage-overview-card-head"><span>Agenda</span><button type="button" aria-expanded={showDateTimeModal} onClick={() => setShowDateTimeModal(v => !v)}>{showDateTimeModal ? t("Listo") : "Cambiar horario"}</button></div>
                <strong>{activeBookingDate ? activeBookingDate.toLocaleDateString(interfaceLocale(), {weekday:"short",day:"numeric",month:"short",year:"numeric"}) : "Fecha pendiente"}</strong><small>{manageAgendaMeta || "Horario por confirmar"}</small>
                {!showEventPackageFlow ? <em className={`loop-manage-overview-availability ${availabilityTone}`}>{availabilityTitle}</em> : null}
                <div className="loop-agenda-worker-control">                {!workerNotRequired ? (
                  <label className="booking-pro-field loop-worker-field-modern">
                    <span>{t("Trabajador")}</span>
                    <LoopMiniSelect value={resourceId || ""} options={bookingWorkerOptions} ariaLabel={t("Trabajador")} className="loop-worker-mini-select" onChange={(nextId) => { setResourceId(nextId); const nextEmp = (employees || []).find((emp) => String(emp.id) === String(nextId)) || null; applyDefaultCommissionFromEmployee(nextEmp, true); }} />
                  </label>
                ) : (
                  <div className="loop-datetime-modal-note">
                    <strong>{soloBusinessMode ? "Atención individual" : "Sin trabajador fijo"}</strong>
                    <span>{t("Esta reserva se agenda por servicio.")}</span>
                  </div>
                )}
</div>
                {showDateTimeModal ? <div className="loop-inline-fields">              <div className={`loop-datetime-modal-grid ${eventMode ? "is-event" : ""}`}>
                <div role="group" aria-label={t("Fecha de la reserva")} className="booking-pro-field loop-date-field loop-date-field-modern">
                  <span>{t("Fecha")}</span>
                  <BookingDateInput value={bookingDateValue || `${activeBookingDate.getFullYear()}-${pad2(activeBookingDate.getMonth() + 1)}-${pad2(activeBookingDate.getDate())}`} onChange={setBookingDateValue} />
                </div>

                <div role="group" aria-label={t("Hora de la reserva")} className="booking-pro-field loop-time-format-field loop-time-format-field-modern">
                  <div className="loop-time-field-top">
                    <span>{t("Hora")}</span>
                  </div>
                  <BookingModalTimeInput value={time} onChange={setTime} timeFormat={bookingTimeFormat} />
                  {!eventMode ? (
                    <div className={`loop-datetime-availability-below ${availabilityTone}`}>
                      <span aria-hidden="true">{availabilityTone === "is-ok" ? "✓" : "!"}</span>
                      <div>
                        <strong>{availabilityTitle}</strong>
                        <small>{availabilityDescription}</small>
                      </div>
                      <button type="button" className="loop-datetime-availability-link" onClick={() => setShowAvailabilitySlotsModal(true)}>{t("Ver horarios")}<em>{groupedTimeSuggestions.reduce((sum, group) => sum + group.slots.length, 0) || 0}</em>
                      </button>
                    </div>
                  ) : null}
                  <small className="loop-time-format-helper">{t("Puedes elegir un horario sugerido o ajustar la hora manualmente.")}</small>
                </div>

              </div>

              <div className="loop-datetime-modal-mode loop-datetime-modal-mode-minimal">
                <span>{t("Modalidad")}</span>
                <div>
                  <strong>{selectedModeLabel}</strong>
                  <small>{locationRequired ? "Se validará cobertura y dirección." : "La reserva se atenderá en el local."}</small>
                </div>
              </div>

</div> : null}
              </article>
            </div>
            <div className="loop-inline-extras">              {!showEventPackageFlow && hasBlockingScheduleIssueForSave ? (
                <div className={`loop-datetime-inline-warning ${availabilityTone}`}>
                  <strong>{availabilityTitle}</strong>
                  <span>{availabilityDescription}</span>
                </div>
              ) : null}

              {showEventPackageFlow ? (
                <div className="booking-event-options loop-create-event-options loop-event-pro-zone">
                  <div className="loop-event-pro-banner">
                    <span>EVENTO</span>
                    <div>
                      <strong>{t("Flujo de paquetes y extras")}</strong>
                      <small>{t("Elige la experiencia, personajes/complementos y equipo operativo sin forzar un trabajador fijo.")}</small>
                    </div>
                  </div>

                  <div className="booking-event-block loop-event-package-block">
                    <div className="loop-event-block-head">
                      <div>
                        <strong>{selectedService?.pricingMode === "duration_tiers" ? "Duración y precio" : "Tipo de experiencia"}</strong>
                        <small>{t("Esto define la duración, precio base y formato de la reserva.")}</small>
                      </div>
                      <em>{packageTotalLabel}</em>
                    </div>
                    <div className="booking-option-grid loop-event-package-grid">
                      {visitOptions.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          className={`booking-choice-card loop-event-package-card ${String(visitTypeId) === String(opt.id) ? "active" : ""}`}
                          onClick={() => setVisitTypeId(opt.id)}
                        >
                          <span>{opt.label}</span>
                          <small>{opt.visitHours ? `${opt.visitHours}h` : `${opt.durationMinutes || duration} min`}{opt.price ? ` · $${opt.price}` : ""}</small>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="booking-event-block loop-event-extras-block">
                    <div className="loop-event-block-head">
                      <div>
                        <strong>{t("Extras / personajes")}</strong>
                        <small>{extraOptions.length ? "Selecciona complementos para sumar al total." : "Este servicio no tiene extras configurados todavía."}</small>
                      </div>
                      <em>{extrasTotalLabel}</em>
                    </div>
                    {extraOptions.length ? (
                      <div className="booking-option-grid loop-event-extras-grid">
                        {extraOptions.map((extra) => {
                          const active = selectedExtraIds.map(String).includes(String(extra.id));
                          return (
                            <button key={extra.id} type="button" className={`booking-choice-card loop-event-extra-card ${active ? "active" : ""}`} onClick={() => toggleExtra(extra.id)}>
                              <span>{active ? "✓ " : ""}{extra.name}</span>
                              <small>{extra.price ? `+$${extra.price}` : "Sin costo extra"}</small>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="loop-event-empty-state">{t("Puedes configurar personajes, add-ons o extras desde Servicios.")}</div>
                    )}
                  </div>
                </div>
              ) : null}

              {showEventPackageFlow && staffEmployees.length ? (
                <div className="booking-event-block loop-create-staff-block">
                  <div className="loop-event-block-head">
                    <div>
                      <strong>{t("Equipo operativo")}</strong>
                      <small className="booking-event-help">{t("Selecciona quién verá esta reserva en su agenda interna. No afecta disponibilidad pública.")}</small>
                    </div>
                    <em>{selectedStaffNames.length ? `${selectedStaffNames.length} asignado${selectedStaffNames.length === 1 ? "" : "s"}` : t("Opcional")}</em>
                  </div>
                  <div className="booking-staff-grid">
                    {staffEmployees.filter((emp) => isSelectableOperationalStaff(emp, eventMode)).map((emp) => {
                      const active = assignedStaffIds.map(String).includes(String(emp.id));
                      return (
                        <button key={emp.id} type="button" className={`booking-staff-chip ${active ? "active" : ""}`} onClick={() => toggleStaff(emp.id)}>
                          {active ? "✓ " : ""}{employeeLabel(emp)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {showLocationSection ? (
                <div className="loop-create-location-block">
                  <div className={`booking-location-box ${isOutsideCoverage ? "is-error" : customerLocation ? "is-ok" : ""}`}>
                    <div>
                      <strong>{customerLocation?.label || "Sin ubicación seleccionada"}</strong>
                      <span>
                        {isOutsideCoverage
                          ? "Este negocio no cubre esta zona."
                          : matchedServiceArea
                            ? `Zona cubierta${deliveryFee > 0 ? ` · +$${deliveryFee}` : " · sin costo adicional"}`
                            : locationSectionCopy}
                      </span>
                    </div>
                    <button type="button" onClick={() => setShowCustomerLocationPicker(true)}>
                      {customerLocation ? t("Cambiar") : "Elegir ubicación"}
                    </button>
                  </div>

                  <label className="booking-pro-field booking-exact-address-field">
                    <span>{locationSectionTitle}{" " + t("/ referencia")}</span>
                    <textarea
                      value={customerExactAddress}
                      onChange={(e) => setCustomerExactAddress(e.target.value)}
                      placeholder="Ej: PH Rivage, torre 2, apto 18B. Tocar intercom 1802."
                      rows={3}
                      maxLength={260}
                    />
                    <small>{String(customerExactAddress || "").length}{t("/260 · Visible para operación al aceptar la reserva.")}</small>
                  </label>
                </div>
              ) : null}

</div>
            <section className="loop-inline-additional" aria-label={t("Opciones adicionales")}>
              <div className="loop-clean-accordion">
                <button type="button" className="loop-clean-accordion-row" aria-expanded={showRetailAddons} onClick={() => setShowRetailAddons(v => !v)}><strong>{t("Venta adicional")}</strong><span>{normalizedRetailRows.length ? normalizedRetailRows.length + " productos" : "Sin productos"} · {showRetailAddons ? t("Cerrar") : "Agregar"}</span></button>
                {showRetailAddons ? <div className="loop-inline-fields">
                  <input aria-label={t("Buscar producto")} disabled={!canViewInventory} value={retailSearch} onChange={e => setRetailSearch(e.target.value)} placeholder={t("Buscar producto del inventario")} />
                  <div className="loop-retail-results">{retailSearchResults.map(item => <button key={item.id} type="button" className="loop-retail-result-row" disabled={retailProductIsBlocked(item)} onClick={() => addRetailProduct(item)}><strong>{item.name || item.title || t("Producto")}</strong><small>{retailStockLabel(item)}</small></button>)}</div>
                  {normalizedRetailRows.map(row => <div key={row.id} className="loop-retail-selected-row">
                    <strong>{row.itemName}</strong>
                    <label>{t("Cant.")}<input inputMode="decimal" disabled={!canEditProductSales} value={row.quantity} onChange={e => updateRetailRow(row.id,"quantity",e.target.value.replace(/[^0-9.]/g,""))} /></label>
                    <label>{t("Precio")}<input inputMode="decimal" disabled={!canEditProductSales} value={row.unitPrice} onChange={e => updateRetailRow(row.id,"unitPrice",e.target.value.replace(/[^0-9.]/g,""))} /></label>
                    <label>{t("Comisión")}<select disabled={!canEditCommissions} value={row.workerCommissionType || "none"} onChange={e => updateRetailRow(row.id,"workerCommissionType",e.target.value)}><option value="none">{t("No")}</option><option value="percentage">%</option><option value="fixed">$</option></select></label>
                    <label>{t("Valor")}<input inputMode="decimal" disabled={!canEditCommissions} value={row.workerCommissionValue || ""} onChange={e => updateRetailRow(row.id,"workerCommissionValue",e.target.value.replace(/[^0-9.]/g,""))} /></label>
                    <button type="button" disabled={!canEditProductSales} onClick={() => removeRetailRow(row.id)}>{t("Quitar")}</button>
                  </div>)}
                  <strong>{t("Total productos:") + " "}{bookingCompactMoney(retailSalesTotalAmount)}</strong>
                  {(canViewFinancials || canViewCommissions) ? <div className="loop-retail-finance-row">{canViewFinancials ? <span>{t("Costo ref.") + " "}{bookingCompactMoney(retailSalesCostAmount)}</span> : null}{canViewCommissions ? <span>{t("Comisión productos") + " "}{bookingCompactMoney(retailSalesPayoutAmount)}</span> : null}{canViewFinancials ? <strong>{t("Neto") + " "}{bookingCompactMoney(retailSalesNetAmount)}</strong> : null}</div> : null}
                </div> : null}
              </div>
              <div className="loop-clean-accordion">
                <button type="button" className="loop-clean-accordion-row" aria-expanded={showInternalNotes} onClick={() => setShowInternalNotes(v => !v)}><strong>{t("Notas internas")}</strong><span>{showInternalNotes ? t("Listo") : notes ? t("Editar") : "Agregar"}</span></button>
                {showInternalNotes ? <label className="loop-inline-fields">{t("Notas internas")}<textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} maxLength={300} placeholder={t("Preferencias o instrucciones para el negocio")} /><small>{String(notes || "").length}/300</small></label> : <p>{notes || "Sin notas"}</p>}
              </div>
            </section>
            {LOOP_BOOKING_MEMBERSHIPS_ENABLED ? <button type="button" className="booking-pro-secondary" onClick={() => setShowMembershipDetails(true)}>{t("Membresía ·") + " "}{membershipSummary ? membershipSummary.stateLabel : "No aplicada"}</button> : null}
            <div className="loop-overview-mobile-submit"><div className="loop-submit-summary"><span>{t("Total de la reserva")}</span><strong>{bookingCompactMoney(totalPrice)}</strong><small>{!serviceId ? "Selecciona un servicio para continuar" : hasBlockingScheduleIssueForSave ? availabilityTitle : "Revisa los datos antes de guardar"}</small></div><button type="submit" disabled={saving || hasBlockingScheduleIssueForSave || (!serviceId && !isDeleteReservationStatus)}>{saving ? t("Guardando...") : editMode ? "Guardar cambios" : t("Crear reserva")}</button></div>
          </section>
        </form>

        <div className="loop-mobile-submit-footer" aria-hidden="false">
          <button type="submit" form="loop-booking-create-form" disabled={saving || hasBlockingScheduleIssueForSave || (!serviceId && !isDeleteReservationStatus)} className="booking-pro-primary loop-mobile-submit-btn">
            {saving ? (isDeleteReservationStatus ? "Quitando..." : editMode ? t("Guardando...") : "Creando...") : isDeleteReservationStatus ? t("Quitar de agenda") : hasBlockingScheduleIssueForSave ? "Corrige disponibilidad" : (editMode ? t("Guardar") : t("Crear reserva"))}
          </button>
        </div>

        {showWhatsAppDetails ? (
          <div className="loop-mini-detail-backdrop" role="dialog" aria-modal="true" aria-label={t("Detalle de WhatsApp")}>
            <div className="loop-mini-detail-card">
              <div className="loop-mini-detail-head"><div><span>WhatsApp</span><strong>{t("Mensajes de la reserva")}</strong></div><button type="button" onClick={() => setShowWhatsAppDetails(false)}>✕</button></div>
              <div className="loop-mini-detail-summary"><strong>{whatsappReady.label}</strong><span>{whatsappReady.helper}</span></div>
              <div className="loop-mini-detail-actions">
                {canSendManualWhatsApp ? <button type="button" disabled={whatsappManualSending || smartWhatsAppAction.disabled} onClick={handleSendManualWhatsApp}>{whatsappManualSending ? "Enviando..." : smartWhatsAppAction.label}</button> : null}
              </div>
              {whatsappLogRows.length ? (
                <div className="loop-mini-detail-list">
                  {whatsappLogRows.map((row) => {
                    const effectiveStatus = bookingModalWhatsAppEffectiveStatus(row);
                    const rowTone = effectiveStatus === "failed" || row.ok === false ? "is-error" : effectiveStatus === "skipped" ? "is-warning" : effectiveStatus === "read" ? "is-read" : effectiveStatus === "delivered" ? "is-delivered" : "is-ok";
                    return (
                      <div key={row.id || row.metaMessageId || `${row.reason}-${row.createdAt}`} className={`loop-whatsapp-history-row ${rowTone}`}>
                        <div><strong>{bookingModalWhatsAppLogStatus(row)}</strong><span>{bookingModalWhatsAppTypeLabel(row)} · {bookingModalWhatsAppStatusHelper(row)}</span><small>{bookingModalWhatsAppMetaLine(row, canManageWhatsAppMessages)}</small></div>
                        <em>{bookingModalLogDate(row.providerStatusUpdatedAt || row.createdAt)}</em>
                      </div>
                    );
                  })}
                </div>
              ) : <p className="loop-mini-empty">{t("No hay mensajes registrados para esta reserva.")}</p>}
            </div>
          </div>
        ) : null}

        {LOOP_BOOKING_MEMBERSHIPS_ENABLED && showMembershipDetails ? (
          <div className="loop-mini-detail-backdrop" role="dialog" aria-modal="true" aria-label={t("Detalle de membresía")}>
            <div className="loop-mini-detail-card">
              <div className="loop-mini-detail-head"><div><span>{t("Membresía")}</span><strong>{membershipSummary?.planName || effectiveSelectedMembershipBenefit?.plan?.name || "Beneficio de membresía"}</strong></div><button type="button" onClick={() => setShowMembershipDetails(false)}>✕</button></div>
              <div className="loop-mini-detail-summary"><strong>{membershipSummary?.stateLabel || (effectiveSelectedMembershipBenefit ? "Beneficio seleccionado" : "No aplicada")}</strong><span>{membershipSummary?.label || effectiveSelectedMembershipBenefit?.label || "Esta reserva no tiene una membresía aplicada."}</span></div>
              {customerId ? (
                <div className="loop-mini-membership-picker">
                  <button type="button" onClick={loadMembershipBenefitsForBooking} disabled={membershipBenefitsLoading}>{membershipBenefitsLoading ? "Cargando..." : "Actualizar beneficios"}</button>
                  {membershipBenefitsError ? <small className="is-error">{membershipBenefitsError}</small> : null}
                  {membershipBenefits.length ? (
                    <label><span>{t("Beneficio disponible")}</span><select value={selectedMembershipBenefitId} onChange={(e) => setSelectedMembershipBenefitId(e.target.value)}><option value="">{t("No aplicar membresía")}</option>{membershipBenefits.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
                  ) : !membershipBenefitsLoading ? <small>{t("No hay membresías activas aplicables para este cliente/servicio.")}</small> : null}
                </div>
              ) : null}
              <div className="loop-mini-detail-actions">
                {businessId && initialBooking?.id ? <button type="button" disabled={membershipReprocessSending || saving} onClick={handleReprocessMembershipBenefit}>{membershipReprocessSending ? "Procesando..." : "Procesar membresía"}</button> : null}
              </div>
            </div>
          </div>
        ) : null}

        {showCustomerDetails ? (
          <div className="loop-mini-detail-backdrop" role="dialog" aria-modal="true" aria-label={t("Perfil del cliente")}>
            <div className={`loop-mini-detail-card ${customerProfileEditMode ? "is-editing-customer" : ""}`}>
              <div className="loop-mini-detail-head">
                <div><span>Cliente</span><strong>{liveCustomerProfile.name || "Cliente"}</strong></div>
                <div className="loop-mini-detail-head-actions">
                  {canEditLiveCustomerProfile && !customerProfileEditMode ? (
                    <button type="button" className="loop-mini-detail-edit-btn" onClick={openCustomerProfileEditor}>{t("Editar")}</button>
                  ) : null}
                  <button type="button" onClick={closeCustomerProfileModal} aria-label={t("Cerrar")}>✕</button>
                </div>
              </div>

              {customerProfileEditMode ? (
                <div className="loop-mini-profile-edit-panel">
                  <div className="loop-mini-profile-edit-grid">
                    <label className="booking-pro-field">
                      <span>{t("Nombre")}</span>
                      <input
                        value={customerProfileEditDraft.name}
                        onPointerDown={stopBookingInputPointerPropagation}
                        onTouchStart={stopBookingInputPointerPropagation}
                        onFocus={stabilizeBookingMobileInput}
                        onChange={(e) => setCustomerProfileEditDraft((draft) => ({ ...draft, name: e.target.value }))}
                        placeholder={t("Nombre del cliente")}
                        autoComplete="name"
                      />
                    </label>
                    <label className="booking-pro-field loop-phone-field">
                      <span>WhatsApp</span>
                      <LoopPhoneInput
                        value={customerProfileEditDraft.phone}
                        onPointerDown={stopBookingInputPointerPropagation}
                        onTouchStart={stopBookingInputPointerPropagation}
                        onFocus={stabilizeBookingMobileInput}
                        onChange={(next) => setCustomerProfileEditDraft((draft) => ({ ...draft, phone: next }))}
                      />
                    </label>
                    <label className="booking-pro-field">
                      <span>Email</span>
                      <input
                        value={customerProfileEditDraft.email}
                        onPointerDown={stopBookingInputPointerPropagation}
                        onTouchStart={stopBookingInputPointerPropagation}
                        onFocus={stabilizeBookingMobileInput}
                        onChange={(e) => setCustomerProfileEditDraft((draft) => ({ ...draft, email: e.target.value }))}
                        placeholder="cliente@ejemplo.com"
                        inputMode="email"
                        autoComplete="email"
                      />
                    </label>
                  </div>

                  {customerProfileEditError ? <div className="loop-mini-profile-edit-error">{customerProfileEditError}</div> : null}

                  <div className="loop-mini-profile-edit-actions">
                    <button type="button" className="is-secondary" onClick={resetCustomerProfileEditor} disabled={customerProfileSaving}>{t("Cancelar")}</button>
                    <button type="button" onClick={saveCustomerProfileEdit} disabled={customerProfileSaving}>{customerProfileSaving ? t("Guardando...") : "Guardar cambios"}</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="loop-mini-profile-grid">
                    <div><span>WhatsApp</span><strong>{liveCustomerProfile.phoneDisplay || liveCustomerProfile.phone || "—"}</strong></div>
                    <div><span>Email</span><strong>{liveCustomerProfile.email || "—"}</strong></div>
                    <div><span>{t("Historial")}</span><strong>{liveCustomerProfile.totalBookings ? `${liveCustomerProfile.totalBookings} reserva${Number(liveCustomerProfile.totalBookings) === 1 ? "" : "s"}` : "Sin reservas"}</strong></div>
                    <div><span>{t("Estado")}</span><strong>{liveCustomerIsLoopUser ? "Usuario Loop" : liveCustomerProfile.customerId || customerId ? "Registrado" : t("Nuevo cliente")}</strong></div>
                  </div>
                  {liveCustomerIsLoopUser ? <div className="loop-mini-profile-sync-note">{t("Este cliente está vinculado a un usuario Loop. El nombre, email y WhatsApp se editan desde su ProfilePage.")}</div> : !canEditLiveCustomerProfile ? <div className="loop-mini-profile-sync-note is-muted">{t("Guarda la reserva para crear este cliente antes de editar su perfil en la base del negocio.")}</div> : null}
                  <Customer360BookingCard snapshot={liveCustomerProfile} />
                </>
              )}
            </div>
          </div>
        ) : null}

        {showReservationSummary ? (
          <div className="loop-mini-detail-backdrop" role="dialog" aria-modal="true" aria-label={t("Resumen completo de la reserva")}>
            <div className="loop-mini-detail-card">
              <div className="loop-mini-detail-head"><div><span>{t("Resumen")}</span><strong>{selectedServiceLabel || t("Reserva")}</strong></div><button type="button" onClick={() => setShowReservationSummary(false)}>✕</button></div>
              <div className="loop-mini-profile-grid">
                <div><span>Cliente</span><strong>{customerName || "Cliente nuevo"}</strong></div>
                <div><span>Servicio</span><strong>{selectedServiceLabel}</strong></div>
                <div><span>{t("Fecha")}</span><strong>{humanDateLabel || "—"}</strong></div>
                <div><span>{t("Hora")}</span><strong>{bookingTimeLabel || "—"}</strong></div>
                <div><span>{t("Duración")}</span><strong>{duration ? `${duration} min` : "—"}</strong></div>
                <div><span>{t("Trabajador")}</span><strong>{selectedWorkerLabel}</strong></div>
                <div><span>{t("Modalidad")}</span><strong>{selectedModeLabel}</strong></div>
                <div><span>Total</span><strong>${Number(totalPrice || 0).toFixed(Number(totalPrice || 0) % 1 === 0 ? 0 : 2)}</strong></div>
              </div>
            </div>
          </div>
        ) : null}

        {showPaymentSettingsModal && canShowBookingPaymentUi ? (
          <AccessibleModal
            open={showPaymentSettingsModal}
            onClose={() => setShowPaymentSettingsModal(false)}
            labelledBy="payment-settings-title"
            width={560}
            dialogClassName="loop-nested-settings-modal loop-payment-editor-modal"
            dialogStyle={{ padding: 0, overflow: "hidden", borderRadius: 22 }}
          >
            <div className="loop-settings-modal-head">
              <div>
                <strong id="payment-settings-title">{t("Cobro de la reserva")}</strong>
                <span>{t("Ajusta el precio o registra lo recibido. Los pagos anteriores se conservan.")}</span>
              </div>
              <button type="button" onClick={() => setShowPaymentSettingsModal(false)} aria-label={t("Cerrar")}>✕</button>
            </div>
            <div className="loop-settings-modal-body loop-payment-editor-body-compact" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
              {!isCoveredPayment ? <BookingCollectionEditor currency={businessDoc?.currency}
              onDone={()=>setShowPaymentSettingsModal(false)}
              extraFields={<>{initialBooking?.id&&<BookingPaymentHistory entries={paymentHistoryEntries} canCorrect={isOwnerOrAdmin&&canEditPayments&&!collectionApplied&&!priceAdjustmentDraft} onCorrect={async correction=>{await httpsCallable(getFunctions(),"correctBookingManualPaymentMethodV1")({businessId,bookingId:initialBooking.id,...correction});const ledger=await getDocs(collection(db,"businesses",businessId,"bookings",initialBooking.id,"paymentLedger"));const entries=ledger.docs.map(d=>({...d.data(),id:d.id}));setPaymentHistoryEntries(entries);const methods=[...new Set(entries.filter(e=>e.status==="approved"&&e.direction==="charge").map(e=>e.methodCorrection?.method||e.method))];setPaymentMethod(methods.length>1?"mixed":methods[0]||"other");}}/>}{!selectedService?.sessionPackage?.enabled && !initialBooking?.sessionProgramId && <details className="loop-payment-extra"><summary>{Number(tipAmount)>0 ? `Propina · ${tipAmount}` : "+ Añadir propina"}</summary><label>{t("Propina")}<input aria-label={t("Propina")} inputMode="decimal" disabled={!canEditPayments} value={tipAmount} onChange={e=>setTipAmount(e.target.value.replace(/[^0-9.]/g,""))} placeholder="0.00" /></label></details>}{canViewCommissions&&<details className="loop-payment-extra"><summary>{t("Más opciones")}</summary><div className="loop-payment-simple-commission-row"><span>{workerCommissionType !== "none" ? `${commissionReadable} de comisión` : "Sin comisión"}</span><button type="button" onClick={()=>setShowCommissionSettingsModal(true)}>{t("Cambiar")}</button></div></details>}<small className="loop-receipt-status">{t("Comprobante automático") + " "}{businessDoc?.manualPaymentReceiptEmailsEnabled === true ? "activado" : "desactivado"}</small></>}
              onAdjustPrice={selectedService?.sessionPackage?.enabled || initialBooking?.sessionProgramId ? undefined : draft=>{setPriceOverride(draft.totalCents/100+membershipBenefitDiscountAmount);setDiscountAmount("0");setPriceAdjustmentDraft({...draft,baseTotalCents:Math.round((Number(initialBooking?.totalPrice ?? totalPrice)-Number(initialBooking?.discountAmount ?? normalizedDiscount))*100)});const paid=Number(amountCharged||0);setPaymentStatus(paid>=draft.totalCents/100 ? "paid" : paid>0 ? "partial" : "pending");}}
              totalCents={Math.round(expectedChargedAmount*100)}
              paidCents={Math.round(Number(initialBooking?.paidAmount ?? initialBooking?.amountCharged ?? 0)*100)}
              onlinePaidCents={verifiedOnlinePaidCents}
              initialReceivedCents={collectionApplied ? Math.max(0,Math.round((Number(amountCharged||0)-Number(initialBooking?.paidAmount ?? initialBooking?.amountCharged ?? 0))*100)) : 0}
              initialMethod={paymentMethod} disabled={!canEditPayments || saving}
              onApply={draft=>{setAmountCharged(String(draft.nextPaidCents/100));setPaymentStatus(draft.paymentStatus);setPaymentMethod(draft.method);setCollectionApplied(true);setShowPaymentSettingsModal(false);}}
            /> : (<>
            <div className="loop-payment-editor-grid-simple">
                <label className="booking-pro-field">
                  <span>{t("Estado")}</span>
                  <LoopMiniSelect
                    value={paymentStatus}
                    disabled={!canEditPayments}
                    ariaLabel="Estado de pago"
                    options={[{ value: "pending", label: "Pendiente" }, { value: "paid", label: "Pagado" }, { value: "covered", label: "Cubierto" }, { value: "partial", label: "Parcial" }, { value: "refunded", label: "Reembolsado" }]}
                    onChange={(nextStatus) => {
                      setPaymentStatus(nextStatus);
                      if (nextStatus === "paid") setAmountCharged(formatMoneyInputValue(expectedChargedAmount));
                      if (nextStatus === "covered") {
                        setAmountCharged("");
                        if (!LOOP_BOOKING_COVERED_PAYMENT_METHODS.has(String(paymentMethod || "").toLowerCase())) {
                          setPaymentMethod("loyalty_reward");
                        }
                      }
                    }}
                  />
                </label>

                <label className="booking-pro-field">
                  <span>{t("Método")}</span>
                  <LoopMiniSelect
                    value={paymentMethod}
                    disabled={!canEditPayments}
                    ariaLabel="Método de pago"
                    className="loop-payment-method-select"
                    options={LOOP_BOOKING_PAYMENT_METHOD_OPTIONS}
                    onChange={(nextMethod) => {
                      setPaymentMethod(nextMethod);
                      if (!showTipPaymentMethodOverride) setTipPaymentMethod(nextMethod);
                      if (LOOP_BOOKING_COVERED_PAYMENT_METHODS.has(String(nextMethod).toLowerCase())) {
                        setPaymentStatus("covered");
                        setAmountCharged("");
                      }
                    }}
                  />
                </label>

                <label className="booking-pro-field loop-payment-modal-money-field loop-payment-modal-charged-field">
                  <span>{t("Cobrado")}</span>
                  <div className="loop-payment-modal-money-input">
                    <input
                      disabled={!canEditPayments || isCoveredPayment}
                      inputMode="decimal"
                      value={isCoveredPayment ? "" : amountCharged}
                      onChange={(e) => setAmountCharged(e.target.value.replace(/[^0-9.]/g, ""))}
                      placeholder={isCoveredPayment ? "Cubierto" : paymentStatus === "paid" ? String(totalPrice || 0) : "0.00"}
                    />
                  </div>
                </label>

                <div className="booking-pro-field loop-payment-tip-field loop-payment-modal-money-field">
                  <span>{t("Propina")}</span>
                  <div className="loop-payment-modal-money-input">
                    <input
                      disabled={!canEditPayments}
                      inputMode="decimal"
                      value={tipAmount}
                      onChange={(e) => setTipAmount(e.target.value.replace(/[^0-9.]/g, ""))}
                      placeholder="0.00"
                    />
                  </div>
                  {showTipPaymentMethodOverride ? (
                    <div className="loop-tip-method-override-row">
                      <LoopMiniSelect value={normalizedTipPaymentMethod} disabled={!canEditPayments} ariaLabel="Método de propina" options={[{ value: "cash", label: "Efectivo" }, { value: "yappy", label: "Yappy" }, { value: "card", label: "Tarjeta" }, { value: "transfer", label: "Transferencia" }, { value: "other", label: "Otro" }]} onChange={setTipPaymentMethod} />
                    </div>
                  ) : null}
                </div>
              </div>

            </>)}
            </div>
            {isCoveredPayment&&<div className="loop-settings-modal-foot"><button type="button" onClick={() => setShowPaymentSettingsModal(false)}>{t("Listo")}</button></div>}
          </AccessibleModal>
        ) : null}

        {canViewPaymentSection && showCommissionSettingsModal ? (
          <AccessibleModal
            open={showCommissionSettingsModal}
            onClose={() => setShowCommissionSettingsModal(false)}
            labelledBy="commission-settings-title"
            width={460}
            dialogClassName="loop-nested-settings-modal loop-commission-editor-modal loop-commission-editor-modal-simple"
            dialogStyle={{ padding: 0, overflow: "hidden", borderRadius: 22 }}
          >
            <div className="loop-settings-modal-head">
              <div>
                <strong id="commission-settings-title">{t("Comisión")}</strong>
                <span>{t("Define solo el tipo y el valor de comisión para esta reserva.")}</span>
              </div>
              <button type="button" onClick={() => setShowCommissionSettingsModal(false)} aria-label={t("Cerrar")}>✕</button>
            </div>
            <div className="loop-settings-modal-body loop-commission-simple-body" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
              <div className="loop-commission-simple-grid">
                <label className="booking-pro-field loop-commission-type-field">
                  <span>{t("Tipo de comisión")}</span>
                  <LoopMiniSelect
                    disabled={!canEditCommissions}
                    value={workerCommissionType}
                    ariaLabel={t("Tipo de comisión")}
                    className="loop-commission-type-select"
                    options={[{ value: "none", label: "Sin comisión" }, { value: "percentage", label: "Porcentaje" }, { value: "fixed", label: "Monto fijo" }]}
                    onChange={(nextType) => {
                      setWorkerCommissionType(nextType);
                      setCommissionManuallyEdited(true);
                    }}
                  />
                </label>

                <label className={`booking-pro-field loop-commission-value-field ${workerCommissionType === "percentage" ? "is-percent" : workerCommissionType === "fixed" ? "is-money" : "is-empty"}`}>
                  <span>{workerCommissionType === "percentage" ? "% comisión" : workerCommissionType === "fixed" ? "Monto comisión" : t("Valor")}</span>
                  {workerCommissionType !== "none" ? (
                    <div className="loop-commission-value-control">
                      <input
                        disabled={!canEditCommissions}
                        inputMode="decimal"
                        value={workerCommissionValue}
                        onChange={(e) => {
                          setWorkerCommissionValue(e.target.value.replace(/[^0-9.]/g, ""));
                          setCommissionManuallyEdited(true);
                        }}
                        placeholder={workerCommissionType === "percentage" ? "20" : "10.00"}
                      />
                    </div>
                  ) : (
                    <div className="loop-commission-empty-control">{t("No aplica")}</div>
                  )}
                </label>
              </div>
            </div>
            <div className="loop-settings-modal-foot">
              <button type="button" onClick={() => setShowCommissionSettingsModal(false)}>{t("Listo")}</button>
            </div>
          </AccessibleModal>
        ) : null}

        <NewCustomerQuickModal
          open={showNewCustomerModal}
          businessId={businessId}
          initialName={customerName}
          initialPhone={customerPhone}
          initialEmail={customerEmail}
          onClose={() => setShowNewCustomerModal(false)}
          onSave={async ({ name, phone, email, identityResolution }) => {
            let resolvedIdentity = identityResolution || null;
            const typedPhone = bookingModalPhoneForStorage(phone || "");
            const typedPhoneNormalized = bookingModalNormalizePhone(typedPhone) ? `+${bookingModalNormalizePhone(typedPhone)}` : "";
            const typedEmail = String(email || "").trim().toLowerCase();

            if (!isOwnerOrAdmin) {
              // LOOP BOOKING MOBILE WORKER CUSTOMER + COMPACT UX V13
              // Workers can create reservations, but Firestore rules intentionally do
              // not let them write CRM customer docs directly. Keep the typed customer
              // locally and let createWorkerBooking create/find the CRM record with
              // Admin SDK in the same server-side transaction path as the booking.
              const identitySnapshot = identityResolution?.customerSnapshot && typeof identityResolution.customerSnapshot === "object" ? identityResolution.customerSnapshot : null;
              const finalName = String(name || identitySnapshot?.name || identitySnapshot?.customerName || customerName || "Cliente").trim() || "Cliente";
              const linkedUid = identitySnapshot?.linkedUserUid || identitySnapshot?.linkedUserId || identitySnapshot?.userUid || identitySnapshot?.userId || identitySnapshot?.platformUserUid || identitySnapshot?.platformUserId || null;
              const finalSnapshot = {
                ...(identitySnapshot || {}),
                id: null,
                customerId: null,
                clientId: null,
                businessId,
                name: finalName,
                customerName: finalName,
                displayName: finalName,
                fullName: finalName,
                phone: typedPhone || identitySnapshot?.phone || identitySnapshot?.phoneNormalized || identitySnapshot?.phoneE164 || "",
                customerPhone: typedPhone || identitySnapshot?.customerPhone || identitySnapshot?.phone || identitySnapshot?.phoneNormalized || "",
                phoneDisplay: typedPhone || identitySnapshot?.phoneDisplay || identitySnapshot?.phone || "",
                phoneNormalized: typedPhoneNormalized || identitySnapshot?.phoneNormalized || identitySnapshot?.phoneE164 || "",
                phoneE164: typedPhoneNormalized || identitySnapshot?.phoneE164 || identitySnapshot?.phoneNormalized || "",
                customerPhoneNormalized: typedPhoneNormalized || identitySnapshot?.customerPhoneNormalized || identitySnapshot?.phoneNormalized || "",
                whatsapp: typedPhoneNormalized || typedPhone || identitySnapshot?.whatsapp || identitySnapshot?.phoneNormalized || "",
                whatsApp: typedPhoneNormalized || typedPhone || identitySnapshot?.whatsApp || identitySnapshot?.phoneNormalized || "",
                email: typedEmail || identitySnapshot?.emailNormalized || identitySnapshot?.email || "",
                customerEmail: typedEmail || identitySnapshot?.customerEmail || identitySnapshot?.emailNormalized || identitySnapshot?.email || "",
                emailNormalized: typedEmail || identitySnapshot?.emailNormalized || "",
                hasUserAccount: Boolean(linkedUid || identitySnapshot?.hasUserAccount),
                linkedUserId: linkedUid,
                linkedUserUid: linkedUid,
                userId: linkedUid,
                userUid: linkedUid,
                platformUserId: identitySnapshot?.platformUserId || linkedUid,
                platformUserUid: identitySnapshot?.platformUserUid || linkedUid,
                identityStatus: identitySnapshot?.identityStatus || identityResolution?.status || "worker_deferred_crm_customer",
                identitySource: identitySnapshot?.identitySource || "worker_booking_customer_deferred",
                identityMatchedBy: identitySnapshot?.identityMatchedBy || null,
                identityContactSource: identitySnapshot?.identityContactSource || "worker_booking_modal",
                identityCanonicalProfile: identitySnapshot?.identityCanonicalProfile === true,
                workerClientSideIdentityDeferred: true,
                crmSaveDeferredToWorkerFunction: true,
                source: "worker_booking_quick_customer",
                updatedAt: new Date().toISOString(),
              };
              setCustomerId(null);
              setCustomerName(finalName);
              setCustomerPhone(typedPhone);
              setCustomerEmail(typedEmail);
              setCustomerSnapshot(finalSnapshot);
              setCustomerWhatsappOptOut(false);
              setShowNewCustomerModal(false);
              return;
            }

            if (!bookingModalStrongCustomerIdentity({ phone: typedPhone, customerPhone: typedPhone, email: typedEmail, customerEmail: typedEmail })) {
              const nameOnlyRecord = await bookingModalCreateNameOnlyCustomerRecord(db, businessId, {
                name,
                customerSnapshot: null,
                createdByRole: "booking_create_name_only_quick_modal",
              });
              const finalName = String(name || nameOnlyRecord?.name || "Cliente").trim() || "Cliente";
              const finalSnapshot = bookingModalNameOnlyCustomerSnapshot({
                businessId,
                name: finalName,
                customerSnapshot: nameOnlyRecord,
                customerId: nameOnlyRecord?.id || nameOnlyRecord?.customerId || null,
                source: "booking_manual_name_only_quick_modal",
              });
              setCustomerId(finalSnapshot.id || finalSnapshot.customerId || null);
              setCustomerName(finalName);
              setCustomerPhone("");
              setCustomerEmail("");
              setCustomerSnapshot(finalSnapshot);
              setCustomerWhatsappOptOut(false);
              setShowNewCustomerModal(false);
              return;
            }

            try {
              if (!resolvedIdentity || ["new_customer"].includes(String(resolvedIdentity.status || ""))) {
                resolvedIdentity = await bookingModalResolveCustomerIdentitySafe(db, businessId, { name, customerName: name, phone: typedPhone, customerPhone: typedPhone, email: typedEmail, customerEmail: typedEmail });
              }
            } catch (identityErr) {
              console.warn("[BookingCreateModal] customer identity resolve failed", identityErr);
            }
            if (resolvedIdentity?.status === "conflict" || resolvedIdentity?.status === "platform_user_missing_phone") {
              setShowNewCustomerModal(false);
              window.setTimeout(() => showBookingGuardrail({
                tone: "danger",
                eyebrow: "Identidad del cliente",
                title: resolvedIdentity?.status === "platform_user_missing_phone" ? "Falta WhatsApp en perfil Loop" : "Revisa email y WhatsApp",
                message: resolvedIdentity?.conflict?.message || "El email y el WhatsApp parecen pertenecer a personas diferentes.",
                summary: [
                  { label: "Email", value: typedEmail || "—" },
                  { label: "WhatsApp", value: typedPhone || "—" },
                  { label: "Acción", value: "Corrige uno de los datos" },
                ],
              }), 0);
              return;
            }

            let customerRecord = null;
            try {
              const identitySnapshot = resolvedIdentity?.customerSnapshot || null;
              const useCanonicalProfile = identitySnapshot?.identityCanonicalProfile === true || identitySnapshot?.identityContactSource === "platform_user_profile";
              customerRecord = await bookingModalWithTimeout(
                findOrCreateCustomerForBusiness(db, businessId, {
                  ...(identitySnapshot || {}),
                  name: useCanonicalProfile ? (identitySnapshot?.name || name) : name,
                  customerName: useCanonicalProfile ? (identitySnapshot?.name || name) : name,
                  phone: useCanonicalProfile ? (identitySnapshot?.phone || identitySnapshot?.phoneNormalized || typedPhone) : typedPhone,
                  customerPhone: useCanonicalProfile ? (identitySnapshot?.phone || identitySnapshot?.phoneNormalized || typedPhone) : typedPhone,
                  email: useCanonicalProfile ? (identitySnapshot?.email || identitySnapshot?.emailNormalized || typedEmail) : typedEmail,
                  customerEmail: useCanonicalProfile ? (identitySnapshot?.email || identitySnapshot?.emailNormalized || typedEmail) : typedEmail,
                }, {
                  incrementBookings: false,
                  linkPlatformUser: true,
                  allowIdentityConflict: false,
                  updateExistingCustomerName: false,
                }),
                9500,
                { __timeout: true }
              );
              if (customerRecord?.__timeout) {
                throw new Error("La sincronización del cliente tardó demasiado. Intenta guardar nuevamente en unos segundos.");
              }
            } catch (saveCustomerErr) {
              console.warn("[BookingCreateModal] customer identity save/update failed", saveCustomerErr);
              setShowNewCustomerModal(false);
              window.setTimeout(() => showBookingGuardrail({
                tone: "danger",
                eyebrow: "Identidad del cliente",
                title: "No pudimos guardar el cliente",
                message: saveCustomerErr?.message || "Revisa los datos e intenta nuevamente.",
                summary: [
                  { label: "Email", value: typedEmail || "—" },
                  { label: "WhatsApp", value: typedPhone || "—" },
                ],
              }), 0);
              return;
            }

            const existingCustomer = customerRecord || resolvedIdentity?.existingCustomer || null;
            const resolvedSnapshot = resolvedIdentity?.customerSnapshot || null;
            const useCanonicalProfile = resolvedSnapshot?.identityCanonicalProfile === true || resolvedSnapshot?.identityContactSource === "platform_user_profile";
            // Identity priority V4.4:
            // If this email/phone belongs to a real Loop user, the canonical ProfilePage snapshot wins.
            // Never let an older business-customer CRM snapshot overwrite ProfilePage contact data here.
            const canonicalPhone = resolvedSnapshot?.phone || resolvedSnapshot?.phoneNormalized || resolvedSnapshot?.phoneE164 || resolvedSnapshot?.platformUserPhone || "";
            const canonicalEmail = resolvedSnapshot?.email || resolvedSnapshot?.emailNormalized || resolvedSnapshot?.customerEmail || resolvedSnapshot?.platformUserEmail || "";
            const canonicalName = resolvedSnapshot?.name || resolvedSnapshot?.customerName || resolvedSnapshot?.displayName || "";
            const finalPhone = bookingModalPhoneForStorage(useCanonicalProfile
              ? (canonicalPhone || existingCustomer?.phone || existingCustomer?.phoneNormalized || "")
              : (typedPhone || existingCustomer?.phone || existingCustomer?.phoneNormalized || resolvedSnapshot?.phone || resolvedSnapshot?.phoneNormalized || ""));
            const finalPhoneNormalized = bookingModalNormalizePhone(finalPhone) ? `+${bookingModalNormalizePhone(finalPhone)}` : "";
            const finalEmail = useCanonicalProfile
              ? (canonicalEmail || existingCustomer?.email || existingCustomer?.emailNormalized || typedEmail || "")
              : (typedEmail || existingCustomer?.email || existingCustomer?.emailNormalized || resolvedSnapshot?.email || resolvedSnapshot?.emailNormalized || "");
            const finalName = useCanonicalProfile
              ? (canonicalName || existingCustomer?.name || name || "Cliente")
              : (customerRecord?.existed === true
                ? (existingCustomer?.name || existingCustomer?.customerName || existingCustomer?.displayName || name || "Cliente")
                : (name || existingCustomer?.name || resolvedSnapshot?.name || "Cliente"));
            const finalCustomerId = bookingModalCustomerRecordId(existingCustomer, bookingModalCustomerRecordId(resolvedSnapshot, resolvedIdentity?.customerId || ""));
            const finalSnapshot = {
              ...(existingCustomer || {}),
              ...(resolvedSnapshot || {}),
              __documentId: finalCustomerId || null,
              id: finalCustomerId || null,
              customerId: finalCustomerId || null,
              clientId: finalCustomerId || null,
              name: finalName,
              customerName: finalName,
              displayName: finalName,
              phone: finalPhone,
              customerPhone: finalPhone,
              phoneDisplay: finalPhone,
              phoneNormalized: finalPhoneNormalized,
              phoneE164: finalPhoneNormalized,
              customerPhoneNormalized: finalPhoneNormalized,
              whatsapp: finalPhone,
              whatsApp: finalPhone,
              email: finalEmail,
              emailNormalized: String(finalEmail || "").trim().toLowerCase(),
              customerEmail: String(finalEmail || "").trim().toLowerCase(),
              identityFieldConflicts: resolvedIdentity?.fieldConflicts || resolvedSnapshot?.identityFieldConflicts || existingCustomer?.identityFieldConflicts || [],
              identityNeedsUserProfileSync: useCanonicalProfile ? false : (resolvedSnapshot?.identityNeedsUserProfileSync === true || existingCustomer?.identityNeedsUserProfileSync === true),
            };
            setCustomerId(finalCustomerId || null);
            setCustomerName(finalName);
            setCustomerPhone(finalPhone);
            setCustomerEmail(finalEmail);
            setCustomerSnapshot(finalSnapshot);
            setCustomerWhatsappOptOut(finalSnapshot?.whatsappOptOut === true || finalSnapshot?.whatsappOptIn === false || finalSnapshot?.customerWhatsappConsent === false);
            setShowNewCustomerModal(false);
          }}
        />


        {bookingGuardrailNotice ? (
          <div className="loop-lifecycle-decision-backdrop" role="presentation" onMouseDown={() => setBookingGuardrailNotice(null)}>
            <section
              className={`loop-lifecycle-decision-modal loop-guardrail-notice-modal is-${bookingGuardrailNotice.tone || "warning"}`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="loop-guardrail-notice-title"
              aria-describedby="loop-guardrail-notice-copy"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="loop-lifecycle-decision-icon" aria-hidden="true">
                {bookingGuardrailNotice.tone === "danger" ? "!" : "i"}
              </div>
              <div className="loop-lifecycle-decision-copy">
                <span>{bookingGuardrailNotice.eyebrow || "Revisa esta reserva"}</span>
                <strong id="loop-guardrail-notice-title">{bookingGuardrailNotice.title || "Falta información"}</strong>
                <p id="loop-guardrail-notice-copy">{bookingGuardrailNotice.message || "Completa la información necesaria para continuar."}</p>
              </div>
              {Array.isArray(bookingGuardrailNotice.summary) && bookingGuardrailNotice.summary.length ? (
                <div className="loop-lifecycle-decision-summary">
                  {bookingGuardrailNotice.summary.slice(0, 3).map((item, idx) => (
                    <div key={`${item.label || "item"}-${idx}`}>
                      <span>{item.label || "Detalle"}</span>
                      <strong>{item.value || "—"}</strong>
                    </div>
                  ))}
                </div>
              ) : null}
              <div className="loop-lifecycle-decision-actions">
                <button type="button" className="primary" onClick={() => setBookingGuardrailNotice(null)}>{bookingGuardrailNotice.primaryLabel || "Entendido"}</button>
              </div>
            </section>
          </div>
        ) : null}


        {bookingDeleteDecision ? (
          <div className="loop-lifecycle-decision-backdrop" role="presentation" onMouseDown={() => setBookingDeleteDecision(null)}>
            <section
              className="loop-lifecycle-decision-modal is-danger"
              role="dialog"
              aria-modal="true"
              aria-labelledby="loop-delete-reservation-title"
              aria-describedby="loop-delete-reservation-copy"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="loop-lifecycle-decision-icon" aria-hidden="true">!</div>
              <div className="loop-lifecycle-decision-copy">
                <span>{t("Quitar de agenda")}</span>
                <strong id="loop-delete-reservation-title">{t("¿Estás seguro que quieres eliminarla?")}</strong>
                <p id="loop-delete-reservation-copy">{t("La reserva se quitará de la agenda para liberar el espacio visual, pero el historial interno queda guardado para auditoría.")}</p>
              </div>
              <div className="loop-lifecycle-decision-summary">
                <div><span>Cliente</span><strong>{effectiveCustomerName || customerName || "Cliente"}</strong></div>
                <div><span>{t("Reserva")}</span><strong>{selectedServiceLabel || "Servicio"}</strong></div>
                <div><span>{t("Resultado")}</span><strong>{t("Oculta de agenda")}</strong></div>
              </div>
              <div className="loop-lifecycle-decision-actions">
                <button type="button" className="ghost" onClick={() => setBookingDeleteDecision(null)}>{t("Volver")}</button>
                <button
                  type="button"
                  className="primary danger"
                  disabled={saving}
                  onClick={() => {
                    setBookingDeleteDecision(null);
                    handleSubmit(null, { confirmDeleteReservation: true, skipPaidCompletionPrompt: true });
                  }}
                >
                  {saving ? "Eliminando..." : "Sí, eliminar reserva"}
                </button>
              </div>
            </section>
          </div>
        ) : null}


        {bookingLifecycleDecision ? (
          <div className="loop-lifecycle-decision-backdrop" role="presentation" onMouseDown={() => setBookingLifecycleDecision(null)}>
            <section
              className={`loop-lifecycle-decision-modal ${bookingLifecycleDecision.type === "complete_without_payment" ? "is-warning" : "is-success"}`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="loop-lifecycle-decision-title"
              aria-describedby="loop-lifecycle-decision-copy"
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="loop-lifecycle-decision-icon" aria-hidden="true">
                {bookingLifecycleDecision.type === "complete_without_payment" ? "!" : "✓"}
              </div>

              {bookingLifecycleDecision.type === "complete_without_payment" ? (
                <>
                  <div className="loop-lifecycle-decision-copy">
                    <span>{t("Completada sin pago")}</span>
                    <strong id="loop-lifecycle-decision-title">{t("¿Cómo quieres manejar el cobro?")}</strong>
                    <p id="loop-lifecycle-decision-copy">{t("Esta reserva quedará completada, pero seguirá apareciendo como pendiente por cobrar en Finanzas.")}</p>
                  </div>
                  <div className="loop-lifecycle-decision-summary">
                    <div><span>{t("Estado")}</span><strong>{t("Completada")}</strong></div>
                    <div><span>{t("Pago")}</span><strong>{t("Pendiente")}</strong></div>
                    <div><span>{t("Resultado")}</span><strong>{t("Completada por cobrar")}</strong></div>
                  </div>
                  <div className="loop-lifecycle-decision-actions">
                    <button type="button" className="ghost" onClick={() => setBookingLifecycleDecision(null)}>{t("Cancelar")}</button>
                    <button type="button" onClick={() => { setBookingLifecycleDecision(null); handleSubmit(null, { allowCompleteWithoutPayment: true }); }}>{t("Dejar por cobrar")}</button>
                    {LOOP_BOOKING_MEMBERSHIPS_ENABLED && effectiveSelectedMembershipBenefit ? (
                      <button type="button" onClick={() => { setPaymentStatus("covered"); setPaymentMethod("membership"); setAmountCharged(""); setBookingLifecycleDecision(null); handleSubmit(null, { allowCompleteWithoutPayment: true }); }}>{t("Cubrir por membresía")}</button>
                    ) : null}
                    <button type="button" onClick={() => { setPaymentStatus("covered"); setPaymentMethod("loyalty_reward"); setAmountCharged(""); setBookingLifecycleDecision(null); handleSubmit(null, { allowCompleteWithoutPayment: true }); }}>{t("Cubrir por fidelidad")}</button>
                    <button type="button" onClick={() => { setPaymentStatus("covered"); setPaymentMethod("courtesy"); setAmountCharged(""); setBookingLifecycleDecision(null); handleSubmit(null, { allowCompleteWithoutPayment: true }); }}>{t("Cortesía")}</button>
                    <button type="button" className="primary" onClick={() => {
                      const autofill = Math.max(0, asMoneyNumber(amountCharged, 0) || expectedChargedAmount || totalPrice || 0);
                      setPaymentStatus("paid");
                      setAmountCharged(String(autofill || ""));
                      setBookingLifecycleDecision(null);
                    }}>{t("Registrar pago ahora")}</button>
                  </div>
                </>
              ) : (
                <>
                  <div className="loop-lifecycle-decision-copy">
                    <span>{t("Pago registrado")}</span>
                    <strong id="loop-lifecycle-decision-title">{t("¿También quieres completar la reserva?")}</strong>
                    <p id="loop-lifecycle-decision-copy">{t("Si el cliente ya fue atendido, podemos guardar el pago y dejar la reserva como completada y cobrada.")}</p>
                  </div>
                  <div className="loop-lifecycle-decision-summary">
                    <div><span>{t("Pago")}</span><strong>{t("Pagado")}</strong></div>
                    <div><span>{t("Reserva")}</span><strong>{bookingEditStatusInfo(bookingStatus).label}</strong></div>
                    <div><span>{t("Recomendación")}</span><strong>{t("Completar si ya asistió")}</strong></div>
                  </div>
                  <div className="loop-lifecycle-decision-actions">
                    <button type="button" className="ghost" onClick={() => setBookingLifecycleDecision(null)}>{t("Cancelar")}</button>
                    <button type="button" onClick={() => { setBookingLifecycleDecision(null); handleSubmit(null, { skipPaidCompletionPrompt: true }); }}>{t("Guardar solo pago")}</button>
                    <button type="button" className="primary" onClick={() => { setBookingLifecycleDecision(null); handleSubmit(null, { skipPaidCompletionPrompt: true, forceComplete: true }); }}>{t("Guardar pago y completar")}</button>
                  </div>
                </>
              )}
            </section>
          </div>
        ) : null}


        {showAvailabilitySlotsModal ? (
          <div className="loop-availability-modal-backdrop" role="presentation" onMouseDown={() => setShowAvailabilitySlotsModal(false)}>
            <div className="loop-availability-modal" role="dialog" aria-modal="true" aria-label={t("Horarios disponibles")} onMouseDown={(e) => e.stopPropagation()}>
              <div className="loop-availability-modal-head">
                <div>
                  <span>{t("Disponibilidad")}</span>
                  <strong>{availabilitySource?.label || selectedWorkerLabel || t("Trabajador")}</strong>
                  <small>{availabilityRangeLabel || "Sin horario configurado"}</small>
                </div>
                <button type="button" onClick={() => setShowAvailabilitySlotsModal(false)} aria-label={t("Cerrar horarios")}>✕</button>
              </div>
              {groupedTimeSuggestions.length ? (
                <div className="loop-create-time-suggestions loop-create-time-suggestions-full loop-create-time-suggestions-modal">
                  {groupedTimeSuggestions.map((group) => (
                    <section key={group.key} className="loop-create-time-group">
                      <strong>{group.label}</strong>
                      <div>
                        {group.slots.map((slot) => (
                          <button key={slot} type="button" onClick={() => { setTime(slot); setShowAvailabilitySlotsModal(false); }} className={slot === time ? "is-active" : ""}>{bookingModalFormatTimeLabel(slot, bookingTimeFormat)}</button>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <div className="loop-availability-empty">
                  <strong>{t("Sin horarios sugeridos")}</strong>
                  <span>{t("Revisa la duración, el trabajador o el horario configurado.")}</span>
                </div>
              )}
            </div>
          </div>
        ) : null}

        <PanamaLocationPickerModal
          open={showCustomerLocationPicker}
          onClose={() => setShowCustomerLocationPicker(false)}
          title={t("Ubicación de atención")}
          subtitle="Elige dónde se realizará el servicio. Se validará contra las zonas donde atiende el negocio."
          initialValue={customerLocation}
          requireAllLevels={false}
          allowPartialSelection
          confirmLabel="Usar ubicación"
          onConfirm={(loc) => {
            setCustomerLocation(loc);
            setShowCustomerLocationPicker(false);
          }}
        />

      </div>
    </AccessibleModal>
  );
}

const styles = `

/* LOOP WHATSAPP PHASE B — manual messages + history */

.loop-whatsapp-ready-pill{display:flex;flex-direction:column;gap:2px;border:1px solid #dbeafe;background:#f8fafc;border-radius:18px;padding:9px 12px;min-width:150px;box-shadow:0 10px 24px rgba(15,23,42,.04)}.loop-whatsapp-ready-pill span{font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em}.loop-whatsapp-ready-pill strong{font-size:12px;font-weight:900;color:#0f172a;white-space:nowrap}.loop-whatsapp-ready-pill.is-ok{background:#ecfdf5;border-color:#99f6e4;color:#047857}.loop-whatsapp-ready-pill.is-warning{background:#fffbeb;border-color:#fde68a;color:#b45309}.loop-whatsapp-ready-pill.is-danger{background:#fff1f2;border-color:#fecdd3;color:#be123c}.loop-whatsapp-history-row small{display:block;margin-top:3px;font-size:11px;font-weight:780;color:#64748b;line-height:1.35}.loop-whatsapp-history-row.is-warning{background:#fffbeb;border-color:#fde68a}.loop-whatsapp-history-row.is-warning strong{color:#b45309}


.loop-whatsapp-manual-btn{border-color:#99f6e4!important;background:#f0fdfa!important;color:#00675f!important;}
.loop-whatsapp-manual-btn.is-disabled{opacity:.65;background:#f8fafc!important;color:#94a3b8!important;border-color:#e2e8f0!important;cursor:not-allowed!important;}
.loop-whatsapp-manual-status{margin:-6px 0 2px;border-radius:16px;padding:10px 12px;font-size:12px;font-weight:900;border:1px solid #e2e8f0;background:#f8fafc;color:#334155;}
.loop-whatsapp-manual-status.is-ok{border-color:#99f6e4;background:#ecfdf5;color:#047857;}
.booking-membership-edit-summary{display:flex;justify-content:space-between;gap:12px;align-items:center;border:1px solid #99f6e4;background:linear-gradient(135deg,#f0fdfa,#fff);border-radius:18px;padding:12px 13px;margin:0 18px 12px}.booking-membership-edit-summary span{display:block;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.10em;color:#00796f}.booking-membership-edit-summary strong{display:block;margin-top:3px;color:#0f172a;font-size:14px;font-weight:950}.booking-membership-edit-summary small{display:block;margin-top:3px;color:#64748b;font-size:12px;font-weight:800}.booking-membership-edit-summary em{font-style:normal;border-radius:999px;background:#ccfbf1;color:#0f766e;padding:7px 10px;font-size:11px;font-weight:950;text-align:right;white-space:nowrap}.loop-whatsapp-manual-status.is-error{border-color:#fecaca;background:#fef2f2;color:#dc2626;}
.loop-whatsapp-history-card{border:1px solid rgba(226,232,240,.92);background:linear-gradient(180deg,#fff,#f8fafc);border-radius:20px;padding:12px;display:grid;gap:10px;box-shadow:0 8px 22px rgba(15,23,42,.04);}
.loop-whatsapp-history-head{display:flex;align-items:center;justify-content:space-between;gap:12px;}
.loop-whatsapp-history-head span{display:block;color:#00675f;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.12em;}
.loop-whatsapp-history-head strong{display:block;color:#0f172a;font-size:13px;font-weight:950;margin-top:2px;}
.loop-whatsapp-history-head em{font-style:normal;color:#64748b;font-size:11px;font-weight:850;}
.loop-whatsapp-history-card p{margin:0;color:#64748b;font-size:12px;font-weight:750;}
.loop-whatsapp-history-list{display:grid;gap:7px;}
.loop-whatsapp-history-row{display:flex;align-items:center;justify-content:space-between;gap:10px;border:1px solid #e2e8f0;background:#fff;border-radius:14px;padding:9px 10px;}
.loop-whatsapp-history-row.is-ok{border-color:#ccfbf1;background:#f0fdfa;}
.loop-whatsapp-history-row.is-delivered{border-color:#99f6e4;background:#ecfdf5;}
.loop-whatsapp-history-row.is-read{border-color:#86efac;background:#f0fdf4;}
.loop-whatsapp-history-row.is-error{border-color:#fecaca;background:#fef2f2;}
.loop-whatsapp-history-row.is-read strong{color:#166534}.loop-whatsapp-history-row.is-delivered strong{color:#047857}
.loop-whatsapp-history-row strong{display:block;color:#0f172a;font-size:12px;font-weight:950;}
.loop-whatsapp-history-row span{display:block;color:#64748b;font-size:11px;font-weight:750;margin-top:1px;}
.loop-whatsapp-history-row em{font-style:normal;color:#64748b;font-size:11px;font-weight:850;white-space:nowrap;}

/* LOOP PHASE 2.5D POLISH — professional booking/payment guardrail modal */
.loop-lifecycle-decision-backdrop{position:fixed;inset:0;z-index:10080;display:grid;place-items:center;padding:18px;background:rgba(15,23,42,.38);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);}
.loop-lifecycle-decision-modal{width:min(520px,calc(100vw - 32px));border:1px solid rgba(226,232,240,.96);border-radius:26px;background:#fff;box-shadow:0 28px 90px rgba(15,23,42,.24);padding:20px;display:grid;gap:14px;color:#0f172a;animation:loopLifecycleIn .16s ease-out both;}
@keyframes loopLifecycleIn{from{opacity:0;transform:translateY(8px) scale(.985)}to{opacity:1;transform:translateY(0) scale(1)}}
.loop-lifecycle-decision-icon{width:44px;height:44px;border-radius:16px;display:grid;place-items:center;font-size:20px;font-weight:950;box-shadow:0 12px 28px rgba(15,23,42,.08);}
.loop-lifecycle-decision-modal.is-warning .loop-lifecycle-decision-icon{background:#fffbeb;color:#b45309;border:1px solid #fde68a;}
.loop-lifecycle-decision-modal.is-success .loop-lifecycle-decision-icon{background:#ecfdf5;color:#047857;border:1px solid #99f6e4;}
.loop-lifecycle-decision-modal.is-danger .loop-lifecycle-decision-icon{background:#fef2f2;color:#dc2626;border:1px solid #fecaca;}
.loop-guardrail-notice-modal{z-index:10090;}
.loop-lifecycle-decision-copy span{display:block;color:#00675f;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.13em;margin-bottom:6px;}
.loop-lifecycle-decision-copy strong{display:block;color:#0f172a;font-size:24px;font-weight:950;letter-spacing:-.045em;line-height:1.05;}
.loop-lifecycle-decision-copy p{margin:8px 0 0;color:#64748b;font-size:13px;font-weight:750;line-height:1.48;}
.loop-lifecycle-decision-summary{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;}
.loop-lifecycle-decision-summary div{border:1px solid #e2e8f0;background:#f8fafc;border-radius:16px;padding:10px;min-width:0;}
.loop-lifecycle-decision-summary span{display:block;color:#64748b;font-size:9px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.loop-lifecycle-decision-summary strong{display:block;margin-top:4px;color:#0f172a;font-size:12px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.loop-lifecycle-decision-actions{display:flex;justify-content:flex-end;gap:9px;flex-wrap:wrap;padding-top:2px;}
.loop-lifecycle-decision-actions button{min-height:42px;border-radius:999px;border:1px solid #dbe5ee;background:#fff;color:#334155;padding:0 15px;font-size:13px;font-weight:950;cursor:pointer;box-shadow:0 8px 20px rgba(15,23,42,.05);}
.loop-lifecycle-decision-actions button.ghost{background:#f8fafc;color:#64748b;}
.loop-lifecycle-decision-actions button.primary{border-color:#00675f;background:#00675f;color:#fff;box-shadow:0 14px 30px rgba(0,103,95,.22);}
.loop-lifecycle-decision-actions button.primary.danger{border-color:#dc2626;background:#dc2626;color:#fff;box-shadow:0 14px 30px rgba(220,38,38,.20);}
@media(max-width:640px){.loop-lifecycle-decision-modal{border-radius:22px;padding:16px}.loop-lifecycle-decision-summary{grid-template-columns:1fr}.loop-lifecycle-decision-actions{display:grid;grid-template-columns:1fr}.loop-lifecycle-decision-actions button{width:100%;}}

/* LOOP PHASE 2.2A — Customer 360 in booking create */
.booking-customer360-card{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:8px;margin-top:10px;padding:10px;border:1px solid #dbeafe;background:linear-gradient(135deg,#f8fafc,#eff6ff);border-radius:16px;color:#0f172a}
.booking-customer360-card div{min-width:0;border:1px solid rgba(226,232,240,.9);background:rgba(255,255,255,.78);border-radius:12px;padding:7px 8px}
.booking-customer360-card span{display:block;color:#64748b;font-size:9px;font-weight:950;text-transform:uppercase;letter-spacing:.05em}
.booking-customer360-card strong{display:block;color:#0f172a;font-size:12px;font-weight:950;margin-top:2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.booking-customer360-card p{grid-column:1 / -1;margin:0;color:#475569;font-size:11px;font-weight:800;line-height:1.35}
@media(max-width:720px){.booking-customer360-card{grid-template-columns:repeat(2,minmax(0,1fr));}}

.booking-exact-address-field textarea{width:100%;min-height:82px;border:1px solid #dbe3ee;border-radius:14px;background:#fff;padding:11px 12px;outline:0;font-size:14px;font-weight:750;color:#0f172a;resize:vertical;box-sizing:border-box}
.booking-exact-address-field textarea:focus{border-color:#0f766e;box-shadow:0 0 0 4px rgba(20,184,166,.10)}
.booking-exact-address-field small{color:#64748b;font-size:11px;font-weight:700}
.booking-location-box{border:1px solid #e2e8f0;background:#fff;border-radius:20px;padding:14px;display:flex;align-items:center;justify-content:space-between;gap:14px;box-shadow:0 8px 22px rgba(15,23,42,.045)}
.booking-location-box strong{display:block;color:#0f172a;font-size:14px;font-weight:950}
.booking-location-box span{display:block;color:#64748b;font-size:12px;line-height:1.35;font-weight:750;margin-top:4px}
.booking-location-box button{border:0;background:#0f766e;color:#fff;border-radius:999px;min-height:38px;padding:0 13px;font-size:12px;font-weight:950;cursor:pointer;white-space:nowrap}
.booking-location-box.is-ok{border-color:#99f6e4;background:#f0fdfa}
.booking-location-box.is-error{border-color:#fecaca;background:#fef2f2}
.booking-location-box.is-error span{color:#dc2626}
@media(max-width:640px){.booking-location-box{display:grid}.booking-location-box button{width:100%}}

.booking-pro-modal{color:#0f172a;display:flex!important;flex-direction:column;gap:16px;max-height:min(86vh,820px);overflow:hidden}
.booking-pro-head{display:flex;align-items:flex-start;justify-content:space-between;gap:14px;flex:0 0 auto}
.booking-pro-kicker{font-size:11px;font-weight:950;letter-spacing:.12em;text-transform:uppercase;color:#0f766e}
.booking-pro-head h2{margin:4px 0 0;font-size:24px;letter-spacing:-.045em;line-height:1}
.booking-pro-head p{margin:4px 0 0;color:#64748b;font-size:12px;font-weight:750;text-transform:capitalize}
.booking-pro-close{width:38px;height:38px;border-radius:13px;border:1px solid #e2e8f0;background:#fff;cursor:pointer;font-weight:900}
.booking-pro-form{display:grid;gap:13px;min-height:0;overflow:auto;padding-right:3px;-webkit-overflow-scrolling:touch}
.booking-pro-section{border:1px solid #e2e8f0;background:linear-gradient(180deg,#fff,#fbfdff);border-radius:22px;padding:14px;display:grid;gap:12px}
.booking-pro-section-title{display:flex;align-items:center;gap:10px}
.booking-pro-section-title>span{width:28px;height:28px;border-radius:999px;background:#0f766e;color:#fff;display:grid;place-items:center;font-size:12px;font-weight:950;flex:0 0 auto}
.booking-pro-section-title div{display:grid;gap:1px}.booking-pro-section-title strong{font-size:14px}.booking-pro-section-title small{font-size:12px;color:#64748b;font-weight:650}
.booking-pro-grid{display:grid;gap:10px}.customer-grid{grid-template-columns:1.12fr .88fr;align-items:start}.booking-grid{grid-template-columns:1.25fr .95fr .6fr .52fr}.event-booking-grid{grid-template-columns:1.15fr 1fr .55fr .55fr}
.booking-pro-customer-picker-cell{grid-column:1/-1;min-width:0;position:relative;z-index:5}.booking-pro-customer-picker-cell .loop-customer-picker{width:100%;max-width:100%;position:relative}
.booking-pro-field{display:grid;gap:6px;min-width:0}.booking-pro-field span{font-size:12px;font-weight:850;color:#0f172a}
.booking-pro-field input,.booking-pro-field select{height:44px;border:1px solid #dbe3ee;border-radius:14px;background:#fff;padding:0 12px;outline:0;font-size:14px;font-weight:750;color:#0f172a;min-width:0}
.booking-pro-field input:focus,.booking-pro-field select:focus{border-color:#0f766e;box-shadow:0 0 0 4px rgba(20,184,166,.10)}
.booking-pro-client-status{align-self:end;min-height:44px;border:1px solid #e2e8f0;border-radius:14px;background:#f8fafc;padding:9px 12px;display:grid;gap:1px}.booking-pro-client-status.is-selected{border-color:#99f6e4;background:#f0fdfa}.booking-pro-client-status strong{font-size:12px;color:#0f172a}.booking-pro-client-status span{font-size:11px;color:#64748b}
.booking-pro-no-worker-note{min-height:44px;border:1px solid #ccfbf1;background:#f0fdfa;border-radius:14px;padding:10px 12px;display:grid;gap:2px}.booking-pro-no-worker-note strong{font-size:12px;color:#0f766e}.booking-pro-no-worker-note span{font-size:11px;color:#64748b;font-weight:700}
.booking-event-options{display:grid;gap:12px}.booking-event-block{border:1px solid #e2e8f0;background:#fff;border-radius:18px;padding:12px;display:grid;gap:10px}.booking-event-block>strong{font-size:13px}.booking-event-help{color:#64748b;font-size:11px;font-weight:700;margin-top:-6px}
.booking-option-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:9px}.booking-choice-card{border:1px solid #e2e8f0;background:#fff;border-radius:16px;padding:11px;text-align:left;cursor:pointer}.booking-choice-card.active{border-color:#0f766e;background:#f0fdfa;box-shadow:0 10px 22px rgba(15,118,110,.10)}.booking-choice-card span{display:block;font-size:13px;font-weight:950}.booking-choice-card small{display:block;color:#64748b;font-size:11px;font-weight:750;margin-top:4px}
.booking-staff-grid{display:flex;flex-wrap:wrap;gap:8px}.booking-staff-chip{border:1px solid #e2e8f0;background:#fff;border-radius:999px;padding:9px 11px;font-size:12px;font-weight:900;cursor:pointer}.booking-staff-chip.active{border-color:#0f766e;background:#ecfdf5;color:#0f766e}
.booking-event-summary{border-radius:18px;background:#0f766e;color:#fff;padding:12px 14px;display:flex;justify-content:space-between;gap:12px;font-size:13px;font-weight:950}.booking-event-summary span{opacity:.9}
.booking-pro-tip{font-size:11px;color:#64748b;font-weight:700}.booking-pro-actions{display:flex;justify-content:flex-end;gap:9px;position:sticky;bottom:0;z-index:5;background:linear-gradient(180deg,rgba(255,255,255,.78),#fff 42%);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);border-top:1px solid #eef2f7;margin:0 -2px;padding:12px 2px 2px}
.booking-pro-secondary,.booking-pro-primary{height:42px;border-radius:13px;padding:0 15px;font-weight:900;cursor:pointer}.booking-pro-secondary{border:1px solid #dbe3ee;background:#fff;color:#0f172a}.booking-pro-primary{border:1px solid #0f766e;background:#0f766e;color:#fff}.booking-pro-primary:disabled{opacity:.65;cursor:not-allowed}
@media(max-width:720px){.booking-pro-modal{gap:12px;max-height:calc(92vh - env(safe-area-inset-bottom,0px))}.booking-pro-head h2{font-size:22px}.booking-pro-section{border-radius:20px;padding:12px}.customer-grid,.booking-grid,.event-booking-grid,.booking-option-grid{grid-template-columns:1fr}.booking-pro-actions{padding-bottom:calc(8px + env(safe-area-inset-bottom,0px))}.booking-pro-secondary,.booking-pro-primary{flex:1}.booking-pro-client-status{align-self:stretch}.booking-pro-field input,.booking-pro-field select{height:46px}.booking-event-summary{display:grid}}

/* =========================================================
   Phase 28A-SAFE — Create Booking Pro Modal from existing logic
   ========================================================= */
.loop-create-pro-modal{max-height:min(88vh,860px)!important;gap:14px!important;overflow:hidden!important;}
.loop-create-pro-head{padding-bottom:2px!important;}
.loop-create-pro-head h2{font-size:28px!important;letter-spacing:-.045em!important;}
.loop-create-steps{display:flex;align-items:center;gap:10px;margin:0 2px 2px;color:#64748b;font-size:12px;font-weight:900;white-space:nowrap;}
.loop-create-steps span{display:inline-flex;align-items:center;gap:7px;}
.loop-create-steps span b{width:24px;height:24px;border-radius:999px;background:#e2e8f0;color:#64748b;display:inline-grid;place-items:center;font-size:11px;font-weight:950;}
.loop-create-steps span.is-active{color:#0f766e;}
.loop-create-steps span.is-active b{background:#0f766e;color:#fff;box-shadow:0 8px 18px rgba(15,118,110,.18);}
.loop-create-steps i{height:1px;flex:1;background:#e2e8f0;min-width:32px;}
.loop-create-pro-form{display:grid!important;grid-template-columns:minmax(0,1fr) 360px;gap:18px;min-height:0;overflow:hidden!important;padding:0!important;align-items:start;}
.loop-create-pro-left{display:flex;flex-direction:column;gap:14px;min-height:0;max-height:calc(min(88vh,860px) - 132px);overflow:auto;padding-right:4px;-webkit-overflow-scrolling:touch;}
.loop-create-section{border-radius:22px!important;padding:16px!important;box-shadow:0 12px 30px rgba(15,23,42,.04)!important;background:linear-gradient(180deg,#fff,#fbfdff)!important;}
.loop-create-service-cards{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;}
.loop-create-service-card{position:relative;min-height:88px;border:1.5px solid #e2e8f0;background:#fff;border-radius:18px;padding:13px;text-align:center;cursor:pointer;display:grid;place-items:center;gap:3px;box-shadow:0 8px 20px rgba(15,23,42,.035);transition:transform .14s ease,border-color .14s ease,box-shadow .14s ease,background .14s ease;}
.loop-create-service-card:hover{transform:translateY(-1px);border-color:rgba(15,118,110,.32);box-shadow:0 12px 26px rgba(15,23,42,.06);}
.loop-create-service-card.is-active{border-color:#0f766e;background:linear-gradient(135deg,#f0fdfa,#fff);box-shadow:0 0 0 4px rgba(15,118,110,.09),0 14px 30px rgba(15,118,110,.08);}
.loop-create-service-icon{width:28px;height:28px;border-radius:12px;background:#f1f5f9;color:#0f766e;display:grid;place-items:center;font-size:13px;font-weight:950;}
.loop-create-service-card.is-active .loop-create-service-icon{background:#0f766e;color:#fff;}
.loop-create-service-card strong{font-size:13px;line-height:1.15;color:#0f172a;font-weight:950;}
.loop-create-service-card small{font-size:11px;color:#64748b;font-weight:800;}
.loop-create-service-select{margin-top:2px;}
.loop-create-event-options{margin-top:2px;}
.loop-create-staff-block,.loop-create-location-block{margin-top:2px;}
.loop-create-mode-note{align-self:end;min-height:44px;}
.loop-create-summary-card{position:sticky;top:0;border:1px solid #e2e8f0;border-radius:24px;background:rgba(255,255,255,.98);box-shadow:0 18px 50px rgba(15,23,42,.09);padding:18px;display:grid;gap:14px;max-height:calc(min(88vh,860px) - 132px);overflow:auto;}
.loop-create-summary-card h3{margin:0;font-size:17px;line-height:1.1;font-weight:950;color:#0f172a;letter-spacing:-.025em;}
.loop-create-summary-group{display:grid;gap:10px;padding-bottom:14px;border-bottom:1px solid #edf2f7;}
.loop-create-summary-row{display:grid;grid-template-columns:minmax(0,1fr) minmax(110px,auto);gap:10px;align-items:start;}
.loop-create-summary-label{display:inline-flex;align-items:center;gap:8px;min-width:0;color:#64748b;font-size:12px;font-weight:850;}
.loop-create-summary-label i{font-style:normal;width:15px;text-align:center;opacity:.78;}
.loop-create-summary-row strong{min-width:0;text-align:right;color:#0f172a;font-size:12.5px;font-weight:900;line-height:1.25;overflow:hidden;text-overflow:ellipsis;}
.loop-create-summary-total{display:flex;justify-content:space-between;align-items:center;gap:12px;font-size:15px;font-weight:950;color:#0f172a;}
.loop-create-summary-total strong{font-size:18px;color:#0f766e;}
.loop-create-confirm-box{border-radius:18px;padding:13px 14px;display:grid;gap:4px;border:1px solid #e2e8f0;background:#f8fafc;}
.loop-create-confirm-box.is-auto{border-color:#99f6e4;background:#f0fdfa;}
.loop-create-confirm-box.is-manual{border-color:#fed7aa;background:#fff7ed;}
.loop-create-confirm-box strong{font-size:13px;font-weight:950;color:#0f172a;}
.loop-create-confirm-box span{font-size:12px;line-height:1.35;color:#64748b;font-weight:750;}
.loop-create-summary-actions{display:grid;grid-template-columns:1fr 1.35fr;gap:10px;}
.loop-create-summary-actions .booking-pro-secondary,.loop-create-summary-actions .booking-pro-primary{height:46px!important;border-radius:15px!important;}
.loop-create-secure-note{display:block;text-align:center;color:#64748b;font-size:11px;font-weight:750;}
.loop-create-pro-modal .booking-pro-actions{display:none!important;}
.loop-create-pro-modal .customer-grid{grid-template-columns:1fr 1fr!important;}
.loop-create-pro-modal .booking-pro-customer-picker-cell{grid-column:1/-1!important;}
.loop-create-pro-modal .booking-grid{grid-template-columns:1.15fr .75fr .62fr 1fr!important;}
.loop-create-pro-modal .event-booking-grid{grid-template-columns:1fr .75fr .62fr 1fr!important;}
@media(max-width:980px){.loop-create-pro-form{grid-template-columns:1fr;overflow:auto!important}.loop-create-pro-left{max-height:none;overflow:visible}.loop-create-summary-card{position:static;max-height:none}.loop-create-service-cards{grid-template-columns:repeat(2,minmax(0,1fr))}.loop-create-steps{overflow:auto;padding-bottom:4px}.loop-create-steps i{min-width:26px}}
@media(max-width:640px){.loop-create-service-cards{grid-template-columns:1fr}.loop-create-pro-modal .customer-grid,.loop-create-pro-modal .booking-grid,.loop-create-pro-modal .event-booking-grid{grid-template-columns:1fr!important}.loop-create-summary-actions{grid-template-columns:1fr}.loop-create-summary-row{grid-template-columns:1fr}.loop-create-summary-row strong{text-align:left}.loop-create-steps span{font-size:11px}.loop-create-steps span b{width:22px;height:22px}}


/* Phase 28B — Smart booking validation */
.loop-create-availability-card{border:1px solid #e2e8f0;border-radius:18px;background:#f8fafc;padding:13px 14px;display:grid;gap:11px;}
.loop-create-availability-card.is-ok{border-color:#99f6e4;background:linear-gradient(135deg,#f0fdfa,#ffffff);}
.loop-create-availability-card.is-error{border-color:#fecaca;background:linear-gradient(135deg,#fff7ed,#ffffff);}
.loop-create-availability-head{display:flex;align-items:flex-start;gap:10px;}
.loop-create-availability-head>span{width:28px;height:28px;border-radius:999px;display:grid;place-items:center;font-weight:950;font-size:13px;flex:0 0 auto;}
.loop-create-availability-card.is-ok .loop-create-availability-head>span{background:#0f766e;color:#fff;}
.loop-create-availability-card.is-error .loop-create-availability-head>span{background:#f97316;color:#fff;}
.loop-create-availability-head strong{display:block;color:#0f172a;font-size:13px;font-weight:950;line-height:1.15;}
.loop-create-availability-head small{display:block;color:#64748b;font-size:12px;font-weight:750;line-height:1.35;margin-top:3px;}
.loop-create-time-suggestions{display:grid;gap:8px;}
.loop-create-time-suggestions>span{font-size:11px;font-weight:950;color:#64748b;text-transform:uppercase;letter-spacing:.055em;}
.loop-create-time-suggestions>div{display:flex;flex-wrap:wrap;gap:8px;}
.loop-create-time-suggestions button{height:32px;border-radius:999px;border:1px solid #dbe3ee;background:#fff;color:#0f172a;padding:0 11px;font-size:12px;font-weight:900;cursor:pointer;}
.loop-create-time-suggestions button:hover{border-color:rgba(15,118,110,.35);background:#f0fdfa;color:#0f766e;}
.loop-create-time-suggestions button.is-active{background:#0f766e;border-color:#0f766e;color:#fff;}
.loop-create-confirm-box.is-blocked{border-color:#fecaca;background:#fff7ed;}
.loop-create-summary-row.has-warning strong{color:#d97706;}
.loop-create-summary-row.has-ok strong{color:#0f766e;}
.booking-pro-primary:disabled{opacity:.55!important;cursor:not-allowed!important;box-shadow:none!important;}


/* Phase 28C — Event packages / extras advanced */
.loop-event-pro-zone{gap:12px!important;}
.loop-event-pro-banner{border:1px solid #99f6e4;background:linear-gradient(135deg,#ecfdf5,#ffffff);border-radius:18px;padding:13px 14px;display:grid;grid-template-columns:auto minmax(0,1fr);gap:12px;align-items:center;}
.loop-event-pro-banner>span{height:28px;border-radius:999px;background:#0f766e;color:#fff;display:inline-flex;align-items:center;justify-content:center;padding:0 10px;font-size:10px;font-weight:950;letter-spacing:.08em;}
.loop-event-pro-banner strong{display:block;color:#0f172a;font-size:13px;font-weight:950;line-height:1.15;}
.loop-event-pro-banner small{display:block;color:#64748b;font-size:12px;font-weight:750;line-height:1.35;margin-top:3px;}
.loop-event-block-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;}
.loop-event-block-head strong{display:block;color:#0f172a;font-size:13px;font-weight:950;line-height:1.15;}
.loop-event-block-head small{display:block;color:#64748b;font-size:11px;font-weight:750;line-height:1.35;margin-top:3px;}
.loop-event-block-head em{font-style:normal;white-space:nowrap;border-radius:999px;background:#f0fdfa;color:#0f766e;border:1px solid #ccfbf1;padding:6px 9px;font-size:11px;font-weight:950;}
.loop-event-package-grid,.loop-event-extras-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
.loop-event-package-card,.loop-event-extra-card{min-height:74px;display:grid!important;align-content:center;gap:5px;}
.loop-event-package-card.active,.loop-event-extra-card.active{border-color:#0f766e!important;background:linear-gradient(135deg,#f0fdfa,#ffffff)!important;box-shadow:0 0 0 4px rgba(15,118,110,.09),0 12px 26px rgba(15,118,110,.08)!important;}
.loop-event-empty-state{border:1px dashed #cbd5e1;border-radius:16px;background:#f8fafc;color:#64748b;font-size:12px;font-weight:800;line-height:1.4;padding:13px;text-align:center;}
.loop-create-location-block{display:grid;gap:10px;border:1px solid #e2e8f0;border-radius:18px;background:#fff;padding:12px;}
.loop-create-location-block .booking-location-box{box-shadow:none!important;}
.loop-create-summary-row strong{max-width:190px;}
@media(max-width:640px){.loop-event-package-grid,.loop-event-extras-grid{grid-template-columns:1fr!important}.loop-event-pro-banner{grid-template-columns:1fr}.loop-event-block-head{display:grid}.loop-event-block-head em{justify-self:start}.loop-create-summary-row strong{max-width:none}}



/* =========================================================
   LOOP MINIMAL DATE TIME SUMMARY + POPUP
   ========================================================= */
.loop-datetime-minimal-section{
  padding:12px !important;
  gap:8px !important;
}
.loop-datetime-minimal-card{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:14px;
  min-height:76px;
  border:1px solid #e2e8f0;
  border-radius:18px;
  background:#fff;
  padding:12px 14px;
}
.loop-datetime-minimal-main{
  min-width:0;
  display:grid;
  gap:4px;
}
.loop-datetime-minimal-topline{
  display:flex;
  align-items:center;
  gap:7px;
  min-width:0;
  color:#64748b;
  font-size:11px;
  font-weight:950;
  text-transform:uppercase;
  letter-spacing:.055em;
}
.loop-datetime-step{
  width:20px;
  height:20px;
  border-radius:999px;
  background:#e7fbf7;
  color:#00675f;
  display:inline-grid;
  place-items:center;
  font-size:10px;
  font-weight:950;
  letter-spacing:0;
}
.loop-datetime-minimal-topline em{
  margin-left:2px;
  font-style:normal;
  text-transform:none;
  letter-spacing:0;
  font-size:11px;
  font-weight:950;
  border-radius:999px;
  padding:3px 7px;
  background:#ecfdf5;
  color:#047857;
  white-space:nowrap;
}
.loop-datetime-minimal-topline em.is-error{
  background:#fff7ed;
  color:#c2410c;
}
.loop-datetime-minimal-main strong{
  display:block;
  color:#0f172a;
  font-size:15px;
  line-height:1.15;
  font-weight:950;
  letter-spacing:-.025em;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}
.loop-datetime-minimal-main small{
  display:block;
  color:#64748b;
  font-size:12px;
  line-height:1.25;
  font-weight:800;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}
.loop-datetime-change-btn{
  flex:0 0 auto;
  height:34px;
  border-radius:999px;
  border:1px solid #cfe8e4;
  background:#f7fffd;
  color:#00675f;
  padding:0 13px;
  font-size:12px;
  font-weight:950;
  cursor:pointer;
}
.loop-datetime-inline-warning{
  border:1px solid #fed7aa;
  border-radius:14px;
  background:#fff7ed;
  color:#9a3412;
  padding:9px 11px;
  display:grid;
  gap:2px;
}
.loop-datetime-inline-warning strong{
  font-size:12px;
  font-weight:950;
}
.loop-datetime-inline-warning span{
  font-size:11px;
  font-weight:750;
  line-height:1.35;
}
.loop-datetime-modal-backdrop{
  position:fixed;
  inset:0;
  z-index:10050;
  background:rgba(15,23,42,.32);
  display:flex;
  align-items:center;
  justify-content:center;
  padding:18px;
}
.loop-datetime-modal{
  width:min(100%,620px);
  max-height:calc(100dvh - 36px);
  overflow:auto;
  background:#fff;
  border:1px solid rgba(226,232,240,.96);
  border-radius:24px;
  box-shadow:0 30px 90px rgba(15,23,42,.26);
  padding:0;
}
.loop-datetime-modal-head{
  display:flex;
  align-items:flex-start;
  justify-content:space-between;
  gap:14px;
  padding:18px 18px 14px;
  border-bottom:1px solid #edf2f7;
  background:linear-gradient(180deg,#ffffff,#fbfdfd);
}
.loop-datetime-modal-head span{
  display:block;
  color:#00675f;
  font-size:10px;
  font-weight:950;
  text-transform:uppercase;
  letter-spacing:.08em;
  margin-bottom:5px;
}
.loop-datetime-modal-head strong{
  display:block;
  color:#0f172a;
  font-size:20px;
  line-height:1.05;
  font-weight:950;
  letter-spacing:-.04em;
}
.loop-datetime-modal-head small{
  display:block;
  margin-top:5px;
  color:#64748b;
  font-size:12px;
  font-weight:750;
}
.loop-datetime-modal-head button{
  width:38px;
  height:38px;
  border-radius:999px;
  border:1px solid #e2e8f0;
  background:#fff;
  color:#0f172a;
  font-weight:950;
  cursor:pointer;
}
.loop-datetime-modal-grid{
  padding:16px 18px 0;
  display:grid;
  grid-template-columns:1fr 1fr;
  gap:12px;
}
.loop-datetime-modal-grid .booking-pro-field:nth-child(3){
  grid-column:1 / -1;
}
.loop-datetime-modal-grid.is-event .loop-datetime-modal-note{
  grid-column:1 / -1;
}
.loop-datetime-modal-note,
.loop-datetime-modal-mode{
  border:1px solid #e2e8f0;
  border-radius:16px;
  background:#f8fafc;
  padding:11px 12px;
  display:grid;
  gap:3px;
}
.loop-datetime-modal-mode{
  margin:12px 18px 0;
}
.loop-datetime-modal-note span,
.loop-datetime-modal-mode span,
.loop-datetime-modal-mode small{
  color:#64748b;
  font-size:11px;
  font-weight:800;
}
.loop-datetime-modal-note strong,
.loop-datetime-modal-mode strong{
  color:#0f172a;
  font-size:13px;
  font-weight:950;
}
.loop-datetime-modal .loop-create-availability-card{
  margin:12px 18px 0;
}
.loop-datetime-modal-foot{
  display:flex;
  justify-content:flex-end;
  gap:10px;
  padding:14px 18px 18px;
}
@media(max-width:640px){
  .loop-datetime-minimal-card{align-items:flex-start;padding:11px 12px;}
  .loop-datetime-minimal-main strong{font-size:14px;white-space:normal;}
  .loop-datetime-minimal-main small{white-space:normal;}
  .loop-datetime-change-btn{height:32px;padding:0 11px;}
  .loop-datetime-modal-backdrop{align-items:flex-end;padding:10px;}
  .loop-datetime-modal{border-radius:26px;max-height:calc(100dvh - 20px);}
  .loop-datetime-modal-grid{grid-template-columns:1fr;}
  .loop-datetime-modal-grid .booking-pro-field:nth-child(3){grid-column:auto;}
  .loop-datetime-modal-foot{position:sticky;bottom:0;background:#fff;border-top:1px solid #edf2f7;}
}

/* =========================================================
   Phase 28D — full-day availability slots + source label
   ========================================================= */
.loop-create-availability-source{border:1px solid #e2e8f0;background:#fff;border-radius:16px;padding:10px 12px;display:flex;align-items:center;justify-content:space-between;gap:12px;}
.loop-create-availability-source span{font-size:11px;font-weight:950;color:#64748b;text-transform:uppercase;letter-spacing:.055em;}
.loop-create-availability-source strong{font-size:12px;font-weight:950;color:#0f172a;text-align:right;}
.loop-create-time-suggestions-full{max-height:240px;overflow:auto;padding-right:2px;border-top:1px solid #edf2f7;padding-top:10px;}
.loop-create-time-group{display:grid;gap:7px;}
.loop-create-time-group+ .loop-create-time-group{margin-top:10px;}
.loop-create-time-group>strong{font-size:11px;font-weight:950;color:#0f172a;letter-spacing:.02em;}
.loop-create-time-group>div{display:flex;flex-wrap:wrap;gap:8px;}
@media(max-width:640px){.loop-create-availability-source{display:grid}.loop-create-availability-source strong{text-align:left}.loop-create-time-suggestions-full{max-height:190px}}


/* =========================================================
   Phase 28E — Minimal Booking Modal Polish
   Goal: lighter, cleaner, faster-feeling booking creation.
   Logic remains unchanged; this is a visual/UX refinement layer.
   ========================================================= */
.loop-create-pro-modal{
  gap:12px!important;
  background:#fff!important;
}
.loop-create-pro-head{
  align-items:center!important;
  padding:0 2px 2px!important;
}
.loop-create-pro-head .booking-pro-kicker{
  color:#0f766e!important;
  font-size:10.5px!important;
  letter-spacing:.14em!important;
}
.loop-create-pro-head h2{
  font-size:25px!important;
  line-height:1.02!important;
}
.loop-create-pro-head p{
  font-size:12px!important;
  color:#718096!important;
  margin-top:6px!important;
}
.loop-create-steps{
  background:#f8fafc!important;
  border:1px solid #edf2f7!important;
  border-radius:999px!important;
  padding:7px 10px!important;
  gap:8px!important;
  margin:0 0 2px!important;
}
.loop-create-steps span{
  font-size:11.5px!important;
  font-weight:850!important;
}
.loop-create-steps span b{
  width:20px!important;
  height:20px!important;
  font-size:10px!important;
  box-shadow:none!important;
}
.loop-create-steps i{
  background:#e7edf3!important;
  min-width:18px!important;
}
.loop-create-pro-form{
  grid-template-columns:minmax(0,1fr) 340px!important;
  gap:16px!important;
}
.loop-create-pro-left{
  gap:12px!important;
  padding-right:6px!important;
}
.loop-create-section{
  border-color:#edf2f7!important;
  border-radius:20px!important;
  box-shadow:none!important;
  padding:15px!important;
  background:#fff!important;
}
.loop-create-section:hover{
  border-color:#e2e8f0!important;
}
.booking-pro-section-title>span{
  width:24px!important;
  height:24px!important;
  font-size:11px!important;
  background:#ecfdf5!important;
  color:#0f766e!important;
  border:1px solid #bbf7d0!important;
}
.booking-pro-section-title strong{
  font-size:13.5px!important;
  letter-spacing:-.01em!important;
}
.booking-pro-section-title small{
  font-size:11.5px!important;
  color:#718096!important;
}
.booking-pro-field span{
  font-size:11.5px!important;
  color:#4b5563!important;
}
.booking-pro-field input,
.booking-pro-field select,
.booking-exact-address-field textarea{
  border-color:#e5eaf0!important;
  background:#fbfdff!important;
  border-radius:13px!important;
  font-size:13.5px!important;
  font-weight:750!important;
}
.booking-pro-field input:focus,
.booking-pro-field select:focus,
.booking-exact-address-field textarea:focus{
  background:#fff!important;
  border-color:#0f766e!important;
  box-shadow:0 0 0 3px rgba(15,118,110,.08)!important;
}
.loop-create-service-cards{
  gap:8px!important;
}
.loop-create-service-card{
  min-height:72px!important;
  border-radius:16px!important;
  padding:11px 12px!important;
  text-align:left!important;
  place-items:stretch!important;
  align-content:center!important;
  box-shadow:none!important;
}
.loop-create-service-card:hover{
  transform:none!important;
  box-shadow:none!important;
}
.loop-create-service-card.is-active{
  background:#f0fdfa!important;
  box-shadow:inset 0 0 0 1px rgba(15,118,110,.22)!important;
}
.loop-create-service-icon{
  position:absolute!important;
  top:10px!important;
  right:10px!important;
  width:22px!important;
  height:22px!important;
  border-radius:999px!important;
  font-size:10px!important;
}
.loop-create-service-card strong{
  padding-right:28px!important;
  text-align:left!important;
  font-size:12.5px!important;
}
.loop-create-service-card small{
  text-align:left!important;
  font-size:11px!important;
}
.booking-choice-card,
.loop-event-package-card,
.loop-event-extra-card{
  box-shadow:none!important;
  border-radius:15px!important;
}
.booking-choice-card.active,
.loop-event-package-card.active,
.loop-event-extra-card.active{
  box-shadow:inset 0 0 0 1px rgba(15,118,110,.20)!important;
}
.loop-create-availability-card{
  border-radius:16px!important;
  box-shadow:none!important;
  padding:12px!important;
}
.loop-create-availability-head>span{
  width:24px!important;
  height:24px!important;
  font-size:11px!important;
}
.loop-create-availability-source{
  border-radius:14px!important;
  padding:9px 10px!important;
}
.loop-create-time-suggestions-full{
  max-height:210px!important;
}
.loop-create-time-group>strong{
  color:#475569!important;
}
.loop-create-time-suggestions button,
.loop-create-time-group button{
  height:30px!important;
  border-radius:999px!important;
  padding:0 10px!important;
  font-size:11.5px!important;
  box-shadow:none!important;
}
.loop-create-time-suggestions button.is-active,
.loop-create-time-group button.is-active{
  background:#0f766e!important;
  border-color:#0f766e!important;
  color:#fff!important;
}
.loop-create-summary-card{
  border-color:#edf2f7!important;
  border-radius:22px!important;
  box-shadow:0 12px 34px rgba(15,23,42,.055)!important;
  padding:17px!important;
  gap:13px!important;
}
.loop-create-summary-card h3{
  font-size:16px!important;
}
.loop-create-summary-group{
  gap:9px!important;
  padding-bottom:12px!important;
  border-bottom-color:#f1f5f9!important;
}
.loop-create-summary-row{
  grid-template-columns:minmax(0,1fr)!important;
  gap:3px!important;
}
.loop-create-summary-label{
  font-size:10.5px!important;
  text-transform:uppercase!important;
  letter-spacing:.055em!important;
  font-weight:950!important;
  color:#94a3b8!important;
}
.loop-create-summary-label i{
  display:none!important;
}
.loop-create-summary-row strong{
  text-align:left!important;
  max-width:none!important;
  font-size:13px!important;
  font-weight:900!important;
  line-height:1.25!important;
}
.loop-create-summary-total{
  border:1px solid #ccfbf1!important;
  background:#f0fdfa!important;
  border-radius:16px!important;
  padding:12px 13px!important;
}
.loop-create-summary-total span{
  font-size:12px!important;
  font-weight:950!important;
  color:#0f766e!important;
  text-transform:uppercase!important;
  letter-spacing:.05em!important;
}
.loop-create-summary-total strong{
  font-size:20px!important;
}
.loop-create-confirm-box{
  border-radius:16px!important;
  padding:12px!important;
}
.loop-create-confirm-box strong{
  font-size:12.5px!important;
}
.loop-create-confirm-box span{
  font-size:11.5px!important;
}
.loop-create-summary-actions{
  grid-template-columns:1fr!important;
  gap:8px!important;
}
.loop-create-summary-actions .booking-pro-primary{
  order:1!important;
  height:48px!important;
  border-radius:15px!important;
}
.loop-create-summary-actions .booking-pro-secondary{
  order:2!important;
  height:40px!important;
  border-color:transparent!important;
  background:#fff!important;
  color:#64748b!important;
}
.loop-create-secure-note{
  font-size:10.5px!important;
}
.booking-pro-client-status,
.booking-pro-no-worker-note,
.booking-location-box,
.loop-create-location-block,
.booking-event-block{
  box-shadow:none!important;
}
@media(max-width:980px){
  .loop-create-pro-form{grid-template-columns:1fr!important;}
  .loop-create-summary-actions{grid-template-columns:1fr 1fr!important;}
  .loop-create-summary-actions .booking-pro-primary{order:2!important;}
  .loop-create-summary-actions .booking-pro-secondary{order:1!important;border-color:#e2e8f0!important;}
}
@media(max-width:640px){
  .loop-create-steps{border-radius:16px!important;}
  .loop-create-summary-actions{grid-template-columns:1fr!important;}
  .loop-create-service-card{min-height:66px!important;}
}


/* =========================================================
   Phase 28G — Minimal customer block
   ========================================================= */
.loop-customer-minimal-section{
  padding:14px 16px!important;
  gap:12px!important;
}
.loop-customer-minimal-head{
  display:flex!important;
  align-items:center!important;
  justify-content:space-between!important;
  gap:14px!important;
}
.loop-customer-minimal-head strong{
  display:block!important;
  font-size:14px!important;
  font-weight:950!important;
  color:#0f172a!important;
  letter-spacing:-.015em!important;
}
.loop-customer-minimal-head small{
  display:block!important;
  margin-top:2px!important;
  font-size:12px!important;
  line-height:1.25!important;
  font-weight:750!important;
  color:#64748b!important;
}
.loop-customer-state-pill{
  flex:0 0 auto!important;
  height:26px!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  border-radius:999px!important;
  padding:0 10px!important;
  border:1px solid #e2e8f0!important;
  background:#f8fafc!important;
  color:#64748b!important;
  font-size:11px!important;
  font-weight:950!important;
}
.loop-customer-state-pill.is-saved{
  border-color:#99f6e4!important;
  background:#ecfdf5!important;
  color:#0f766e!important;
}
.loop-customer-minimal-search{
  position:relative!important;
  z-index:8!important;
}
.loop-customer-minimal-search .loop-customer-picker,
.loop-customer-minimal-search > *{
  width:100%!important;
  max-width:100%!important;
}
.loop-customer-minimal-fields{
  display:grid!important;
  grid-template-columns:1fr 1fr!important;
  gap:10px!important;
}
.loop-customer-minimal-fields .booking-pro-field span{
  font-size:11px!important;
  color:#64748b!important;
  text-transform:none!important;
  letter-spacing:0!important;
}
.loop-customer-minimal-fields .booking-pro-field input{
  height:40px!important;
  border-radius:12px!important;
  background:#fff!important;
  font-size:13px!important;
  font-weight:800!important;
}
.loop-customer-minimal-note{
  display:flex!important;
  align-items:center!important;
  min-height:30px!important;
  border-radius:12px!important;
  background:#f8fafc!important;
  border:1px solid #edf2f7!important;
  padding:7px 10px!important;
}
.loop-customer-minimal-note span{
  font-size:11.5px!important;
  font-weight:750!important;
  line-height:1.3!important;
  color:#64748b!important;
}
.loop-customer-minimal-note.is-ok{
  background:#f0fdfa!important;
  border-color:#ccfbf1!important;
}
.loop-customer-minimal-note.is-ok span{
  color:#0f766e!important;
}
.loop-customer-minimal-section .booking-pro-section-title,
.loop-customer-minimal-section .booking-pro-client-status,
.loop-customer-minimal-section .customer-grid,
.loop-customer-minimal-section .booking-pro-customer-picker-cell{
  display:none!important;
}
@media(max-width:720px){
  .loop-customer-minimal-fields{grid-template-columns:1fr!important;}
  .loop-customer-minimal-head{align-items:flex-start!important;}
}

/* Phase 29A — Edit Reservation Pro */
.booking-edit-title-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:4px}
.booking-edit-title-row h2{margin:0!important}
.booking-edit-status-pill{display:inline-flex;align-items:center;justify-content:center;min-height:24px;border-radius:999px;padding:0 10px;font-size:11px;font-weight:950;letter-spacing:.02em;border:1px solid #e2e8f0;background:#f8fafc;color:#475569;white-space:nowrap}
.booking-edit-status-pill.is-confirmed{background:#ecfdf5;border-color:#99f6e4;color:#047857}
.booking-edit-status-pill.is-pending{background:#fffbeb;border-color:#fde68a;color:#b45309}
.booking-edit-status-pill.is-cancelled{background:#fef2f2;border-color:#fecaca;color:#dc2626}
.booking-edit-status-pill.is-neutral{background:#f8fafc;border-color:#e2e8f0;color:#475569}
.booking-edit-status-pill.is-progress{background:#eff6ff!important;border-color:#bfdbfe!important;color:#1d4ed8!important;}
.booking-edit-status-pill.is-completed{background:#f0fdf4!important;border-color:#bbf7d0!important;color:#15803d!important;}
.booking-edit-status-pill.is-warning{background:#fff7ed!important;border-color:#fed7aa!important;color:#c2410c!important;}

.booking-edit-summary-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:4px}
.booking-edit-summary-head h3{margin:0!important}
.booking-edit-changes-card{border:1px solid #dbeafe;background:linear-gradient(180deg,#eff6ff,#ffffff);border-radius:18px;padding:12px;margin:12px 0;display:grid;gap:9px}
.booking-edit-changes-card>strong{font-size:12px;font-weight:950;color:#1d4ed8;text-transform:uppercase;letter-spacing:.07em}
.booking-edit-changes-card>small{font-size:12px;font-weight:800;color:#64748b;line-height:1.4}
.booking-edit-changes-list{display:grid;gap:7px}
.booking-edit-change-row{display:grid;gap:3px;padding:8px 0;border-top:1px solid rgba(191,219,254,.72)}
.booking-edit-change-row:first-child{border-top:0;padding-top:0}
.booking-edit-change-row span{font-size:11px;font-weight:950;color:#64748b;text-transform:uppercase;letter-spacing:.07em}
.booking-edit-change-row p{margin:0;display:flex;align-items:center;gap:7px;min-width:0;font-size:12px;line-height:1.35}
.booking-edit-change-row em{font-style:normal;color:#94a3b8;font-weight:850;text-decoration:line-through;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.booking-edit-change-row b{color:#1d4ed8;font-weight:950;flex:0 0 auto}
.booking-edit-change-row strong{color:#0f172a;font-weight:950;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.loop-create-confirm-box.is-edit{background:#eff6ff!important;border-color:#bfdbfe!important;color:#1e3a8a!important}
.loop-create-confirm-box.is-edit strong{color:#1d4ed8!important}
.loop-create-pro-form.is-edit-mode .booking-pro-section-title>span{background:#1d4ed8;color:#fff}
.loop-create-summary-card.is-edit-mode{border-color:#bfdbfe!important;box-shadow:0 18px 50px rgba(37,99,235,.08)!important}

/* =========================================================
   LOOP MOBILE OVERLAY SAFE — BOOKING CREATE MODAL / PHASE 3
   Keeps create/edit booking modal usable above Android/iOS navigation.
   ========================================================= */
:root{
  --loop-overlay-safe-top:max(env(safe-area-inset-top,0px),0px);
  --loop-overlay-safe-bottom:max(env(safe-area-inset-bottom,0px),34px);
}
@media(max-width:720px){
  .loop-create-pro-modal{
    width:100%!important;
    max-width:560px!important;
    max-height:calc(100dvh - var(--loop-overlay-safe-top) - var(--loop-overlay-safe-bottom) - 18px)!important;
    border-radius:28px!important;
    overflow:hidden!important;
    display:flex!important;
    flex-direction:column!important;
    margin:0 auto!important;
  }
  .loop-create-pro-head{
    flex:0 0 auto!important;
    position:sticky!important;
    top:0!important;
    z-index:10!important;
    padding:14px 16px 10px!important;
    background:rgba(255,255,255,.97)!important;
    backdrop-filter:blur(14px)!important;
    -webkit-backdrop-filter:blur(14px)!important;
    border-bottom:1px solid #eef2f7!important;
  }
  .loop-create-steps{
    flex:0 0 auto!important;
    overflow-x:auto!important;
    scrollbar-width:none!important;
    -webkit-overflow-scrolling:touch!important;
    padding:0 16px 6px!important;
    margin:0!important;
  }
  .loop-create-steps::-webkit-scrollbar{display:none!important;}
  .loop-create-pro-form{
    flex:1 1 auto!important;
    min-height:0!important;
    overflow-y:auto!important;
    overscroll-behavior:contain!important;
    -webkit-overflow-scrolling:touch!important;
    padding:12px 14px calc(94px + var(--loop-overlay-safe-bottom))!important;
    box-sizing:border-box!important;
  }
  .booking-pro-actions,
  .loop-create-summary-actions{
    position:sticky!important;
    bottom:0!important;
    z-index:20!important;
    margin-left:-14px!important;
    margin-right:-14px!important;
    padding:10px 14px calc(10px + var(--loop-overlay-safe-bottom))!important;
    background:linear-gradient(180deg,rgba(255,255,255,.74),rgba(255,255,255,.98) 38%,#fff)!important;
    backdrop-filter:blur(14px)!important;
    -webkit-backdrop-filter:blur(14px)!important;
    border-top:1px solid #eef2f7!important;
  }
  .booking-pro-secondary,
  .booking-pro-primary{
    min-height:46px!important;
    height:46px!important;
    border-radius:16px!important;
    font-size:13.5px!important;
  }
  .loop-create-summary-card{
    position:relative!important;
    top:auto!important;
    max-height:none!important;
    overflow:visible!important;
  }
  .booking-pro-field input,
  .booking-pro-field select,
  .booking-exact-address-field textarea{
    font-size:16px!important;
  }
}

.booking-payment-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:12px;}
.booking-payment-preview{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-top:12px;}
.booking-payment-options{display:grid;gap:8px;margin-top:12px}.booking-payment-options label{display:flex;align-items:flex-start;gap:8px;color:#475569;font-size:12px;font-weight:850}.booking-payment-options input{margin-top:2px;accent-color:#00675f;}
.booking-payment-preview div{border:1px solid #e2e8f0;background:#f8fafc;border-radius:16px;padding:11px 12px;}
.booking-payment-preview span{display:block;color:#64748b;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;}
.booking-payment-preview strong{display:block;margin-top:4px;color:#00675f;font-size:18px;font-weight:950;letter-spacing:-.04em;}
@media(max-width:720px){.booking-payment-grid,.booking-payment-preview{grid-template-columns:1fr!important;}}

/* Loop payment/comission minimal phase */
.booking-payment-quick-card{display:grid;grid-template-columns:1.05fr 1fr 1fr;gap:10px;border:1px solid #e7edf3;background:#fbfdff;border-radius:22px;padding:12px;}
.booking-payment-main-field{margin:0!important;}
.booking-payment-main-field select,.booking-payment-main-field input{background:#fff!important;}
.booking-payment-advanced-toggle{width:100%;min-height:46px;margin-top:12px;border:1px solid #dbe5ee;border-radius:16px;background:#fff;color:#0f172a;display:flex;align-items:center;justify-content:space-between;gap:12px;padding:0 14px;font-size:13px;font-weight:950;cursor:pointer;}
.booking-payment-advanced-toggle span{color:#334155;}
.booking-payment-advanced-toggle strong{border-radius:999px;background:#f0fdfa;color:#00675f;border:1px solid #ccfbf1;padding:6px 9px;font-size:11px;font-weight:950;}
.booking-payment-advanced-panel{margin-top:10px;border:1px solid #eef2f7;background:#fff;border-radius:18px;padding:12px;}
.booking-payment-preview-minimal{margin-top:12px!important;}
.booking-payment-preview-minimal div{background:#fff!important;}
@media(max-width:760px){.booking-payment-quick-card{grid-template-columns:1fr!important;padding:10px;border-radius:18px}.booking-payment-advanced-toggle{min-height:44px}}


/* Loop availability compact modal phase */
.loop-create-availability-card-compact{display:flex!important;align-items:center!important;justify-content:space-between!important;gap:12px!important;padding:12px 14px!important;}
.loop-create-availability-card-compact .loop-create-availability-head{flex:1 1 auto!important;min-width:0!important;}
.loop-create-availability-card-compact .loop-create-availability-source,.loop-create-availability-card-compact .loop-create-time-suggestions-full{display:none!important;}
.loop-create-availability-open{flex:0 0 auto;min-height:40px;border:1px solid #dbe5ee;background:#fff;color:#0f172a;border-radius:999px;padding:0 12px;display:inline-flex;align-items:center;gap:8px;font-size:12px;font-weight:950;cursor:pointer;box-shadow:0 8px 18px rgba(15,23,42,.045)}
.loop-create-availability-open span{color:#00675f;white-space:nowrap}.loop-create-availability-open strong{min-width:24px;height:24px;border-radius:999px;background:#f0fdfa;border:1px solid #ccfbf1;color:#00675f;display:grid;place-items:center;font-size:11px;font-weight:950}
.loop-availability-modal-backdrop{position:fixed;inset:0;z-index:50000;background:rgba(15,23,42,.42);display:flex;align-items:center;justify-content:center;padding:18px;backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px)}
.loop-availability-modal{width:min(560px,100%);max-height:min(720px,calc(100dvh - 36px));background:#fff;border:1px solid #e2e8f0;border-radius:26px;box-shadow:0 30px 80px rgba(15,23,42,.24);overflow:hidden;display:flex;flex-direction:column}
.loop-availability-modal-head{flex:0 0 auto;display:flex;align-items:flex-start;justify-content:space-between;gap:14px;padding:18px 18px 14px;border-bottom:1px solid #eef2f7;background:linear-gradient(180deg,#fff,#fbfdff)}
.loop-availability-modal-head span{display:block;color:#00675f;font-size:11px;font-weight:950;text-transform:uppercase;letter-spacing:.13em}.loop-availability-modal-head strong{display:block;margin-top:4px;color:#0f172a;font-size:18px;font-weight:950;letter-spacing:-.04em}.loop-availability-modal-head small{display:block;margin-top:4px;color:#64748b;font-size:12px;font-weight:800}.loop-availability-modal-head button{width:38px;height:38px;border-radius:999px;border:1px solid #e2e8f0;background:#fff;color:#0f172a;font-weight:950;cursor:pointer}
.loop-create-time-suggestions-modal{padding:16px;overflow:auto;max-height:calc(100dvh - 180px)}
.loop-create-time-suggestions-modal .loop-create-time-group{padding:12px;border:1px solid #edf2f7;border-radius:18px;background:#fbfdff;margin-bottom:10px}
.loop-availability-empty{padding:22px;display:grid;gap:6px;color:#64748b;font-weight:800}.loop-availability-empty strong{color:#0f172a;font-size:15px;font-weight:950}.loop-availability-empty span{font-size:13px;line-height:1.4}
@media(max-width:640px){.loop-create-availability-card-compact{align-items:flex-start!important;display:grid!important}.loop-create-availability-open{width:100%;justify-content:space-between}.loop-availability-modal-backdrop{align-items:flex-end;padding:0}.loop-availability-modal{width:100%;max-height:82dvh;border-radius:26px 26px 0 0;border-bottom:0}.loop-create-time-suggestions-modal{max-height:calc(82dvh - 96px)}}


/* Phase 1 — Minimal create booking service search + automatic payout clarity */
.loop-service-search-bar{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:10px;align-items:end}
.loop-service-search-bar button{height:42px;border-radius:14px;border:1px solid #dbe5ee;background:#fff;color:#00675f;font-size:12px;font-weight:950;padding:0 14px;cursor:pointer;white-space:nowrap}
.loop-service-quick-grid{margin-top:2px}
.loop-service-results-panel{border:1px solid #e7edf3;background:#fbfdff;border-radius:18px;padding:11px;display:grid;gap:10px}
.loop-service-results-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px}
.loop-service-results-head span{font-size:11px;font-weight:950;color:#00675f;text-transform:uppercase;letter-spacing:.09em}
.loop-service-results-head small{font-size:11px;font-weight:800;color:#64748b;text-align:right;line-height:1.3}
.loop-service-results-scroll{display:grid;gap:7px;max-height:238px;overflow:auto;padding-right:3px;-webkit-overflow-scrolling:touch}
.loop-service-result-row{width:100%;min-height:48px;border:1px solid #e2e8f0;background:#fff;border-radius:14px;padding:9px 11px;display:grid;grid-template-columns:22px minmax(0,1fr) auto;gap:9px;align-items:center;text-align:left;cursor:pointer}
.loop-service-result-row:hover{border-color:rgba(0,103,95,.28);background:#f8fffd}
.loop-service-result-row.is-active{border-color:#99f6e4;background:#ecfdf5;box-shadow:inset 0 0 0 1px rgba(0,103,95,.12)}
.loop-service-result-row>span{width:20px;height:20px;border-radius:999px;display:grid;place-items:center;background:#f1f5f9;color:#00675f;font-size:11px;font-weight:950}
.loop-service-result-row.is-active>span{background:#00675f;color:#fff}
.loop-service-result-row strong{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:13px;font-weight:950;color:#0f172a}
.loop-service-result-row small{white-space:nowrap;font-size:11.5px;font-weight:850;color:#64748b}
.loop-service-empty-state{border:1px dashed #cbd5e1;border-radius:14px;background:#fff;color:#64748b;padding:16px;display:grid;gap:5px;text-align:center}
.loop-service-empty-state strong{color:#0f172a;font-size:13px;font-weight:950}
.loop-service-empty-state span{font-size:12px;font-weight:800}
.loop-auto-payout-card{margin-top:10px;border:1px solid #ccfbf1;background:linear-gradient(135deg,#f0fdfa,#ffffff);border-radius:18px;padding:12px;display:flex;align-items:center;justify-content:space-between;gap:12px}
.loop-auto-payout-card span{display:block;font-size:10.5px;font-weight:950;letter-spacing:.1em;text-transform:uppercase;color:#00675f}
.loop-auto-payout-card strong{display:block;margin-top:3px;font-size:16px;font-weight:950;color:#0f172a;letter-spacing:-.025em}
.loop-auto-payout-card small{display:block;margin-top:3px;font-size:11.5px;font-weight:800;color:#64748b;line-height:1.35}
.loop-auto-payout-card button{flex:0 0 auto;min-height:38px;border-radius:999px;border:1px solid rgba(0,103,95,.20);background:#fff;color:#00675f;padding:0 13px;font-size:12px;font-weight:950;cursor:pointer}
@media(max-width:720px){.loop-service-search-bar{grid-template-columns:1fr}.loop-service-search-bar button{width:100%;justify-content:center}.loop-service-results-head{display:grid}.loop-service-results-head small{text-align:left}.loop-service-results-scroll{max-height:260px}.loop-service-result-row{grid-template-columns:22px minmax(0,1fr);align-items:start}.loop-service-result-row small{grid-column:2;white-space:normal}.loop-auto-payout-card{display:grid}.loop-auto-payout-card button{width:100%}}



/* =========================================================
   LOOP CREATE MODAL — FINAL PROPOSAL LAYOUT HOTFIX
   Goal: one-page minimal proposal layout, not wizard.
   ========================================================= */
.loop-create-pro-modal{
  max-height:min(88vh,820px)!important;
  gap:0!important;
  overflow:hidden!important;
  background:#fff!important;
}
.loop-create-pro-head{
  padding:18px 26px 16px!important;
  border-bottom:1px solid #eef3f7!important;
  margin:0 -16px!important;
  background:linear-gradient(180deg,#fff 0%,#fbfdfc 100%)!important;
}
.loop-create-pro-head .booking-pro-kicker{color:#00675f!important;letter-spacing:.18em!important;font-size:10px!important}
.loop-create-pro-head h2{font-size:26px!important;font-weight:950!important;letter-spacing:-.04em!important;margin-top:8px!important}
.loop-create-pro-head p{font-size:12px!important;color:#64748b!important;font-weight:850!important;text-transform:capitalize!important}
.loop-create-steps{
  margin:0 -16px!important;
  padding:13px 26px!important;
  border-bottom:1px solid #edf3f8!important;
  background:#ffffff!important;
  gap:12px!important;
}
.loop-create-steps span{font-size:12px!important;color:#334155!important;font-weight:900!important}
.loop-create-steps span b{width:22px!important;height:22px!important;font-size:10px!important}
.loop-create-steps span.is-active b{background:#00675f!important}
.loop-create-pro-form{
  grid-template-columns:minmax(0,720px) 300px!important;
  gap:18px!important;
  padding:20px 20px 18px!important;
  background:#ffffff!important;
  overflow:auto!important;
  max-height:calc(min(88vh,820px) - 138px)!important;
  align-items:start!important;
}
.loop-create-pro-left{
  max-height:none!important;
  overflow:visible!important;
  padding:0!important;
  gap:14px!important;
}
.loop-create-section{
  border-radius:18px!important;
  padding:14px!important;
  background:#fff!important;
  border:1px solid #dfe9f1!important;
  box-shadow:0 10px 24px rgba(15,23,42,.035)!important;
}
.loop-create-section .booking-pro-section-title{gap:11px!important}
.loop-create-section .booking-pro-section-title>span{
  width:24px!important;height:24px!important;
  background:#e8fbf7!important;color:#00675f!important;
  border:1px solid #99f6e4!important;
  font-size:11px!important;
}
.loop-create-section .booking-pro-section-title strong{font-size:14px!important;font-weight:950!important}
.loop-create-section .booking-pro-section-title small{font-size:11px!important;color:#64748b!important;font-weight:750!important}
.loop-customer-minimal-section{padding:14px!important}
.loop-customer-minimal-head strong{font-size:14px!important}
.loop-customer-minimal-head small{font-size:11px!important}
.loop-customer-minimal-fields{grid-template-columns:1fr 1fr!important;gap:10px!important}
.loop-service-search-bar{display:grid!important;grid-template-columns:minmax(0,1fr) 112px!important;align-items:end!important;gap:10px!important}
.loop-service-search-bar button{height:42px!important;border-radius:14px!important;border:1px solid #cbdde6!important;background:#fff!important;color:#00675f!important;font-weight:950!important}
.loop-create-service-cards{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:10px!important}
.loop-create-service-card{
  min-height:72px!important;
  align-items:center!important;
  justify-items:start!important;
  text-align:left!important;
  grid-template-columns:26px minmax(0,1fr)!important;
  grid-template-rows:auto auto!important;
  column-gap:10px!important;
  padding:12px!important;
}
.loop-create-service-icon{grid-row:1/3!important;width:24px!important;height:24px!important;border-radius:999px!important}
.loop-create-service-card strong{font-size:13px!important}.loop-create-service-card small{font-size:11px!important;color:#64748b!important}
.loop-service-results-panel{border-radius:16px!important;border:1px solid #e2e8f0!important;background:#fbfdff!important;padding:10px!important}
.loop-service-results-scroll{max-height:220px!important}
.booking-grid{grid-template-columns:1.2fr .62fr .5fr .78fr!important;gap:10px!important}
.booking-pro-field span{font-size:11px!important;color:#334155!important;font-weight:900!important}
.booking-pro-field input,.booking-pro-field select{height:42px!important;border-radius:13px!important;font-size:13px!important}
.loop-create-mode-note,.booking-pro-no-worker-note{min-height:42px!important;border-radius:14px!important}
.loop-create-availability-card{display:flex!important;align-items:center!important;justify-content:space-between!important;gap:12px!important;border-radius:16px!important;padding:12px!important;background:#f8fffd!important;border:1px solid #bdeee4!important}
.loop-create-availability-open{height:38px!important;border-radius:13px!important;white-space:nowrap!important}
.booking-payment-quick-card{grid-template-columns:1fr 1fr 1fr!important;gap:10px!important}
.loop-auto-payout-card{
  border-radius:18px!important;
  background:linear-gradient(180deg,#f0fdfa,#ffffff)!important;
  border:1px solid #99f6e4!important;
  padding:14px!important;
}
.loop-auto-payout-card span{color:#00675f!important;letter-spacing:.12em!important;text-transform:uppercase!important;font-size:10px!important;font-weight:950!important}
.loop-auto-payout-card strong{font-size:20px!important;color:#064e3b!important}
.booking-payment-preview-minimal{grid-template-columns:repeat(3,1fr)!important;border-radius:16px!important}
.loop-create-summary-card{
  position:sticky!important;
  top:0!important;
  width:300px!important;
  max-height:calc(min(88vh,820px) - 178px)!important;
  overflow:auto!important;
  border-radius:20px!important;
  padding:16px!important;
  border:1px solid #dfe9f1!important;
  background:linear-gradient(180deg,#ffffff,#fbfefc)!important;
  box-shadow:0 16px 34px rgba(15,23,42,.055)!important;
}
.loop-create-summary-card h3{font-size:15px!important;margin:0!important;font-weight:950!important}
.loop-create-summary-group{padding:12px 0!important;border-bottom:1px solid #edf2f7!important;gap:8px!important}
.loop-create-summary-row{display:grid!important;grid-template-columns:1fr auto!important;gap:10px!important;align-items:start!important}
.loop-create-summary-label{font-size:10px!important;letter-spacing:.08em!important;text-transform:uppercase!important;color:#94a3b8!important;font-weight:950!important}
.loop-create-summary-label i{display:none!important}
.loop-create-summary-row strong{font-size:12.5px!important;text-align:right!important;color:#0f172a!important;max-width:140px!important;overflow:hidden!important;text-overflow:ellipsis!important}
.loop-create-summary-total{
  margin-top:14px!important;
  min-height:58px!important;
  border-radius:18px!important;
  background:#003f3a!important;
  color:#fff!important;
  padding:0 18px!important;
}
.loop-create-summary-total span{font-size:10px!important;letter-spacing:.15em!important;text-transform:uppercase!important;color:#b7efe7!important;font-weight:950!important}
.loop-create-summary-total strong{font-size:25px!important;color:#14b8a6!important}
.loop-create-confirm-box{border-radius:16px!important;margin-top:12px!important;padding:12px!important;background:#fff7ed!important;border:1px solid #fed7aa!important}
.loop-create-summary-actions{display:grid!important;grid-template-columns:1fr!important;gap:10px!important;margin-top:14px!important}
.loop-create-summary-actions .booking-pro-primary,.loop-create-summary-actions .booking-pro-secondary{height:46px!important;border-radius:15px!important;width:100%!important}
.loop-create-summary-actions .booking-pro-primary{background:#00675f!important;border-color:#00675f!important}
.loop-create-summary-actions .booking-pro-secondary{background:#fff!important}
.loop-create-secure-note{display:block!important;text-align:center!important;margin-top:8px!important;color:#64748b!important;font-size:11px!important;font-weight:750!important}
@media(max-width:980px){
  .loop-create-pro-form{grid-template-columns:1fr!important;padding:16px!important;max-height:calc(90vh - 132px)!important}
  .loop-create-summary-card{position:relative!important;width:auto!important;max-height:none!important;top:auto!important}
  .booking-grid,.booking-payment-quick-card,.loop-customer-minimal-fields{grid-template-columns:1fr!important}
  .loop-create-service-cards{grid-template-columns:1fr!important}
}

/* =========================================================
   LOOP CREATE MODAL — BRAND COLOR POLISH HOTFIX
   Goal: make active UI visually match the cream/teal proposal board.
   ========================================================= */
.loop-create-pro-modal{
  --loop-cream:#fbfaf5;
  --loop-cream-2:#f7f4ec;
  --loop-ink:#0f172a;
  --loop-muted:#64748b;
  --loop-line:#e4edf4;
  --loop-teal:#00675f;
  --loop-teal-dark:#003f3a;
  --loop-teal-soft:#e8fbf7;
  background:var(--loop-cream)!important;
  border:1px solid rgba(0,63,58,.10)!important;
  border-radius:24px!important;
  box-shadow:0 30px 90px rgba(15,23,42,.18)!important;
  max-height:min(90vh,840px)!important;
}
.loop-create-proposal-topbar{
  flex:0 0 auto!important;
  display:flex!important;
  align-items:center!important;
  gap:12px!important;
  margin:-16px -16px 0!important;
  min-height:44px!important;
  padding:0 24px!important;
  color:#fff!important;
  background:linear-gradient(135deg,var(--loop-teal-dark),var(--loop-teal))!important;
  border-radius:24px 24px 0 0!important;
  box-shadow:inset 0 -1px 0 rgba(255,255,255,.10)!important;
}
.loop-create-proposal-topbar span{
  width:22px!important;height:22px!important;border-radius:6px!important;
  display:grid!important;place-items:center!important;
  background:rgba(255,255,255,.92)!important;color:var(--loop-teal-dark)!important;
  font-size:11px!important;font-weight:950!important;line-height:1!important;
}
.loop-create-proposal-topbar strong{
  color:#fff!important;font-size:14px!important;font-weight:950!important;letter-spacing:.01em!important;
}
.loop-create-pro-head{
  margin:0 -16px!important;
  padding:14px 24px 12px!important;
  background:linear-gradient(180deg,#fffdf8 0%,var(--loop-cream) 100%)!important;
  border-bottom:1px solid rgba(0,63,58,.08)!important;
}
.loop-create-pro-head .booking-pro-kicker{color:var(--loop-teal)!important;font-size:9.5px!important;letter-spacing:.2em!important;}
.loop-create-pro-head h2{color:var(--loop-ink)!important;font-size:23px!important;line-height:1.05!important;letter-spacing:-.055em!important;}
.loop-create-pro-head p{color:#64748b!important;font-size:12px!important;font-weight:850!important;}
.loop-create-steps{margin:0 -16px!important;padding:12px 24px!important;background:#fffdf8!important;border-top:1px solid rgba(255,255,255,.65)!important;border-bottom:1px solid rgba(0,63,58,.08)!important;}
.loop-create-steps span{color:#344256!important;font-size:11.5px!important;font-weight:950!important;}
.loop-create-steps span b{background:#e6edf3!important;color:#64748b!important;border:1px solid #dce7ef!important;}
.loop-create-steps span.is-active{color:var(--loop-teal-dark)!important;}
.loop-create-steps span.is-active b{background:var(--loop-teal)!important;border-color:var(--loop-teal)!important;color:#fff!important;box-shadow:0 8px 18px rgba(0,103,95,.20)!important;}
.loop-create-steps i{background:linear-gradient(90deg,#dbe5ee,#eef4f8)!important;}
.loop-create-pro-form{background:var(--loop-cream)!important;grid-template-columns:minmax(0,690px) 304px!important;gap:18px!important;padding:18px!important;}
.loop-create-pro-left{gap:12px!important;}
.loop-create-section{background:#fff!important;border:1px solid rgba(203,213,225,.86)!important;border-radius:19px!important;box-shadow:0 12px 28px rgba(15,23,42,.045)!important;padding:14px!important;}
.loop-create-section:hover{box-shadow:0 16px 36px rgba(15,23,42,.06)!important;border-color:rgba(0,103,95,.20)!important;}
.loop-create-section .booking-pro-section-title>span{background:var(--loop-teal-soft)!important;color:var(--loop-teal)!important;border:1px solid #b7efe7!important;}
.loop-create-section .booking-pro-section-title strong{font-size:14.5px!important;color:var(--loop-ink)!important;}
.loop-create-section .booking-pro-section-title small{color:#64748b!important;font-weight:800!important;}
.loop-customer-minimal-section{background:linear-gradient(180deg,#ffffff 0%,#fffdfa 100%)!important;}
.loop-customer-minimal-head button,.loop-service-search-bar button,.loop-auto-payout-card button,.loop-create-availability-open{border-color:rgba(0,103,95,.18)!important;color:var(--loop-teal)!important;background:#fff!important;}
.booking-pro-field input,.booking-pro-field select,.booking-exact-address-field textarea,.loop-service-search-input{background:#fff!important;border:1px solid #dbe5ee!important;color:var(--loop-ink)!important;box-shadow:none!important;}
.booking-pro-field input:focus,.booking-pro-field select:focus,.booking-exact-address-field textarea:focus,.loop-service-search-input:focus{border-color:rgba(0,103,95,.45)!important;box-shadow:0 0 0 4px rgba(0,103,95,.08)!important;}
.loop-create-service-card,.loop-service-result-row{background:#fff!important;border-color:#dfe9f1!important;box-shadow:0 8px 20px rgba(15,23,42,.035)!important;}
.loop-create-service-card.is-active,.loop-service-result-row.is-active{background:#effdfa!important;border-color:#29c4b8!important;box-shadow:inset 0 0 0 1px rgba(0,103,95,.16),0 12px 26px rgba(0,103,95,.08)!important;}
.loop-create-service-icon,.loop-service-result-row>span{background:#f1f5f9!important;color:var(--loop-teal)!important;}
.loop-create-service-card.is-active .loop-create-service-icon,.loop-service-result-row.is-active>span{background:var(--loop-teal)!important;color:#fff!important;}
.loop-service-results-panel{background:#fffdf8!important;border-color:#e3ebe9!important;}
.loop-create-mode-note,.booking-pro-no-worker-note,.loop-create-availability-card{background:var(--loop-teal-soft)!important;border-color:#b7efe7!important;color:var(--loop-teal-dark)!important;}
.booking-payment-quick-card{background:#fff!important;border:1px solid #dfe9f1!important;border-radius:18px!important;}
.loop-auto-payout-card{background:linear-gradient(135deg,#effdfa 0%,#ffffff 72%)!important;border-color:#b7efe7!important;box-shadow:0 12px 28px rgba(0,103,95,.055)!important;}
.loop-auto-payout-card strong{color:var(--loop-teal-dark)!important;}
.booking-payment-preview-minimal div{background:#fffdf8!important;border-color:#e5eee8!important;}
.booking-payment-preview-minimal strong{color:var(--loop-teal)!important;}
.loop-create-summary-card{background:linear-gradient(180deg,#fffef8 0%,#f8fbf8 100%)!important;border:1px solid rgba(0,63,58,.12)!important;box-shadow:0 18px 42px rgba(15,23,42,.075)!important;}
.loop-create-summary-card::before{content:"";display:block;height:4px;margin:-16px -16px 14px;background:linear-gradient(90deg,var(--loop-teal),#14b8a6);border-radius:20px 20px 0 0;}
.loop-create-summary-card h3{color:var(--loop-ink)!important;font-size:16px!important;}
.loop-create-summary-group{border-bottom:1px solid rgba(0,63,58,.08)!important;}
.loop-create-summary-label{color:#8896a8!important;}
.loop-create-summary-row strong{color:var(--loop-ink)!important;font-weight:950!important;}
.loop-create-summary-total{background:linear-gradient(135deg,var(--loop-teal-dark),#004b44)!important;box-shadow:0 14px 30px rgba(0,63,58,.20)!important;}
.loop-create-summary-total span{color:#a9eee6!important;}
.loop-create-summary-total strong{color:#2dd4bf!important;}
.loop-create-confirm-box{background:#fff7ed!important;border-color:#fed7aa!important;color:#334155!important;}
.loop-create-summary-actions .booking-pro-primary,.booking-pro-primary{background:var(--loop-teal)!important;border-color:var(--loop-teal)!important;color:#fff!important;box-shadow:0 14px 28px rgba(0,103,95,.18)!important;}
.loop-create-summary-actions .booking-pro-primary:hover,.booking-pro-primary:hover{background:var(--loop-teal-dark)!important;border-color:var(--loop-teal-dark)!important;}
.loop-create-summary-actions .booking-pro-secondary,.booking-pro-secondary{background:#fff!important;color:#475569!important;border-color:#dfe9f1!important;}
.booking-pro-close{background:#fff!important;border:1px solid #e2e8f0!important;color:#0f172a!important;box-shadow:0 10px 24px rgba(15,23,42,.06)!important;}
@media(max-width:980px){.loop-create-proposal-topbar{margin:-16px -16px 0!important;padding:0 18px!important;border-radius:22px 22px 0 0!important;}.loop-create-pro-form{grid-template-columns:1fr!important;background:var(--loop-cream)!important;}.loop-create-summary-card::before{margin:-16px -16px 14px!important;}}


/* LOOP CREATE MODAL — FINAL NOISE REDUCTION PAYMENT HOTFIX */
.loop-create-modal-host{padding:0!important;background:transparent!important;box-shadow:none!important;border-radius:0!important;}
.loop-create-pro-modal{max-width:1040px!important;background:#fffdfa!important;border:1px solid rgba(203,213,225,.70)!important;border-radius:18px!important;box-shadow:0 24px 70px rgba(15,23,42,.18)!important;overflow:hidden!important;}
.loop-create-steps{display:none!important;}
.loop-create-pro-head{padding:18px 24px 14px!important;border-bottom:1px solid #edf2f7!important;background:#fffdfa!important;}
.loop-create-pro-head p{margin-top:4px!important;font-size:12px!important;}
.loop-create-pro-form{padding:18px 24px 20px!important;gap:16px!important;background:#fffdfa!important;}
.loop-create-pro-left{gap:10px!important;}
.loop-create-section{border:1px solid rgba(226,232,240,.95)!important;border-radius:14px!important;box-shadow:none!important;background:#fff!important;padding:14px!important;}
.loop-create-section:hover{box-shadow:none!important;border-color:rgba(203,213,225,.95)!important;}
.loop-create-section .booking-pro-section-title{margin-bottom:10px!important;}
.loop-create-section .booking-pro-section-title>span{width:22px!important;height:22px!important;font-size:10px!important;background:#ecfdf5!important;color:#00796f!important;border:1px solid #b7efe7!important;}
.loop-create-section .booking-pro-section-title small,.is-quiet-title small,.loop-customer-minimal-head small,.loop-visually-muted-label,.lcp-label{display:none!important;}
.loop-customer-minimal-head.is-quiet{display:flex!important;align-items:center!important;justify-content:space-between!important;margin-bottom:10px!important;}
.loop-customer-minimal-head.is-quiet strong,.booking-pro-section-title strong{font-size:14px!important;font-weight:950!important;color:#0f172a!important;}
.loop-new-customer-action{height:34px!important;border-radius:999px!important;border:1px solid #dbe5ee!important;background:#fff!important;color:#005f55!important;font-weight:950!important;font-size:12px!important;padding:0 14px!important;cursor:pointer!important;}
.loop-customer-minimal-search{margin-top:0!important;}
.lcp-input-shell{height:42px!important;border-radius:12px!important;box-shadow:none!important;}
.loop-customer-minimal-fields,.loop-customer-minimal-note{display:none!important;}
.loop-service-search-bar{display:grid!important;grid-template-columns:minmax(0,1fr) auto!important;gap:10px!important;align-items:end!important;margin-top:2px!important;}
.loop-service-search-bar .booking-pro-field{margin:0!important;}
.loop-service-search-bar button{height:42px!important;border-radius:12px!important;white-space:nowrap!important;}
.loop-service-quick-grid{grid-template-columns:1fr!important;margin-top:8px!important;}
.loop-create-service-card{min-height:40px!important;display:flex!important;align-items:center!important;gap:10px!important;border-radius:12px!important;padding:9px 12px!important;box-shadow:none!important;background:#f8fffd!important;}
.loop-create-service-card strong{font-size:13px!important;line-height:1.1!important;}
.loop-create-service-card small{margin-left:auto!important;font-size:12px!important;}
.loop-create-service-icon{width:24px!important;height:24px!important;flex:0 0 24px!important;}
.loop-service-results-panel{border-radius:12px!important;box-shadow:none!important;margin-top:8px!important;}
.booking-grid,.event-booking-grid{gap:10px!important;}
.booking-pro-field span{font-size:11px!important;margin-bottom:5px!important;}
.booking-pro-field input,.booking-pro-field select,.booking-pro-field textarea{border-radius:12px!important;box-shadow:none!important;min-height:40px!important;}
.loop-create-mode-note{min-height:58px!important;border-radius:12px!important;padding:9px 12px!important;}
.loop-create-availability-card{border-radius:12px!important;box-shadow:none!important;margin-top:10px!important;padding:9px 12px!important;}
.loop-create-availability-head{gap:8px!important;}
.loop-create-availability-head>span{width:24px!important;height:24px!important;flex:0 0 24px!important;}
.loop-create-availability-open{min-height:36px!important;border-radius:11px!important;}
.loop-payment-simple-section{padding:14px!important;}
.loop-payment-simple-card{display:grid!important;grid-template-columns:minmax(0,1.08fr) minmax(250px,.82fr)!important;gap:14px!important;align-items:stretch!important;}
.loop-payment-simple-left{border:1px solid #edf2f7!important;border-radius:14px!important;background:#fff!important;padding:10px 12px!important;display:grid!important;gap:0!important;}
.loop-payment-row-field,.loop-payment-row-button{display:grid!important;grid-template-columns:minmax(120px,1fr) minmax(110px,.75fr)!important;align-items:center!important;gap:12px!important;min-height:38px!important;border:0!important;border-bottom:1px solid #eef2f7!important;background:transparent!important;padding:0!important;margin:0!important;text-align:left!important;}
.loop-payment-row-field:last-child,.loop-payment-row-button:last-child{border-bottom:0!important;}
.loop-payment-row-field span,.loop-payment-row-button span{font-size:13px!important;color:#64748b!important;font-weight:850!important;text-transform:none!important;letter-spacing:0!important;margin:0!important;}
.loop-payment-row-field input,.loop-payment-row-field select{height:34px!important;min-height:34px!important;border:0!important;background:transparent!important;text-align:right!important;padding:0!important;font-size:13px!important;font-weight:950!important;color:#0f172a!important;}
.loop-payment-row-field input:focus,.loop-payment-row-field select:focus{box-shadow:none!important;outline:none!important;}
.loop-payment-row-button{cursor:pointer!important;color:#0f172a!important;}
.loop-payment-row-button strong{text-align:right!important;color:#0f172a!important;font-size:13px!important;font-weight:950!important;}
.loop-payment-simple-auto{border:1px solid #d7f3ec!important;border-radius:14px!important;background:#f3fffb!important;padding:14px 16px!important;display:grid!important;gap:10px!important;align-content:start!important;}
.loop-payment-simple-auto>strong{font-size:14px!important;color:#005f55!important;font-weight:950!important;}
.loop-payment-simple-auto div{display:flex!important;align-items:center!important;justify-content:space-between!important;gap:12px!important;border-bottom:1px solid rgba(0,103,95,.10)!important;padding-bottom:8px!important;}
.loop-payment-simple-auto div:last-of-type{border-bottom:0!important;padding-bottom:0!important;}
.loop-payment-simple-auto span{font-size:13px!important;color:#64748b!important;font-weight:850!important;}
.loop-payment-simple-auto b{font-size:17px!important;color:#00675f!important;font-weight:950!important;}
.loop-payment-simple-auto small{font-size:11px!important;line-height:1.35!important;color:#64748b!important;font-weight:700!important;}
.loop-payment-advanced-slim{margin-top:10px!important;border-radius:12px!important;min-height:40px!important;}
.loop-payment-advanced-panel-slim{border-radius:12px!important;box-shadow:none!important;}
.booking-payment-preview-minimal{display:none!important;}
.loop-notes-section{padding:12px 14px!important;}
.loop-create-summary-card{box-shadow:none!important;border-radius:14px!important;background:#fff!important;}
.loop-create-summary-row{padding:7px 0!important;}
.loop-create-summary-label i{display:none!important;}
@media(max-width:900px){.loop-payment-simple-card{grid-template-columns:1fr!important}.loop-payment-row-field,.loop-payment-row-button{grid-template-columns:1fr!important;gap:4px!important;padding:7px 0!important}.loop-payment-row-field input,.loop-payment-row-field select,.loop-payment-row-button strong{text-align:left!important}.loop-service-search-bar{grid-template-columns:1fr!important}.loop-create-pro-form{grid-template-columns:1fr!important}.loop-create-summary-card{position:relative!important;top:auto!important}}



/* LOOP CREATE MODAL — FINAL UX POLISH ACCORDIONS INPUTS HOTFIX */
.loop-create-pro-modal{--loop-field-bg:#fffdfa;--loop-field-border:#dfe8ef;--loop-field-border-hover:#b9d8d2;--loop-field-focus:#00675f;}
.loop-create-pro-modal .booking-pro-field select,
.loop-create-pro-modal .booking-pro-field input,
.loop-payment-row-field select,
.loop-payment-row-field input{
  appearance:none!important;
  -webkit-appearance:none!important;
  border:1px solid var(--loop-field-border)!important;
  background:linear-gradient(180deg,#fff 0%,#fffdfa 100%)!important;
  border-radius:10px!important;
  color:#0f172a!important;
  transition:border-color 140ms ease, box-shadow 140ms ease, background 140ms ease!important;
}
.loop-create-pro-modal .booking-pro-field select,
.loop-payment-row-field select{
  padding-right:30px!important;
  background-image:linear-gradient(180deg,#fff 0%,#fffdfa 100%), url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 20 20' fill='none'%3E%3Cpath d='M5 7.5L10 12.5L15 7.5' stroke='%23005f55' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")!important;
  background-repeat:no-repeat,no-repeat!important;
  background-position:0 0, right 10px center!important;
  background-size:auto,14px!important;
}
.loop-create-pro-modal .booking-pro-field select:hover,
.loop-create-pro-modal .booking-pro-field input:hover,
.loop-payment-row-field select:hover,
.loop-payment-row-field input:hover{border-color:var(--loop-field-border-hover)!important;}
.loop-create-pro-modal .booking-pro-field select:focus,
.loop-create-pro-modal .booking-pro-field input:focus,
.loop-payment-row-field select:focus,
.loop-payment-row-field input:focus{border-color:var(--loop-field-focus)!important;box-shadow:0 0 0 3px rgba(0,103,95,.10)!important;outline:none!important;background:#fff!important;}
.loop-payment-simple-left{border-radius:12px!important;background:#fffdfa!important;border-color:#e2e8f0!important;}
.loop-payment-row-field,.loop-payment-row-button{min-height:34px!important;border-bottom-color:#edf2f7!important;}
.loop-payment-row-field span,.loop-payment-row-button span{font-size:12px!important;color:#64748b!important;}
.loop-payment-row-field input,.loop-payment-row-field select{height:30px!important;min-height:30px!important;text-align:left!important;padding:0 30px 0 10px!important;font-size:12.5px!important;background-color:#fff!important;}
.loop-payment-row-field input{padding-right:10px!important;}
.loop-payment-row-button{border-radius:10px!important;padding:0 10px!important;margin-top:4px!important;background:#fbfffd!important;border:1px solid #d7f3ec!important;}
.loop-payment-row-button strong{font-size:12.5px!important;color:#00675f!important;}
.loop-payment-simple-auto{border-radius:12px!important;background:#f7fffb!important;padding:12px 14px!important;box-shadow:none!important;}
.loop-payment-simple-auto>strong{font-size:13px!important;}
.loop-payment-simple-auto div{padding-bottom:7px!important;}
.loop-payment-simple-auto span{font-size:12px!important;}
.loop-payment-simple-auto b{font-size:15px!important;}
.loop-payment-simple-auto small{font-size:10.5px!important;}
.loop-payment-advanced-trigger{margin-top:10px!important;}
.loop-payment-advanced-trigger.is-open .loop-accordion-left i{transform:rotate(0deg)!important;}
.loop-create-accordion-stack{display:grid;gap:8px;margin-top:10px;}
.loop-clean-accordion{border:1px solid #e4edf4;background:#fffdfa;border-radius:12px;overflow:hidden;box-shadow:none;}
.loop-clean-accordion-row{width:100%;min-height:44px;border:0;background:transparent;color:#0f172a;display:flex;align-items:center;justify-content:space-between;gap:14px;padding:8px 12px;cursor:pointer;text-align:left;}
.loop-clean-accordion-row:hover{background:#fbfffd;}
.loop-clean-accordion-row:focus-visible{outline:3px solid rgba(0,103,95,.12);outline-offset:-3px;}
.loop-accordion-left{display:flex;align-items:center;gap:9px;min-width:0;}
.loop-accordion-left i{width:18px;height:18px;border-radius:999px;background:#eefaf7;color:#00675f;border:1px solid #d7f3ec;display:inline-flex;align-items:center;justify-content:center;font-style:normal;font-size:12px;line-height:1;transform:rotate(-90deg);transition:transform 140ms ease;flex:0 0 18px;}
.loop-clean-accordion.is-open .loop-accordion-left i{transform:rotate(0deg);}
.loop-accordion-left strong{font-size:13px;font-weight:950;letter-spacing:-.01em;color:#0f172a;white-space:nowrap;}
.loop-accordion-left small{font-size:11px;font-weight:750;color:#64748b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.loop-clean-accordion-row em{font-style:normal;font-size:11px;font-weight:950;color:#00675f;background:#f0fdfa;border:1px solid #ccfbf1;border-radius:999px;padding:5px 8px;white-space:nowrap;}
.loop-clean-accordion-panel{border-top:1px solid #edf2f7;background:#fff;padding:10px 12px;}
.loop-notes-compact-field{margin:0!important;}
.loop-notes-compact-field textarea{min-height:78px!important;border-radius:10px!important;background:#fffdfa!important;}
.loop-retail-addon-panel{display:flex;align-items:center;justify-content:space-between;gap:12px;}
.loop-retail-empty-state{display:grid;gap:2px;min-width:0;}
.loop-retail-empty-state strong{font-size:13px;font-weight:950;color:#0f172a;}
.loop-retail-empty-state span{font-size:11.5px;font-weight:750;color:#64748b;}
.loop-retail-add-button{height:34px;border:1px solid #cfe9e3;background:#f3fffb;color:#005f55;border-radius:10px;padding:0 12px;font-size:12px;font-weight:950;cursor:pointer;white-space:nowrap;}
.loop-retail-add-button:hover{background:#e9fbf7;}
.booking-payment-advanced-panel.loop-payment-advanced-panel-slim{border:1px solid #e4edf4!important;background:#fffdfa!important;border-radius:12px!important;padding:10px!important;margin-top:8px!important;}
.booking-payment-advanced-panel .booking-payment-grid{gap:8px!important;}
.booking-payment-options{margin-top:8px!important;}
@media(max-width:900px){.loop-payment-simple-card{grid-template-columns:1fr!important}.loop-retail-addon-panel{align-items:stretch;flex-direction:column}.loop-retail-add-button{width:100%}.loop-accordion-left{align-items:flex-start}.loop-accordion-left small{display:none}.loop-payment-row-field{grid-template-columns:1fr!important}.loop-payment-row-field input,.loop-payment-row-field select{text-align:left!important;width:100%!important}}

/* LOOP CREATE MODAL — SERVICE COMBOBOX PAYMENT POPUP HOTFIX */
.loop-create-proposal-topbar{display:none!important;}
.loop-create-pro-modal{max-height:calc(100dvh - 28px)!important;overflow:hidden!important;}
.loop-create-pro-head{padding-top:18px!important;}
.loop-service-search-bar,.loop-create-service-cards,.loop-service-results-panel{display:none!important;}
.loop-service-combobox{position:relative;display:grid;gap:8px;}
.loop-service-combobox-trigger{width:100%;min-height:44px;border:1px solid #dbe7ef;background:#fff;border-radius:12px;padding:8px 12px;display:flex;align-items:center;justify-content:space-between;gap:12px;text-align:left;cursor:pointer;box-shadow:none;}
.loop-service-combobox-trigger:hover{border-color:#b9d8d2;background:#fffdfa;}
.loop-service-combobox.is-open .loop-service-combobox-trigger{border-color:#00675f;box-shadow:0 0 0 3px rgba(0,103,95,.08);}
.loop-service-combobox-main{display:grid;gap:2px;min-width:0;}
.loop-service-combobox-main strong{font-size:14px;font-weight:950;color:#0f172a;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.loop-service-combobox-main small{font-size:11.5px;font-weight:800;color:#64748b;}
.loop-service-combobox-chevron{width:24px;height:24px;border-radius:999px;display:inline-flex;align-items:center;justify-content:center;background:#eefaf7;color:#00675f;font-weight:950;transition:transform 140ms ease;}
.loop-service-combobox.is-open .loop-service-combobox-chevron{transform:rotate(180deg);}
.loop-service-combobox-panel{position:relative;margin-top:2px;border:1px solid #dbe7ef;background:#fff;border-radius:14px;box-shadow:0 16px 42px rgba(15,23,42,.11);padding:8px;z-index:8;}
.loop-service-combobox-search{height:38px;border:1px solid #e3edf4;background:#fbfdff;border-radius:10px;display:flex;align-items:center;gap:8px;padding:0 10px;margin-bottom:8px;color:#00675f;}
.loop-service-combobox-search input{width:100%;border:0!important;outline:0!important;background:transparent!important;box-shadow:none!important;height:34px!important;font-size:13px!important;font-weight:800!important;color:#0f172a!important;padding:0!important;}
.loop-service-combobox-list{max-height:220px;overflow:auto;display:grid;gap:4px;padding-right:2px;}
.loop-service-combobox-option{width:100%;min-height:40px;border:0;background:transparent;border-radius:9px;display:grid;grid-template-columns:24px minmax(0,1fr) auto;align-items:center;gap:8px;padding:7px 8px;text-align:left;cursor:pointer;color:#0f172a;}
.loop-service-combobox-option:hover{background:#f8fafc;}
.loop-service-combobox-option.is-active{background:#eefaf7;color:#00443f;}
.loop-service-option-check{width:20px;height:20px;border-radius:999px;background:#eef2f7;display:inline-flex;align-items:center;justify-content:center;font-size:11px;font-weight:950;color:#00675f;}
.loop-service-combobox-option.is-active .loop-service-option-check{background:#00675f;color:#fff;}
.loop-service-combobox-option strong{font-size:13px;font-weight:920;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.loop-service-combobox-option small{font-size:11.5px;font-weight:850;color:#64748b;white-space:nowrap;}
.loop-service-combobox-foot{padding:8px 4px 2px;color:#94a3b8;font-size:11px;font-weight:850;text-align:right;}
.loop-payment-popup-trigger{width:100%;min-height:42px;margin-top:10px;border:1px solid #e4edf4;background:#fffdfa;border-radius:12px;color:#0f172a;display:flex;align-items:center;justify-content:space-between;gap:14px;padding:8px 12px;cursor:pointer;text-align:left;}
.loop-payment-popup-trigger:hover{background:#fbfffd;border-color:#cfe9e3;}
.loop-payment-popup-trigger .loop-accordion-left i{transform:none!important;}
.loop-payment-popup-trigger em{font-style:normal;font-size:11px;font-weight:950;color:#00675f;background:#f0fdfa;border:1px solid #ccfbf1;border-radius:999px;padding:5px 8px;white-space:nowrap;}
.loop-nested-settings-modal{font-family:Inter,ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif!important;}
.loop-settings-modal-head{padding:16px 18px;border-bottom:1px solid #e6edf3;background:#fffdfa;display:flex;align-items:flex-start;justify-content:space-between;gap:12px;}
.loop-settings-modal-head div{display:grid;gap:3px;}
.loop-settings-modal-head strong{font-size:17px;font-weight:950;color:#0f172a;letter-spacing:-.02em;}
.loop-settings-modal-head span{font-size:12px;font-weight:750;color:#64748b;}
.loop-settings-modal-head button{width:34px;height:34px;border-radius:10px;border:1px solid #dbe7ef;background:#fff;color:#0f172a;cursor:pointer;font-size:16px;}
.loop-settings-modal-body{padding:16px 18px;background:#fff;}
.loop-settings-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
.loop-settings-grid.is-single{grid-template-columns:1fr;}
.loop-settings-field-wide{grid-column:1 / -1;}
.loop-settings-options{margin-top:12px!important;display:grid!important;gap:8px!important;}
.loop-settings-options label{font-size:12px!important;font-weight:800!important;color:#475569!important;display:flex!important;align-items:center!important;gap:8px!important;}
.loop-settings-modal-foot{padding:12px 18px;border-top:1px solid #e6edf3;background:#fbfdff;display:flex;justify-content:flex-end;}
.loop-settings-modal-foot button{height:36px;border:0;border-radius:10px;background:#00675f;color:#fff;font-size:13px;font-weight:950;padding:0 18px;cursor:pointer;}
.loop-clean-accordion{margin-top:0!important;}
.loop-create-accordion-stack .loop-clean-accordion:not(.is-open) .loop-clean-accordion-panel{display:none!important;}
@media(max-width:760px){.loop-settings-grid{grid-template-columns:1fr}.loop-service-combobox-option{grid-template-columns:24px minmax(0,1fr)}.loop-service-combobox-option small{grid-column:2;}.loop-service-combobox-panel{position:relative}.loop-create-pro-modal{max-height:calc(100dvh - 12px)!important;}}

/* LOOP CREATE MODAL — SERVICE + DURATION SINGLE ROW POLISH V56 */
.booking-pro-modal.loop-create-pro-modal .loop-service-combo-row {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) 164px !important;
  gap: 10px !important;
  align-items: stretch !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-combo-row:not(.has-duration) {
  grid-template-columns: minmax(0, 1fr) !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-combo-row .loop-service-combobox {
  min-width: 0 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-combo-row .loop-service-combobox-trigger {
  min-height: 58px !important;
  border-radius: 14px !important;
  padding: 10px 12px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-combobox-action {
  flex: 0 0 auto !important;
  height: 36px !important;
  min-width: 92px !important;
  padding: 0 16px !important;
  border-radius: 999px !important;
  border: 1px solid #cfe8e4 !important;
  background: linear-gradient(180deg, #ffffff 0%, #fbfffd 100%) !important;
  color: #00675f !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  font-size: 12px !important;
  font-weight: 950 !important;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04) !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-combobox-trigger:hover .loop-service-combobox-action {
  border-color: #9fd8d0 !important;
  background: #eefaf7 !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-combobox-chevron {
  display: none !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-duration-inline {
  min-width: 0 !important;
  min-height: 58px !important;
  border: 1px solid #dbe7ef !important;
  border-radius: 14px !important;
  background: linear-gradient(135deg, #f8fffd 0%, #ffffff 100%) !important;
  padding: 9px 10px !important;
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) !important;
  align-content: center !important;
  gap: 6px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-duration-inline-label {
  color: #00675f !important;
  font-size: 10px !important;
  line-height: 1 !important;
  font-weight: 950 !important;
  letter-spacing: .08em !important;
  text-transform: uppercase !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-duration-inline .loop-service-duration-control {
  width: 100% !important;
  height: 34px !important;
  border-radius: 999px !important;
  padding: 0 10px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-duration-inline .loop-service-duration-control input {
  height: 30px !important;
  font-size: 13px !important;
}
.booking-pro-modal.loop-create-pro-modal .loop-service-combobox-panel {
  z-index: 2147483400 !important;
}
@media (max-width: 760px) {
  .booking-pro-modal.loop-create-pro-modal .loop-service-combo-row {
    grid-template-columns: 1fr !important;
    gap: 8px !important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-service-duration-inline {
    min-height: 50px !important;
    grid-template-columns: auto minmax(118px, 150px) !important;
    align-items: center !important;
    align-content: center !important;
  }
}




/* LOOP CREATE MODAL — TOP ACTIONS DATE STATUS HOTFIX */
.loop-booking-top-actions-head{
  display:grid!important;
  grid-template-columns:minmax(220px,1fr) auto auto!important;
  align-items:center!important;
  gap:14px!important;
  padding:18px 22px!important;
}
.loop-booking-head-copy{min-width:0!important;}
.loop-booking-head-actions{
  display:flex!important;
  align-items:center!important;
  justify-content:flex-end!important;
  gap:10px!important;
  min-width:max-content!important;
}
.loop-booking-head-actions .booking-pro-primary,
.loop-booking-head-actions .booking-pro-secondary{
  min-width:140px!important;
  height:42px!important;
  border-radius:14px!important;
  padding:0 18px!important;
}
.loop-booking-head-actions .booking-pro-close{
  position:static!important;
  flex:0 0 42px!important;
  width:42px!important;
  height:42px!important;
}
.loop-booking-status-quick{
  display:flex!important;
  align-items:center!important;
  justify-content:flex-end!important;
  gap:6px!important;
  flex-wrap:wrap!important;
  max-width:430px!important;
}
.loop-booking-status-quick button{
  min-height:32px!important;
  border:1px solid #dbe5ee!important;
  background:#fff!important;
  color:#334155!important;
  border-radius:999px!important;
  padding:0 11px!important;
  font-size:11px!important;
  font-weight:950!important;
  cursor:pointer!important;
  white-space:nowrap!important;
}
.loop-booking-status-quick button.is-active{
  border-color:#00675f!important;
  background:#00675f!important;
  color:#fff!important;
}
.loop-date-field input[type="date"]{
  appearance:auto!important;
  -webkit-appearance:auto!important;
}
.loop-create-summary-actions{display:none!important;}
@media(max-width:980px){
  .loop-booking-top-actions-head{grid-template-columns:1fr!important;align-items:stretch!important;}
  .loop-booking-status-quick,.loop-booking-head-actions{justify-content:flex-start!important;}
  .loop-booking-head-actions .booking-pro-primary,.loop-booking-head-actions .booking-pro-secondary{flex:1 1 auto!important;min-width:0!important;}
}

/* LOOP BOOKING — EFFICIENCY SCROLL RETAIL HOTFIX */
.loop-booking-top-actions-head{
  grid-template-columns:minmax(240px,1fr) minmax(260px,auto) auto!important;
  gap:12px!important;
  align-items:center!important;
  padding:16px 18px!important;
  background:rgba(255,255,255,.98)!important;
  border-bottom:1px solid #eef3f6!important;
}
.loop-booking-head-copy .booking-pro-kicker{font-size:9px!important;letter-spacing:.14em!important;color:#00675f!important;margin-bottom:4px!important;}
.loop-booking-head-copy h2{font-size:22px!important;line-height:1.02!important;}
.loop-booking-head-copy p{font-size:12px!important;line-height:1.35!important;max-width:360px!important;}
.loop-booking-status-quick{
  justify-content:center!important;
  padding:4px!important;
  gap:4px!important;
  background:#f8fafc!important;
  border:1px solid #e2e8f0!important;
  border-radius:999px!important;
  max-width:none!important;
  flex-wrap:nowrap!important;
  overflow:auto!important;
  scrollbar-width:none!important;
}
.loop-booking-status-quick::-webkit-scrollbar{display:none!important;}
.loop-booking-status-quick button{
  min-height:30px!important;
  border:0!important;
  background:transparent!important;
  padding:0 12px!important;
  color:#475569!important;
  box-shadow:none!important;
}
.loop-booking-status-quick button.is-active{
  background:#00675f!important;
  color:#fff!important;
  box-shadow:0 8px 18px rgba(0,103,95,.16)!important;
}
.loop-booking-head-actions .booking-pro-primary{min-width:154px!important;background:#00675f!important;}
.loop-booking-head-actions .booking-pro-secondary{min-width:120px!important;background:#fff!important;}
.loop-create-pro-modal{
  height:min(92dvh,860px)!important;
  max-height:min(92dvh,860px)!important;
  overflow:hidden!important;
  display:flex!important;
}
.loop-create-pro-form{
  flex:1 1 auto!important;
  min-height:0!important;
  overflow:auto!important;
  -webkit-overflow-scrolling:touch!important;
  align-items:start!important;
  scrollbar-gutter:stable!important;
}
.loop-create-pro-left{
  overflow:visible!important;
  max-height:none!important;
  padding-right:0!important;
}
.loop-create-summary-card{
  max-height:none!important;
  overflow:visible!important;
  position:sticky!important;
  top:0!important;
}
.loop-create-summary-card::before{display:none!important;}
.loop-create-section{border-radius:16px!important;padding:14px!important;}
.booking-pro-grid.booking-grid{
  grid-template-columns:minmax(130px,1.05fr) minmax(130px,1.05fr) minmax(92px,.72fr) minmax(88px,.62fr) minmax(118px,.9fr)!important;
  gap:8px!important;
  align-items:end!important;
}
.loop-create-mode-note{
  min-height:40px!important;
  padding:0 12px!important;
  border-radius:12px!important;
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  background:#f3fffb!important;
}
.loop-create-mode-note span{display:none!important;}
.loop-create-mode-note strong{font-size:12px!important;}
.loop-create-availability-card-compact{
  display:flex!important;
  align-items:center!important;
  justify-content:space-between!important;
  gap:10px!important;
  padding:9px 10px!important;
  border-radius:13px!important;
  margin-top:8px!important;
}
.loop-create-availability-head{display:flex!important;align-items:center!important;gap:9px!important;min-width:0!important;}
.loop-create-availability-head span{width:24px!important;height:24px!important;font-size:12px!important;}
.loop-create-availability-head strong{font-size:13px!important;}
.loop-create-availability-head small{font-size:11px!important;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:360px;}
.loop-create-availability-open{height:32px!important;border-radius:10px!important;padding:0 10px!important;gap:8px!important;white-space:nowrap!important;}
.loop-create-availability-open strong{min-width:22px!important;height:22px!important;}
.loop-retail-search-mode{display:grid!important;gap:10px!important;}
.loop-retail-search-box{height:38px;border:1px solid #dbe5ee;border-radius:12px;background:#fff;display:flex;align-items:center;gap:8px;padding:0 12px;}
.loop-retail-search-box span{color:#64748b;font-weight:950;}
.loop-retail-search-box input{border:0;outline:0;background:transparent;width:100%;height:100%;font-size:13px;font-weight:800;color:#0f172a;}
.loop-retail-results{display:grid;gap:6px;max-height:178px;overflow:auto;padding-right:2px;}
.loop-retail-result-row{height:42px;border:1px solid #e2e8f0;background:#fff;border-radius:12px;padding:0 10px;display:grid;grid-template-columns:24px minmax(0,1fr) auto;align-items:center;gap:8px;text-align:left;cursor:pointer;}
.loop-retail-result-row:hover{border-color:#99f6e4;background:#f3fffb;}
.loop-retail-result-row span{width:22px;height:22px;border-radius:999px;background:#ecfdf5;color:#00675f;display:grid;place-items:center;font-weight:950;}
.loop-retail-result-row strong{font-size:12.5px;font-weight:950;color:#0f172a;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.loop-retail-result-row small{font-size:11px;font-weight:800;color:#64748b;white-space:nowrap;}
.loop-retail-selected-list{display:grid;gap:7px;border-top:1px solid #edf2f7;padding-top:10px;}
.loop-retail-selected-row{display:grid;grid-template-columns:minmax(0,1fr) 64px 76px 70px 62px 70px 30px;gap:8px;align-items:end;border:1px solid #edf2f7;border-radius:12px;padding:8px;background:#fff;}
.loop-retail-selected-row>strong{font-size:12.5px;font-weight:950;color:#0f172a;align-self:center;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.loop-retail-selected-row label{display:grid;gap:3px;}
.loop-retail-selected-row label span{font-size:9px;text-transform:uppercase;letter-spacing:.08em;color:#64748b;font-weight:950;}
.loop-retail-selected-row input,.loop-retail-selected-row select{height:30px;border:1px solid #dbe5ee;border-radius:9px;padding:0 8px;font-weight:850;min-width:0;}
.loop-retail-selected-row b{font-size:12px;font-weight:950;color:#00675f;align-self:center;text-align:right;}
.loop-retail-selected-row button{height:30px;border:1px solid #fee2e2;background:#fff;color:#dc2626;border-radius:9px;font-size:18px;font-weight:900;cursor:pointer;}
.loop-retail-total-row{display:flex;align-items:center;justify-content:space-between;border:1px solid #ccfbf1;background:#f0fdfa;border-radius:12px;padding:9px 11px;}
.loop-retail-total-row span{font-size:11px;font-weight:900;color:#0f766e;text-transform:uppercase;letter-spacing:.08em;}
.loop-retail-total-row strong{font-size:15px;font-weight:950;color:#00675f;}
.loop-retail-finance-row{display:flex;align-items:center;justify-content:space-between;gap:8px;border:1px solid #edf2f7;background:#f8fafc;border-radius:12px;padding:8px 10px;flex-wrap:wrap;}
.loop-retail-finance-row span{font-size:11px;font-weight:850;color:#64748b;}
.loop-retail-finance-row strong{font-size:12px;font-weight:950;color:#0f766e;}
@media(max-width:1100px){
  .loop-booking-top-actions-head{grid-template-columns:1fr!important;align-items:stretch!important;}
  .loop-booking-status-quick{justify-content:flex-start!important;border-radius:14px!important;}
  .booking-pro-grid.booking-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
  .loop-create-summary-card{position:relative!important;top:auto!important;}
}
@media(max-width:680px){
  .loop-retail-selected-row{grid-template-columns:1fr 1fr!important;}
  .loop-retail-selected-row>b,.loop-retail-selected-row>button{align-self:center;}
  .loop-create-availability-card-compact{align-items:stretch!important;display:grid!important;}
  .loop-create-availability-head small{white-space:normal;max-width:none;}
}


/* LOOP BOOKING — RESPONSIVE EDITOR FLOW HOTFIX */
.loop-booking-top-actions-head{
  grid-template-columns:minmax(0,1fr) auto!important;
  grid-template-areas:
    "copy actions"
    "status status"!important;
  align-items:start!important;
  gap:10px 14px!important;
  padding:14px 18px!important;
}
.loop-booking-head-copy{grid-area:copy!important;}
.loop-booking-head-actions{grid-area:actions!important;}
.loop-booking-status-quick{
  grid-area:status!important;
  display:grid!important;
  grid-template-columns:repeat(6,minmax(96px,1fr))!important;
  width:100%!important;
  max-width:none!important;
  overflow:visible!important;
  border-radius:16px!important;
  padding:4px!important;
  gap:4px!important;
}
.loop-booking-status-quick button{
  width:100%!important;
  min-width:0!important;
  padding:0 8px!important;
  overflow:visible!important;
  text-overflow:clip!important;
  white-space:nowrap!important;
  font-size:11px!important;
}
.loop-booking-head-actions .booking-pro-primary,
.loop-booking-head-actions .booking-pro-secondary{
  height:40px!important;
}
.loop-mobile-edit-summary{display:none;}
.loop-create-pro-form.is-mobile-collapsed{display:grid!important;}
@media(max-width:1020px){
  .loop-booking-top-actions-head{
    grid-template-columns:1fr!important;
    grid-template-areas:
      "copy"
      "status"
      "actions"!important;
    align-items:stretch!important;
    gap:10px!important;
  }
  .loop-booking-head-copy p{max-width:none!important;}
  .loop-booking-head-actions{justify-content:stretch!important;width:100%!important;}
  .loop-booking-head-actions .booking-pro-primary,
  .loop-booking-head-actions .booking-pro-secondary{flex:1 1 0!important;min-width:0!important;}
  .loop-booking-status-quick{grid-template-columns:repeat(3,minmax(0,1fr))!important;border-radius:16px!important;}
  .loop-booking-status-quick button{font-size:10.5px!important;min-height:32px!important;}
  .loop-create-pro-form{grid-template-columns:1fr!important;}
  .loop-create-summary-card{display:none!important;}
}
@media(max-width:720px){
  .loop-create-modal-host{width:100vw!important;max-width:100vw!important;}
  .loop-create-pro-modal{height:calc(100dvh - 6px)!important;max-height:calc(100dvh - 6px)!important;border-radius:18px 18px 0 0!important;}
  .loop-booking-top-actions-head{padding:12px!important;}
  .loop-booking-head-copy h2{font-size:21px!important;}
  .loop-booking-head-copy p{font-size:11.5px!important;}
  .loop-booking-status-quick{grid-template-columns:repeat(3,minmax(0,1fr))!important;gap:4px!important;}
  .loop-booking-status-quick button{font-size:10px!important;padding:0 4px!important;}
  .loop-booking-head-actions{display:grid!important;grid-template-columns:minmax(0,1fr) minmax(0,.78fr) 42px!important;gap:8px!important;}
  .loop-booking-head-actions .booking-pro-primary,
  .loop-booking-head-actions .booking-pro-secondary{width:100%!important;min-width:0!important;padding:0 10px!important;font-size:13px!important;}
  .loop-booking-head-actions .booking-pro-close{width:42px!important;min-width:42px!important;}
  .loop-create-pro-modal.is-mobile-edit-preview .loop-booking-status-quick{display:none!important;}
  .loop-create-pro-modal.is-mobile-edit-preview .loop-booking-head-actions{display:flex!important;justify-content:flex-end!important;}
  .loop-create-pro-modal.is-mobile-edit-preview .loop-booking-head-actions .booking-pro-primary,
  .loop-create-pro-modal.is-mobile-edit-preview .loop-booking-head-actions .booking-pro-secondary{display:none!important;}
  .loop-create-pro-form.is-create-mode{display:block!important;}
  .loop-create-pro-form.is-edit-mode.is-mobile-collapsed{display:none!important;}
  .loop-mobile-edit-summary{display:none;padding:12px;background:#fff;}
  .loop-create-pro-form.is-edit-mode.is-mobile-collapsed + *{display:none!important;}
  .loop-mobile-edit-summary{display:block;}
  .loop-mobile-edit-summary-card{border:1px solid #e2e8f0;border-radius:18px;background:#fff;padding:14px;display:grid;gap:12px;box-shadow:0 12px 30px rgba(15,23,42,.06);}
  .loop-mobile-edit-main{display:grid;gap:4px;}
  .loop-mobile-edit-main span{justify-self:start;border:1px solid #bfdbfe;background:#eff6ff;color:#1d4ed8;border-radius:999px;padding:4px 8px;font-size:11px;font-weight:950;}
  .loop-mobile-edit-main strong{font-size:20px;line-height:1.1;color:#0f172a;font-weight:950;}
  .loop-mobile-edit-main small{font-size:12px;line-height:1.35;color:#64748b;font-weight:800;}
  .loop-mobile-edit-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;}
  .loop-mobile-edit-grid div{border:1px solid #edf2f7;border-radius:14px;padding:10px;background:#fbfdff;display:grid;gap:4px;}
  .loop-mobile-edit-grid span{font-size:9.5px;text-transform:uppercase;letter-spacing:.08em;font-weight:950;color:#64748b;}
  .loop-mobile-edit-grid strong{font-size:13px;font-weight:950;color:#0f172a;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
  .loop-mobile-edit-open-btn{height:44px;border:0;border-radius:14px;background:#00675f;color:#fff;font-size:14px;font-weight:950;cursor:pointer;}
  .booking-pro-section-title span{width:22px!important;height:22px!important;font-size:10px!important;}
  .booking-pro-section-title strong{font-size:15px!important;}
  .loop-create-section{padding:12px!important;border-radius:16px!important;}
  .booking-pro-grid.booking-grid{grid-template-columns:1fr 1fr!important;}
  .loop-create-mode-note{grid-column:1/-1!important;justify-content:flex-start!important;}
}
@media(max-width:430px){
  .loop-booking-status-quick{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
  .loop-booking-head-actions{grid-template-columns:1fr .78fr 40px!important;}
  .loop-booking-head-actions .booking-pro-primary,.loop-booking-head-actions .booking-pro-secondary{height:42px!important;}
  .booking-pro-grid.booking-grid{grid-template-columns:1fr!important;}
}

/* LOOP PAYMENT SLIM FINANCE PANEL — compact, single-card version */
.loop-payment-section.loop-payment-simple-section{padding:12px!important;}
.loop-payment-slim-card{
  border:1px solid #e4edf4!important;
  border-radius:16px!important;
  background:#ffffff!important;
  padding:10px 12px!important;
  display:grid!important;
  gap:8px!important;
}
.loop-payment-slim-main{
  display:grid!important;
  grid-template-columns:repeat(4,minmax(0,1fr))!important;
  gap:8px!important;
}
.loop-payment-slim-field{
  min-width:0!important;
  display:grid!important;
  gap:5px!important;
  margin:0!important;
}
.loop-payment-slim-field span,
.loop-payment-slim-metric span{
  color:#64748b!important;
  font-size:10.5px!important;
  line-height:1!important;
  font-weight:900!important;
  letter-spacing:.01em!important;
  margin:0!important;
}
.loop-payment-slim-field input,
.loop-payment-slim-field select{
  width:100%!important;
  height:31px!important;
  min-height:31px!important;
  border:1px solid #e3ebf3!important;
  border-radius:10px!important;
  background:#fbfdff!important;
  color:#0f172a!important;
  font-size:12.5px!important;
  font-weight:950!important;
  padding:0 10px!important;
  box-shadow:none!important;
  outline:none!important;
}
.loop-payment-slim-field input:focus,
.loop-payment-slim-field select:focus{border-color:#99d9cf!important;box-shadow:0 0 0 3px rgba(0,103,95,.08)!important;background:#ffffff!important;}
.loop-payment-slim-summary{
  display:grid!important;
  grid-template-columns:repeat(4,minmax(0,1fr))!important;
  border:1px solid #edf2f7!important;
  border-radius:13px!important;
  overflow:hidden!important;
  background:#fbfffd!important;
}
.loop-payment-slim-metric{
  min-height:40px!important;
  border:0!important;
  border-right:1px solid #edf2f7!important;
  background:transparent!important;
  padding:8px 10px!important;
  display:flex!important;
  align-items:center!important;
  justify-content:space-between!important;
  gap:10px!important;
  text-align:left!important;
}
.loop-payment-slim-metric:last-child{border-right:0!important;}
.loop-payment-slim-metric strong{
  color:#00675f!important;
  font-size:13px!important;
  line-height:1!important;
  font-weight:950!important;
  white-space:nowrap!important;
}
.loop-payment-slim-metric.is-primary strong{font-size:15px!important;}
.loop-payment-slim-metric.is-clickable{cursor:pointer!important;}
.loop-payment-slim-metric.is-clickable:hover{background:#f0fdfa!important;}
.loop-payment-slim-settings{
  width:100%!important;
  min-height:34px!important;
  border:1px solid #e4edf4!important;
  background:#ffffff!important;
  border-radius:13px!important;
  color:#0f172a!important;
  display:flex!important;
  align-items:center!important;
  justify-content:space-between!important;
  gap:12px!important;
  padding:6px 10px!important;
  cursor:pointer!important;
  text-align:left!important;
}
.loop-payment-slim-settings:hover{background:#fbfffd!important;border-color:#cfe9e3!important;}
.loop-payment-slim-settings span{min-width:0!important;display:flex!important;align-items:center!important;gap:8px!important;}
.loop-payment-slim-settings i{
  width:20px!important;height:20px!important;border-radius:999px!important;background:#f0fdfa!important;color:#00675f!important;display:inline-flex!important;align-items:center!important;justify-content:center!important;font-style:normal!important;font-size:11px!important;flex:0 0 auto!important;
}
.loop-payment-slim-settings strong{font-size:12.5px!important;font-weight:950!important;color:#0f172a!important;white-space:nowrap!important;}
.loop-payment-slim-settings small{font-size:11px!important;font-weight:800!important;color:#64748b!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;}
.loop-payment-slim-settings em{font-style:normal!important;font-size:10.5px!important;font-weight:950!important;color:#00675f!important;background:#f0fdfa!important;border:1px solid #ccfbf1!important;border-radius:999px!important;padding:4px 8px!important;white-space:nowrap!important;}

@media(max-width:900px){
  .loop-payment-slim-main{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
  .loop-payment-slim-summary{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
  .loop-payment-slim-metric:nth-child(2n){border-right:0!important;}
  .loop-payment-slim-metric:nth-child(-n+2){border-bottom:1px solid #edf2f7!important;}
}
@media(max-width:560px){
  .loop-payment-slim-main{grid-template-columns:1fr!important;}
  .loop-payment-slim-summary{grid-template-columns:1fr!important;}
  .loop-payment-slim-metric{border-right:0!important;border-bottom:1px solid #edf2f7!important;}
  .loop-payment-slim-metric:last-child{border-bottom:0!important;}
  .loop-payment-slim-settings small{display:none!important;}
}

/* =========================================================
   LOOP CUSTOMER PICKER — SCROLL + PRO POLISH V1
   Keeps customer results usable inside the create booking modal.
   ========================================================= */
.loop-customer-minimal-section{
  overflow:visible!important;
}
.loop-customer-minimal-search{
  position:relative!important;
  z-index:180!important;
}
.loop-customer-minimal-search .loop-customer-picker{
  position:relative!important;
  z-index:181!important;
}
.loop-customer-minimal-search .lcp-input-shell{
  height:46px!important;
  border-radius:14px!important;
  border-color:#dbe8ee!important;
  background:#fff!important;
  box-shadow:none!important;
}
.loop-customer-minimal-search .lcp-input-shell.is-open{
  border-color:#00675f!important;
  box-shadow:0 0 0 3px rgba(0,103,95,.08)!important;
}
.loop-customer-minimal-search .lcp-menu{
  z-index:50000!important;
  top:calc(100% + 8px)!important;
  max-height:min(430px, calc(100vh - 230px))!important;
  overflow-y:auto!important;
  overflow-x:hidden!important;
  border-radius:18px!important;
  border:1px solid rgba(203,213,225,.9)!important;
  box-shadow:0 20px 60px rgba(15,23,42,.16)!important;
  scrollbar-width:thin!important;
  scrollbar-color:#cbd5e1 transparent!important;
}
.loop-customer-minimal-search .lcp-menu::-webkit-scrollbar{
  width:8px!important;
}
.loop-customer-minimal-search .lcp-menu::-webkit-scrollbar-track{
  background:transparent!important;
}
.loop-customer-minimal-search .lcp-menu::-webkit-scrollbar-thumb{
  background:#cbd5e1!important;
  border-radius:999px!important;
}
.loop-customer-minimal-search .lcp-use-new{
  min-height:54px!important;
  padding:11px 14px!important;
  background:#ffffff!important;
  border-bottom:1px solid #f1f5f9!important;
}
.loop-customer-minimal-search .lcp-use-new:hover{
  background:#f0fdfa!important;
}
.loop-customer-minimal-search .lcp-use-new-plus{
  width:30px!important;
  height:30px!important;
  background:#00675f!important;
}
.loop-customer-minimal-search .lcp-menu-head{
  position:sticky!important;
  top:0!important;
  z-index:2!important;
  min-height:34px!important;
  padding:8px 14px!important;
  background:rgba(248,250,252,.96)!important;
  backdrop-filter:blur(10px)!important;
  -webkit-backdrop-filter:blur(10px)!important;
}
.loop-customer-minimal-search .lcp-row{
  min-height:56px!important;
  padding:10px 14px!important;
  grid-template-columns:34px minmax(0,1fr) auto!important;
  border-bottom:1px solid #f1f5f9!important;
}
.loop-customer-minimal-search .lcp-row:hover,
.loop-customer-minimal-search .lcp-row.is-active{
  background:#f0fdfa!important;
}
.loop-customer-minimal-search .lcp-avatar{
  width:32px!important;
  height:32px!important;
  background:#ecfeff!important;
  border-color:#ccfbf1!important;
  color:#00675f!important;
}
.loop-customer-minimal-search .lcp-main strong{
  font-size:13px!important;
  font-weight:950!important;
}
.loop-customer-minimal-search .lcp-main small{
  font-size:11px!important;
  color:#64748b!important;
}
.loop-customer-minimal-search .lcp-stats b{
  font-size:12px!important;
  color:#0f172a!important;
}
.loop-customer-minimal-search .lcp-stats small{
  font-size:10px!important;
  color:#94a3b8!important;
}
.loop-customer-minimal-search .lcp-show-more{
  position:sticky!important;
  bottom:0!important;
  z-index:2!important;
  min-height:42px!important;
  background:rgba(248,250,252,.96)!important;
  backdrop-filter:blur(10px)!important;
  -webkit-backdrop-filter:blur(10px)!important;
  border-top:1px solid #e2e8f0!important;
}
@media(max-width:720px){
  .loop-customer-minimal-search .lcp-menu{
    max-height:min(58vh, 420px)!important;
    overflow-y:auto!important;
    border-radius:18px!important;
  }
  .loop-customer-minimal-search .lcp-row{
    grid-template-columns:32px minmax(0,1fr)!important;
  }
  .loop-customer-minimal-search .lcp-stats{
    grid-column:2!important;
    text-align:left!important;
    display:flex!important;
    gap:8px!important;
  }
}

.loop-retail-deduction-hint{display:flex;align-items:center;justify-content:space-between;gap:8px;border:1px dashed #cbd5e1;background:#f8fafc;border-radius:11px;padding:8px 10px;font-size:11px;font-weight:850;color:#64748b;}
.loop-retail-deduction-hint strong{color:#b45309;}
.loop-retail-stock-warning{grid-column:1/-1;color:#b45309;background:#fffbeb;border:1px solid #fde68a;border-radius:9px;padding:6px 8px;font-size:11px;font-weight:850;}
.loop-retail-result-row:disabled{opacity:.58;cursor:not-allowed;background:#f8fafc;}



/* LOOP WOMPI F2A — visible inside the reservation edit modal */
.loop-wompi-inline-card{margin-top:12px;border:1px solid #cdeee9;background:linear-gradient(135deg,#f7fffd 0%,#ffffff 72%);border-radius:18px;padding:14px;box-shadow:0 14px 35px rgba(15,23,42,.06);}
.loop-wompi-inline-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:12px;}
.loop-wompi-inline-head div{display:flex;flex-direction:column;gap:3px;}
.loop-wompi-inline-head span{font-size:10px;font-weight:950;letter-spacing:.09em;text-transform:uppercase;color:#00796f;}
.loop-wompi-inline-head strong{font-size:15px;font-weight:950;color:#0f172a;}
.loop-wompi-inline-head small{font-size:11px;font-weight:800;color:#64748b;}
.loop-wompi-inline-head em{font-style:normal;font-size:11px;font-weight:950;color:#00675f;background:#e6fbf7;border:1px solid #b7efe5;border-radius:999px;padding:7px 10px;white-space:nowrap;}
.loop-wompi-inline-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin-bottom:10px;}
.loop-wompi-inline-grid div{border:1px solid #e2e8f0;background:#fff;border-radius:14px;padding:10px;display:flex;flex-direction:column;gap:3px;}
.loop-wompi-inline-grid span,.loop-wompi-inline-ref span{font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.06em;color:#94a3b8;}
.loop-wompi-inline-grid strong{font-size:15px;font-weight:950;color:#0f172a;}
.loop-wompi-inline-ref{display:flex;align-items:center;justify-content:space-between;gap:8px;border:1px dashed #cbd5e1;background:#f8fafc;border-radius:13px;padding:9px 10px;margin-bottom:10px;}
.loop-wompi-inline-ref strong{font-size:12px;font-weight:950;color:#334155;word-break:break-all;}
.loop-wompi-inline-actions{display:flex;gap:8px;flex-wrap:wrap;margin:10px 0;}
.loop-wompi-inline-actions button{border:1px solid #cbd5e1;background:#fff;color:#334155;border-radius:999px;padding:10px 13px;font-size:12px;font-weight:950;cursor:pointer;}
.loop-wompi-inline-actions button:first-child{background:#00675f;border-color:#00675f;color:white;}
.loop-wompi-inline-actions button.success{background:#0f9f6e;border-color:#0f9f6e;color:white;}
.loop-wompi-inline-actions button:disabled{opacity:.55;cursor:not-allowed;}
.loop-wompi-inline-status{border:1px solid #dbeafe;background:#f8fbff;color:#475569;border-radius:13px;padding:9px 10px;font-size:12px;font-weight:850;line-height:1.35;}
.loop-wompi-inline-status.is-error{border-color:#fde68a;background:#fffbeb;color:#b45309;}
@media(max-width:720px){.loop-wompi-inline-grid{grid-template-columns:1fr}.loop-wompi-inline-head{flex-direction:column}.loop-wompi-inline-head em{align-self:flex-start}}


/* LOOP MOBILE KEYBOARD STABILITY V1 */
.loop-new-customer-stable-modal input,
.loop-customer-minimal-section input,
.loop-customer-picker input {
  -webkit-user-select: text !important;
  user-select: text !important;
  touch-action: manipulation !important;
}
@media (max-width: 980px) {
  .loop-new-customer-stable-modal {
    max-height: calc(100dvh - 28px) !important;
  }
  .loop-new-customer-stable-modal .loop-settings-modal-body {
    max-height: calc(100dvh - 154px) !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch !important;
  }
  .loop-customer-minimal-search,
  .loop-customer-minimal-fields {
    touch-action: manipulation !important;
  }
}


/* LOOP D2.9.1A.2 — reservation modal shell redesign */
.loop-create-pro-modal{gap:10px!important;max-height:min(90vh,860px)!important;}
.loop-booking-top-actions-head{
  display:grid!important;
  grid-template-columns:1fr!important;
  grid-template-areas:
    "copy"
    "actions"
    "status"!important;
  align-items:start!important;
  gap:10px 14px!important;
  padding:14px 18px 10px!important;
  border-bottom:1px solid #e7eef5!important;
  background:rgba(255,255,255,.96)!important;
  position:sticky!important;
  top:0!important;
  z-index:30!important;
  backdrop-filter:blur(14px)!important;
  -webkit-backdrop-filter:blur(14px)!important;
}
.loop-booking-head-copy{grid-area:copy!important;min-width:0!important;display:grid!important;gap:4px!important;}
.loop-booking-head-copy .booking-pro-kicker{font-size:9px!important;letter-spacing:.18em!important;margin:0!important;}
.booking-edit-title-row{display:flex!important;align-items:center!important;gap:8px!important;min-width:0!important;}
.booking-edit-title-row h2,.loop-booking-head-copy h2{font-size:21px!important;line-height:1.02!important;margin:0!important;letter-spacing:-.055em!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;}
.booking-edit-status-pill{padding:5px 8px!important;font-size:10px!important;border-radius:999px!important;white-space:nowrap!important;}
.loop-booking-head-copy p{margin:0!important;max-width:none!important;font-size:11.5px!important;line-height:1.25!important;color:#64748b!important;font-weight:850!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;text-transform:none!important;}
.loop-booking-head-actions{grid-area:actions!important;display:flex!important;align-items:center!important;justify-content:flex-end!important;gap:8px!important;flex-wrap:wrap!important;width:100%!important;}
.loop-booking-head-actions .booking-pro-primary,.loop-booking-head-actions .booking-pro-secondary{height:38px!important;min-width:0!important;padding:0 14px!important;border-radius:14px!important;font-size:13px!important;box-shadow:none!important;}
.loop-booking-head-actions .booking-pro-primary{order:1!important;min-width:148px!important;box-shadow:0 12px 24px rgba(0,103,95,.16)!important;}
.loop-booking-head-actions .loop-whatsapp-manual-btn{order:2!important;}
.loop-booking-head-actions .booking-pro-secondary:not(.loop-whatsapp-manual-btn){order:3!important;}
.loop-booking-head-actions .booking-pro-close{grid-area:close!important;order:99!important;width:40px!important;height:40px!important;min-width:40px!important;border-radius:14px!important;margin-left:2px!important;}
.loop-booking-status-quick{
  grid-area:status!important;
  display:flex!important;
  align-items:center!important;
  gap:6px!important;
  width:100%!important;
  padding:4px!important;
  border-radius:16px!important;
  background:#f8fafc!important;
  border:1px solid #e2e8f0!important;
  overflow-x:auto!important;
  scrollbar-width:none!important;
}
.loop-booking-status-quick::-webkit-scrollbar{display:none!important;}
.loop-booking-status-quick button{flex:1 0 126px!important;height:31px!important;min-height:31px!important;border-radius:12px!important;font-size:10.5px!important;padding:0 9px!important;white-space:nowrap!important;}
.loop-whatsapp-ready-pill{display:none!important;}
.loop-booking-ops-strip{
  display:grid!important;
  grid-template-columns:repeat(4,minmax(0,1fr))!important;
  gap:8px!important;
  padding:0 18px 2px!important;
}
.loop-booking-ops-strip>div{border:1px solid #e3edf5!important;background:#fff!important;border-radius:14px!important;padding:9px 11px!important;min-width:0!important;}
.loop-booking-ops-strip span{display:block!important;font-size:9px!important;font-weight:950!important;letter-spacing:.1em!important;text-transform:uppercase!important;color:#94a3b8!important;margin-bottom:3px!important;}
.loop-booking-ops-strip strong{display:block!important;font-size:12px!important;font-weight:950!important;color:#0f172a!important;overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important;}
.booking-membership-edit-summary{margin:0 18px!important;padding:10px 12px!important;border-radius:16px!important;}
.loop-booking-details-card{margin:0 18px 4px!important;border-radius:16px!important;padding:0!important;overflow:hidden!important;background:#fff!important;}
.loop-booking-details-card .loop-booking-details-summary{list-style:none!important;cursor:pointer!important;display:flex!important;align-items:center!important;justify-content:space-between!important;gap:10px!important;padding:11px 13px!important;}
.loop-booking-details-card .loop-booking-details-summary::-webkit-details-marker{display:none!important;}
.loop-booking-details-summary span{display:block!important;font-size:9px!important;font-weight:950!important;letter-spacing:.1em!important;text-transform:uppercase!important;color:#00796f!important;}
.loop-booking-details-summary strong{font-size:13px!important;font-weight:950!important;color:#0f172a!important;}
.loop-booking-details-summary em{font-style:normal!important;border:1px solid #99f6e4!important;background:#f0fdfa!important;color:#00796f!important;border-radius:999px!important;padding:7px 10px!important;font-size:11px!important;font-weight:950!important;white-space:nowrap!important;}
.loop-booking-details-card[open] .loop-whatsapp-history-list{padding:0 12px 12px!important;}
.loop-booking-details-card[open] p{padding:0 12px 12px!important;margin:0!important;}
.loop-create-pro-form{padding:0 18px 18px!important;gap:12px!important;}
.loop-create-pro-form.is-edit-mode{grid-template-columns:minmax(0,1.45fr) minmax(280px,.72fr)!important;}
.loop-create-section{border-radius:18px!important;padding:12px!important;}
.loop-create-summary-card{top:102px!important;border-radius:18px!important;}
@media(max-width:1060px){
  .loop-booking-top-actions-head{grid-template-columns:1fr!important;grid-template-areas:"copy" "actions" "status"!important;}
  .loop-booking-head-actions{justify-content:stretch!important;}
  .loop-booking-head-actions .booking-pro-primary,.loop-booking-head-actions .booking-pro-secondary{flex:1 1 auto!important;}
  .loop-booking-ops-strip{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
  .loop-create-pro-form.is-edit-mode{grid-template-columns:1fr!important;}
}
@media(max-width:720px){
  .loop-booking-top-actions-head{padding:12px!important;}
  .loop-booking-head-actions{display:grid!important;grid-template-columns:1fr 1fr 40px!important;}
  .loop-booking-head-actions .loop-whatsapp-manual-btn,.loop-booking-head-actions .booking-pro-secondary:not(:last-child){display:none!important;}
  .loop-booking-head-actions .booking-pro-primary{grid-column:1/2!important;min-width:0!important;}
  .loop-booking-head-actions .booking-pro-secondary{grid-column:2/3!important;}
  .loop-booking-head-actions .booking-pro-close{grid-column:3/4!important;}
  .loop-booking-status-quick button{flex:1 0 46%!important;}
  .loop-booking-ops-strip{padding:0 12px!important;grid-template-columns:1fr 1fr!important;}
  .booking-membership-edit-summary,.loop-booking-details-card{margin-left:12px!important;margin-right:12px!important;}
  .loop-create-pro-form{padding:0 12px 14px!important;}
}



/* LOOP D2.9.1A.4 — minimal reservation modal redesign */
.loop-booking-minimal-head{display:grid!important;grid-template-columns:minmax(0,1fr) auto!important;align-items:start!important;gap:14px!important;padding:18px 18px 10px!important;border-bottom:1px solid #e8eef5!important;background:#fff!important;}
.loop-booking-minimal-head .booking-edit-title-row h2{font-size:24px!important;line-height:1.02!important;margin:4px 0 0!important;max-width:none!important;}
.loop-booking-minimal-head .loop-booking-head-copy p{font-size:12px!important;text-transform:none!important;line-height:1.3!important;}
.loop-booking-head-actions-minimal{display:flex!important;align-items:center!important;gap:8px!important;position:relative!important;flex-wrap:nowrap!important;}
.loop-booking-head-actions-minimal .booking-pro-primary{min-height:40px!important;padding:0 18px!important;border-radius:14px!important;}
.loop-more-actions-wrap{position:relative!important;}
.loop-more-actions-btn{min-height:40px!important;padding:0 15px!important;border-radius:14px!important;}
.loop-more-actions-menu{position:absolute!important;right:0!important;top:calc(100% + 8px)!important;z-index:80!important;width:240px!important;background:#fff!important;border:1px solid #dbe5ee!important;border-radius:18px!important;box-shadow:0 22px 60px rgba(15,23,42,.18)!important;padding:8px!important;display:grid!important;gap:4px!important;}
.loop-more-actions-menu button{width:100%!important;text-align:left!important;border:0!important;background:transparent!important;color:#0f172a!important;padding:10px 11px!important;border-radius:12px!important;font-size:12px!important;font-weight:900!important;cursor:pointer!important;}
.loop-more-actions-menu button:hover{background:#f8fafc!important;}
.loop-more-actions-menu button.is-danger{color:#dc2626!important;}
.loop-booking-ops-minimal{display:grid!important;grid-template-columns:repeat(4,minmax(0,1fr))!important;gap:8px!important;padding:10px 16px!important;border-bottom:1px solid #e8eef5!important;background:#fbfdff!important;}
.loop-ops-item{min-height:54px!important;border:1px solid #dbe5ee!important;background:#fff!important;border-radius:16px!important;padding:9px 11px!important;display:grid!important;align-content:center!important;gap:3px!important;text-align:left!important;color:#0f172a!important;cursor:pointer!important;}
.loop-ops-item span{font-size:10px!important;font-weight:950!important;text-transform:uppercase!important;letter-spacing:.08em!important;color:#94a3b8!important;}
.loop-ops-item strong{font-size:13px!important;font-weight:950!important;color:#0f172a!important;overflow:hidden!important;text-overflow:ellipsis!important;white-space:nowrap!important;}
.loop-ops-item em{position:absolute!important;right:10px!important;top:10px!important;font-style:normal!important;font-size:10px!important;font-weight:950!important;color:#00796f!important;background:#ecfdf5!important;border:1px solid #99f6e4!important;border-radius:999px!important;padding:3px 7px!important;}
.loop-ops-item{position:relative!important;}
.loop-ops-item.is-select{cursor:default!important;}
.loop-ops-item.is-select select{border:0!important;background:transparent!important;color:#0f172a!important;font-size:13px!important;font-weight:950!important;outline:none!important;padding:0!important;max-width:100%!important;}
.loop-create-pro-form.is-edit-mode{grid-template-columns:1fr!important;padding-top:10px!important;}
.loop-create-pro-form.is-edit-mode .loop-create-summary-card{display:none!important;}
.loop-create-pro-form.is-edit-mode .loop-create-pro-left{max-width:860px!important;margin:0 auto!important;width:100%!important;}
.loop-customer-compact-insight{display:flex!important;align-items:center!important;justify-content:space-between!important;gap:10px!important;border:1px solid #dbe5ee!important;background:#f8fafc!important;border-radius:16px!important;padding:10px 12px!important;margin-top:10px!important;}
.loop-customer-compact-insight span{font-size:10px!important;font-weight:950!important;letter-spacing:.08em!important;text-transform:uppercase!important;color:#94a3b8!important;}
.loop-customer-compact-insight strong{font-size:12px!important;font-weight:900!important;color:#475569!important;margin-right:auto!important;}
.loop-customer-compact-insight button{border:1px solid #99f6e4!important;background:#f0fdfa!important;color:#00675f!important;border-radius:999px!important;padding:7px 10px!important;font-size:11px!important;font-weight:950!important;cursor:pointer!important;}
.loop-create-pro-form.is-edit-mode .loop-payment-section .booking-pro-section-title{margin-bottom:8px!important;}
.loop-create-pro-form.is-edit-mode .loop-payment-section{padding-bottom:14px!important;}
.loop-create-pro-form.is-edit-mode .loop-payment-slim-summary,.loop-create-pro-form.is-edit-mode .loop-payment-slim-settings{display:none!important;}
.loop-mini-detail-backdrop{position:fixed!important;inset:0!important;z-index:9999!important;background:rgba(15,23,42,.38)!important;display:grid!important;place-items:center!important;padding:20px!important;}
.loop-mini-detail-card{width:min(620px,94vw)!important;max-height:min(78vh,760px)!important;overflow:auto!important;background:#fff!important;border:1px solid #dbe5ee!important;border-radius:24px!important;box-shadow:0 30px 90px rgba(15,23,42,.26)!important;padding:16px!important;display:grid!important;gap:12px!important;}
.loop-mini-detail-head{display:flex!important;align-items:flex-start!important;justify-content:space-between!important;gap:14px!important;border-bottom:1px solid #edf2f7!important;padding-bottom:12px!important;}
.loop-mini-detail-head span{display:block!important;font-size:10px!important;font-weight:950!important;letter-spacing:.1em!important;text-transform:uppercase!important;color:#00796f!important;}
.loop-mini-detail-head strong{display:block!important;font-size:18px!important;font-weight:950!important;color:#0f172a!important;letter-spacing:-.03em!important;}
.loop-mini-detail-head button{width:36px!important;height:36px!important;border-radius:14px!important;border:1px solid #dbe5ee!important;background:#fff!important;font-weight:950!important;cursor:pointer!important;}
.loop-mini-detail-summary{border:1px solid #ccfbf1!important;background:#f0fdfa!important;border-radius:16px!important;padding:12px!important;display:grid!important;gap:3px!important;}
.loop-mini-detail-summary strong{font-size:14px!important;font-weight:950!important;color:#00675f!important;}
.loop-mini-detail-summary span{font-size:12px!important;font-weight:850!important;color:#475569!important;}
.loop-mini-detail-actions{display:flex!important;gap:8px!important;flex-wrap:wrap!important;}
.loop-mini-detail-actions button,.loop-mini-membership-picker button{border:1px solid #99f6e4!important;background:#00675f!important;color:#fff!important;border-radius:999px!important;padding:9px 12px!important;font-size:12px!important;font-weight:950!important;cursor:pointer!important;}
.loop-mini-detail-list{display:grid!important;gap:8px!important;}
.loop-mini-empty{margin:0!important;color:#64748b!important;font-weight:850!important;}
.loop-mini-profile-grid{display:grid!important;grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:8px!important;}
.loop-mini-profile-grid div{border:1px solid #e2e8f0!important;background:#f8fafc!important;border-radius:14px!important;padding:10px!important;min-width:0!important;}
.loop-mini-profile-grid span{display:block!important;font-size:10px!important;font-weight:950!important;letter-spacing:.08em!important;text-transform:uppercase!important;color:#94a3b8!important;margin-bottom:3px!important;}
.loop-mini-profile-grid strong{display:block!important;font-size:13px!important;font-weight:950!important;color:#0f172a!important;overflow:hidden!important;text-overflow:ellipsis!important;}
.loop-mini-detail-head-actions{display:flex!important;align-items:center!important;gap:8px!important;flex-shrink:0!important;}
.loop-mini-detail-head-actions button{width:auto!important;min-width:36px!important;}
.loop-mini-detail-edit-btn{height:36px!important;border-radius:999px!important;padding:0 14px!important;border:1px solid #99f6e4!important;background:#f0fdfa!important;color:#00675f!important;font-size:12px!important;font-weight:950!important;cursor:pointer!important;}
.loop-mini-profile-sync-note{border:1px solid #ccfbf1!important;background:#f0fdfa!important;color:#00675f!important;border-radius:14px!important;padding:10px 12px!important;font-size:12px!important;font-weight:850!important;line-height:1.35!important;}
.loop-mini-profile-sync-note.is-muted{border-color:#e2e8f0!important;background:#f8fafc!important;color:#64748b!important;}
.loop-mini-profile-edit-panel{display:grid!important;gap:12px!important;}
.loop-mini-profile-edit-grid{display:grid!important;grid-template-columns:1fr 1fr!important;gap:10px!important;}
.loop-mini-profile-edit-grid .booking-pro-field:first-child{grid-column:1/-1!important;}
.loop-mini-profile-edit-grid .booking-pro-field{margin:0!important;}
.loop-mini-profile-edit-grid .booking-pro-field input{height:46px!important;}
.loop-mini-profile-edit-error{border:1px solid #fecaca!important;background:#fff7f7!important;color:#b91c1c!important;border-radius:14px!important;padding:10px 12px!important;font-size:12px!important;font-weight:850!important;line-height:1.35!important;}
.loop-mini-profile-edit-actions{display:flex!important;justify-content:flex-end!important;gap:8px!important;flex-wrap:wrap!important;}
.loop-mini-profile-edit-actions button{height:42px!important;border-radius:14px!important;border:1px solid #0f766e!important;background:#00796f!important;color:#fff!important;padding:0 16px!important;font-size:13px!important;font-weight:950!important;cursor:pointer!important;}
.loop-mini-profile-edit-actions button.is-secondary{background:#fff!important;color:#475569!important;border-color:#dbe5ee!important;}
.loop-mini-profile-edit-actions button:disabled{opacity:.62!important;cursor:not-allowed!important;}
.loop-mini-membership-picker{display:grid!important;gap:10px!important;}
.loop-mini-membership-picker label{display:grid!important;gap:6px!important;}
.loop-mini-membership-picker label span{font-size:10px!important;font-weight:950!important;letter-spacing:.08em!important;text-transform:uppercase!important;color:#94a3b8!important;}
.loop-mini-membership-picker select{min-height:42px!important;border-radius:14px!important;border:1px solid #dbe5ee!important;padding:0 10px!important;font-weight:900!important;}
.loop-mini-membership-picker small{font-weight:850!important;color:#64748b!important;}
.loop-mini-membership-picker small.is-error{color:#dc2626!important;}
@media(max-width:760px){.loop-booking-minimal-head{grid-template-columns:1fr!important}.loop-booking-head-actions-minimal{justify-content:space-between!important}.loop-booking-ops-minimal{grid-template-columns:1fr 1fr!important}.loop-mini-profile-grid,.loop-mini-profile-edit-grid{grid-template-columns:1fr!important}.loop-create-pro-form.is-edit-mode .loop-create-pro-left{max-width:none!important}}


/* LOOP D2.9.1A.6 — header overlap + compact action rail fix
   Purpose: the minimal reservation header must never allow long title text
   to sit under the action buttons. Keep the title as a clean single-line
   identity row, move actions into a compact rail below it, and keep the ops
   strip directly under the header. */
.loop-booking-minimal-head{
  display:grid!important;
  grid-template-columns:1fr!important;
  grid-template-areas:"copy" "actions"!important;
  gap:9px!important;
  padding:14px 16px 10px!important;
  align-items:stretch!important;
  border-bottom:1px solid #e8eef5!important;
  background:#fff!important;
}
.loop-booking-minimal-head .loop-booking-head-copy{
  grid-area:copy!important;
  min-width:0!important;
  width:100%!important;
  overflow:hidden!important;
}
.loop-booking-minimal-head .booking-edit-title-row{
  display:flex!important;
  align-items:center!important;
  gap:8px!important;
  min-width:0!important;
  width:100%!important;
  overflow:hidden!important;
}
.loop-booking-minimal-head .booking-edit-title-row h2{
  min-width:0!important;
  max-width:100%!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
  font-size:21px!important;
  line-height:1.04!important;
  margin:3px 0 0!important;
  letter-spacing:-.04em!important;
}
.loop-booking-minimal-head .booking-edit-status-pill{
  flex:0 0 auto!important;
  max-width:128px!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
}
.loop-booking-minimal-head .loop-booking-head-copy p{
  max-width:100%!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
  margin-top:3px!important;
  font-size:11.5px!important;
  line-height:1.25!important;
  text-transform:none!important;
}
.loop-booking-minimal-head .booking-pro-kicker{
  font-size:8.5px!important;
  letter-spacing:.16em!important;
  margin-bottom:2px!important;
}
.loop-booking-head-actions-minimal{
  grid-area:actions!important;
  width:100%!important;
  display:flex!important;
  align-items:center!important;
  justify-content:flex-end!important;
  gap:8px!important;
  min-width:0!important;
  position:relative!important;
  flex-wrap:nowrap!important;
}
.loop-booking-head-actions-minimal .booking-pro-primary,
.loop-booking-head-actions-minimal .booking-pro-secondary,
.loop-booking-head-actions-minimal .loop-more-actions-btn{
  height:38px!important;
  min-height:38px!important;
  border-radius:14px!important;
  padding:0 15px!important;
  font-size:13px!important;
  white-space:nowrap!important;
  box-shadow:none!important;
}
.loop-booking-head-actions-minimal .booking-pro-primary{
  min-width:132px!important;
  box-shadow:0 12px 24px rgba(0,103,95,.14)!important;
}
.loop-booking-head-actions-minimal .booking-pro-close{
  position:static!important;
  flex:0 0 40px!important;
  width:40px!important;
  height:40px!important;
  min-width:40px!important;
  border-radius:14px!important;
  margin-left:0!important;
}
.loop-more-actions-menu{
  right:42px!important;
  top:calc(100% + 8px)!important;
  z-index:120!important;
}
.loop-booking-ops-minimal{
  padding:8px 16px!important;
  gap:7px!important;
}
.loop-ops-item{
  min-height:48px!important;
  padding:8px 10px!important;
  border-radius:14px!important;
}
.loop-ops-item span{font-size:9px!important;}
.loop-ops-item strong{font-size:12.5px!important;}
.loop-ops-item em{top:8px!important;right:8px!important;padding:2px 6px!important;font-size:9.5px!important;}
.loop-create-pro-form.is-edit-mode{padding-top:12px!important;}
@media(max-width:760px){
  .loop-booking-head-actions-minimal{
    display:grid!important;
    grid-template-columns:minmax(0,1fr) minmax(0,1fr) 40px!important;
    gap:8px!important;
  }
  .loop-booking-head-actions-minimal .booking-pro-primary,
  .loop-booking-head-actions-minimal .booking-pro-secondary,
  .loop-booking-head-actions-minimal .loop-more-actions-btn{
    width:100%!important;
    min-width:0!important;
    padding:0 10px!important;
  }
  .loop-more-actions-wrap{min-width:0!important;}
  .loop-booking-minimal-head .booking-edit-title-row h2{font-size:20px!important;}
  .loop-booking-ops-minimal{grid-template-columns:1fr 1fr!important;padding:8px 12px!important;}
}


/* LOOP D2.9.1A.9 — dropdown layering + select popover fix */
.loop-mini-select{
  position:relative!important;
  width:100%!important;
  min-width:0!important;
  z-index:60!important;
}
.loop-mini-select.is-open{z-index:99990!important;}
.loop-mini-select-trigger{
  width:100%!important;
  height:31px!important;
  min-height:31px!important;
  border:1px solid #e3ebf3!important;
  border-radius:10px!important;
  background:#fbfdff!important;
  color:#0f172a!important;
  font-size:12.5px!important;
  font-weight:950!important;
  padding:0 9px!important;
  display:flex!important;
  align-items:center!important;
  justify-content:space-between!important;
  gap:8px!important;
  cursor:pointer!important;
  box-shadow:none!important;
  outline:none!important;
  line-height:1!important;
}
.loop-mini-select-trigger span{
  min-width:0!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
  color:#0f172a!important;
  font-size:12.5px!important;
  font-weight:950!important;
  letter-spacing:0!important;
  text-transform:none!important;
  margin:0!important;
}
.loop-mini-select-trigger i{
  flex:0 0 auto!important;
  color:#00675f!important;
  font-style:normal!important;
  font-size:13px!important;
  line-height:1!important;
}
.loop-mini-select-trigger:focus-visible{
  border-color:#99d9cf!important;
  box-shadow:0 0 0 3px rgba(0,103,95,.08)!important;
  background:#ffffff!important;
}
.loop-mini-select-menu{
  position:absolute!important;
  top:calc(100% + 6px)!important;
  left:0!important;
  right:0!important;
  min-width:168px!important;
  width:max-content!important;
  max-width:min(260px,calc(100vw - 48px))!important;
  border:1px solid #dbe7ef!important;
  border-radius:14px!important;
  background:#ffffff!important;
  padding:6px!important;
  box-shadow:0 20px 50px rgba(15,23,42,.18)!important;
  display:grid!important;
  gap:2px!important;
  z-index:99999!important;
}
.loop-mini-select-menu button{
  width:100%!important;
  min-height:34px!important;
  border:0!important;
  border-radius:10px!important;
  background:transparent!important;
  color:#0f172a!important;
  padding:8px 10px!important;
  text-align:left!important;
  font-size:12.5px!important;
  font-weight:850!important;
  cursor:pointer!important;
  box-shadow:none!important;
  white-space:nowrap!important;
}
.loop-mini-select-menu button:hover,
.loop-mini-select-menu button:focus-visible{
  background:#f1f5f9!important;
  outline:none!important;
}
.loop-mini-select-menu button.is-selected{
  background:#ecfdf5!important;
  color:#00675f!important;
  font-weight:950!important;
}
.loop-payment-slim-card,
.loop-payment-slim-main,
.loop-payment-slim-field,
.loop-booking-ops-minimal,
.loop-ops-item{
  overflow:visible!important;
}
.loop-more-actions-wrap{z-index:100000!important;}
.loop-more-actions-menu{z-index:100001!important;background:#ffffff!important;box-shadow:0 24px 60px rgba(15,23,42,.22)!important;}


/* LOOP D2.9.1A.10 — restore compact header + wider edit surface
   Fix after dropdown patch: keep actions in the title row again and let
   the edit cards use more horizontal space so the modal stops feeling like
   a narrow form floating inside a large modal. */
.loop-create-modal-host{z-index:9999!important;}
.booking-pro-modal.loop-create-pro-modal{
  width:min(1040px,calc(100vw - 44px))!important;
  max-height:min(92vh,900px)!important;
  border-radius:22px!important;
  overflow:hidden!important;
}
.loop-booking-minimal-head{
  display:grid!important;
  grid-template-columns:minmax(0,1fr) auto!important;
  grid-template-areas:"copy actions"!important;
  align-items:center!important;
  column-gap:18px!important;
  row-gap:6px!important;
  padding:18px 22px 14px!important;
  min-height:0!important;
  border-bottom:1px solid #e8eef5!important;
  background:#ffffff!important;
}
.loop-booking-minimal-head .loop-booking-head-copy{
  grid-area:copy!important;
  min-width:0!important;
  width:100%!important;
  overflow:hidden!important;
}
.loop-booking-minimal-head .booking-pro-kicker{
  font-size:8.5px!important;
  letter-spacing:.17em!important;
  margin:0 0 5px!important;
  color:#00675f!important;
}
.loop-booking-minimal-head .booking-edit-title-row{
  display:flex!important;
  align-items:center!important;
  gap:9px!important;
  min-width:0!important;
  width:100%!important;
}
.loop-booking-minimal-head .booking-edit-title-row h2{
  min-width:0!important;
  max-width:100%!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
  font-size:22px!important;
  line-height:1.04!important;
  margin:0!important;
  letter-spacing:-.045em!important;
}
.loop-booking-minimal-head .booking-edit-status-pill{
  flex:0 0 auto!important;
  max-width:132px!important;
  border-radius:999px!important;
  padding:5px 9px!important;
  font-size:10px!important;
}
.loop-booking-minimal-head .loop-booking-head-copy p{
  margin:4px 0 0!important;
  max-width:100%!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  white-space:nowrap!important;
  font-size:11.5px!important;
  line-height:1.25!important;
  text-transform:none!important;
  color:#64748b!important;
}
.loop-booking-head-actions-minimal{
  grid-area:actions!important;
  width:auto!important;
  display:flex!important;
  align-items:center!important;
  justify-content:flex-end!important;
  gap:8px!important;
  min-width:max-content!important;
  position:relative!important;
  flex-wrap:nowrap!important;
}
.loop-booking-head-actions-minimal .booking-pro-primary,
.loop-booking-head-actions-minimal .booking-pro-secondary,
.loop-booking-head-actions-minimal .loop-more-actions-btn{
  height:40px!important;
  min-height:40px!important;
  border-radius:15px!important;
  padding:0 16px!important;
  font-size:13px!important;
  white-space:nowrap!important;
}
.loop-booking-head-actions-minimal .booking-pro-primary{
  min-width:132px!important;
  box-shadow:0 12px 24px rgba(0,103,95,.14)!important;
}
.loop-booking-head-actions-minimal .booking-pro-close{
  position:static!important;
  flex:0 0 40px!important;
  width:40px!important;
  height:40px!important;
  min-width:40px!important;
  border-radius:15px!important;
  margin-left:0!important;
}
.loop-more-actions-wrap{position:relative!important;z-index:100000!important;}
.loop-more-actions-menu{
  right:0!important;
  top:calc(100% + 10px)!important;
  z-index:100001!important;
  background:#ffffff!important;
  box-shadow:0 24px 60px rgba(15,23,42,.22)!important;
}
.loop-booking-ops-minimal{
  padding:12px 18px!important;
  gap:8px!important;
  border-bottom:1px solid #edf2f7!important;
}
.loop-ops-item{
  min-height:54px!important;
  padding:10px 11px!important;
  border-radius:15px!important;
}
.loop-create-pro-form.is-edit-mode{
  padding:14px 20px 26px!important;
}
.loop-create-pro-form.is-edit-mode .loop-create-pro-left{
  width:min(100%,980px)!important;
  max-width:980px!important;
  margin:0 auto!important;
}
.loop-create-pro-form.is-edit-mode .booking-pro-section,
.loop-create-pro-form.is-edit-mode .loop-create-section{
  width:100%!important;
  max-width:100%!important;
}
.loop-customer-minimal-section,
.loop-datetime-minimal-section,
.loop-payment-section{
  border-radius:19px!important;
}
@media(max-width:860px){
  .booking-pro-modal.loop-create-pro-modal{width:calc(100vw - 18px)!important;}
  .loop-booking-minimal-head{
    grid-template-columns:1fr!important;
    grid-template-areas:"copy" "actions"!important;
    padding:14px 14px 12px!important;
    align-items:stretch!important;
  }
  .loop-booking-head-actions-minimal{
    width:100%!important;
    display:grid!important;
    grid-template-columns:minmax(0,1fr) minmax(0,1fr) 40px!important;
    gap:8px!important;
    min-width:0!important;
  }
  .loop-booking-head-actions-minimal .booking-pro-primary,
  .loop-booking-head-actions-minimal .booking-pro-secondary,
  .loop-booking-head-actions-minimal .loop-more-actions-btn{
    width:100%!important;
    min-width:0!important;
    padding:0 10px!important;
  }
  .loop-create-pro-form.is-edit-mode{padding:12px 12px 22px!important;}
  .loop-booking-ops-minimal{grid-template-columns:1fr 1fr!important;padding:10px 12px!important;}
}



/* LOOP D2.9.3E — minimal tip payment method */
.loop-tip-payment-field .loop-tip-payment-inline{display:grid;grid-template-columns:minmax(0,1fr) minmax(112px,.42fr);gap:8px;align-items:center;}
.loop-tip-payment-field .loop-mini-select{min-width:0!important;}
.loop-tip-payment-field .loop-mini-select-trigger{min-height:32px!important;height:32px!important;padding:0 9px!important;font-size:11px!important;border-radius:12px!important;background:#f8fafc!important;}
@media (max-width: 760px){.loop-tip-payment-field .loop-tip-payment-inline{grid-template-columns:1fr;}.loop-tip-payment-field .loop-mini-select-trigger{height:36px!important;}}


/* LOOP D2.9.3J — premium booking modal redesign + payment/commission popups */
.loop-create-pro-modal{max-width:1120px!important;border-radius:28px!important;background:#fff!important;box-shadow:0 30px 90px rgba(15,23,42,.24)!important;}
.loop-booking-minimal-head{padding:24px 28px 22px!important;border-bottom:1px solid #eef3f7!important;background:linear-gradient(180deg,#fff,#fbfdff)!important;}
.loop-booking-head-copy .booking-pro-kicker{color:#00675f!important;letter-spacing:.14em!important;font-size:10px!important;font-weight:950!important;}
.loop-booking-head-copy h2{font-size:24px!important;letter-spacing:-.045em!important;}
.loop-booking-head-copy p{font-size:13px!important;font-weight:800!important;color:#64748b!important;}
.loop-booking-head-actions-minimal{gap:10px!important;}
.loop-booking-head-actions-minimal .booking-pro-primary{min-height:42px!important;border-radius:14px!important;padding:0 24px!important;background:#007568!important;box-shadow:0 14px 30px rgba(0,103,95,.18)!important;}
.loop-booking-head-actions-minimal .booking-pro-secondary{min-height:42px!important;border-radius:14px!important;background:#fff!important;border:1px solid #dbe7ef!important;color:#0f172a!important;box-shadow:none!important;}
.loop-booking-head-actions-minimal .booking-pro-close{width:42px!important;height:42px!important;border-radius:16px!important;background:#fff!important;border:1px solid #dbe7ef!important;color:#0f172a!important;}
.loop-booking-ops-minimal{grid-template-columns:1fr 1fr 1fr 1fr auto!important;gap:0!important;margin:18px 28px 0!important;padding:0!important;border:1px solid #e7eef5!important;border-radius:18px!important;background:#fff!important;overflow:hidden!important;}
.loop-booking-ops-minimal>div,.loop-booking-ops-minimal>button{border:0!important;border-right:1px solid #edf2f7!important;border-radius:0!important;background:#fff!important;min-height:72px!important;padding:14px 16px!important;text-align:left!important;box-shadow:none!important;}
.loop-booking-ops-minimal>button:last-child{border-right:0!important;}
.loop-booking-ops-minimal span{font-size:10px!important;letter-spacing:.08em!important;text-transform:none!important;color:#64748b!important;margin-bottom:6px!important;}
.loop-booking-ops-minimal strong{font-size:13px!important;color:#0f172a!important;}
.loop-booking-ops-minimal em{top:12px!important;right:12px!important;border-radius:999px!important;background:#ecfdf5!important;color:#007568!important;border:1px solid rgba(0,103,95,.16)!important;font-size:10px!important;}
.loop-ops-summary-btn{min-width:128px!important;text-align:center!important;}
.loop-ops-summary-btn strong{color:#00675f!important;}
.loop-create-pro-form.is-create-mode,.loop-create-pro-form.is-edit-mode{padding:22px 28px 28px!important;background:#fff!important;}
.loop-create-pro-left{max-width:none!important;width:100%!important;}
.loop-create-section{border:1px solid #e7eef5!important;border-radius:22px!important;padding:18px!important;margin-bottom:14px!important;background:#fff!important;box-shadow:0 8px 24px rgba(15,23,42,.035)!important;}
.loop-customer-minimal-head small,.loop-customer-minimal-fields,.loop-customer-minimal-note{display:none!important;}
.loop-customer-minimal-head{margin-bottom:12px!important;}
/* LOOP WORKSPACE V3.2 — hide CustomerPicker new-client helper */
.loop-customer-one-line-note.is-new-customer-only,
.loop-customer-compact-insight.is-new-customer-only{display:none!important;visibility:hidden!important;height:0!important;min-height:0!important;margin:0!important;padding:0!important;border:0!important;overflow:hidden!important;}
.loop-customer-one-line-note.is-new-customer-only span{font-size:11px!important;letter-spacing:0!important;text-transform:none!important;color:#475569!important;font-weight:900!important;}
.loop-customer-one-line-note.is-new-customer-only strong{font-size:11px!important;color:#64748b!important;font-weight:750!important;}
.booking-pro-section-title{align-items:center!important;gap:12px!important;margin-bottom:12px!important;}
.booking-pro-section-title>span{width:28px!important;height:28px!important;border-radius:999px!important;background:#dcfce7!important;color:#007568!important;display:inline-flex!important;align-items:center!important;justify-content:center!important;font-weight:950!important;}
.loop-payment-slim-card{border:1px solid #e7eef5!important;border-radius:20px!important;background:#fff!important;padding:0!important;overflow:hidden!important;}
.loop-payment-slim-main{display:grid!important;grid-template-columns:repeat(4,minmax(0,1fr))!important;gap:0!important;border-bottom:1px solid #eef3f7!important;}
.loop-payment-slim-field{padding:12px!important;border-right:1px solid #eef3f7!important;}
.loop-payment-slim-field:last-child{border-right:0!important;}
.loop-payment-slim-field span{font-size:11px!important;color:#64748b!important;font-weight:900!important;margin-bottom:6px!important;}
.loop-payment-slim-field input,.loop-payment-slim-field .loop-mini-select-trigger{height:40px!important;border-radius:12px!important;background:#fbfdff!important;}
.loop-payment-slim-summary{display:none!important;}
.loop-payment-quiet-row{display:flex!important;align-items:center!important;justify-content:space-between!important;gap:12px!important;padding:11px 12px!important;background:#fbfdff!important;border-bottom:1px solid #eef3f7!important;}
.loop-payment-quiet-note{display:flex!important;align-items:center!important;gap:8px!important;min-width:0!important;color:#64748b!important;font-size:12px!important;font-weight:800!important;}
.loop-payment-quiet-note i{width:22px;height:22px;border-radius:999px;background:#e8f1ff;color:#2563eb;display:inline-flex;align-items:center;justify-content:center;font-style:normal;flex:0 0 auto;}
.loop-payment-quiet-note strong{color:#475569!important;font-size:12px!important;}
.loop-payment-quiet-note small{color:#64748b!important;font-size:11px!important;}
.loop-payment-commission-btn{border:1px solid rgba(0,103,95,.18)!important;background:#ecfdf5!important;color:#00675f!important;border-radius:999px!important;padding:9px 12px!important;font-size:12px!important;font-weight:950!important;}
.loop-payment-slim-settings{border:0!important;border-top:1px solid #eef3f7!important;background:#fff!important;border-radius:0!important;margin:0!important;padding:12px!important;}
.loop-payment-slim-settings span strong{font-size:13px!important;}
.loop-payment-slim-settings span small{font-size:11px!important;}
.loop-payment-advanced-inline{display:grid!important;grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:10px!important;padding:12px!important;border-top:1px solid #eef3f7!important;background:#fbfdff!important;}
.loop-settings-modal-head{padding:18px 20px!important;background:linear-gradient(135deg,#fff,#f0fdfa)!important;border-bottom:1px solid #e7eef5!important;}
.loop-settings-modal-head strong{font-size:20px!important;letter-spacing:-.035em!important;}
.loop-settings-modal-head span{font-size:12px!important;color:#64748b!important;font-weight:750!important;}
.loop-settings-modal-body{padding:18px 20px!important;}
.loop-payment-editor-summary,.loop-commission-estimate-grid{display:grid!important;grid-template-columns:repeat(3,minmax(0,1fr))!important;gap:8px!important;margin-bottom:14px!important;}
.loop-payment-editor-summary div,.loop-commission-estimate-grid div{border:1px solid #e7eef5!important;background:#fbfdff!important;border-radius:16px!important;padding:11px!important;}
.loop-payment-editor-summary span,.loop-commission-estimate-grid span{display:block!important;color:#64748b!important;font-size:10px!important;font-weight:950!important;letter-spacing:.06em!important;text-transform:uppercase!important;}
.loop-payment-editor-summary strong,.loop-commission-estimate-grid strong{display:block!important;margin-top:5px!important;color:#0f172a!important;font-size:15px!important;font-weight:950!important;}
.loop-commission-estimate-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
.loop-commission-estimate-grid .is-total{background:#ecfdf5!important;border-color:rgba(0,103,95,.18)!important;}
.loop-payment-editor-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important;gap:10px!important;}
.loop-payment-editor-commission-card{margin-top:14px;border:1px solid rgba(0,103,95,.18);background:#f0fdfa;border-radius:18px;padding:14px;display:flex;align-items:center;justify-content:space-between;gap:12px;}
.loop-payment-editor-commission-card span{display:block;color:#00675f;font-size:10px;font-weight:950;letter-spacing:.08em;text-transform:uppercase;}
.loop-payment-editor-commission-card strong{display:block;margin-top:4px;color:#0f172a;font-size:18px;font-weight:950;}
.loop-payment-editor-commission-card small{display:block;margin-top:3px;color:#64748b;font-weight:800;font-size:12px;}
.loop-payment-editor-commission-card button{border:0;background:#00675f;color:#fff;border-radius:999px;padding:10px 13px;font-weight:950;}
@media(max-width:980px){.loop-booking-ops-minimal{grid-template-columns:1fr 1fr!important;margin:12px!important}.loop-payment-slim-main{grid-template-columns:1fr 1fr!important}.loop-payment-editor-summary,.loop-commission-estimate-grid,.loop-payment-editor-grid,.loop-payment-advanced-inline{grid-template-columns:1fr!important}.loop-payment-quiet-row,.loop-payment-editor-commission-card{align-items:flex-start!important;flex-direction:column!important}}

/* LOOP D2.9.3J.2 — custom dropdown layering + payment section cleanup */
.loop-payment-section-headline{display:flex!important;align-items:center!important;justify-content:flex-start!important;gap:12px!important;}
.loop-payment-section-headline>div:first-of-type{min-width:0!important;flex:1 1 auto!important;}
.loop-payment-title-actions{margin-left:auto!important;display:inline-flex!important;align-items:center!important;gap:8px!important;flex:0 0 auto!important;}
.loop-payment-head-action{min-height:36px!important;border-radius:999px!important;border:1px solid #dbe7ef!important;background:#fff!important;color:#334155!important;padding:0 13px!important;font-size:12px!important;font-weight:950!important;cursor:pointer!important;box-shadow:0 8px 20px rgba(15,23,42,.045)!important;}
.loop-payment-head-action:hover{border-color:#bfe5dd!important;background:#fbfffd!important;color:#00675f!important;}
.loop-payment-head-action.is-primary{border-color:rgba(0,103,95,.18)!important;background:#ecfdf5!important;color:#00675f!important;}
.loop-payment-head-action.is-open{background:#0f766e!important;border-color:#0f766e!important;color:#fff!important;}
.loop-payment-head-action:disabled{opacity:.5!important;cursor:not-allowed!important;}
.loop-payment-section .loop-payment-quiet-row,.loop-payment-section .loop-payment-slim-settings.loop-payment-advanced-toggle{display:none!important;}
.loop-payment-section,.loop-payment-slim-card,.loop-payment-slim-main,.loop-payment-slim-field,.loop-tip-payment-inline,.loop-booking-ops-strip,.loop-ops-item{overflow:visible!important;}
.loop-payment-slim-card{position:relative!important;z-index:20!important;border-radius:20px!important;}
.loop-payment-slim-main{position:relative!important;z-index:21!important;border-bottom:0!important;}
.loop-payment-slim-field{position:relative!important;z-index:22!important;}
.loop-payment-slim-field:focus-within,.loop-payment-slim-field:has(.loop-mini-select.is-open){z-index:100000!important;}
.loop-mini-select{position:relative!important;}
.loop-mini-select.is-open{z-index:999990!important;}
.loop-mini-select-menu{position:absolute!important;top:calc(100% + 7px)!important;left:0!important;right:auto!important;min-width:max(100%,190px)!important;max-width:min(280px,calc(100vw - 40px))!important;max-height:260px!important;overflow:auto!important;z-index:999999!important;border:1px solid #dbe7ef!important;border-radius:16px!important;background:#fff!important;box-shadow:0 22px 55px rgba(15,23,42,.18)!important;padding:7px!important;}
.loop-mini-select-menu button{min-height:38px!important;border-radius:11px!important;padding:0 11px!important;text-align:left!important;font-size:13px!important;font-weight:900!important;color:#0f172a!important;}
.loop-mini-select-menu button:hover{background:#f0fdfa!important;color:#00675f!important;}
.loop-mini-select-menu button.is-selected{background:#e6fbf5!important;color:#00675f!important;}
.loop-payment-advanced-inline{border-top:1px solid #eef3f7!important;background:#fbfdff!important;border-radius:0 0 20px 20px!important;}
.loop-payment-slim-card .loop-payment-advanced-inline{position:relative!important;z-index:1!important;}
@media(max-width:980px){.loop-payment-section-headline{flex-wrap:wrap!important}.loop-payment-title-actions{width:100%!important;justify-content:flex-start!important}.loop-payment-head-action{min-height:34px!important}.loop-mini-select-menu{min-width:min(260px,calc(100vw - 40px))!important}}



/* LOOP D2.9.3J.6 — status chip dropdown opens downward above content */
.loop-booking-ops-strip.loop-booking-ops-minimal{
  position:relative!important;
  z-index:1000000!important;
  overflow:visible!important;
  isolation:isolate!important;
}
.loop-create-pro-form,
.loop-create-pro-left,
.loop-customer-minimal-section{
  position:relative!important;
  z-index:1!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select{
  cursor:pointer!important;
  overflow:visible!important;
  isolation:isolate!important;
  position:relative!important;
  z-index:10!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select > span{
  position:relative!important;
  z-index:4!important;
  pointer-events:none!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select{
  position:absolute!important;
  inset:0!important;
  width:100%!important;
  height:100%!important;
  z-index:3!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-trigger{
  position:absolute!important;
  inset:0!important;
  width:100%!important;
  height:100%!important;
  min-height:100%!important;
  border:0!important;
  background:transparent!important;
  border-radius:999px!important;
  box-shadow:none!important;
  padding:0 42px 0 48px!important;
  cursor:pointer!important;
  display:block!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-trigger span{
  position:absolute!important;
  left:48px!important;
  right:42px!important;
  bottom:13px!important;
  margin:0!important;
  display:block!important;
  color:#0f172a!important;
  font-size:12px!important;
  font-weight:950!important;
  line-height:1.15!important;
  letter-spacing:0!important;
  text-transform:none!important;
  white-space:nowrap!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
  pointer-events:none!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-trigger i{
  position:absolute!important;
  right:16px!important;
  top:50%!important;
  bottom:auto!important;
  width:22px!important;
  height:22px!important;
  border-radius:999px!important;
  transform:translateY(-50%)!important;
  display:flex!important;
  align-items:center!important;
  justify-content:center!important;
  background:#ecfdf5!important;
  border:1px solid rgba(0,103,95,.14)!important;
  color:#007568!important;
  font-size:15px!important;
  font-weight:950!important;
  line-height:1!important;
  box-shadow:0 6px 14px rgba(0,103,95,.08)!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select:hover .loop-mini-select-trigger i,
.loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select.is-open .loop-mini-select-trigger i{
  background:#dffbf3!important;
  border-color:rgba(0,103,95,.22)!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-menu{
  top:calc(100% + 10px)!important;
  bottom:auto!important;
  left:0!important;
  right:auto!important;
  min-width:224px!important;
  max-width:min(290px,calc(100vw - 48px))!important;
  max-height:250px!important;
  overflow:auto!important;
  z-index:1000002!important;
  border-radius:18px!important;
  border:1px solid #dbe7ef!important;
  background:#fff!important;
  box-shadow:0 26px 70px rgba(15,23,42,.22)!important;
  padding:8px!important;
  transform:translateZ(0)!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-menu::after{
  content:""!important;
  position:absolute!important;
  left:42px!important;
  top:-7px!important;
  width:14px!important;
  height:14px!important;
  background:#fff!important;
  border-left:1px solid #dbe7ef!important;
  border-top:1px solid #dbe7ef!important;
  transform:rotate(45deg)!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-menu button{
  position:relative!important;
  z-index:1!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select:has(.loop-mini-select.is-open){
  z-index:1000001!important;
  border-color:rgba(0,103,95,.28)!important;
  box-shadow:0 16px 36px rgba(15,23,42,.10)!important;
}
@media(max-width:620px){
  .loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-menu{
    top:calc(100% + 8px)!important;
    min-width:min(260px,calc(100vw - 40px))!important;
  }
  .loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-menu::after{display:none!important;}
}


/* LOOP D2.9.3J.7 — status chip alignment + payment options closed */
.loop-booking-ops-minimal .loop-ops-item.is-select{
  display:block!important;
  min-height:58px!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select > span{
  position:absolute!important;
  left:48px!important;
  top:14px!important;
  margin:0!important;
  line-height:1!important;
  z-index:5!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select{
  position:absolute!important;
  inset:0!important;
  z-index:4!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-trigger{
  inset:0!important;
  padding:0 44px 0 48px!important;
  display:block!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-trigger span{
  left:48px!important;
  right:44px!important;
  top:31px!important;
  bottom:auto!important;
  text-align:left!important;
  font-size:12px!important;
  line-height:1.12!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-trigger i{
  right:16px!important;
  top:50%!important;
  bottom:auto!important;
  transform:translateY(-50%)!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select.is-open .loop-mini-select-trigger i{
  transform:translateY(-50%) rotate(180deg)!important;
}
.loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-menu{
  top:calc(100% + 10px)!important;
  bottom:auto!important;
  z-index:2147483000!important;
}
.loop-payment-slim-card .loop-payment-advanced-inline{
  display:grid!important;
}


/* LOOP PHASE 4L — compact payment popup + phone input */
.loop-phone-field .loop-phone-input-shell{width:100%;min-height:44px;border:1px solid #dbe5ee;background:#fff;border-radius:14px;display:grid;grid-template-columns:auto minmax(0,1fr);align-items:center;gap:8px;padding:5px 10px 5px 6px;box-sizing:border-box;transition:border-color .14s ease,box-shadow .14s ease;}
.loop-phone-field .loop-phone-input-shell:focus-within{border-color:#008878;box-shadow:0 0 0 3px rgba(0,103,95,.12);}
.loop-phone-country-pill{height:32px;min-width:62px;border-radius:999px;background:#f0fdfa;border:1px solid #99f6e4;color:#00675f;display:inline-flex;align-items:center;justify-content:center;font-size:12px;font-weight:950;}
.loop-phone-input-shell input{height:34px!important;min-height:34px!important;border:0!important;background:transparent!important;padding:0!important;box-shadow:none!important;outline:none!important;color:#0f172a!important;font-size:14px!important;font-weight:900!important;}
.loop-payment-editor-modal{max-width:min(100%,500px)!important;}
.loop-payment-editor-modal .loop-settings-modal-head{padding:18px 20px!important;background:#fff!important;}
.loop-payment-editor-modal .loop-settings-modal-head span{max-width:360px!important;}
.loop-payment-editor-body-compact{display:grid!important;gap:12px!important;padding:14px 20px 16px!important;}
.loop-payment-editor-hero-compact{border:1px solid #d7f3ec;background:linear-gradient(135deg,#f7fffb,#fff);border-radius:18px;padding:13px 14px;display:flex;align-items:flex-start;justify-content:space-between;gap:12px;}
.loop-payment-editor-hero-compact span{display:block;color:#008878;font-size:10px;font-weight:950;letter-spacing:.10em;text-transform:uppercase;}
.loop-payment-editor-hero-compact strong{display:block;margin-top:4px;color:#0f172a;font-size:17px;font-weight:950;letter-spacing:-.03em;}
.loop-payment-editor-hero-compact small{display:block;margin-top:4px;color:#64748b;font-size:12px;font-weight:800;}
.loop-payment-editor-hero-compact em{font-style:normal;border-radius:999px;background:#ecfdf5;border:1px solid #bbf7d0;color:#047857;padding:6px 9px;font-size:11px;font-weight:950;white-space:nowrap;}
.loop-payment-editor-grid-compact{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
.loop-payment-editor-grid-compact .booking-pro-field{margin:0!important;}
.loop-tip-method-discreet{border:1px solid #e2e8f0;background:#f8fafc;border-radius:14px;padding:10px 11px;display:flex;align-items:center;gap:8px;color:#64748b;font-size:12px;font-weight:850;}
.loop-tip-info-dot{width:18px;height:18px;border-radius:999px;background:#fff;border:1px solid #cbd5e1;color:#64748b;display:inline-flex;align-items:center;justify-content:center;font-size:11px;font-weight:950;cursor:help;flex:0 0 auto;}
.loop-tip-method-discreet button{margin-left:auto;border:0;background:transparent;color:#00675f;font-size:12px;font-weight:950;cursor:pointer;white-space:nowrap;}
.loop-tip-method-override-row{width:min(240px,100%);}
.loop-payment-advanced-details{border:1px solid #edf2f7;border-radius:14px;background:#fffdfa;padding:0;overflow:hidden;}
.loop-payment-advanced-details summary{cursor:pointer;list-style:none;padding:11px 12px;color:#64748b;font-size:12px;font-weight:950;}
.loop-payment-advanced-details summary::-webkit-details-marker{display:none;}
.loop-payment-advanced-details-body{border-top:1px solid #edf2f7;padding:12px;display:grid;grid-template-columns:1fr 1fr;gap:10px;}
.loop-payment-editor-mini-commission{grid-column:1/-1;border:1px solid #d7f3ec;background:#f7fffb;border-radius:14px;padding:11px;display:grid;grid-template-columns:minmax(0,1fr) auto;align-items:center;gap:10px;}
.loop-payment-editor-mini-commission span{display:block;color:#008878;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.10em;}
.loop-payment-editor-mini-commission strong{display:block;color:#0f172a;font-size:15px;font-weight:950;margin-top:2px;}
.loop-payment-editor-mini-commission small{display:block;color:#64748b;font-size:11px;font-weight:800;margin-top:2px;}
.loop-payment-editor-mini-commission button{border:0;border-radius:999px;background:#00675f;color:#fff;height:34px;padding:0 12px;font-size:12px;font-weight:950;cursor:pointer;}
@media(max-width:620px){.loop-payment-editor-grid-compact,.loop-payment-advanced-details-body{grid-template-columns:1fr}.loop-payment-editor-hero-compact{display:grid}.loop-tip-method-discreet{align-items:flex-start}.loop-tip-method-discreet button{margin-left:0}.loop-payment-editor-mini-commission{grid-template-columns:1fr}.loop-payment-editor-mini-commission button{width:100%;}}



/* LOOP PAYMENT POPUP SIMPLE V4L3 */
.loop-payment-editor-modal{
  max-width:min(100%,520px)!important;
}
.loop-payment-editor-modal .loop-settings-modal-head{
  padding:20px 22px 16px!important;
  background:#ffffff!important;
  border-bottom:1px solid #e6edf3!important;
  align-items:center!important;
}
.loop-payment-editor-modal .loop-settings-modal-head strong{
  font-size:22px!important;
  line-height:1.05!important;
  letter-spacing:-.045em!important;
}
.loop-payment-editor-modal .loop-settings-modal-head span{
  display:none!important;
}
.loop-payment-editor-modal .loop-settings-modal-head button{
  width:38px!important;
  height:38px!important;
  border-radius:12px!important;
  background:#ffffff!important;
}
.loop-payment-editor-body-compact{
  padding:16px 22px 18px!important;
  gap:14px!important;
  background:#ffffff!important;
}
.loop-payment-editor-hero-compact,
.loop-payment-advanced-details{
  display:none!important;
}
.loop-payment-editor-grid-simple{
  display:grid!important;
  grid-template-columns:repeat(2,minmax(0,1fr))!important;
  gap:14px 12px!important;
}
.loop-payment-editor-grid-simple .booking-pro-field{
  margin:0!important;
  min-width:0!important;
}
.loop-payment-editor-grid-simple .booking-pro-field > span{
  display:block!important;
  margin:0 0 7px!important;
  color:#334155!important;
  font-size:12px!important;
  font-weight:950!important;
  letter-spacing:-.01em!important;
  text-transform:none!important;
}
.loop-payment-editor-grid-simple .booking-pro-field input,
.loop-payment-editor-grid-simple .booking-pro-field select,
.loop-payment-editor-grid-simple .loop-mini-select-trigger{
  min-height:48px!important;
  border-radius:13px!important;
  background:#ffffff!important;
  border:1px solid #dbe5ee!important;
  color:#0f172a!important;
  font-size:15px!important;
  font-weight:900!important;
  box-shadow:none!important;
}
.loop-payment-editor-grid-simple .booking-pro-field input:focus{
  border-color:rgba(0,103,95,.35)!important;
  box-shadow:0 0 0 3px rgba(0,103,95,.10)!important;
}
.loop-payment-tip-field{
  position:relative!important;
}
.loop-tip-method-discreet.loop-tip-method-inline{
  width:max-content!important;
  max-width:100%!important;
  min-height:20px!important;
  margin:6px 0 0 auto!important;
  padding:3px 6px!important;
  border-radius:8px!important;
  border:1px solid #e6edf3!important;
  background:#fbfdff!important;
  display:flex!important;
  align-items:center!important;
  justify-content:flex-end!important;
  gap:5px!important;
  box-shadow:none!important;
  color:#64748b!important;
  font-size:9.5px!important;
  line-height:1!important;
  font-weight:850!important;
}
.loop-tip-method-inline .loop-tip-info-dot{
  width:13px!important;
  height:13px!important;
  border-radius:999px!important;
  background:#ffffff!important;
  border:1px solid #dbe5ee!important;
  color:#94a3b8!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  font-size:8px!important;
  font-weight:950!important;
  text-transform:none!important;
}
.loop-tip-method-inline button{
  border:0!important;
  background:transparent!important;
  color:#00675f!important;
  font-size:9.5px!important;
  line-height:1!important;
  font-weight:950!important;
  padding:0!important;
  cursor:pointer!important;
  white-space:nowrap!important;
}
.loop-tip-method-override-row{
  margin-top:7px!important;
}
.loop-payment-simple-commission-row{
  min-height:48px!important;
  border:1px solid #e6edf3!important;
  background:#ffffff!important;
  border-radius:16px!important;
  display:flex!important;
  align-items:center!important;
  justify-content:space-between!important;
  gap:14px!important;
  padding:0 16px!important;
}
.loop-payment-simple-commission-row span{
  color:#0f172a!important;
  font-size:16px!important;
  font-weight:750!important;
  letter-spacing:-.02em!important;
}
.loop-payment-simple-commission-row button{
  border:0!important;
  background:transparent!important;
  color:#0f172a!important;
  font-size:15px!important;
  font-weight:800!important;
  padding:8px 0!important;
  cursor:pointer!important;
}
.loop-payment-simple-commission-row button:hover{
  color:#00675f!important;
}
.loop-payment-editor-modal .loop-settings-modal-foot{
  padding:12px 22px!important;
  background:#fbfdff!important;
  border-top:1px solid #e6edf3!important;
}
.loop-payment-editor-modal .loop-settings-modal-foot button{
  height:42px!important;
  min-width:78px!important;
  border-radius:12px!important;
  background:#00675f!important;
  color:#ffffff!important;
  font-size:14px!important;
  font-weight:950!important;
}
@media(max-width:640px){
  .loop-payment-editor-modal{max-width:calc(100vw - 18px)!important;}
  .loop-payment-editor-grid-simple{grid-template-columns:1fr!important;gap:12px!important;}
  .loop-tip-method-discreet.loop-tip-method-inline{width:100%!important;justify-content:space-between!important;}
  .loop-payment-simple-commission-row{align-items:center!important;}
}

/* LOOP D2.9.3J.8 — CustomerPicker layer above lower cards
   Fix: customer results were being painted under Pago y comisión because
   later payment styles gave the payment card z-index 20+ while the customer
   section was reset to z-index:1. When the picker is active, the customer
   card becomes the active stacking layer and the result menu floats above
   service/payment/notes without changing layout. */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-left,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-main-grid,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-section,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-section{
  overflow:visible!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section{
  position:relative!important;
  z-index:60!important;
  overflow:visible!important;
  isolation:isolate!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section:focus-within,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section:has(.lcp-menu),
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section:has(.lcp-input-shell.is-open){
  z-index:2147483000!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search{
  position:relative!important;
  z-index:2147483100!important;
  overflow:visible!important;
  isolation:isolate!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .loop-customer-picker,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search > *{
  position:relative!important;
  z-index:2147483200!important;
  overflow:visible!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-menu{
  position:absolute!important;
  z-index:2147483300!important;
  left:0!important;
  right:0!important;
  top:calc(100% + 8px)!important;
  overflow-y:auto!important;
  overflow-x:hidden!important;
  max-height:min(430px, calc(100vh - 210px))!important;
  pointer-events:auto!important;
}
/* Keep lower cards calm unless their own dropdown is open. */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-datetime-minimal-section,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-section,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-accordion-stack,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-post-payment-actions-row{
  position:relative!important;
  z-index:5!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-card,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-main,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-slim-field{
  position:relative!important;
  z-index:auto!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-section:has(.loop-mini-select.is-open){
  z-index:2147482000!important;
}
@media(max-width:980px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-menu{
    max-height:min(360px, calc(100dvh - 190px))!important;
  }
}

/* LOOP CUSTOMER STATUS BADGES V4.9 — live Loop/WhatsApp/Email readiness */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-note.loop-customer-status-badges,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-note.loop-customer-status-badges,
.loop-customer-minimal-note.loop-customer-status-badges{
  display:flex!important;
  align-items:center!important;
  flex-wrap:wrap!important;
  gap:8px!important;
  margin-top:12px!important;
  padding:0!important;
  min-height:0!important;
  background:transparent!important;
  border:0!important;
  box-shadow:none!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badges .loop-customer-status-badge,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badges .loop-customer-status-badge,
.loop-customer-status-badges .loop-customer-status-badge{
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  width:max-content!important;
  min-height:26px!important;
  padding:0 11px!important;
  border-radius:999px!important;
  font-size:11px!important;
  line-height:1!important;
  font-weight:950!important;
  letter-spacing:-.01em!important;
  font-style:normal!important;
  white-space:nowrap!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badges .loop-customer-status-badge.is-ok,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badges .loop-customer-status-badge.is-ok,
.loop-customer-status-badges .loop-customer-status-badge.is-ok{
  background:#dcfce7!important;
  border:1px solid #86efac!important;
  color:#047857!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badges .loop-customer-status-badge.is-loop,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badges .loop-customer-status-badge.is-loop,
.loop-customer-status-badges .loop-customer-status-badge.is-loop{
  background:#ecfdf5!important;
  border-color:#6ee7b7!important;
  color:#00675f!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badges .loop-customer-status-badge.is-muted,
.booking-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badges .loop-customer-status-badge.is-muted,
.loop-customer-status-badges .loop-customer-status-badge.is-muted{
  background:#f8fafc!important;
  border:1px solid #e2e8f0!important;
  color:#64748b!important;
}


/* LOOP BOOKING MOBILE DATE ACTIONS V51 — right-side compact action rail.
   Keeps "Horario disponible" + "Cambiar" in the empty right area of Fecha,
   but lowers the availability pill so it never overlaps the date text. */
@media(max-width:980px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block{
    position:relative!important;
    padding-right:124px!important;
    min-height:126px!important;
    align-content:start!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block .loop-ordered-info-row{
    grid-template-columns:26px 82px minmax(0,1fr)!important;
    min-height:28px!important;
    padding:1px 0!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block .loop-ordered-info-row strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block .loop-mode-mini-pill{
    max-width:100%!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block .loop-ordered-info-row.is-date strong{
    white-space:normal!important;
    line-height:1.16!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block .loop-availability-mini-pill.loop-ordered-availability,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block .loop-ordered-availability{
    position:absolute!important;
    right:0!important;
    top:23px!important;
    margin:0!important;
    width:auto!important;
    max-width:116px!important;
    min-height:26px!important;
    height:26px!important;
    padding:0 9px!important;
    border-radius:999px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    font-size:9.5px!important;
    line-height:1!important;
    font-weight:950!important;
    z-index:3!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block .loop-datetime-change-btn.loop-ordered-date-change,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block .loop-ordered-date-change{
    position:absolute!important;
    right:0!important;
    top:58px!important;
    margin:0!important;
    width:auto!important;
    min-width:74px!important;
    height:32px!important;
    min-height:32px!important;
    padding:0 12px!important;
    border-radius:13px!important;
    display:inline-flex!important;
    align-items:center!important;
    justify-content:center!important;
    font-size:11px!important;
    line-height:1!important;
    font-weight:950!important;
    z-index:3!important;
  }

  /* Once actions are in the rail, prevent old vertical spacing from returning. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-availability-mini-pill.loop-ordered-availability,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-ordered-date-change{
    justify-self:auto!important;
  }
}

@media(max-width:380px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block{
    padding-right:108px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block .loop-ordered-info-row{
    grid-template-columns:24px 76px minmax(0,1fr)!important;
    column-gap:7px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block .loop-availability-mini-pill.loop-ordered-availability,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block .loop-ordered-availability{
    max-width:102px!important;
    font-size:9px!important;
    padding:0 7px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block .loop-datetime-change-btn.loop-ordered-date-change,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-ordered-date-block .loop-ordered-date-change{
    min-width:66px!important;
    padding:0 10px!important;
  }
}


/* LOOP BOOKING PAYMENT V52 — inline compact payment + cleaner commission header */
.loop-payment-section-headline{
  align-items:center!important;
  gap:10px!important;
  margin-bottom:10px!important;
}
.loop-payment-title-left{
  display:inline-flex!important;
  align-items:center!important;
  gap:10px!important;
  flex:0 1 auto!important;
  min-width:0!important;
}
.loop-payment-title-left > span{
  flex:0 0 auto!important;
}
.loop-payment-title-left > strong{
  display:block!important;
  line-height:1.05!important;
}
.loop-payment-title-actions-inline{
  margin-left:10px!important;
  display:inline-flex!important;
  align-items:center!important;
  gap:6px!important;
  flex-wrap:nowrap!important;
}
.loop-payment-title-actions-inline .loop-payment-head-action{
  min-height:28px!important;
  height:28px!important;
  padding:0 9px!important;
  font-size:10.5px!important;
  border-radius:999px!important;
  min-width:auto!important;
}
.loop-payment-section .loop-payment-slim-card{
  border-radius:18px!important;
}
.loop-payment-section .loop-payment-slim-main{
  display:grid!important;
  grid-template-columns:repeat(2,minmax(0,1fr))!important;
  gap:8px!important;
  padding:10px!important;
  border-bottom:0!important;
}
.loop-payment-section .loop-payment-slim-field{
  display:grid!important;
  grid-template-columns:72px minmax(0,1fr)!important;
  align-items:center!important;
  gap:10px!important;
  border:1px solid #e7eef5!important;
  border-radius:14px!important;
  padding:8px 10px!important;
  min-height:50px!important;
  background:#fff!important;
}
.loop-payment-section .loop-payment-slim-field:last-child{
  border-right:1px solid #e7eef5!important;
}
.loop-payment-section .loop-payment-slim-field > span{
  margin:0!important;
  font-size:11px!important;
  font-weight:950!important;
  color:#64748b!important;
  white-space:nowrap!important;
}
.loop-payment-section .loop-payment-slim-field .loop-mini-select,
.loop-payment-section .loop-payment-slim-field .loop-money-payment-inline,
.loop-payment-section .loop-payment-slim-field .loop-tip-payment-inline{
  width:100%!important;
  min-width:0!important;
}
.loop-payment-section .loop-payment-slim-field .loop-mini-select-trigger,
.loop-payment-section .loop-payment-slim-field input{
  min-height:34px!important;
  height:34px!important;
  border:0!important;
  background:transparent!important;
  box-shadow:none!important;
  padding:0 20px 0 0!important;
  border-radius:0!important;
  color:#0f172a!important;
  font-size:13px!important;
  font-weight:900!important;
}
.loop-payment-section .loop-payment-slim-field .loop-mini-select-trigger span{
  font-size:13px!important;
  font-weight:900!important;
}
.loop-payment-section .loop-payment-slim-field .loop-mini-select-trigger i{
  right:0!important;
}
.loop-payment-section .loop-money-payment-inline,
.loop-payment-section .loop-tip-payment-inline{
  border:0!important;
  background:transparent!important;
  padding:0!important;
  min-height:34px!important;
}
.loop-payment-section .loop-tip-payment-field .loop-tip-payment-inline{
  grid-template-columns:minmax(0,1fr)!important;
}
.loop-payment-section .loop-tip-payment-field .loop-mini-select{
  display:none!important;
}
.loop-payment-editor-modal .loop-settings-modal-body{
  padding:14px 18px 16px!important;
}
.loop-payment-editor-grid-simple{
  display:grid!important;
  grid-template-columns:1fr!important;
  gap:10px!important;
}
.loop-payment-editor-grid-simple .booking-pro-field{
  display:grid!important;
  grid-template-columns:76px minmax(0,1fr)!important;
  align-items:center!important;
  gap:10px!important;
  min-height:48px!important;
  border:1px solid #e6edf3!important;
  border-radius:14px!important;
  padding:8px 12px!important;
  background:#fff!important;
}
.loop-payment-editor-grid-simple .booking-pro-field > span{
  margin:0!important;
  color:#64748b!important;
  font-size:12px!important;
  font-weight:950!important;
  white-space:nowrap!important;
}
.loop-payment-editor-grid-simple .booking-pro-field input,
.loop-payment-editor-grid-simple .loop-mini-select-trigger{
  min-height:32px!important;
  height:32px!important;
  border:0!important;
  background:transparent!important;
  box-shadow:none!important;
  padding:0 22px 0 0!important;
  border-radius:0!important;
  font-size:14px!important;
  font-weight:900!important;
}
.loop-payment-editor-grid-simple .loop-mini-select-trigger span{
  font-size:14px!important;
  font-weight:900!important;
}
.loop-payment-editor-grid-simple .loop-mini-select-trigger i{
  right:0!important;
}
.loop-payment-editor-grid-simple .loop-payment-modal-money-input,
.loop-payment-editor-grid-simple .loop-tip-payment-inline{
  border:0!important;
  background:transparent!important;
  padding:0!important;
  min-height:32px!important;
}
.loop-payment-editor-grid-simple .loop-payment-tip-field{
  gap:10px!important;
}
.loop-payment-editor-grid-simple .loop-payment-tip-field .loop-tip-method-override-row{
  grid-column:2!important;
  margin-top:4px!important;
}
.loop-payment-simple-commission-row{
  min-height:42px!important;
  border-radius:14px!important;
  padding:0 14px!important;
}
.loop-payment-simple-commission-row span{
  font-size:14px!important;
  font-weight:850!important;
}
.loop-payment-simple-commission-row button{
  color:#00675f!important;
  font-size:13px!important;
  font-weight:950!important;
}
@media(max-width:760px){
  .loop-payment-section-headline{
    justify-content:flex-start!important;
  }
  .loop-payment-title-left{
    gap:8px!important;
  }
  .loop-payment-title-left > strong{
    font-size:14px!important;
  }
  .loop-payment-title-actions-inline{
    width:auto!important;
    margin-left:8px!important;
  }
  .loop-payment-section .loop-payment-slim-main{
    grid-template-columns:1fr!important;
  }
  .loop-payment-section .loop-payment-slim-field{
    grid-template-columns:76px minmax(0,1fr)!important;
  }
}


/* LOOP BOOKING SERVICE/DATE VISUAL V65 — fixed labels stronger, selected values lighter. */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-ordered-label{
  color:#536174!important;
  font-size:10px!important;
  line-height:1!important;
  font-weight:950!important;
  letter-spacing:.13em!important;
  text-transform:uppercase!important;
  font-family:inherit!important;
}

.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-ordered-service-trigger strong,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-ordered-info-row strong{
  color:#111827!important;
  font-size:13.25px!important;
  line-height:1.18!important;
  font-weight:650!important;
  letter-spacing:-.012em!important;
}

.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-ordered-duration-control input{
  color:#111827!important;
  font-size:13.25px!important;
  font-weight:650!important;
  letter-spacing:-.01em!important;
}

.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-ordered-duration-control em{
  color:#00675f!important;
  font-size:10.5px!important;
  font-weight:760!important;
  letter-spacing:.01em!important;
}

.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-mode-mini-pill{
  color:#334155!important;
  font-size:10px!important;
  font-weight:650!important;
  letter-spacing:-.005em!important;
  background:#f8fafc!important;
  border-color:#dbe7ef!important;
}

.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-ordered-service-trigger em,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-ordered-date-change,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-ordered-availability{
  font-weight:850!important;
}

@media(max-width:980px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-ordered-label{
    font-size:9.6px!important;
    letter-spacing:.125em!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-ordered-service-trigger strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-ordered-info-row strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section .loop-ordered-duration-control input{
    font-size:12.8px!important;
    font-weight:650!important;
  }
}



/* LOOP CUSTOMER PICKER MOBILE LAYERS V94 — sane layer contract.
   Customer status badges should never create a mega stacking context.
   Customer dropdown can float above cards, but all mini-detail dialogs win. */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section{
  position:relative!important;
  z-index:60!important;
  isolation:auto!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section:focus-within,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section:has(.lcp-menu),
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-section:has(.lcp-input-shell.is-open){
  z-index:160!important;
  isolation:auto!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search{
  position:relative!important;
  z-index:161!important;
  isolation:auto!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .loop-customer-picker,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search > *{
  position:relative!important;
  z-index:162!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-menu{
  z-index:170!important;
  max-height:min(300px, 42dvh)!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-note.loop-customer-status-badges,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badges,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badges .loop-customer-status-badge{
  position:relative!important;
  z-index:0!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-mini-detail-backdrop{
  z-index:2147483600!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-mini-detail-card{
  position:relative!important;
  z-index:1!important;
}
@media(max-width:760px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-menu{
    max-height:min(38dvh, 260px)!important;
    border-radius:15px!important;
    box-shadow:0 14px 36px rgba(15,23,42,.16)!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-use-new{
    grid-template-columns:24px minmax(0,1fr)!important;
    gap:8px!important;
    min-height:42px!important;
    padding:8px 10px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-use-new-plus{
    width:24px!important;
    height:24px!important;
    font-size:14px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-use-new strong{
    font-size:11.5px!important;
    line-height:1.1!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-use-new small{
    display:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-menu-head{
    position:static!important;
    min-height:24px!important;
    padding:5px 10px!important;
    font-size:8.5px!important;
    line-height:1!important;
    letter-spacing:.055em!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-row{
    grid-template-columns:28px minmax(0,1fr)!important;
    gap:8px!important;
    min-height:46px!important;
    padding:7px 10px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-avatar{
    width:28px!important;
    height:28px!important;
    font-size:10px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-main strong{
    font-size:12px!important;
    line-height:1.12!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-main small{
    font-size:10px!important;
    line-height:1.14!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-whatsapp-badge,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-platform-badge,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-stats{
    display:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-show-more{
    min-height:34px!important;
    padding:7px 10px!important;
    font-size:10.5px!important;
  }
}
@media(max-width:380px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-menu{
    max-height:min(36dvh, 235px)!important;
  }
}


/* LOOP BOOKING SERVICE SUMMARY V53 — quieter service/date card */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section{
  padding:14px 18px 16px!important;
  overflow:visible!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-shell.loop-booking-summary-v53{
  display:grid!important;
  grid-template-columns:1fr!important;
  gap:12px!important;
  overflow:visible!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-card{
  position:relative!important;
  display:grid!important;
  grid-template-columns:42px minmax(0,1fr)!important;
  column-gap:16px!important;
  align-items:start!important;
  min-width:0!important;
  min-height:126px!important;
  padding:20px 22px!important;
  border:1px solid rgba(205,222,232,.88)!important;
  border-radius:24px!important;
  background:linear-gradient(180deg, rgba(255,255,255,.96), rgba(255,255,255,.92))!important;
  box-shadow:0 16px 42px rgba(15,23,42,.075)!important;
  overflow:visible!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-step,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-workspace-step.loop-summary-step{
  width:36px!important;
  height:36px!important;
  min-width:36px!important;
  border-radius:999px!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  border:1px solid rgba(20,184,166,.24)!important;
  background:linear-gradient(180deg, rgba(220,252,245,.76), rgba(236,253,245,.62))!important;
  color:#00756c!important;
  font-size:13px!important;
  font-weight:950!important;
  box-shadow:0 6px 16px rgba(20,184,166,.08)!important;
  margin-top:2px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-main{
  min-width:0!important;
  display:grid!important;
  gap:10px!important;
  align-content:start!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-topline{
  display:flex!important;
  align-items:center!important;
  justify-content:space-between!important;
  gap:14px!important;
  min-width:0!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-title{
  display:block!important;
  color:#0f1b3d!important;
  font-size:13px!important;
  line-height:1!important;
  font-weight:950!important;
  letter-spacing:.14em!important;
  text-transform:uppercase!important;
  white-space:nowrap!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-change-btn{
  flex:0 0 auto!important;
  min-height:34px!important;
  padding:0 16px!important;
  border-radius:999px!important;
  border:1px solid rgba(20,184,166,.42)!important;
  background:#fff!important;
  color:#00756c!important;
  font-size:11.5px!important;
  line-height:1!important;
  font-weight:900!important;
  cursor:pointer!important;
  box-shadow:0 8px 20px rgba(15,23,42,.05)!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-change-btn:hover{
  background:#f0fdfa!important;
  transform:translateY(-1px)!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-service-picker{
  position:relative!important;
  z-index:60!important;
  width:100%!important;
  min-width:0!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-service-picker.is-open{
  z-index:260!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-primary-value,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-ordered-service-trigger.loop-summary-primary-value{
  width:100%!important;
  min-height:auto!important;
  padding:0!important;
  border:0!important;
  border-radius:0!important;
  background:transparent!important;
  box-shadow:none!important;
  display:flex!important;
  align-items:center!important;
  justify-content:flex-start!important;
  text-align:left!important;
  cursor:pointer!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-primary-value strong,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-ordered-service-trigger.loop-summary-primary-value strong{
  min-width:0!important;
  max-width:100%!important;
  color:#475569!important;
  font-size:24px!important;
  line-height:1.12!important;
  font-weight:520!important;
  letter-spacing:-.03em!important;
  white-space:normal!important;
  overflow:visible!important;
  text-overflow:clip!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip{
  justify-self:start!important;
  min-height:34px!important;
  width:max-content!important;
  display:inline-flex!important;
  align-items:center!important;
  gap:7px!important;
  padding:0 13px!important;
  border-radius:999px!important;
  border:1px solid rgba(20,184,166,.26)!important;
  background:rgba(240,253,250,.78)!important;
  color:#475569!important;
  box-shadow:inset 0 1px 0 rgba(255,255,255,.9)!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-chip-icon{
  width:18px!important;
  height:18px!important;
  display:inline-flex!important;
  align-items:center!important;
  justify-content:center!important;
  color:#00756c!important;
  font-size:16px!important;
  line-height:1!important;
  font-weight:900!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip input{
  width:36px!important;
  border:0!important;
  outline:0!important;
  padding:0!important;
  background:transparent!important;
  color:#475569!important;
  font-size:15px!important;
  line-height:1!important;
  font-weight:650!important;
  text-align:right!important;
  appearance:textfield!important;
  -moz-appearance:textfield!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip input::-webkit-outer-spin-button,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip input::-webkit-inner-spin-button{
  -webkit-appearance:none!important;
  margin:0!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip em{
  color:#475569!important;
  font-size:15px!important;
  line-height:1!important;
  font-weight:650!important;
  font-style:normal!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-date-value{
  min-width:0!important;
  display:block!important;
  color:#475569!important;
  font-size:22px!important;
  line-height:1.18!important;
  font-weight:520!important;
  letter-spacing:-.03em!important;
  white-space:normal!important;
  overflow:visible!important;
  text-overflow:clip!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-meta-row{
  display:flex!important;
  align-items:center!important;
  flex-wrap:wrap!important;
  gap:8px!important;
  min-width:0!important;
  color:#8290a3!important;
  font-size:14.5px!important;
  line-height:1.2!important;
  font-weight:520!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-meta-row span{
  min-width:0!important;
  max-width:100%!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-meta-row i{
  width:4px!important;
  height:4px!important;
  border-radius:999px!important;
  background:rgba(20,184,166,.44)!important;
  flex:0 0 auto!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-availability,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-availability-mini-pill.loop-summary-availability{
  position:static!important;
  justify-self:start!important;
  width:max-content!important;
  min-height:32px!important;
  margin-top:2px!important;
  padding:0 13px!important;
  border-radius:999px!important;
  border:1px solid rgba(20,184,166,.18)!important;
  background:rgba(220,252,245,.82)!important;
  color:#00756c!important;
  font-size:13px!important;
  line-height:1!important;
  font-weight:760!important;
  font-style:normal!important;
  letter-spacing:-.01em!important;
  box-shadow:none!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-service-menu,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-ordered-service-menu.loop-summary-service-menu{
  position:absolute!important;
  top:calc(100% + 10px)!important;
  left:0!important;
  right:0!important;
  z-index:99999!important;
  max-height:min(360px, 52vh)!important;
  border-radius:18px!important;
  overflow:hidden!important;
  box-shadow:0 24px 60px rgba(15,23,42,.18)!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-ordered-service-trigger em,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-ordered-step-spacer,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-ordered-divider{
  display:none!important;
}
@media(max-width:760px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section{
    padding:12px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-shell.loop-booking-summary-v53{
    gap:10px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-card{
    grid-template-columns:34px minmax(0,1fr)!important;
    column-gap:11px!important;
    min-height:auto!important;
    padding:15px!important;
    border-radius:20px!important;
    box-shadow:0 12px 30px rgba(15,23,42,.065)!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-step,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-workspace-step.loop-summary-step{
    width:30px!important;
    height:30px!important;
    min-width:30px!important;
    font-size:11.5px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-main{
    gap:7px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-topline{
    gap:8px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-title{
    font-size:10.5px!important;
    letter-spacing:.125em!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-change-btn{
    min-height:30px!important;
    padding:0 12px!important;
    font-size:10.5px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-primary-value strong{
    font-size:18px!important;
    line-height:1.12!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-date-value{
    font-size:17px!important;
    line-height:1.18!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-meta-row{
    gap:6px!important;
    font-size:12.5px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip{
    min-height:30px!important;
    padding:0 10px!important;
    gap:5px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip input,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip em{
    font-size:13px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-availability{
    min-height:28px!important;
    padding:0 10px!important;
    font-size:11.5px!important;
  }
}
@media(max-width:380px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-card{
    grid-template-columns:30px minmax(0,1fr)!important;
    column-gap:9px!important;
    padding:13px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-change-btn{
    min-height:28px!important;
    padding:0 10px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-primary-value strong{
    font-size:16.5px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-date-value{
    font-size:15.5px!important;
  }
}


/* LOOP BOOKING SERVICE SUMMARY V53.1 — compact right rail, same look with lower height */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section{
  padding:10px 14px 12px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-shell.loop-booking-summary-v53{
  gap:8px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-card{
  grid-template-columns:30px minmax(0,1fr)!important;
  column-gap:12px!important;
  min-height:unset!important;
  padding:12px 16px!important;
  border-radius:19px!important;
  box-shadow:0 10px 28px rgba(15,23,42,.055)!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-service-card{
  padding-top:12px!important;
  padding-bottom:12px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-agenda-card{
  padding-top:11px!important;
  padding-bottom:12px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-step,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-workspace-step.loop-summary-step{
  width:28px!important;
  height:28px!important;
  min-width:28px!important;
  font-size:11px!important;
  margin-top:0!important;
  box-shadow:0 4px 12px rgba(20,184,166,.06)!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-main{
  gap:5px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-topline{
  min-height:28px!important;
  gap:8px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-title{
  font-size:10.5px!important;
  line-height:1!important;
  letter-spacing:.18em!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-change-btn{
  min-height:28px!important;
  padding:0 12px!important;
  border-radius:999px!important;
  font-size:10.5px!important;
  line-height:1!important;
  font-weight:850!important;
  box-shadow:0 8px 20px rgba(20,184,166,.055)!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-primary-value,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-ordered-service-trigger.loop-summary-primary-value{
  min-height:20px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-primary-value strong,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-ordered-service-trigger.loop-summary-primary-value strong{
  font-size:18px!important;
  line-height:1.08!important;
  font-weight:540!important;
  letter-spacing:-.025em!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip{
  min-height:26px!important;
  padding:0 9px!important;
  gap:5px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-chip-icon{
  width:15px!important;
  height:15px!important;
  font-size:13px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip input{
  width:31px!important;
  font-size:12.5px!important;
  font-weight:720!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip em{
  font-size:12.5px!important;
  font-weight:720!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-date-value{
  font-size:16.5px!important;
  line-height:1.13!important;
  font-weight:540!important;
  letter-spacing:-.025em!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-meta-row{
  gap:5px!important;
  font-size:12px!important;
  line-height:1.12!important;
  font-weight:560!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-meta-row i{
  width:3px!important;
  height:3px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-availability,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-availability-mini-pill.loop-summary-availability{
  min-height:25px!important;
  margin-top:1px!important;
  padding:0 10px!important;
  font-size:11px!important;
  font-weight:780!important;
}
@media(max-width:760px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section{
    padding:10px 10px 11px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-shell.loop-booking-summary-v53{
    gap:8px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-card{
    grid-template-columns:28px minmax(0,1fr)!important;
    column-gap:10px!important;
    padding:11px 12px!important;
    border-radius:18px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-step,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-workspace-step.loop-summary-step{
    width:26px!important;
    height:26px!important;
    min-width:26px!important;
    font-size:10px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-main{
    gap:5px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-title{
    font-size:10px!important;
    letter-spacing:.16em!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-change-btn{
    min-height:26px!important;
    padding:0 10px!important;
    font-size:10px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-primary-value strong{
    font-size:16px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-date-value{
    font-size:15px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-meta-row{
    font-size:11.5px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip{
    min-height:24px!important;
    padding:0 8px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip input,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip em{
    font-size:11.5px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-availability{
    min-height:23px!important;
    padding:0 9px!important;
    font-size:10.5px!important;
  }
}
@media(max-width:380px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-card{
    grid-template-columns:25px minmax(0,1fr)!important;
    column-gap:8px!important;
    padding:10px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-step,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-workspace-step.loop-summary-step{
    width:24px!important;
    height:24px!important;
    min-width:24px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-primary-value strong{
    font-size:15px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-date-value{
    font-size:14px!important;
  }
}


/* LOOP BOOKING MOBILE POPUP CONTRACT V100
   One clear rule for nested popups in create/edit booking:
   - customer/service menus never render as tiny clipped popups
   - date/time modal owns its own scroll area
   - sticky action bars do not cover form fields
   - mobile submit footer stays behind nested popups */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1{
  --loop-booking-overlay-safe-bottom:max(env(safe-area-inset-bottom,0px), 14px);
  --loop-booking-footer-h:76px;
}

/* Main mobile footer should never visually win over nested sheets/modals. */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1:has(.loop-datetime-modal),
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1:has(.loop-service-combobox-panel),
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1:has(.loop-mini-detail-backdrop),
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1:has(.loop-availability-modal){
  overflow:hidden!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1:has(.loop-datetime-modal) .loop-mobile-submit-footer,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1:has(.loop-service-combobox-panel) .loop-mobile-submit-footer,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1:has(.loop-mini-detail-backdrop) .loop-mobile-submit-footer,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1:has(.loop-availability-modal) .loop-mobile-submit-footer{
  opacity:0!important;
  pointer-events:none!important;
  transform:translateY(110%)!important;
}

/* Service selector: on phone/tablet it becomes a real bottom sheet, not a small clipped panel inside the card. */
@media(max-width:980px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-datetime-section,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-summary-service-card,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-summary-main,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-summary-service-picker{
    overflow:visible!important;
    isolation:auto!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-summary-service-picker.is-open{
    z-index:2147483600!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-service-menu,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-ordered-service-menu.loop-summary-service-menu,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-panel.loop-summary-service-menu,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-unified-service-menu.loop-summary-service-menu{
    position:fixed!important;
    left:12px!important;
    right:12px!important;
    top:auto!important;
    bottom:calc(12px + var(--loop-booking-overlay-safe-bottom))!important;
    width:auto!important;
    max-width:none!important;
    min-width:0!important;
    max-height:min(72dvh,560px)!important;
    min-height:min(280px,calc(100dvh - 160px))!important;
    display:flex!important;
    flex-direction:column!important;
    overflow:hidden!important;
    z-index:2147483647!important;
    border-radius:26px!important;
    border:1px solid #dbe7ef!important;
    background:#ffffff!important;
    box-shadow:0 -22px 70px rgba(15,23,42,.28)!important;
    padding:12px!important;
    transform:translateZ(0)!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-search{
    flex:0 0 auto!important;
    position:relative!important;
    z-index:2!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-list{
    flex:1 1 auto!important;
    min-height:0!important;
    max-height:none!important;
    overflow-y:auto!important;
    overflow-x:hidden!important;
    -webkit-overflow-scrolling:touch!important;
    overscroll-behavior:contain!important;
    padding:4px 1px 8px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-foot{
    flex:0 0 auto!important;
    margin-top:6px!important;
    padding:9px 4px 0!important;
    border-top:1px solid #eef2f7!important;
    background:#ffffff!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-service-combobox-option{
    min-height:52px!important;
    border-radius:16px!important;
    padding:10px 12px!important;
  }
}

/* Date/time editor: bottom sheet with a dedicated scroll body and safe sticky footer. */
@media(max-width:980px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-datetime-modal-backdrop{
    position:fixed!important;
    inset:0!important;
    z-index:2147483646!important;
    display:flex!important;
    align-items:flex-end!important;
    justify-content:center!important;
    padding:0!important;
    background:rgba(15,23,42,.48)!important;
    overflow:hidden!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-datetime-modal{
    width:100%!important;
    max-width:720px!important;
    height:auto!important;
    max-height:min(88dvh,760px)!important;
    margin:0!important;
    border-radius:28px 28px 0 0!important;
    display:flex!important;
    flex-direction:column!important;
    overflow:hidden!important;
    background:#ffffff!important;
    border:1px solid #dbe7ef!important;
    border-bottom:0!important;
    box-shadow:0 -26px 80px rgba(15,23,42,.28)!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-datetime-modal-head{
    flex:0 0 auto!important;
    position:relative!important;
    z-index:3!important;
    background:linear-gradient(180deg,#ffffff 0%,#fbfdff 100%)!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-datetime-modal-grid{
    flex:1 1 auto!important;
    min-height:0!important;
    max-height:none!important;
    overflow-y:auto!important;
    overflow-x:hidden!important;
    -webkit-overflow-scrolling:touch!important;
    overscroll-behavior:contain!important;
    padding-bottom:calc(var(--loop-booking-footer-h) + var(--loop-booking-overlay-safe-bottom) + 22px)!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-datetime-modal-foot{
    flex:0 0 auto!important;
    position:sticky!important;
    left:0!important;
    right:0!important;
    bottom:0!important;
    z-index:4!important;
    background:linear-gradient(180deg,rgba(255,255,255,.86) 0%,#ffffff 38%)!important;
    border-top:1px solid #e7edf3!important;
    padding:12px 18px calc(12px + var(--loop-booking-overlay-safe-bottom))!important;
    box-shadow:0 -18px 36px rgba(15,23,42,.08)!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-datetime-modal-foot .booking-pro-primary,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-datetime-modal-foot .booking-pro-secondary{
    min-height:48px!important;
  }
}

/* Any mini-detail popup uses the same readable safe area. */
@media(max-width:980px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-mini-detail-card,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-availability-modal{
    max-height:min(86dvh,720px)!important;
    display:flex!important;
    flex-direction:column!important;
    overflow:hidden!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-mini-detail-card > :not(.loop-mini-detail-head),
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-time-suggestions-modal{
    min-height:0!important;
    overflow-y:auto!important;
    -webkit-overflow-scrolling:touch!important;
  }
}



/* LOOP BOOKING MOBILE WORKER CUSTOMER + COMPACT UX V13
   Compact reservation creation on mobile, and keep the customer picker usable
   when the native keyboard is open. */
@media(max-width:760px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1{
    --loop-booking-keyboard-offset: var(--loop-booking-keyboard-offset, 0px);
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-minimal-head{
    grid-template-columns:minmax(0,1fr) 50px!important;
    padding:14px 16px 10px!important;
    gap:8px!important;
    min-height:0!important;
    border-bottom:1px solid #edf3f7!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-kicker{
    font-size:10px!important;
    letter-spacing:.18em!important;
    margin-bottom:4px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-edit-title-row h2{
    font-size:24px!important;
    line-height:1.02!important;
    letter-spacing:-.055em!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
    max-width:calc(100vw - 126px)!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-head-copy p{
    font-size:13px!important;
    line-height:1.2!important;
    margin-top:4px!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-close{
    width:46px!important;
    height:46px!important;
    min-width:46px!important;
    border-radius:999px!important;
    margin:0!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal{
    display:flex!important;
    gap:8px!important;
    margin:0!important;
    padding:10px 16px!important;
    overflow-x:auto!important;
    overflow-y:hidden!important;
    border-bottom:1px solid #edf3f7!important;
    scrollbar-width:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal::-webkit-scrollbar{display:none!important;}
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > div,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > button{
    flex:0 0 auto!important;
    min-width:112px!important;
    max-width:132px!important;
    min-height:48px!important;
    padding:8px 10px!important;
    border-radius:16px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal span{
    font-size:9px!important;
    letter-spacing:.14em!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal strong{
    font-size:13px!important;
    line-height:1.05!important;
    max-width:100%!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal em{
    display:none!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form{
    gap:10px!important;
    padding-bottom:calc(96px + env(safe-area-inset-bottom,0px) + var(--loop-booking-keyboard-offset,0px))!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-section.loop-create-section{
    padding:14px!important;
    border-radius:22px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-head{
    gap:10px!important;
    align-items:flex-start!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-head-actions{
    grid-template-columns:1fr 1fr!important;
    gap:8px!important;
    width:100%!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-new-customer-action{
    min-height:42px!important;
    height:42px!important;
    border-radius:15px!important;
    font-size:13px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search{
    position:relative!important;
    z-index:90!important;
    margin-top:10px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-fields{
    grid-template-columns:1fr 1fr!important;
    gap:8px!important;
    margin-top:8px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .booking-pro-field input,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-phone-field input{
    min-height:42px!important;
    height:42px!important;
    font-size:14px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badges{
    display:flex!important;
    gap:6px!important;
    margin-top:8px!important;
    overflow-x:auto!important;
    scrollbar-width:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badges::-webkit-scrollbar{display:none!important;}
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-status-badge{
    flex:0 0 auto!important;
    min-height:26px!important;
    padding:0 10px!important;
    font-size:10.5px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53{
    gap:10px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-card{
    min-height:0!important;
    padding:14px!important;
    border-radius:20px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-service-card .loop-summary-main{
    display:grid!important;
    grid-template-columns:minmax(0,1fr) auto!important;
    grid-template-areas:"top top" "service duration"!important;
    column-gap:10px!important;
    row-gap:8px!important;
    align-items:center!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-service-card .loop-summary-topline{grid-area:top!important;}
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-service-picker{grid-area:service!important;min-width:0!important;}
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-service-trigger{
    min-height:44px!important;
    padding:0!important;
    border:0!important;
    background:transparent!important;
    box-shadow:none!important;
    justify-content:flex-start!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-service-trigger strong{
    font-size:20px!important;
    line-height:1.08!important;
    font-weight:950!important;
    white-space:normal!important;
    overflow:visible!important;
    display:-webkit-box!important;
    -webkit-line-clamp:2!important;
    -webkit-box-orient:vertical!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip{
    grid-area:duration!important;
    width:auto!important;
    min-width:92px!important;
    height:44px!important;
    padding:0 12px!important;
    border-radius:18px!important;
    align-self:center!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-summary-v53 .loop-summary-duration-chip input{
    width:34px!important;
    font-size:14px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-summary-change-btn{
    min-height:34px!important;
    padding:0 12px!important;
    border-radius:999px!important;
    font-size:12px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-summary-date-value{
    font-size:17px!important;
    line-height:1.14!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-summary-meta-row{
    flex-wrap:wrap!important;
    gap:6px!important;
    font-size:11px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-mobile-submit-footer{
    min-height:calc(78px + env(safe-area-inset-bottom,0px))!important;
    padding:10px 16px calc(10px + env(safe-area-inset-bottom,0px))!important;
    transform:translateY(calc(-1 * min(var(--loop-booking-keyboard-offset,0px), 220px)))!important;
  }
}

@media(max-width:760px){
  body.loop-booking-keyboard-active .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-mobile-submit-footer{
    opacity:.97!important;
  }
  body.loop-booking-keyboard-active .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-customer-minimal-search .lcp-menu{
    max-height:min(34dvh,260px)!important;
  }
}


/* LOOP BOOKING UX V2 A2.1 — compact collection entry + flatter professional chrome */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal{
  display:flex!important;
  align-items:stretch!important;
  gap:0!important;
  margin:0!important;
  padding:8px 22px!important;
  background:#fff!important;
  border-top:0!important;
  border-bottom:1px solid #e7edf3!important;
  box-shadow:none!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > .loop-ops-item{
  min-width:0!important;
  min-height:48px!important;
  border:0!important;
  border-right:1px solid #e8eef3!important;
  border-radius:0!important;
  background:transparent!important;
  box-shadow:none!important;
  padding:7px 18px!important;
  transition:background .14s ease,color .14s ease!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > .loop-ops-item:first-child{padding-left:8px!important;}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > .loop-ops-item:last-child{border-right:0!important;}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > button.loop-ops-item:hover{background:#f8fbfb!important;}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-ops-item > span{
  color:#718096!important;font-size:9.5px!important;font-weight:900!important;letter-spacing:.10em!important;text-transform:uppercase!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-ops-item > strong{
  margin-top:3px!important;color:#0f172a!important;font-size:12.5px!important;line-height:1.18!important;font-weight:850!important;letter-spacing:-.01em!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-ops-item > em{
  margin-top:3px!important;color:#007568!important;font-size:10px!important;font-style:normal!important;font-weight:850!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-payment-ops-item{
  background:linear-gradient(180deg,rgba(240,253,250,.72),rgba(255,255,255,.92))!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-payment-ops-item.is-payment-status-pending{
  background:linear-gradient(180deg,rgba(255,251,235,.72),rgba(255,255,255,.94))!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-ops-item.is-select{min-height:48px!important;}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-ops-item.is-select > span{left:18px!important;top:9px!important;}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-trigger{
  border-radius:0!important;padding:0 36px 0 18px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-trigger span{
  left:18px!important;right:34px!important;top:27px!important;bottom:auto!important;font-size:12.5px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal .loop-ops-item.is-select .loop-mini-select-trigger i{
  right:10px!important;width:20px!important;height:20px!important;border-radius:7px!important;background:#f0fdfa!important;box-shadow:none!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-accordion-stack{margin-top:0!important;}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-editor-modal .loop-payment-simple-commission-row{
  margin-top:2px!important;padding-top:12px!important;border-top:1px solid #edf2f6!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-payment-editor-modal .loop-payment-simple-commission-row::before{
  content:"Opciones avanzadas";display:block;margin-right:auto;color:#94a3b8;font-size:9px;font-weight:900;letter-spacing:.10em;text-transform:uppercase;
}
@media(max-width:760px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal{padding:8px 12px!important;gap:0!important;}
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > .loop-ops-item{
    min-width:118px!important;max-width:150px!important;min-height:48px!important;border-radius:0!important;padding:7px 12px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal > .loop-ops-item:first-child{padding-left:12px!important;}
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-ops-minimal em{display:block!important;}
}


/* LOOP BOOKING UX V2 A2.1B — dedicated create quickbar; avoids legacy smart-chip collisions */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quickbar{
  width:auto!important;
  margin:0 28px!important;
  padding:12px 0!important;
  display:grid!important;
  grid-template-columns:minmax(210px,.72fr) minmax(320px,1.28fr)!important;
  gap:0!important;
  align-items:stretch!important;
  border-bottom:1px solid #e7edf3!important;
  background:#fff!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quickbar.has-membership{
  grid-template-columns:minmax(190px,.68fr) minmax(285px,1.12fr) minmax(220px,.8fr)!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-status,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-payment,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-membership{
  min-width:0!important;
  min-height:58px!important;
  border:0!important;
  border-right:1px solid #e6edf2!important;
  border-radius:0!important;
  background:#fff!important;
  box-shadow:none!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-membership{
  border-right:0!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-status{
  padding:8px 20px 7px 6px!important;
  display:grid!important;
  align-content:center!important;
  gap:5px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-status>span,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-payment span,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-membership span{
  display:block!important;
  margin:0!important;
  color:#718096!important;
  font-size:9.5px!important;
  line-height:1!important;
  font-weight:900!important;
  letter-spacing:.10em!important;
  text-transform:uppercase!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-status .loop-mini-select{
  position:relative!important;
  inset:auto!important;
  width:100%!important;
  min-height:28px!important;
  height:28px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-status .loop-mini-select-trigger{
  position:relative!important;
  inset:auto!important;
  width:100%!important;
  min-height:28px!important;
  height:28px!important;
  padding:0 34px 0 0!important;
  border:0!important;
  border-radius:0!important;
  background:transparent!important;
  box-shadow:none!important;
  display:flex!important;
  align-items:center!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-status .loop-mini-select-trigger span{
  position:static!important;
  inset:auto!important;
  margin:0!important;
  color:#0f172a!important;
  font-size:13.5px!important;
  font-weight:900!important;
  letter-spacing:-.01em!important;
  text-transform:none!important;
  overflow:visible!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-status .loop-mini-select-trigger i{
  position:absolute!important;
  right:4px!important;
  top:50%!important;
  width:22px!important;
  height:22px!important;
  transform:translateY(-50%)!important;
  border-radius:7px!important;
  background:#f0fdfa!important;
  box-shadow:none!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-status .loop-mini-select.is-open .loop-mini-select-trigger i{
  transform:translateY(-50%) rotate(180deg)!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-status .loop-mini-select-menu{
  top:calc(100% + 8px)!important;
  left:0!important;
  right:auto!important;
  min-width:210px!important;
  z-index:2147483000!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-payment,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-membership{
  appearance:none!important;
  padding:8px 18px!important;
  display:flex!important;
  align-items:center!important;
  justify-content:space-between!important;
  gap:14px!important;
  text-align:left!important;
  cursor:pointer!important;
  transition:background .15s ease!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-payment:hover,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-membership:hover{
  background:#f8fbfb!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-payment.is-payment-status-pending{
  background:linear-gradient(90deg,rgba(255,251,235,.72),#fff 58%)!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-payment strong,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-membership strong{
  display:block!important;
  margin-top:5px!important;
  color:#0f172a!important;
  font-size:13.5px!important;
  line-height:1.15!important;
  font-weight:900!important;
  letter-spacing:-.015em!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-payment em,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-membership em{
  flex:0 0 auto!important;
  border:1px solid #b9e8e1!important;
  border-radius:9px!important;
  background:#fff!important;
  color:#00675f!important;
  padding:7px 10px!important;
  font-size:10.5px!important;
  line-height:1!important;
  font-style:normal!important;
  font-weight:900!important;
  white-space:nowrap!important;
}
@media(max-width:760px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quickbar,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quickbar.has-membership{
    margin:0 14px!important;
    grid-template-columns:1fr!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-status,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-payment,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-membership{
    min-height:52px!important;
    border-right:0!important;
    border-bottom:1px solid #e8eef3!important;
    padding:8px 4px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-create-quick-membership{
    border-bottom:0!important;
  }
}


/* LOOP BOOKING UX V2 A2.1D — dedicated manage/edit quickbar */
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quickbar{
  width:auto!important;
  margin:0 28px!important;
  padding:12px 0!important;
  display:grid!important;
  grid-template-columns:minmax(190px,.72fr) minmax(300px,1.28fr) minmax(260px,.95fr)!important;
  gap:0!important;
  align-items:stretch!important;
  border-bottom:1px solid #e7edf3!important;
  background:#fff!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-status,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-payment{
  min-width:0!important;
  min-height:58px!important;
  border:0!important;
  border-right:1px solid #e6edf2!important;
  border-radius:0!important;
  background:#fff!important;
  box-shadow:none!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-status{
  padding:8px 20px 7px 6px!important;
  display:grid!important;
  align-content:center!important;
  gap:5px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-status>span,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-payment span,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-actions span{
  display:block!important;
  margin:0!important;
  color:#718096!important;
  font-size:9.5px!important;
  line-height:1!important;
  font-weight:900!important;
  letter-spacing:.10em!important;
  text-transform:uppercase!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-status .loop-mini-select{
  position:relative!important;
  inset:auto!important;
  width:100%!important;
  min-height:28px!important;
  height:28px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-status .loop-mini-select-trigger{
  position:relative!important;
  inset:auto!important;
  width:100%!important;
  min-height:28px!important;
  height:28px!important;
  padding:0 34px 0 0!important;
  border:0!important;
  border-radius:0!important;
  background:transparent!important;
  box-shadow:none!important;
  display:flex!important;
  align-items:center!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-status .loop-mini-select-trigger span{
  position:static!important;
  inset:auto!important;
  margin:0!important;
  color:#0f172a!important;
  font-size:13.5px!important;
  font-weight:900!important;
  letter-spacing:-.01em!important;
  text-transform:none!important;
  overflow:visible!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-status .loop-mini-select-trigger i{
  position:absolute!important;
  right:4px!important;
  top:50%!important;
  width:22px!important;
  height:22px!important;
  transform:translateY(-50%)!important;
  border-radius:7px!important;
  background:#f0fdfa!important;
  box-shadow:none!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-status .loop-mini-select.is-open .loop-mini-select-trigger i{
  transform:translateY(-50%) rotate(180deg)!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-status .loop-mini-select-menu{
  top:calc(100% + 8px)!important;
  left:0!important;
  right:auto!important;
  min-width:210px!important;
  z-index:2147483000!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-payment{
  appearance:none!important;
  padding:8px 18px!important;
  display:flex!important;
  align-items:center!important;
  justify-content:space-between!important;
  gap:14px!important;
  text-align:left!important;
  cursor:pointer!important;
  transition:background .15s ease!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-payment:hover{
  background:#f8fbfb!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-payment.is-payment-status-pending{
  background:linear-gradient(90deg,rgba(255,251,235,.72),#fff 58%)!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-payment strong{
  display:block!important;
  margin-top:5px!important;
  color:#0f172a!important;
  font-size:13.5px!important;
  line-height:1.15!important;
  font-weight:900!important;
  letter-spacing:-.015em!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-payment em{
  flex:0 0 auto!important;
  border:1px solid #b9e8e1!important;
  border-radius:9px!important;
  background:#fff!important;
  color:#00675f!important;
  padding:7px 10px!important;
  font-size:10.5px!important;
  line-height:1!important;
  font-style:normal!important;
  font-weight:900!important;
  white-space:nowrap!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-actions{
  min-width:0!important;
  display:grid!important;
  grid-template-columns:repeat(auto-fit,minmax(76px,1fr))!important;
  gap:6px!important;
  align-items:stretch!important;
  padding:6px 0 6px 14px!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-actions>button{
  min-width:0!important;
  min-height:46px!important;
  border:1px solid #e2e8f0!important;
  border-radius:10px!important;
  background:#fff!important;
  box-shadow:none!important;
  padding:7px 9px!important;
  text-align:left!important;
  cursor:pointer!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-actions>button:hover{
  border-color:#b9e8e1!important;
  background:#f8fbfb!important;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-actions strong{
  display:block!important;
  margin-top:5px!important;
  color:#0f172a!important;
  font-size:11.5px!important;
  line-height:1.15!important;
  font-weight:850!important;
  white-space:nowrap!important;
  overflow:hidden!important;
  text-overflow:ellipsis!important;
}

@media(max-width:900px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quickbar{
    grid-template-columns:1fr 1.2fr!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-actions{
    grid-column:1/-1!important;
    padding:8px 0 0!important;
    border-top:1px solid #e8eef3!important;
  }
}
@media(max-width:760px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quickbar{
    margin:0 14px!important;
    grid-template-columns:1fr!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-status,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-payment{
    min-height:52px!important;
    border-right:0!important;
    border-bottom:1px solid #e8eef3!important;
    padding:8px 4px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-booking-manage-quick-actions{
    grid-column:auto!important;
    grid-template-columns:1fr!important;
    padding:8px 0 0!important;
  }
}


/* LOOP BOOKING UX V2 A2.2B — Manage Reservation Workspace V2
   Edit mode defaults to a compact operational overview. The original full
   editor remains mounted and opens on demand, preserving all existing logic. */
@media(min-width:761px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-manage-overview-v2{
    height:auto!important;
    min-height:0!important;
    max-height:min(88vh,760px)!important;
    align-self:center!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-manage-overview-v2 .loop-create-pro-form.is-manage-overview-collapsed{
    display:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-manage-overview-v2 .loop-mobile-edit-summary{
    display:none!important;
  }
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-v2{
  display:grid;
  gap:14px;
  padding:16px 28px 22px;
  background:#fff;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-heading{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:16px;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-heading>div>span{
  display:block;
  color:#007568;
  font-size:9.5px;
  line-height:1;
  font-weight:950;
  letter-spacing:.11em;
  text-transform:uppercase;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-heading>div>strong{
  display:block;
  margin-top:5px;
  color:#0f172a;
  font-size:17px;
  line-height:1.1;
  font-weight:950;
  letter-spacing:-.025em;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-heading>button{
  min-height:36px;
  border:1px solid #b9e8e1;
  border-radius:10px;
  background:#fff;
  color:#00675f;
  padding:0 13px;
  font-size:11px;
  font-weight:950;
  cursor:pointer;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-grid{
  display:grid;
  grid-template-columns:1.05fr .95fr 1.15fr;
  gap:10px;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-card{
  min-width:0;
  min-height:132px;
  display:flex;
  flex-direction:column;
  border:1px solid #dce6ee;
  border-radius:15px;
  background:#fff;
  padding:14px 15px;
  box-shadow:0 8px 26px rgba(15,23,42,.035);
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-card-head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-card-head>span{
  color:#607089;
  font-size:9.5px;
  font-weight:950;
  letter-spacing:.10em;
  text-transform:uppercase;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-card-head>button{
  border:0;
  background:transparent;
  color:#007568;
  padding:3px 0;
  font-size:10.5px;
  font-weight:950;
  cursor:pointer;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-card>strong{
  display:block;
  margin-top:15px;
  color:#0f172a;
  font-size:16px;
  line-height:1.15;
  font-weight:950;
  letter-spacing:-.025em;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-card>small{
  display:block;
  margin-top:5px;
  color:#64748b;
  font-size:11px;
  line-height:1.25;
  font-weight:750;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-card-actions{
  margin-top:auto;
  padding-top:10px;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-card-actions>button{
  border:1px solid #e0e8ef;
  border-radius:9px;
  background:#f8fafc;
  color:#334155;
  padding:7px 9px;
  font-size:10.5px;
  font-weight:900;
  cursor:pointer;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-service-note{
  margin-top:auto;
  padding-top:10px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:10px;
  color:#64748b;
  font-size:10.5px;
  font-weight:800;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-service-note b{
  color:#0f172a;
  font-size:12px;
  font-weight:950;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-availability{
  align-self:flex-start;
  margin-top:auto;
  padding:5px 8px;
  border-radius:999px;
  background:#ecfdf5;
  color:#007568;
  font-size:9.5px;
  line-height:1;
  font-style:normal;
  font-weight:900;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-availability.is-error,
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-availability.error{
  background:#fff1f2;
  color:#b42318;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-actions{
  display:grid;
  grid-template-columns:repeat(4,minmax(0,1fr));
  gap:8px;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-actions>button{
  min-width:0;
  min-height:66px;
  display:grid;
  grid-template-columns:minmax(0,1fr) auto;
  grid-template-areas:"label action" "value action";
  align-content:center;
  column-gap:10px;
  border:1px solid #e0e8ef;
  border-radius:13px;
  background:#fbfcfd;
  padding:10px 12px;
  text-align:left;
  cursor:pointer;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-actions>button:hover{
  border-color:#b9e8e1;
  background:#f7fffd;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-actions>button>span{
  grid-area:label;
  color:#64748b;
  font-size:9px;
  font-weight:950;
  letter-spacing:.09em;
  text-transform:uppercase;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-actions>button>strong{
  grid-area:value;
  min-width:0;
  margin-top:5px;
  color:#0f172a;
  font-size:11.5px;
  line-height:1.2;
  font-weight:900;
  white-space:nowrap;
  overflow:hidden;
  text-overflow:ellipsis;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-actions>button>em{
  grid-area:action;
  align-self:center;
  color:#007568;
  font-size:10.5px;
  font-style:normal;
  font-weight:950;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-actions>button.is-primary-detail{
  border-color:#b9e8e1;
  background:#f0fdfa;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-editor-toolbar{
  grid-column:1/-1;
  display:flex;
  align-items:center;
  gap:14px;
  margin:0 0 2px;
  padding:0 0 12px;
  border-bottom:1px solid #e7edf3;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-editor-toolbar>button{
  border:1px solid #dbe5ee;
  border-radius:10px;
  background:#fff;
  color:#00675f;
  padding:8px 11px;
  font-size:10.5px;
  font-weight:950;
  cursor:pointer;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-editor-toolbar span{
  display:block;
  color:#64748b;
  font-size:9px;
  font-weight:950;
  letter-spacing:.10em;
  text-transform:uppercase;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-editor-toolbar strong{
  display:block;
  margin-top:3px;
  color:#0f172a;
  font-size:12px;
  font-weight:900;
}
.booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form.is-edit-mode:not(.is-manage-overview-collapsed){
  padding-top:14px!important;
}
@media(max-width:980px) and (min-width:761px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-grid{
    grid-template-columns:1fr 1fr;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-card.is-agenda{
    grid-column:1/-1;
    min-height:108px;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-actions{
    grid-template-columns:1fr 1fr;
  }
}
@media(max-width:760px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-manage-overview-v2{
    display:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1 .loop-create-pro-form.is-manage-overview-collapsed{
    display:grid!important;
  }
}


/* LOOP BOOKING UX V2 A2.4A — Create Reservation Workspace V3
   Create mode is a guided, content-driven workspace. Manage mode remains A2.2B. */
@media (min-width: 761px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3{
    height:auto!important;
    min-height:0!important;
    max-height:calc(100dvh - 44px)!important;
    background:#ffffff!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-minimal-head{
    padding:18px 24px 16px!important;
    min-height:0!important;
    background:#fff!important;
    border-bottom:1px solid #e8eef3!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-hero-icon{
    display:none!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .booking-pro-kicker{
    font-size:9.5px!important;
    letter-spacing:.16em!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .booking-edit-title-row{
    margin-top:5px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .booking-edit-title-row h2{
    font-size:25px!important;
    letter-spacing:-.045em!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-head-copy p{
    margin-top:5px!important;
    font-size:11.5px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-head-actions-minimal .booking-pro-primary{
    height:42px!important;
    min-width:122px!important;
    padding:0 18px!important;
    border-radius:11px!important;
    box-shadow:0 10px 24px rgba(0,103,95,.16)!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .booking-pro-close{
    width:40px!important;
    height:40px!important;
    border-radius:11px!important;
    box-shadow:none!important;
  }

  /* Status / collection stays easy to reach, but becomes a calm utility strip. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-create-quickbar-v3,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-create-quickbar-v3.has-membership{
    margin:0 24px!important;
    padding:7px 0!important;
    min-height:0!important;
    grid-template-columns:minmax(165px,.72fr) minmax(270px,1.28fr) minmax(190px,.82fr)!important;
    border-bottom:1px solid #e8eef3!important;
    background:#fff!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-create-quickbar-v3:not(.has-membership){
    grid-template-columns:minmax(165px,.72fr) minmax(300px,1.35fr)!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-create-quick-status,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-create-quick-payment,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-create-quick-membership{
    min-height:48px!important;
    padding-top:5px!important;
    padding-bottom:5px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-create-quick-status{
    padding-left:4px!important;
    padding-right:16px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-create-quick-payment{
    background:linear-gradient(90deg,rgba(255,251,235,.58),#fff 55%)!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-create-quick-payment strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-create-quick-membership strong{
    margin-top:3px!important;
    font-size:12.5px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-create-quick-payment em,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-booking-create-quick-membership em{
    border-radius:8px!important;
    padding:6px 9px!important;
    font-size:10px!important;
  }

  /* Small orientation row: tells the user what matters without another card. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-flow-heading-v3{
    width:min(100%,1040px)!important;
    margin:0 auto!important;
    padding:15px 0 10px!important;
    display:flex!important;
    align-items:end!important;
    justify-content:space-between!important;
    gap:20px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-flow-heading-v3>div{
    display:grid!important;
    gap:3px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-flow-heading-v3 span,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-optional-heading-v3 span{
    color:#007568!important;
    font-size:9px!important;
    line-height:1!important;
    font-weight:950!important;
    letter-spacing:.13em!important;
    text-transform:uppercase!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-flow-heading-v3 strong{
    color:#0f172a!important;
    font-size:16px!important;
    line-height:1.1!important;
    font-weight:950!important;
    letter-spacing:-.025em!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-flow-heading-v3>small{
    color:#64748b!important;
    font-size:11px!important;
    font-weight:800!important;
    text-align:right!important;
    white-space:nowrap!important;
  }

  /* Main create content: tighter, consistent and content-driven. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-pro-form.is-create-mode{
    display:block!important;
    padding:0 24px 18px!important;
    overflow-y:auto!important;
    max-height:calc(100dvh - 225px)!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-pro-form.is-create-mode .loop-create-pro-left{
    width:min(100%,1040px)!important;
    max-width:1040px!important;
    margin:0 auto!important;
    display:grid!important;
    grid-template-columns:minmax(0,1.06fr) minmax(0,.94fr)!important;
    grid-template-areas:
      "client details"
      "optional optional"!important;
    gap:12px 14px!important;
    align-items:stretch!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-client-card-v3{
    grid-area:client!important;
    min-height:0!important;
    height:auto!important;
    padding:15px!important;
    border:1px solid #e2e8f0!important;
    border-radius:15px!important;
    background:#fff!important;
    box-shadow:none!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-service-agenda-card-v3{
    grid-area:details!important;
    min-height:0!important;
    height:auto!important;
    padding:10px!important;
    border:1px solid #e2e8f0!important;
    border-radius:15px!important;
    background:#fbfcfd!important;
    box-shadow:none!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-customer-minimal-head{
    margin:0 0 10px!important;
    min-height:30px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-customer-minimal-head small{
    display:none!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-workspace-title-inline{
    gap:8px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-workspace-step{
    width:24px!important;
    height:24px!important;
    flex:0 0 24px!important;
    font-size:10px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-workspace-title-inline strong{
    font-size:15px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-new-customer-action{
    min-height:30px!important;
    height:30px!important;
    padding:0 10px!important;
    border-radius:8px!important;
    font-size:10.5px!important;
    box-shadow:none!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-customer-minimal-search{
    margin-top:0!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-customer-minimal-search input{
    min-height:42px!important;
    height:42px!important;
    border-radius:11px!important;
    font-size:13px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-customer-status-badges{
    margin-top:9px!important;
    gap:6px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-customer-status-badge{
    min-height:22px!important;
    padding:0 8px!important;
    border-radius:7px!important;
    font-size:9.5px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-service-datetime-shell{
    gap:8px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-summary-card{
    min-height:0!important;
    border-radius:12px!important;
    padding:11px 12px!important;
    border:1px solid #e2e8f0!important;
    background:#fff!important;
    box-shadow:none!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-summary-service-card{
    min-height:82px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-summary-agenda-card{
    min-height:96px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-summary-title{
    font-size:9.5px!important;
    letter-spacing:.13em!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-summary-main>strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-summary-date-value{
    margin-top:5px!important;
    font-size:14px!important;
    line-height:1.15!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-summary-meta-row{
    margin-top:4px!important;
    font-size:10px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-summary-change-btn{
    min-height:28px!important;
    height:28px!important;
    border-radius:8px!important;
    padding:0 9px!important;
    font-size:10px!important;
    box-shadow:none!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-summary-availability{
    margin-top:5px!important;
    min-height:22px!important;
    border-radius:7px!important;
    padding:0 8px!important;
    font-size:9.5px!important;
  }

  /* Optional content reads as one secondary zone rather than two primary cards. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-optional-heading-v3{
    grid-area:optional!important;
    display:flex!important;
    align-items:center!important;
    gap:8px!important;
    margin:1px 0 -5px!important;
    padding:6px 2px 0!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-optional-heading-v3 small{
    color:#94a3b8!important;
    font-size:10.5px!important;
    font-weight:750!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-options-v3{
    grid-area:optional!important;
    margin-top:24px!important;
    padding:0!important;
    border:0!important;
    background:transparent!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-post-payment-actions-row{
    grid-template-columns:1fr 1fr!important;
    gap:8px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-clean-accordion{
    border:1px solid #e2e8f0!important;
    border-radius:11px!important;
    background:#fff!important;
    box-shadow:none!important;
    overflow:hidden!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-clean-accordion-row{
    min-height:43px!important;
    padding:8px 10px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-accordion-left strong{
    font-size:11.5px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-accordion-left small{
    font-size:9.5px!important;
    color:#94a3b8!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-clean-accordion-row>em{
    border:0!important;
    background:#f8fafc!important;
    border-radius:7px!important;
    padding:5px 7px!important;
    font-size:9.5px!important;
  }
}

@media (max-width: 980px) and (min-width: 761px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-pro-form.is-create-mode .loop-create-pro-left{
    grid-template-columns:1fr!important;
    grid-template-areas:
      "client"
      "details"
      "optional"!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-flow-heading-v3{
    align-items:flex-start!important;
    flex-direction:column!important;
    gap:5px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-flow-heading-v3>small{
    text-align:left!important;
  }
}

@media (max-width: 760px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-flow-heading-v3{
    display:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-optional-heading-v3{
    margin:0!important;
  }
}


/* LOOP BOOKING UX V2 A2.4B — Create Workspace polish
   Remove the awkward nested desktop scrollbar and stop the client card
   from stretching to the height of Service + Agenda. */
@media (min-width: 761px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3{
    overflow-y:auto!important;
    overflow-x:hidden!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-pro-form.is-create-mode{
    max-height:none!important;
    overflow:visible!important;
    padding-bottom:14px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-pro-form.is-create-mode .loop-create-pro-left{
    align-items:start!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-client-card-v3,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-service-agenda-card-v3{
    align-self:start!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-client-card-v3{
    min-height:0!important;
    padding-bottom:13px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-optional-heading-v3{
    margin-top:-2px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v3 .loop-create-options-v3{
    margin-top:20px!important;
  }
}


/* LOOP BOOKING UX V2 A2.5 — CREATE RESERVATION WORKSPACE V4
   One visual surface, three equal operational columns, dynamic customer state,
   secondary options, and a single end-of-flow CTA. */
@media (min-width:761px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4{
    width:min(1120px,calc(100vw - 64px))!important;
    max-height:calc(100dvh - 44px)!important;
    border-radius:22px!important;
    overflow-y:auto!important;
    overflow-x:hidden!important;
  }

  /* Header: title + close only. The primary CTA belongs at the end of the flow. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-booking-minimal-head{
    padding:17px 22px 14px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-header-submit-btn{
    display:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .booking-edit-title-row h2{
    font-size:24px!important;
  }

  /* Utility bar: visible, but not a "step". */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-booking-create-quickbar-v3,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-booking-create-quickbar-v3.has-membership{
    margin:0 22px!important;
    padding:5px 0!important;
    min-height:44px!important;
    border-bottom:1px solid #e7edf3!important;
    grid-template-columns:minmax(150px,.62fr) minmax(280px,1.38fr) minmax(180px,.78fr)!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-booking-create-quickbar-v3:not(.has-membership){
    grid-template-columns:minmax(150px,.62fr) minmax(310px,1.45fr)!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-booking-create-quick-status,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-booking-create-quick-payment,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-booking-create-quick-membership{
    min-height:42px!important;
    padding-top:4px!important;
    padding-bottom:4px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-booking-create-quick-status>span,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-booking-create-quick-payment span,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-booking-create-quick-membership span{
    font-size:8.5px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-booking-create-quick-payment strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-booking-create-quick-membership strong{
    font-size:12px!important;
  }

  /* V3 orientation copy is intentionally gone in V4. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-flow-heading-v3{
    display:none!important;
  }

  /* The form itself becomes ONE panel. No card-inside-card geometry. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-pro-form.is-create-mode{
    display:block!important;
    max-height:none!important;
    overflow:visible!important;
    padding:14px 22px 16px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-pro-form.is-create-mode .loop-create-pro-left{
    width:100%!important;
    max-width:none!important;
    margin:0!important;
    display:grid!important;
    grid-template-columns:repeat(3,minmax(0,1fr))!important;
    grid-template-areas:none!important;
    gap:0!important;
    align-items:stretch!important;
    border:1px solid #dfe7ee!important;
    border-radius:17px!important;
    background:#fff!important;
    overflow:visible!important;
    box-shadow:0 10px 30px rgba(15,23,42,.035)!important;
  }

  /* Column 1 — Customer */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-client-card-v3{
    grid-column:1!important;
    grid-row:1!important;
    min-width:0!important;
    min-height:188px!important;
    margin:0!important;
    padding:16px 16px 14px!important;
    border:0!important;
    border-right:1px solid #e5ebf0!important;
    border-radius:0!important;
    background:transparent!important;
    box-shadow:none!important;
    align-self:stretch!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-v4-column-head{
    min-height:38px!important;
    margin:0 0 13px!important;
    align-items:flex-start!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-workspace-title-inline{
    align-items:flex-start!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-v4-step-copy{
    display:grid!important;
    gap:4px!important;
    min-width:0!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-v4-step-copy>span{
    color:#64748b!important;
    font-size:9px!important;
    line-height:1!important;
    font-weight:950!important;
    letter-spacing:.13em!important;
    text-transform:uppercase!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-v4-step-copy>strong{
    color:#0f172a!important;
    font-size:14px!important;
    line-height:1.15!important;
    font-weight:950!important;
    letter-spacing:-.02em!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-workspace-step{
    width:24px!important;
    height:24px!important;
    flex:0 0 24px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-customer-head-actions{
    gap:5px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-new-customer-action{
    height:28px!important;
    min-height:28px!important;
    padding:0 8px!important;
    border-radius:8px!important;
    font-size:9.5px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-new-customer-action.is-secondary{
    border-color:#e2e8f0!important;
    color:#475569!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-customer-minimal-search{
    margin:0!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-customer-minimal-search input{
    height:39px!important;
    min-height:39px!important;
    border-radius:10px!important;
    background:#fbfcfd!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-customer-minimal-fields{
    margin-top:8px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-customer-status-badges{
    margin-top:10px!important;
  }

  /* Saved customer: search/contact inputs collapse automatically. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-client-card-v3.is-selected-v4 .loop-customer-minimal-search,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-client-card-v3.is-selected-v4 .loop-customer-minimal-fields{
    display:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-client-card-v3.is-selected-v4{
    min-height:146px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-client-card-v3.is-selected-v4 .loop-customer-status-badges{
    margin-top:18px!important;
  }

  /* Columns 2 + 3 — Service and Agenda live on the same surface. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-service-agenda-v4{
    grid-column:2 / 4!important;
    grid-row:1!important;
    min-width:0!important;
    margin:0!important;
    padding:0!important;
    border:0!important;
    border-radius:0!important;
    background:transparent!important;
    box-shadow:none!important;
    align-self:stretch!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-service-agenda-v4 .loop-service-datetime-shell{
    min-height:100%!important;
    display:grid!important;
    grid-template-columns:repeat(2,minmax(0,1fr))!important;
    gap:0!important;
    align-items:stretch!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-service-agenda-v4 .loop-summary-card{
    min-width:0!important;
    min-height:188px!important;
    margin:0!important;
    padding:16px!important;
    border:0!important;
    border-radius:0!important;
    background:transparent!important;
    box-shadow:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-service-agenda-v4 .loop-summary-agenda-card{
    border-left:1px solid #e5ebf0!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-summary-topline{
    min-height:28px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-summary-title{
    font-size:9px!important;
    letter-spacing:.13em!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-summary-main>strong,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-summary-date-value{
    margin-top:12px!important;
    font-size:15px!important;
    line-height:1.18!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-summary-change-btn{
    height:27px!important;
    min-height:27px!important;
    padding:0 8px!important;
    border-radius:8px!important;
    font-size:9.5px!important;
  }

  /* Schedule/location extras generated by the service section remain readable. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-service-agenda-v4>.loop-datetime-inline-warning,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-service-agenda-v4>.loop-location-create-zone,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-service-agenda-v4>.booking-event-options{
    margin:0 16px 14px!important;
  }

  /* Secondary zone spans the entire panel and feels like one lightweight footer. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-options-heading-v4{
    grid-column:1 / -1!important;
    grid-row:auto!important;
    min-height:34px!important;
    margin:0!important;
    padding:11px 14px 6px!important;
    border-top:1px solid #e5ebf0!important;
    display:flex!important;
    align-items:center!important;
    gap:8px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-options-heading-v4>span{
    color:#64748b!important;
    font-size:8.5px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-options-heading-v4>small{
    color:#94a3b8!important;
    font-size:9.5px!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-options-v3{
    grid-column:1 / -1!important;
    grid-row:auto!important;
    margin:0!important;
    padding:0 10px 10px!important;
    border:0!important;
    background:transparent!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-post-payment-actions-row{
    grid-template-columns:1fr 1fr!important;
    gap:7px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-clean-accordion{
    border:0!important;
    border-radius:9px!important;
    background:#f8fafc!important;
    box-shadow:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-clean-accordion-row{
    min-height:39px!important;
    padding:7px 9px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-accordion-left small{
    display:none!important;
  }

  /* End-of-flow summary + only primary create CTA on desktop. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-footer-v4{
    display:flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:18px!important;
    margin:12px 0 0!important;
    padding:0 2px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-footer-summary-v4{
    min-width:0!important;
    display:flex!important;
    align-items:center!important;
    gap:9px!important;
    color:#64748b!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-footer-summary-v4 span{
    font-size:11px!important;
    font-weight:800!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-footer-summary-v4 i{
    width:3px!important;
    height:3px!important;
    border-radius:999px!important;
    background:#cbd5e1!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-footer-summary-v4 strong{
    color:#0f172a!important;
    font-size:13px!important;
    font-weight:950!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-footer-summary-v4 small{
    color:#007568!important;
    font-size:10px!important;
    font-weight:850!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-footer-submit-v4{
    min-width:138px!important;
    min-height:40px!important;
    height:40px!important;
    border-radius:10px!important;
    padding:0 17px!important;
    box-shadow:0 8px 20px rgba(0,103,95,.14)!important;
  }

  /* Legacy right summary is not part of the V4 create workspace. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-summary-card{
    display:none!important;
  }
}

/* Tablet: same visual language, one column per row to avoid cramped inputs. */
@media (max-width:980px) and (min-width:761px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-pro-form.is-create-mode .loop-create-pro-left{
    grid-template-columns:1fr!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-client-card-v3,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-service-agenda-v4,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-options-heading-v4,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-options-v3{
    grid-column:1!important;
    grid-row:auto!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-client-card-v3{
    border-right:0!important;
    border-bottom:1px solid #e5ebf0!important;
  }
}

/* Mobile keeps the existing mobile-first controls and footer behavior. */
@media (max-width:760px){
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-create-footer-v4{
    display:none!important;
  }
}


/* LOOP BOOKING UX V2 A2.6 — scalable service chooser + explicit worker + agenda modal */
@media (min-width:761px){
  /* Service picker remains anchored, but it never grows beyond its own scroll body. */
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-summary-service-menu{
    max-height:min(430px,calc(100dvh - 150px))!important;
    overflow:hidden!important;
    display:flex!important;
    flex-direction:column!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-summary-service-menu .loop-service-combobox-search,
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-summary-service-menu .loop-service-combobox-foot{
    flex:0 0 auto!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-summary-service-menu .loop-service-combobox-list{
    flex:1 1 auto!important;
    min-height:0!important;
    max-height:min(300px,48dvh)!important;
    overflow-y:auto!important;
    overflow-x:hidden!important;
    overscroll-behavior:contain!important;
    -webkit-overflow-scrolling:touch!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-service-meta-v6{
    display:flex!important;
    align-items:center!important;
    gap:8px!important;
    margin-top:10px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-service-price-v6{
    color:#0f172a!important;
    font-size:11.5px!important;
    font-weight:950!important;
  }

  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-agenda-worker-v6{
    margin-top:12px!important;
    padding-top:10px!important;
    border-top:1px solid #edf2f6!important;
    display:flex!important;
    align-items:center!important;
    justify-content:space-between!important;
    gap:10px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-agenda-worker-v6>div{
    min-width:0!important;
    display:grid!important;
    gap:3px!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-agenda-worker-v6 span{
    color:#94a3b8!important;
    font-size:8.5px!important;
    font-weight:950!important;
    letter-spacing:.10em!important;
    text-transform:uppercase!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-agenda-worker-v6 strong{
    color:#0f172a!important;
    font-size:12.5px!important;
    font-weight:950!important;
    white-space:nowrap!important;
    overflow:hidden!important;
    text-overflow:ellipsis!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-agenda-worker-v6 button{
    flex:0 0 auto!important;
    min-height:27px!important;
    border:1px solid #b9e8e1!important;
    border-radius:8px!important;
    background:#fff!important;
    color:#00675f!important;
    padding:0 8px!important;
    font-size:9.5px!important;
    font-weight:950!important;
    cursor:pointer!important;
  }

  /* Agenda editor: professional information hierarchy. */
  .booking-pro-modal.loop-create-pro-modal .loop-datetime-modal{
    width:min(640px,calc(100vw - 36px))!important;
    max-height:calc(100dvh - 42px)!important;
    border-radius:22px!important;
    overflow:auto!important;
    background:#fff!important;
    box-shadow:0 30px 90px rgba(15,23,42,.26)!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-datetime-modal-head{
    padding:18px 20px 15px!important;
    border-bottom:1px solid #e8eef3!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-datetime-modal-head>div{
    display:grid!important;
    gap:3px!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-datetime-modal-head span{
    color:#007568!important;
    font-size:9px!important;
    font-weight:950!important;
    letter-spacing:.14em!important;
    text-transform:uppercase!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-datetime-modal-head strong{
    color:#0f172a!important;
    font-size:22px!important;
    line-height:1.05!important;
    font-weight:950!important;
    letter-spacing:-.035em!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-datetime-modal-head small{
    color:#64748b!important;
    font-size:11px!important;
    font-weight:800!important;
  }

  .booking-pro-modal.loop-create-pro-modal .loop-datetime-modal-grid{
    padding:16px 20px 0!important;
    display:grid!important;
    grid-template-columns:1fr 1fr!important;
    grid-template-areas:
      "worker worker"
      "date time"!important;
    gap:12px!important;
    align-items:start!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-worker-field-modern{
    grid-area:worker!important;
    padding:12px 14px!important;
    border:1px solid #e2e8f0!important;
    border-radius:13px!important;
    background:#fbfcfd!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-date-field-modern{
    grid-area:date!important;
    padding:12px 14px!important;
    border:1px solid #e2e8f0!important;
    border-radius:13px!important;
    background:#fff!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-time-format-field-modern{
    grid-area:time!important;
    padding:12px 14px!important;
    border:1px solid #e2e8f0!important;
    border-radius:13px!important;
    background:#fff!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-date-field-modern>span,
  .booking-pro-modal.loop-create-pro-modal .loop-time-field-top>span,
  .booking-pro-modal.loop-create-pro-modal .loop-worker-field-modern>span{
    display:block!important;
    margin-bottom:7px!important;
    color:#64748b!important;
    font-size:9px!important;
    font-weight:950!important;
    letter-spacing:.10em!important;
    text-transform:uppercase!important;
  }

  .booking-pro-modal.loop-create-pro-modal .loop-date-calendar-trigger{
    min-height:46px!important;
    height:46px!important;
    border:0!important;
    border-radius:10px!important;
    background:#f8fafc!important;
    box-shadow:none!important;
    padding:0 11px!important;
  }

  .booking-pro-modal.loop-create-pro-modal .loop-time12-control.loop-time12-control-modern{
    min-height:46px!important;
    height:46px!important;
    padding:0 6px!important;
    border:0!important;
    border-radius:10px!important;
    background:#f8fafc!important;
    box-shadow:none!important;
    grid-template-columns:minmax(0,1fr) auto minmax(0,1fr) minmax(64px,.82fr)!important;
    gap:0!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select-trigger{
    min-height:40px!important;
    height:40px!important;
    border:0!important;
    background:transparent!important;
    box-shadow:none!important;
    border-radius:8px!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select-trigger span{
    position:static!important;
    color:#0f172a!important;
    font-size:15px!important;
    font-weight:950!important;
    text-align:center!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-time12-control-modern .loop-mini-select-trigger i{
    display:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-time12-separator{
    color:#94a3b8!important;
    font-size:15px!important;
  }

  .booking-pro-modal.loop-create-pro-modal .loop-datetime-availability-below{
    margin-top:9px!important;
    min-height:44px!important;
    border-radius:10px!important;
    padding:8px 9px!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-datetime-availability-below>span{
    width:24px!important;
    height:24px!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-datetime-availability-below strong{
    font-size:11.5px!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-datetime-availability-below small{
    display:none!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-datetime-availability-link{
    min-height:28px!important;
    border-radius:8px!important;
    padding:0 8px!important;
    font-size:9.5px!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-time-format-helper{
    display:block!important;
    margin-top:7px!important;
    color:#94a3b8!important;
    font-size:9.5px!important;
    font-weight:750!important;
    line-height:1.3!important;
  }

  .booking-pro-modal.loop-create-pro-modal .loop-datetime-modal-mode-minimal{
    margin:12px 20px 0!important;
    padding:11px 13px!important;
    border:1px solid #e2e8f0!important;
    border-radius:12px!important;
    background:#f8fafc!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-datetime-modal-foot{
    padding:14px 20px 18px!important;
    border-top:0!important;
    gap:8px!important;
  }
  .booking-pro-modal.loop-create-pro-modal .loop-datetime-modal-foot button{
    min-height:40px!important;
    height:40px!important;
    border-radius:10px!important;
  }
}

@media(max-width:760px){
  .booking-pro-modal.loop-create-pro-modal .loop-datetime-modal-grid{
    grid-template-columns:1fr!important;
    grid-template-areas:
      "worker"
      "date"
      "time"!important;
  }
  .booking-pro-modal.loop-create-pro-modal.loop-reservation-workspace-v1.is-create-workspace-v4 .loop-agenda-worker-v6{
    align-items:flex-start!important;
  }
}

`;

